/*
@license
  Concept by RoarTheme (https://roartheme.co)
  Access unminified JS in assets/theme.js

  Use this event listener to run your own JS outside of this file.
  Documentation - https://roartheme.co/blogs/concept/javascript-events-for-developers

  document.addEventListener('page:loaded', function() {
    // Page has loaded and theme assets are ready
  });
*/

window.theme = window.theme || {};
window.Shopify = window.Shopify || {};

theme.config = {
  hasSessionStorage: true,
  hasLocalStorage: true,
  mqlSmall: false,
  mediaQuerySmall: 'screen and (max-width: 767px)',
  motionReduced: window.matchMedia('(prefers-reduced-motion: reduce)').matches,
  isTouch: ('ontouchstart' in window) || (navigator.MaxTouchPoints > 0) || (navigator.msMaxTouchPoints > 0),
  rtl: document.documentElement.getAttribute('dir') === 'rtl' ? true : false
};

theme.supportsPassive = false;

try {
  const opts = Object.defineProperty({}, 'passive', {
    get: function () {
      theme.supportsPassive = true;
    }
  });
  window.addEventListener('testPassive', null, opts);
  window.removeEventListener('testPassive', null, opts);
} catch (e) { }

document.documentElement.classList.add(theme.config.isTouch ? 'touch' : 'no-touch');
console.log(theme.settings.themeName + ' theme (' + theme.settings.themeVersion + ') by RoarTheme | Learn more at https://roartheme.co');

(function () {
  /*============================================================================
    Things that require DOM to be ready
  ==============================================================================*/
  theme.DOMready = function (callback) {
    if (document.readyState != 'loading') callback();
    else document.addEventListener('DOMContentLoaded', callback);
  }

  theme.headerGroup = {
    rounded: () => {
      return Array.from(document.querySelectorAll('.shopify-section-group-header-group .section--rounded'));
    },
    sections: () => {
      return Array.from(document.querySelectorAll('.shopify-section-group-header-group'));
    },
    init: () => {
      let previousSection = null;
      const rounded = theme.headerGroup.rounded();
      const sections = theme.headerGroup.sections();

      sections.forEach((shopifySection, index) => {
        const section = shopifySection.querySelector('.section');
        if (section) {
          section.classList.remove('section--next-rounded');
          section.classList.remove('section--first-rounded');
          section.classList.remove('section--last-rounded');

          if (section.classList.contains('section--rounded')) {
            if (index === 0) {
              section.classList.add('section--first-rounded');
            }

            if (index === rounded.length - 1) {
              section.classList.add('section--last-rounded');
            }

            if (previousSection && previousSection.classList.contains('section--rounded')) {
              previousSection.classList.add('section--next-rounded');
            }
          }

          if (index === sections.length - 1) {
            const nextSection = document.querySelector('.main-content .shopify-section:first-child .section');
            if (nextSection && nextSection.classList.contains('section--rounded')) {
              section.classList.add('section--next-rounded');
            }
          }

          previousSection = section;
        }
      });
    }
  };

  theme.a11y = {
    trapFocusHandlers: {},

    getFocusableElements: (container) => {
      return Array.from(
        container.querySelectorAll(
          'summary, a[href], button:enabled, [tabindex]:not([tabindex^="-"]), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe'
        )
      );
    },

    trapFocus: (container, elementToFocus = container) => {
      const elements = theme.a11y.getFocusableElements(container);
      const first = elements[0];
      const last = elements[elements.length - 1];

      theme.a11y.removeTrapFocus();

      theme.a11y.trapFocusHandlers.focusin = (event) => {
        if (event.target !== container && event.target !== last && event.target !== first) return;

        document.addEventListener('keydown', theme.a11y.trapFocusHandlers.keydown);
      };

      theme.a11y.trapFocusHandlers.focusout = function () {
        document.removeEventListener('keydown', theme.a11y.trapFocusHandlers.keydown);
      };

      theme.a11y.trapFocusHandlers.keydown = function (event) {
        if (event.code.toUpperCase() !== 'TAB') return; // If not TAB key
        // On the last focusable element and tab forward, focus the first element.
        if (event.target === last && !event.shiftKey) {
          event.preventDefault();
          first.focus();
        }

        //  On the first focusable element and tab backward, focus the last element.
        if ((event.target === container || event.target === first) && event.shiftKey) {
          event.preventDefault();
          last.focus();
        }
      };

      document.addEventListener('focusout', theme.a11y.trapFocusHandlers.focusout);
      document.addEventListener('focusin', theme.a11y.trapFocusHandlers.focusin);

      elementToFocus.focus();

      if (elementToFocus.tagName === 'INPUT' && ['search', 'text', 'email', 'url'].includes(elementToFocus.type) && elementToFocus.value) {
        elementToFocus.setSelectionRange(0, elementToFocus.value.length);
      }
    },

    removeTrapFocus: (elementToFocus = null) => {
      document.removeEventListener('focusin', theme.a11y.trapFocusHandlers.focusin);
      document.removeEventListener('focusout', theme.a11y.trapFocusHandlers.focusout);
      document.removeEventListener('keydown', theme.a11y.trapFocusHandlers.keydown);

      if (elementToFocus && typeof elementToFocus.focus === 'function') elementToFocus.focus();
    }
  };

  theme.utils = {
    throttle: (callback) => {
      let requestId = null, lastArgs;
      const later = (context) => () => {
        requestId = null;
        callback.apply(context, lastArgs);
      };
      const throttled = (...args) => {
        lastArgs = args;
        if (requestId === null) {
          requestId = requestAnimationFrame(later(this));
        }
      };
      throttled.cancel = () => {
        cancelAnimationFrame(requestId);
        requestId = null;
      };
      return throttled;
    },

    debounce: (fn, wait) => {
      let timer;
      return (...args) => {
        clearTimeout(timer);
        timer = setTimeout(() => fn.apply(this, args), wait);
      };
    },

    waitForEvent: (element, eventName) => {
      return new Promise((resolve) => {
        const done = (event) => {
          if (event.target === element) {
            element.removeEventListener(eventName, done);
            resolve(event);
          }
        };
        element.addEventListener(eventName, done);
      });
    },

    fetchConfig: (type = 'json', method = 'POST') => {
      return {
        method: method,
        headers: { 'Content-Type': 'application/json', 'Accept': `application/${type}` }
      };
    },

    postLink: (path, options) => {
      options = options || {};
      const method = options['method'] || 'post';
      const params = options['parameters'] || {};

      const form = document.createElement("form");
      form.setAttribute("method", method);
      form.setAttribute("action", path);

      for (const key in params) {
        const hiddenField = document.createElement("input");
        hiddenField.setAttribute("type", "hidden");
        hiddenField.setAttribute("name", key);
        hiddenField.setAttribute("value", params[key]);
        form.appendChild(hiddenField);
      }
      document.body.appendChild(form);
      form.submit();
      document.body.removeChild(form);
    },

    postLink2: (path, options) => {
      options = options || {};
      const method = options['method'] || 'post';
      const params = options['parameters'] || {};

      const form = document.createElement("form");
      form.setAttribute("method", method);
      form.setAttribute("action", path);

      for (const key in params) {
        if (typeof params[key] === 'string') {
          const hiddenField = document.createElement("input");
          hiddenField.setAttribute("type", "hidden");
          hiddenField.setAttribute("name", key);
          hiddenField.setAttribute("value", params[key]);
          form.appendChild(hiddenField);
        }
        else {
          for (const index in params[key]) {
            for (const key2 in params[key][index]) {
              const hiddenField = document.createElement("input");
              hiddenField.setAttribute("type", "hidden");
              hiddenField.setAttribute("name", `${key}[${index}][${key2}]`);
              hiddenField.setAttribute("value", params[key][index][key2]);
              form.appendChild(hiddenField);
            }
          }
        }
      }
      document.body.appendChild(form);
      form.submit();
      document.body.removeChild(form);
    },

    sectionId: (element) => {
      if (element.hasAttribute('data-section-id')) return element.getAttribute('data-section-id');

      element = element.classList.contains('shopify-section') ? element : element.closest('.shopify-section');
      return element.id.replace('shopify-section-', '');
    },

    imageLoaded: (imageOrArray) => {
      if (!imageOrArray) {
        return Promise.resolve();
      }
      imageOrArray = imageOrArray instanceof Element ? [imageOrArray] : Array.from(imageOrArray);
      return Promise.all(imageOrArray.map((image) => {
        return new Promise((resolve) => {
          if (image.tagName === "IMG" && image.complete || !image.offsetParent) {
            resolve();
          } else {
            image.onload = () => resolve();
          }
        });
      }));
    },

    visibleMedia: (media) => {
      return Array.from(media).find((item) => {
        const style = window.getComputedStyle(item);
        return style.display !== 'none';
      });
    },

    setScrollbarWidth: () => {
      const scrollbarWidth = window.innerWidth - document.body.clientWidth;
      document.documentElement.style.setProperty('--scrollbar-width', `${scrollbarWidth > 0 ? scrollbarWidth : 0}px`);
    },

    externalLinksNewTab: () => {
      if (theme.settings.externalLinksNewTab) {
        document.addEventListener('click', (evt) => {
          const link = evt.target.tagName === 'A' ? evt.target : evt.target.closest('a');
          if (link && link.tagName === 'A' && window.location.hostname !== new URL(link.href).hostname) {
            link.target = '_blank';
          }
        });
      }
    }
  };

  theme.HTMLUpdateUtility = {
    /**
     * Used to swap an HTML node with a new node.
     * The new node is inserted as a previous sibling to the old node, the old node is hidden, and then the old node is removed.
     *
     * The function currently uses a double buffer approach, but this should be replaced by a view transition once it is more widely supported https://developer.mozilla.org/en-US/docs/Web/API/View_Transitions_API
     */
    viewTransition: (oldNode, newContent, preProcessCallbacks = [], postProcessCallbacks = []) => {
      preProcessCallbacks?.forEach((callback) => callback(newContent));

      const newNodeWrapper = document.createElement('div');
      theme.HTMLUpdateUtility.setInnerHTML(newNodeWrapper, newContent.outerHTML);
      const newNode = newNodeWrapper.firstChild;

      // dedupe IDs
      const uniqueKey = Date.now();
      oldNode.querySelectorAll('[id], [form]').forEach((element) => {
        element.id && (element.id = `${element.id}-${uniqueKey}`);
        element.form && element.setAttribute('form', `${element.form.getAttribute('id')}-${uniqueKey}`);
      });

      oldNode.parentNode.insertBefore(newNode, oldNode);
      oldNode.style.display = 'none';

      postProcessCallbacks?.forEach((callback) => callback(newNode));

      setTimeout(() => oldNode.remove(), 500);
    },

    // Sets inner HTML and reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
    setInnerHTML: (element, html) => {
      element.innerHTML = html;
      element.querySelectorAll('script').forEach((oldScriptTag) => {
        const newScriptTag = document.createElement('script');
        Array.from(oldScriptTag.attributes).forEach((attribute) => {
          newScriptTag.setAttribute(attribute.name, attribute.value);
        });
        newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
        oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
      });
    }
  };

  theme.Currency = {
    formatMoney: (cents, format = "") => {
      if (typeof cents === "string") {
        cents = cents.replace(".", "");
      }
      const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/, formatString = format || window.themeVariables.settings.moneyFormat;
      function defaultTo(value2, defaultValue) {
        return value2 == null || value2 !== value2 ? defaultValue : value2;
      }
      function formatWithDelimiters(number, precision, thousands, decimal) {
        precision = defaultTo(precision, 2);
        thousands = defaultTo(thousands, ",");
        decimal = defaultTo(decimal, ".");
        if (isNaN(number) || number == null) {
          return 0;
        }
        number = (number / 100).toFixed(precision);
        let parts = number.split("."), dollarsAmount = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, "$1" + thousands), centsAmount = parts[1] ? decimal + parts[1] : "";
        return dollarsAmount + centsAmount;
      }
      let value = "";
      switch (formatString.match(placeholderRegex)[1]) {
        case "amount":
          value = formatWithDelimiters(cents, 2);
          break;
        case "amount_no_decimals":
          value = formatWithDelimiters(cents, 0);
          break;
        case "amount_with_space_separator":
          value = formatWithDelimiters(cents, 2, " ", ".");
          break;
        case "amount_with_comma_separator":
          value = formatWithDelimiters(cents, 2, ".", ",");
          break;
        case "amount_with_apostrophe_separator":
          value = formatWithDelimiters(cents, 2, "'", ".");
          break;
        case "amount_no_decimals_with_comma_separator":
          value = formatWithDelimiters(cents, 0, ".", ",");
          break;
        case "amount_no_decimals_with_space_separator":
          value = formatWithDelimiters(cents, 0, " ");
          break;
        case "amount_no_decimals_with_apostrophe_separator":
          value = formatWithDelimiters(cents, 0, "'");
          break;
        default:
          value = formatWithDelimiters(cents, 2);
          break;
      }
      if (formatString.indexOf("with_comma_separator") !== -1) {
        return formatString.replace(placeholderRegex, value);
      } else {
        return formatString.replace(placeholderRegex, value);
      }
    }
  };

  theme.pubsub = {
    PUB_SUB_EVENTS: {
      cartUpdate: 'cartUpdate',
      quantityUpdate: 'quantityUpdate',
      variantChange: 'variantChange',
      cartError: 'cartError',
      discountUpdate: 'discountUpdate',
      facetUpdate: 'facetUpdate',
      quantityRules: 'quantityRules',
      quantityBoundries: 'quantityBoundries',
      optionValueSelectionChange: 'optionValueSelectionChange',
    },

    subscribers: {},

    subscribe: (eventName, callback) => {
      if (theme.pubsub.subscribers[eventName] === undefined) {
        theme.pubsub.subscribers[eventName] = [];
      }

      theme.pubsub.subscribers[eventName] = [...theme.pubsub.subscribers[eventName], callback];

      return function unsubscribe() {
        theme.pubsub.subscribers[eventName] = theme.pubsub.subscribers[eventName].filter((cb) => {
          return cb !== callback
        });
      }
    },

    publish: (eventName, data) => {
      if (theme.pubsub.subscribers[eventName]) {
        theme.pubsub.subscribers[eventName].forEach((callback) => {
          callback(data);
        })
      }
    }
  };

  theme.scrollShadow = {
    Updater: (function () {
      class Updater {
        constructor(targetElement) {
          this.cb = () => this.update(targetElement);
          this.rO = new ResizeObserver(this.cb);
          this.mO = new MutationObserver(() => this.on(this.el));
        }
        on(element) {
          if (this.el) {
            this.el.removeEventListener("scroll", this.cb);
            this.rO.disconnect();
            this.mO.disconnect();
          }
          if (!element)
            return;
          if (element.nodeName === "TABLE" && !/scroll|auto/.test(getComputedStyle(element).getPropertyValue("overflow"))) {
            this.rO.observe(element);
            element = element.tBodies[0];
          }
          element.addEventListener("scroll", this.cb);
          [element, ...element.children].forEach((el) => this.rO.observe(el));
          this.mO.observe(element, { childList: true });
          this.el = element;
        }
        update(targetElement) {
          const scrollTop = this.el.scrollTop;
          const scrollBottom = this.el.scrollHeight - this.el.offsetHeight - this.el.scrollTop;
          const scrollLeft = this.el.scrollLeft;
          const scrollRight = this.el.scrollWidth - this.el.offsetWidth - this.el.scrollLeft * (theme.config.rtl ? -1 : 1);
          let cssText = `--t: ${Math.floor(scrollTop) > 0 ? 1 : 0}; --b: ${Math.floor(scrollBottom) > 0 ? 1 : 0}; --l: ${Math.floor(scrollLeft) > 0 ? 1 : 0}; --r: ${Math.floor(scrollRight) > 0 ? 1 : 0};`;
          if (this.el.nodeName === "TBODY") {
            const clientRect = this.el.getBoundingClientRect();
            const rootClientRect = this.el.parentElement.getBoundingClientRect();
            cssText += `top: ${clientRect.top - rootClientRect.top}px; bottom: ${rootClientRect.bottom - clientRect.bottom}px; left: ${clientRect.left - rootClientRect.left}px; right: ${rootClientRect.right - clientRect.right}px;`;
          }
          requestAnimationFrame(() => {
            targetElement.style.cssText = cssText;
          });
        }
      }

      return Updater;
    })()
  };

  theme.cookiesEnabled = function () {
    let cookieEnabled = navigator.cookieEnabled;

    if (!cookieEnabled) {
      document.cookie = 'testcookie';
      cookieEnabled = document.cookie.indexOf('testcookie') !== -1;
    }
    return cookieEnabled;
  };

  theme.isStorageSupported = function (type) {
    // Return false if we are in an iframe without access to sessionStorage
    if (window.self !== window.top) {
      return false;
    }

    const testKey = 'test';
    let storage;
    if (type === 'session') {
      storage = window.sessionStorage;
    }
    if (type === 'local') {
      storage = window.localStorage;
    }

    try {
      storage.setItem(testKey, '1');
      storage.removeItem(testKey);
      return true;
    }
    catch (error) {
      // Do nothing, this may happen in Safari in incognito mode
      return false;
    }
  };

  theme.getElementWidth = function (element, value) {
    const style = window.getComputedStyle(element);

    const text = document.createElement('span');
    text.style.fontFamily = style.fontFamily;
    text.style.fontSize = style.fontSize;
    text.style.fontWeight = style.fontWeight;
    text.style.visibility = 'hidden';
    text.style.position = 'absolute';
    text.innerHTML = value;

    document.body.appendChild(text);
    const width = text.getBoundingClientRect().width;
    text.remove();

    return width;
  };

  theme.HoverButton = (function () {
    class HoverButton {
      constructor(container) {
        this.container = container;
      }

      get btnFill() {
        return this.container.querySelector('[data-fill]');
      }

      load() {
        if (theme.config.isTouch || document.body.getAttribute('data-button-hover') === 'none') return;

        this.container.addEventListener('mouseenter', this.onEnterHandler.bind(this));
        this.container.addEventListener('mouseleave', this.onLeaveHandler.bind(this));
      }

      onEnterHandler() {
        if (this.btnFill) {
          Motion.animate(this.btnFill, { y: ['76%', '0%'] }, { duration: 0.6 });
        }
      }

      onLeaveHandler() {
        if (this.btnFill) {
          Motion.animate(this.btnFill, { y: '-76%' }, { duration: 0.6 });
        }
      }

      unload() {
        // todo
      }
    }

    return HoverButton;
  })();

  theme.MagnetButton = (function () {
    const config = {
      magnet: 10
    };

    class MagnetButton {
      constructor(container) {
        this.container = container;
        this.magnet = container.hasAttribute('data-magnet') ? parseInt(container.getAttribute('data-magnet')) : config.magnet;
      }

      get btnText() {
        return this.container.querySelector('[data-text]');
      }

      get bounding() {
        return this.container.getBoundingClientRect();
      }

      load() {
        if (theme.config.isTouch || document.body.getAttribute('data-button-hover') === 'none') return;

        this.container.addEventListener('mousemove', this.onMoveHandler.bind(this));
        this.container.addEventListener('mouseleave', this.onLeaveHandler.bind(this));
      }

      onMoveHandler(event) {
        if (theme.config.motionReduced) return;
        if (this.magnet === 0) return;

        if (this.btnText) {
          Motion.animate(this.btnText, {
            x: ((event.clientX - this.bounding.left) / this.container.offsetWidth - 0.5) * this.magnet,
            y: ((event.clientY - this.bounding.top) / this.container.offsetHeight - 0.5) * this.magnet,
          }, { duration: 1.5, easing: Motion.spring() });
        }
        else {
          Motion.animate(this.container, {
            x: ((event.clientX - this.bounding.left) / this.container.offsetWidth - 0.5) * this.magnet,
            y: ((event.clientY - this.bounding.top) / this.container.offsetHeight - 0.5) * this.magnet,
          }, { duration: 1.5, easing: Motion.spring() });
        }
      }

      onLeaveHandler() {
        if (theme.config.motionReduced) return;
        if (this.magnet === 0) return;

        if (this.btnText) {
          Motion.animate(this.btnText, { x: 0, y: 0 }, { duration: 1.5, easing: Motion.spring() });
        }
        else {
          Motion.animate(this.container, { x: 0, y: 0 }, { duration: 1.5, easing: Motion.spring() });
        }
      }

      unload() {
        // todo
      }
    }

    return MagnetButton;
  })();

  theme.RevealButton = (function () {
    const config = {
      magnet: 25
    };

    class RevealButton {
      constructor(container) {
        this.container = container;
        this.magnet = container.hasAttribute('data-magnet') ? parseInt(container.getAttribute('data-magnet')) : config.magnet;
      }

      get btnText() {
        return this.container.querySelector('[data-text]');
      }

      get btnReveal() {
        return this.container.querySelector('[data-reveal]');
      }

      get bounding() {
        return this.container.getBoundingClientRect();
      }

      load() {
        if (theme.config.isTouch) return;

        this.container.addEventListener('mousemove', this.onMoveHandler.bind(this));
        this.container.addEventListener('mouseleave', this.onLeaveHandler.bind(this));
      }

      onMoveHandler(event) {
        if (this.btnReveal) {
          Motion.animate(this.btnReveal, {
            opacity: 1,
            x: ((event.clientX - this.bounding.left) / this.container.offsetWidth - 0.5) * this.magnet,
            y: ((event.clientY - this.bounding.top) / this.container.offsetHeight - 0.5) * this.magnet,
          }, { duration: 0.2, easing: 'steps(2, start)' });
        }
        else if (this.btnText) {
          Motion.animate(this.btnText, {
            x: ((event.clientX - this.bounding.left) / this.container.offsetWidth - 0.5) * this.magnet,
            y: ((event.clientY - this.bounding.top) / this.container.offsetHeight - 0.5) * this.magnet,
          }, { duration: 1.5, easing: Motion.spring() });
        }
        else {
          Motion.animate(this.container, {
            x: ((event.clientX - this.bounding.left) / this.container.offsetWidth - 0.5) * this.magnet,
            y: ((event.clientY - this.bounding.top) / this.container.offsetHeight - 0.5) * this.magnet,
          }, { duration: 1.5, easing: Motion.spring() });
        }
      }

      onLeaveHandler() {
        if (this.btnReveal) {
          Motion.animate(this.btnReveal, { x: 0, y: 0, opacity: 0 }, { duration: 0.2, easing: [0.61, 1, 0.88, 1] });
        }
        else if (this.btnText) {
          Motion.animate(this.btnText, { x: 0, y: 0 }, { duration: 1.5, easing: Motion.spring() });
        }
        else {
          Motion.animate(this.container, { x: 0, y: 0 }, { duration: 1.5, easing: Motion.spring() });
        }
      }

      unload() {
        // todo
      }
    }

    return RevealButton;
  })();

  theme.Animation = (function () {
    class Animation {
      constructor(container) {
        this.container = container;
      }

      get immediate() {
        return this.container.hasAttribute('data-immediate');
      }

      get media() {
        return Array.from(this.container.querySelectorAll('img, iframe, svg, g-map'));
      }

      get type() {
        return this.container.hasAttribute('data-animate') ? this.container.getAttribute('data-animate') : 'fade-up';
      }

      get delay() {
        return this.container.hasAttribute('data-animate-delay') ? parseInt(this.container.getAttribute('data-animate-delay')) / 1000 : 0;
      }

      get paused() {
        return this.container.hasAttribute('paused');
      }

      beforeLoad() {
        if (this.type === 'none' || this.paused) return;

        switch (this.type) {
          case 'fade-in':
            Motion.animate(this.container, { opacity: 0 }, { duration: 0 });
            break;

          case 'fade-up':
            Motion.animate(this.container, { transform: 'translateY(min(2rem, 90%))', opacity: 0 }, { duration: 0 });
            break;

          case 'fade-up-large':
            Motion.animate(this.container, { transform: 'translateY(90%)', opacity: 0 }, { duration: 0 });
            break;

          case 'zoom-out':
            Motion.animate(this.container, { transform: 'scale(1.3)' }, { duration: 0 });
            break;
        }
      }

      async load() {
        if (this.type === 'none' || this.paused) return;

        switch (this.type) {
          case 'fade-in':
            await Motion.animate(this.container, { opacity: 1 }, { duration: 1.5, delay: this.delay, easing: [0.16, 1, 0.3, 1] }).finished;
            break;

          case 'fade-up':
            await Motion.animate(this.container, { transform: 'translateY(0)', opacity: 1 }, { duration: 1.5, delay: this.delay, easing: [0.16, 1, 0.3, 1] }).finished;
            break;

          case 'fade-up-large':
            await Motion.animate(this.container, { transform: 'translateY(0)', opacity: 1 }, { duration: 1, delay: this.delay, easing: [0.16, 1, 0.3, 1] }).finished;
            break;

          case 'zoom-out':
            await Motion.animate(this.container, { transform: 'scale(1)' }, { duration: 1.3, delay: this.delay, easing: [0.16, 1, 0.3, 1] }).finished;
            break;
        }

        this.container.classList.add('animate');
      }

      async reset(duration) {
        if (this.type === 'none') return;

        switch (this.type) {
          case 'fade-in':
            await Motion.animate(this.container, { opacity: 0 }, { duration: duration ? duration : 1.5, delay: this.delay, easing: duration ? 'none' : [0.16, 1, 0.3, 1] }).finished;
            break;

          case 'fade-up':
            await Motion.animate(this.container, { transform: 'translateY(max(-2rem, -90%))', opacity: 0 }, { duration: duration ? duration : 1.5, delay: this.delay, easing: duration ? 'none' : [0.16, 1, 0.3, 1] }).finished;
            break;

          case 'fade-up-large':
            await Motion.animate(this.container, { transform: 'translateY(-90%)', opacity: 0 }, { duration: duration ? duration : 1, delay: this.delay, easing: duration ? 'none' : [0.16, 1, 0.3, 1] }).finished;
            break;

          case 'zoom-out':
            await Motion.animate(this.container, { transform: 'scale(0)' }, { duration: duration ? duration : 1.3, delay: this.delay, easing: duration ? 'none' : [0.16, 1, 0.3, 1] }).finished;
            break;
        }

        this.container.classList.remove('animate');
      }

      reload() {
        if (this.type === 'none') return;

        this.container.removeAttribute('paused');
        this.beforeLoad();
        this.load();
      }

      unload() {
        // todo
      }
    }

    return Animation;
  })();

  theme.Carousel = (function () {
    class Carousel {
      constructor(container, options, navigation) {
        this.container = container;
        this.options = options;
        this.prevButton = navigation.previous;
        this.nextButton = navigation.next;
      }

      load() {
        this.slider = new Flickity(this.container, this.options);

        this.prevButton.addEventListener('click', this.onPrevButtonClick.bind(this));
        this.nextButton.addEventListener('click', this.onNextButtonClick.bind(this));

        this.slider.on('dragStart', this.onDragStartHandler.bind(this));
        this.slider.on('select', this.onSelectHandler.bind(this));
      }

      onDragStartHandler() {
        this.prevRemoveTransform();
        this.nextRemoveTransform();
      }

      onSelectHandler() {
        if (!this.slider.slides[this.slider.selectedIndex - 1]) {
          this.prevButton.disabled = true;
          this.nextButton.disabled = false;
        }
        else if (!this.slider.slides[this.slider.selectedIndex + 1]) {
          this.prevButton.disabled = false;
          this.nextButton.disabled = true;
        }
        else {
          this.prevButton.disabled = false;
          this.nextButton.disabled = false;
        }
      }

      onPrevButtonClick(event) {
        event.preventDefault();

        this.slider.previous();
        this.nextRemoveTransform();
      }

      onNextButtonClick(event) {
        event.preventDefault();

        this.slider.next();
        this.prevRemoveTransform();
      }

      prevRemoveTransform() {
        this.prevButton.style.transform = null;
        this.prevButton.querySelector('[data-fill]').style.transform = null;
      }

      nextRemoveTransform() {
        this.nextButton.style.transform = null;
        this.nextButton.querySelector('[data-fill]').style.transform = null;
      }

      unload() {
        // todo
      }
    }

    return Carousel;
  })();

  // Delay JavaScript until user interaction
  theme.initWhenVisible = (function () {
    class ScriptLoader {
      constructor(callback, delay = 5000) {
        this.callback = callback;
        this.triggered = false;
        this.timeoutId = null;

        this.interactionEvents = ['click', 'mousemove', 'keydown', 'touchstart', 'touchmove', 'wheel'];
        this.handleInteraction = this.handleInteraction.bind(this);

        this.interactionEvents.forEach(eventType => {
          window.addEventListener(eventType, this.handleInteraction, { passive: true });
        });

        this.timeoutId = setTimeout(() => {
          if (!this.triggered) {
            this.handleInteraction({ type: 'timeout' });
          }
        }, delay);
      }

      handleInteraction(event) {
        if (this.triggered) return;

        this.triggered = true;
        this.callback(event);
        this.cleanup();
      }

      cleanup() {
        this.interactionEvents.forEach(eventType => {
          window.removeEventListener(eventType, this.handleInteraction, { passive: true });
        });

        clearTimeout(this.timeoutId);
        this.timeoutId = null;
        this.callback = null;
      }
    }

    return ScriptLoader;
  })();

  // Improve initial load time by skipping the rendering of offscreen content
  new theme.initWhenVisible(() => {
    document.body.removeAttribute('data-page-rendering');
  });

  // Improve iOS Safari resizing
  theme.windowWidth = window.innerWidth;
  window.addEventListener('resize', () => {
    const { innerWidth } = window;
    if (innerWidth !== theme.windowWidth) {
      theme.windowWidth = innerWidth;
      document.dispatchEvent(new CustomEvent('window:resize'));
    }
  });

  theme.DOMready(theme.headerGroup.init);
  theme.DOMready(theme.utils.setScrollbarWidth);
  theme.DOMready(theme.utils.externalLinksNewTab);
  document.addEventListener('window:resize', theme.utils.throttle(theme.utils.setScrollbarWidth));

  /*============================================================================
    Things that don't require DOM to be ready
  ==============================================================================*/

  theme.config.hasSessionStorage = theme.isStorageSupported('session');
  theme.config.hasLocalStorage = theme.isStorageSupported('local');

  // Trigger events when going between breakpoints
  const mql = window.matchMedia(theme.config.mediaQuerySmall);
  theme.config.mqlSmall = mql.matches;
  mql.onchange = (mql) => {
    if (mql.matches) {
      theme.config.mqlSmall = true;
      document.dispatchEvent(new CustomEvent('matchSmall'));
    }
    else {
      theme.config.mqlSmall = false;
      document.dispatchEvent(new CustomEvent('unmatchSmall'));
    }
  }

  // Page has loaded and theme assets are ready
  window.addEventListener('DOMContentLoaded', () => {
    document.body.classList.add('loaded');
    document.dispatchEvent(new CustomEvent('page:loaded'));
  });
  window.addEventListener('pageshow', (event) => {
    // Removes unload class when returning to page via history
    if (event.persisted) {
      document.body.classList.remove('unloading');
    }
  });
  window.addEventListener('beforeunload', () => {
    document.body.classList.add('unloading');
  });
})();

// Prevent vertical scroll while using flickity sliders
new theme.initWhenVisible(() => {
  var e = !1;
  var t;

  document.body.addEventListener('touchstart', function (i) {
    if (!i.target.closest('.flickity-slider')) {
      return e = !1;
      void 0;
    }
    e = !0;
    t = {
      x: i.touches[0].pageX,
      y: i.touches[0].pageY
    }
  }, theme.supportsPassive ? { passive: true } : false);

  document.body.addEventListener('touchmove', function (i) {
    if (e && i.cancelable) {
      var n = {
        x: i.touches[0].pageX - t.x,
        y: i.touches[0].pageY - t.y
      };
      Math.abs(n.x) > Flickity.defaults.dragThreshold && i.preventDefault()
    }
  }, theme.supportsPassive ? { passive: false } : false);
});

class LoadingBar extends HTMLElement {
  constructor() {
    super();

    document.addEventListener('page:loaded', () => {
      Motion.animate(this, { opacity: 0 }, { duration: 1 }).finished.then(() => {
        this.hidden = true;
      });
    });
  }
}
customElements.define('loading-bar', LoadingBar);

class MouseCursor extends HTMLElement {
  constructor() {
    super();

    if (theme.config.isTouch) return;

    this.config = {
      posX: 0,
      posY: 0,
    };

    document.addEventListener('mousemove', (event) => {
      this.config.posX += (event.clientX - this.config.posX) / 4;
      this.config.posY += (event.clientY - this.config.posY) / 4;

      this.style.setProperty('--x', `${this.config.posX}px`);
      this.style.setProperty('--y', `${this.config.posY}px`);
    });
  }
}
customElements.define('mouse-cursor', MouseCursor);

class CustomHeader extends HTMLElement {
  constructor() {
    super();
  }

  get allowTransparent() {
    if (document.querySelector('.shopify-section:first-child [allow-transparent-header]')) {
      return true;
    }

    return false;
  }

  get headerSection() {
    return document.querySelector('.header-section');
  }

  connectedCallback() {
    this.init();
    if (window.ResizeObserver) {
      new ResizeObserver(this.setHeight.bind(this)).observe(this);
    }

    if (Shopify.designMode) {
      const section = this.closest('.shopify-section');
      section.addEventListener('shopify:section:load', this.init.bind(this));
      section.addEventListener('shopify:section:unload', this.init.bind(this));
      section.addEventListener('shopify:section:reorder', this.init.bind(this));
    }
  }

  init() {
    this.setHeight();

    if (this.allowTransparent) {
      this.headerSection.classList.add('header-transparent');
      this.headerSection.classList.add('no-animate');

      setTimeout(() => {
        this.headerSection.classList.remove('no-animate');
      }, 500);
    }
    else {
      this.headerSection.classList.remove('header-transparent');
    }
  }

  setHeight() {
    requestAnimationFrame(() => {
      document.documentElement.style.setProperty('--header-height', Math.round(this.clientHeight) + 'px');

      if (this.classList.contains('header--center') && document.documentElement.style.getPropertyValue('--header-nav-height').length === 0) {
        document.documentElement.style.setProperty('--header-nav-height', Math.round(document.getElementById('MenuToggle')?.clientHeight) + 'px');
      }
    });
  }
}
customElements.define('custom-header', CustomHeader, { extends: 'header' });

class StickyHeader extends CustomHeader {
  constructor() {
    super();
  }

  get isAlwaysSticky() {
    return this.getAttribute('data-sticky-type') === 'always';
  }

  connectedCallback() {
    super.connectedCallback();

    this.currentScrollTop = 0;
    this.firstScrollTop = window.scrollY;
    this.headerBounds = this.headerSection.getBoundingClientRect();

    this.beforeInit();
  }

  beforeInit() {
    this.headerSection.classList.add('header-sticky');
    this.headerSection.setAttribute('data-sticky-type', this.getAttribute('data-sticky-type'));

    if (this.isAlwaysSticky) {
      this.headerSection.classList.add('header-sticky');
    }

    window.addEventListener('scroll', theme.utils.throttle(this.onScrollHandler.bind(this)), false);
  }

  onScrollHandler() {
    const scrollTop = window.scrollY;

    if (scrollTop > (this.headerBounds.top + this.firstScrollTop + this.headerBounds.height)) {
      this.headerSection.classList.add('header-scrolled');
      document.documentElement.style.setProperty('--sticky-header-height', Math.round(this.clientHeight) + 'px');
      document.dispatchEvent(new CustomEvent('header:scrolled', { bubbles: true, detail: { scrolled: true } }));

      if (scrollTop > (this.headerBounds.top + this.firstScrollTop + this.headerBounds.height * 2)) {
        this.headerSection.classList.add('header-nav-scrolled');
        this.headerSection.querySelectorAll('details').forEach((details) => {
          if (details.hasAttribute('open')) {
            details.open = false;
          }
        });
      }
    }
    else {
      this.headerSection.classList.remove('header-scrolled');
      this.headerSection.classList.remove('header-nav-scrolled');
      document.dispatchEvent(new CustomEvent('header:scrolled', { bubbles: true, detail: { scrolled: false } }));
    }

    if (scrollTop > (this.headerBounds.bottom + this.firstScrollTop + 100)) {
      if (scrollTop > this.currentScrollTop) {
        this.headerSection.classList.add('header-hidden');
      }
      else {
        this.headerSection.classList.remove('header-hidden');
      }
    }
    else {
      this.headerSection.classList.remove('header-hidden');
    }

    this.currentScrollTop = scrollTop;
  }
}
customElements.define('sticky-header', StickyHeader, { extends: 'header' });

class RevealLink extends HTMLAnchorElement {
  constructor() {
    super();

    this.revealButton = new theme.RevealButton(this);
    this.revealButton.load();
  }
}
customElements.define('reveal-link', RevealLink, { extends: 'a' });

class HoverLink extends HTMLAnchorElement {
  constructor() {
    super();

    this.hoverButton = new theme.HoverButton(this);
    this.hoverButton.load();
  }
}
customElements.define('hover-link', HoverLink, { extends: 'a' });

class MagnetLink extends HoverLink {
  constructor() {
    super();

    this.magnetButton = new theme.MagnetButton(this);
    this.magnetButton.load();
  }
}
customElements.define('magnet-link', MagnetLink, { extends: 'a' });

class HoverButton extends HTMLButtonElement {
  constructor() {
    super();

    this.hoverButton = new theme.HoverButton(this);
    this.hoverButton.load();

    if (this.type === 'submit' && this.form) {
      this.form.addEventListener('submit', () => this.setAttribute('aria-busy', 'true'));
    }
    window.addEventListener('pageshow', () => this.removeAttribute('aria-busy'));

    Motion.inView(this, this.init.bind(this));
  }

  static get observedAttributes() {
    return ['aria-busy'];
  }

  get contentElement() {
    return this._contentElement = this._contentElement || this.querySelector('.btn-text');
  }

  get animationElement() {
    return this._animationElement = this._animationElement || document.createRange().createContextualFragment(`
      <span class="btn-loader">
        <span></span>
        <span></span>
        <span></span>
      </span>
    `).firstElementChild;
  }

  get controlledElement() {
    return this.hasAttribute('aria-controls') ? document.getElementById(this.getAttribute('aria-controls')) : null;
  }

  init() {
    this.append(this.animationElement);
  }

  async attributeChangedCallback(name, oldValue, newValue) {
    if (newValue === 'true') {
      Motion.timeline([
        [this.contentElement, { opacity: 0 }, { duration: 0.15 }],
        [this.animationElement, { opacity: 1 }, { duration: 0.15 }]
      ]);
      Motion.animate(this.animationElement.children, { transform: ['scale(1.6)', 'scale(0.6)'] }, { duration: 0.35, delay: Motion.stagger(0.35 / 2), direction: 'alternate', repeat: Infinity });
    }
    else {
      Motion.timeline([
        [this.animationElement, { opacity: 0 }, { duration: 0.15 }],
        [this.contentElement, { opacity: 1 }, { duration: 0.15 }]
      ]);
    }
  }
}
customElements.define('hover-button', HoverButton, { extends: 'button' });

class MagnetButton extends HoverButton {
  constructor() {
    super();

    this.magnetButton = new theme.MagnetButton(this);
    this.magnetButton.load();
  }
}
customElements.define('magnet-button', MagnetButton, { extends: 'button' });

class HoverElement extends HTMLElement {
  constructor() {
    super();

    this.hoverButton = new theme.HoverButton(this);
    this.hoverButton.load();
  }
}
customElements.define('hover-element', HoverElement);

class MagnetElement extends HoverElement {
  constructor() {
    super();

    this.magnetButton = new theme.MagnetButton(this);
    this.magnetButton.load();
  }
}
customElements.define('magnet-element', MagnetElement);

class AnimateElement extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    if (theme.config.motionReduced) return;

    this.animation = new theme.Animation(this);
    this.animation.beforeLoad();

    Motion.inView(this, async () => {
      if (!this.immediate && this.media) await theme.utils.imageLoaded(this.media);
      this.animation.load();
    });
  }

  reset() {
    this.animation?.reset();
  }

  refresh() {
    this.animation?.reload();
  }
}
customElements.define('animate-element', AnimateElement);

class AnimatePicture extends HTMLPictureElement {
  constructor() {
    super();
  }

  connectedCallback() {
    if (theme.config.motionReduced) return;

    this.animation = new theme.Animation(this);
    this.animation.beforeLoad();

    Motion.inView(this, async () => {
      if (!this.immediate && this.media) await theme.utils.imageLoaded(this.media);
      this.animation.load();
    });
  }
}
customElements.define('animate-picture', AnimatePicture, { extends: 'picture' });

class AnnouncementBar extends HTMLElement {
  constructor() {
    super();

    this.selectedIndex = this.selectedIndex;

    if (!theme.config.isTouch || Shopify.designMode) {
      Motion.inView(this, this.init.bind(this), { margin: '200px 0px 200px 0px' });
    }
    else {
      new theme.initWhenVisible(this.init.bind(this));
    }
  }

  static get observedAttributes() {
    return ['selected-index'];
  }

  get selectedIndex() {
    return parseInt(this.getAttribute('selected-index')) || 0;
  }

  set selectedIndex(index) {
    this.setAttribute('selected-index', Math.min(Math.max(index, 0), this.items.length - 1).toString());
  }

  get items() {
    return this._items = this._items || Array.from(this.children);
  }

  get autoplay() {
    return this.hasAttribute('autoplay');
  }

  get speed() {
    return this.hasAttribute('autoplay') ? parseInt(this.getAttribute('autoplay-speed')) * 1000 : 5000;
  }

  init() {
    if (this.initialized) return;
    this.initialized = true;

    if (this.items.length > 1) {
      this.slider = new Flickity(this, {
        accessibility: false,
        fade: true,
        pageDots: false,
        prevNextButtons: false,
        wrapAround: true,
        rightToLeft: theme.config.rtl,
        autoPlay: this.autoplay ? this.speed : false,
        on: {
          ready: function () {
            setTimeout(() => this.element.setAttribute('loaded', ''));
          }
        }
      });

      this.slider.on('change', this.onChange.bind(this));
      this.addEventListener('slider:previous', () => this.slider.previous());
      this.addEventListener('slider:next', () => this.slider.next());
      this.addEventListener('slider:play', () => this.slider.playPlayer());
      this.addEventListener('slider:pause', () => this.slider.pausePlayer());

      if (Shopify.designMode) {
        this.addEventListener('shopify:block:select', (event) => this.slider.select(this.items.indexOf(event.target)));
      }
    }
  }

  disconnectedCallback() {
    if (this.slider) this.slider.destroy();
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (name === 'selected-index' && oldValue !== null && oldValue !== newValue) {
      const focusableEvents = 'button, [href]';

      const fromElement = this.items[parseInt(oldValue)];
      const toElement = this.items[parseInt(newValue)];

      fromElement.querySelectorAll(focusableEvents).forEach((el) => {
        el.setAttribute('tabindex', '-1');
      });
      toElement.querySelectorAll(focusableEvents).forEach((el) => {
        el.removeAttribute('tabindex');
      });
    }
  }

  onChange() {
    this.selectedIndex = this.slider.selectedIndex;
    this.dispatchEvent(new CustomEvent('slider:change', { bubbles: true, detail: { currentPage: this.slider.selectedIndex } }));
  }
}
customElements.define('announcement-bar', AnnouncementBar);

class AccordionsDetails extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('toggle', this.onToggle);
  }

  get items() {
    return this._items = this._items || Array.from(this.querySelectorAll('details[is="accordion-details"]'));
  }

  onToggle(event) {
    const { current: target, open } = event.detail;
    this.items.forEach((item) => {
      if (item !== target) {
        item.close();
      }
    });

    if (open) {
      let headerHeight = 0;
      if (!theme.config.mqlSmall && document.querySelector('header.header')) {
        headerHeight = Math.round(document.querySelector('header.header').clientHeight);
      }
      setTimeout(() => {
        window.scrollTo({
          top: target.getBoundingClientRect().top + window.scrollY - headerHeight,
          behavior: theme.config.motionReduced ? 'auto' : 'smooth'
        });
      }, 250);
    }
  }
}
customElements.define('accordions-details', AccordionsDetails);

class AccordionDetails extends HTMLDetailsElement {
  constructor() {
    super();

    this._open = this.hasAttribute('open');
    this.summaryElement = this.querySelector('summary');
    this.contentElement = this.querySelector('summary + *');
    this.setAttribute('aria-expanded', this._open ? 'true' : 'false');

    this.summaryElement.addEventListener('click', this.onSummaryClick.bind(this));

    if (Shopify.designMode) {
      this.addEventListener('shopify:block:select', () => {
        if (this.designModeActive) this.open = true;
      });
      this.addEventListener('shopify:block:deselect', () => {
        if (this.designModeActive) this.open = false;
      });
    }
  }

  get designModeActive() {
    return true;
  }

  get controlledElement() {
    return this.closest('accordions-details');
  }

  static get observedAttributes() {
    return ['open'];
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (name === 'open') {
      this.setAttribute('aria-expanded', newValue === '' ? 'true' : 'false');
    }
  }

  get open() {
    return this._open;
  }

  set open(value) {
    if (value !== this._open) {
      this._open = value;

      if (this.isConnected) {
        this.transition(value);
      }
      else {
        value ? this.setAttribute('open', '') : this.removeAttribute('open');
      }
    }

    this.setAttribute('aria-expanded', value ? 'true' : 'false');
    this.dispatchEventHandler();
  }

  onSummaryClick(event) {
    event.preventDefault();
    this.open = !this.open;
  }

  close() {
    this._open = false;
    this.transition(false);
  }

  async transition(value) {
    this.style.overflow = 'hidden';

    if (value) {
      this.setAttribute('open', '');

      await Motion.timeline([
        [this, { height: [`${this.summaryElement.clientHeight}px`, `${this.scrollHeight}px`] }, { duration: 0.25, easing: 'ease' }],
        [this.contentElement, { opacity: [0, 1], transform: ['translateY(10px)', 'translateY(0)'] }, { duration: 0.15, at: '-0.1' }]
      ]).finished;
    }
    else {
      await Motion.timeline([
        [this.contentElement, { opacity: 0 }, { duration: 0.15 }],
        [this, { height: [`${this.clientHeight}px`, `${this.summaryElement.clientHeight}px`] }, { duration: 0.25, at: '<', easing: 'ease' }]
      ]).finished;

      this.removeAttribute('open');
    }

    this.style.height = 'auto';
    this.style.overflow = 'visible';
  }

  dispatchEventHandler() {
    (this.controlledElement ?? this).dispatchEvent(new CustomEvent('toggle', { bubbles: true, detail: { current: this, open: this.open } }));
  }
}
customElements.define('accordion-details', AccordionDetails, { extends: 'details' });

class FooterDetails extends AccordionDetails {
  constructor() {
    super();

    this.load();
    document.addEventListener('matchSmall', this.load.bind(this));
    document.addEventListener('unmatchSmall', this.load.bind(this));
  }

  get designModeActive() {
    return theme.config.mqlSmall;
  }

  get openDefault() {
    return this.hasAttribute('data-opened');
  }

  load() {
    if (!theme.config.mqlSmall || this.openDefault) {
      this.open = true;
    }
    else {
      if (this.open && !this.openDefault) {
        this.open = false;
      }
    }
  }
}
customElements.define('footer-details', FooterDetails, { extends: 'details' });

class GestureElement extends HTMLElement {
  constructor() {
    super();
    this.config = {
      thresholdY: Math.max(25, Math.floor(0.15 * window.innerHeight)),
      velocityThreshold: 10,
      disregardVelocityThresholdY: Math.floor(0.5 * this.clientHeight),
      pressThreshold: 8,
      diagonalSwipes: false,
      diagonalLimit: Math.tan(((45 * 1.5) / 180) * Math.PI),
      longPressTime: 500,
    };

    this.handlers = {
      panstart: [],
      panmove: [],
      panend: [],
      swipeup: [],
      swipedown: [],
      longpress: [],
    };

    Motion.inView(this, this.init.bind(this));
  }

  init() {
    this.addEventListener('touchstart', this.onTouchStart.bind(this), theme.supportsPassive ? { passive: true } : false);
    this.addEventListener('touchmove', this.onTouchMove.bind(this), theme.supportsPassive ? { passive: true } : false);
    this.addEventListener('touchend', this.onTouchEnd.bind(this), theme.supportsPassive ? { passive: true } : false);
  }

  on(type, fn) {
    if (this.handlers[type]) {
      this.handlers[type].push(fn);
      return {
        type,
        fn,
        cancel: () => this.off(type, fn),
      };
    }
  }

  off(type, fn) {
    if (this.handlers[type]) {
      const idx = this.handlers[type].indexOf(fn);
      if (idx !== -1) {
        this.handlers[type].splice(idx, 1);
      }
    }
  }

  fire(type, event) {
    for (let i = 0; i < this.handlers[type].length; i++) {
      this.handlers[type][i](event);
    }
  }

  onTouchStart(event) {
    this.thresholdY = this.config.thresholdY;
    this.disregardVelocityThresholdY = this.config.disregardVelocityThresholdY;
    this.touchStartY = event.changedTouches[0].screenY;
    this.touchMoveY = null;
    this.touchEndY = null;
    this.swipingDirection = null;

    // Long press.
    this.longPressTimer = setTimeout(() => this.fire('longpress', event), this.config.longPressTime);
    this.fire('panstart', event);
  }

  onTouchMove(event) {
    const touchMoveY = event.changedTouches[0].screenY - (this.touchStartY ?? 0);
    this.velocityY = touchMoveY - (this.touchMoveY ?? 0);
    this.touchMoveY = touchMoveY;
    const absTouchMoveY = Math.abs(this.touchMoveY);
    this.swipingVertical = absTouchMoveY > this.thresholdY;
    this.swipingDirection = this.swipingVertical ? 'vertical' : 'pre-vertical';

    if (absTouchMoveY > this.config.pressThreshold) {
      clearTimeout(this.longPressTimer ?? undefined);
    }
    this.fire('panmove', event);
  }

  onTouchEnd(event) {
    this.touchEndY = event.changedTouches[0].screenY;
    this.fire('panend', event);
    clearTimeout(this.longPressTimer ?? undefined);

    const y = this.touchEndY - (this.touchStartY ?? 0);
    const absY = Math.abs(y);

    if (absY > this.thresholdY) {
      this.swipedVertical = this.config.diagonalSwipes ? y <= this.config.diagonalLimit : absY > this.thresholdY;
      if (this.swipedVertical) {
        if (y < 0) {
          // Upward swipe.
          if ((this.velocityY ?? 0) < -this.config.velocityThreshold || y < -this.disregardVelocityThresholdY) {
            this.fire('swipeup', event);
          }
        } else {
          // Downward swipe.
          if ((this.velocityY ?? 0) > this.config.velocityThreshold || y > this.disregardVelocityThresholdY) {
            this.fire('swipedown', event);
          }
        }
      }
    }
  }
}
customElements.define('gesture-element', GestureElement);

class OverlayElement extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('mousemove', this.onMouseMove);
    this.addEventListener('mouseleave', this.onMouseLeave);
    this.addEventListener('mousedown', this.onMouseDown);
    this.addEventListener('mouseup', this.onMouseUp);
  }

  get cursor() {
    return document.querySelector('mouse-cursor');
  }

  onMouseMove() {
    this.cursor.classList.add('active');
  }
  onMouseLeave() {
    this.cursor.classList.remove('active');
  }
  onMouseDown() {
    this.cursor.classList.add('pressed');
  }
  onMouseUp() {
    this.cursor.classList.remove('pressed');
  }
}
customElements.define('overlay-element', OverlayElement);

const lockLayerCount = new WeakMap();
class ModalElement extends HTMLElement {
  constructor() {
    super();

    this.events = {
      afterHide: 'modal:afterHide',
      afterShow: 'modal:afterShow',
      closeAll: 'modal:closeAll'
    };

    this.classes = {
      open: 'has-modal-open',
      opening: 'has-modal-opening'
    };
  }

  static get observedAttributes() {
    return ['id', 'open'];
  }

  get shouldLock() {
    return false;
  }

  get shouldAppendToBody() {
    return false;
  }

  get shouldCloseAll() {
    return false;
  }

  get open() {
    return this.hasAttribute('open');
  }

  get controls() {
    return Array.from(document.querySelectorAll(`[aria-controls="${this.id}"]`));
  }

  get overlay() {
    return this._overlay = this._overlay || this.querySelector('.fixed-modal');
  }

  get gesture() {
    return this._gesture = this._gesture || this.querySelector('gesture-element');
  }

  get designMode() {
    return this.hasAttribute('shopify-design-mode');
  }

  get focusElement() {
    return this.querySelector('button');
  }

  connectedCallback() {
    this.abortController = new AbortController();

    this.controls.forEach((button) => button.addEventListener('click', this.onButtonClick.bind(this), { signal: this.abortController.signal }));
    document.addEventListener('keyup', (event) => event.code === 'Escape' && this.hide(), { signal: this.abortController.signal });
    document.addEventListener(this.events.closeAll, () => this.hide(), { signal: this.abortController.signal });

    if (this.gesture) {
      this.gestureConfig = {
        animationFrame: null,
        moveY: 0,
        maxGestureDistance: 0,
        endPoint: 0,
        layerHeight: null
      };

      this.gestureWrap = this.gesture.parentElement;

      setTimeout(() => {
        this.gesture.on('panstart', this.onPanStart.bind(this));
        this.gesture.on('panmove', this.onPanMove.bind(this));
        this.gesture.on('panend', this.onPanEnd.bind(this));
      }, 75);
    }

    if (Shopify.designMode && this.designMode) {
      const section = this.closest('.shopify-section');
      section.addEventListener('shopify:section:select', (event) => this.show(null, !event.detail.load), { signal: this.abortController.signal });
      section.addEventListener('shopify:section:deselect', this.hide.bind(this), { signal: this.abortController.signal });
    }
  }

  disconnectedCallback() {
    this.abortController?.abort();

    if (Shopify.designMode) {
      document.body.classList.remove(this.classes.open);
    }
  }

  attributeChangedCallback(name, oldValue, newValue) {
    switch (name) {
      case 'open':
        this.controls.forEach((button) => button.setAttribute('aria-expanded', newValue === null ? 'false' : 'true'));

        if (oldValue === null && (newValue === "" || newValue === 'immediate')) {
          this.hidden = false;
          this.removeAttribute('inert');

          this.originalParentBeforeAppend = null;
          if (this.shouldAppendToBody && this.parentElement !== document.body) {
            this.originalParentBeforeAppend = this.parentElement;
            document.body.append(this);
          }
          const showTransitionPromise = this.showTransition(newValue !== 'immediate') || Promise.resolve();
          showTransitionPromise.then(() => {
            this.afterShow();
            this.dispatchEvent(new CustomEvent(this.events.afterShow, { bubbles: true }));
          });
        }
        else if (oldValue !== null && newValue === null) {
          this.setAttribute('inert', '');

          const hideTransitionPromise = this.hideTransition() || Promise.resolve();
          hideTransitionPromise.then(() => {
            this.afterHide();

            if (!this.hasAttribute('inert')) return;

            if (this.parentElement === document.body && this.originalParentBeforeAppend) {
              this.originalParentBeforeAppend.appendChild(this);
              this.originalParentBeforeAppend = null;
            }
            this.dispatchEvent(new CustomEvent(this.events.afterHide, { bubbles: true }));

            this.hidden = true;
          });
        }
        this.dispatchEvent(new CustomEvent('toggle', { bubbles: true }));

        break;
    }
  }

  onButtonClick(event) {
    event.preventDefault();

    this.open ? this.hide() : this.show(event.currentTarget);
  }

  hide() {
    if (!this.open) return;

    this.beforeHide();
    unlockBodyScroll();
    this.resetGesture();
    this.removeAttribute('open');

    return theme.utils.waitForEvent(this, this.events.afterHide);
  }
  show(activeElement = null, animate = true) {
    if (this.open) return;

    if (this.shouldCloseAll) {
      document.dispatchEvent(new CustomEvent(this.events.closeAll), { bubbles: true });
    }

    this.beforeShow();
    this.activeElement = activeElement;
    this.setAttribute('open', animate ? '' : 'immediate');

    if (this.shouldLock) {
      document.body.classList.add(this.classes.opening);
      lockBodyScroll();
    }

    return theme.utils.waitForEvent(this, this.events.afterShow);
  }

  beforeHide() { }
  beforeShow() { }

  afterHide() {
    setTimeout(() => {
      theme.a11y.removeTrapFocus(this.activeElement);
      console.log("afterHide");
      // document.querySelector('.editDrawerHeader')?.classList.remove('hidden');
      if (this.shouldLock) {
        lockLayerCount.set(ModalElement, lockLayerCount.get(ModalElement) - 1);

        document.body.classList.toggle(this.classes.open, lockLayerCount.get(ModalElement) > 0);
      }
    },100); 
  }
  afterShow() {
    theme.a11y.trapFocus(this, this.focusElement);
    
    if (this.shouldLock) {
      lockLayerCount.set(ModalElement, lockLayerCount.get(ModalElement) + 1);

      document.body.classList.remove(this.classes.opening);
      document.body.classList.add(this.classes.open);
    }
  }

  showTransition() {
    setTimeout(() => {
      this.setAttribute('active', '');
    }, 75);
    return new Promise((resolve) => {
      const computedStyle = window.getComputedStyle(this.overlay);
      const hasTransition = computedStyle.transitionProperty !== 'none' && parseFloat(computedStyle.transitionDuration) > 0;

      if (!hasTransition) {
        resolve();
        return;
      }

      this.overlay.addEventListener('transitionend', resolve, { once: true });
    });
  }
  hideTransition() {
    this.removeAttribute('active');
    return new Promise((resolve) => {
      this.overlay.addEventListener('transitionend', resolve, { once: true });
    });
  }

  resetGesture() {
    if (this.gesture) {
      this.gestureWrap.style.transform = '';
      this.gestureWrap.style.transition = '';
      this.overlay.style.opacity = '';
      this.overlay.style.transition = '';
    }
  }

  onPanStart() {
    this.removeTransition(this.gestureWrap, 'transform');

    if (this.hasAttribute('open')) {
      if (this.gestureConfig.layerHeight === null) {
        this.gestureConfig.layerHeight = this.gestureWrap.getBoundingClientRect().height;
      }
      this.gestureConfig.maxGestureDistance = this.gestureConfig.layerHeight - 50;
      this.gestureConfig.endPoint = this.gestureConfig.layerHeight * 0.3;
    }
  }

  onPanMove() {
    if (this.gestureConfig.animationFrame) return;

    this.gestureConfig.animationFrame = requestAnimationFrame(() => {
      const clamp = (a, min = 0, max = 1) => Math.min(max, Math.max(min, a));
      const invlerp = (x, y, a) => clamp((a - x) / (y - x));

      // Give an indication of whether we've passed the swiping threshold.
      this.gestureWrap.style.transition = 'transform .1s linear';
      this.overlay.style.transition = 'opacity .1s linear';

      if (this.hasAttribute('open')) {
        if (this.gestureConfig.layerHeight === null) {
          this.gestureConfig.layerHeight = this.gestureWrap.getBoundingClientRect().height;
        }

        this.gestureConfig.moveY = this.gesture.touchMoveY;
        this.gestureConfig.maxGestureDistance = this.gestureConfig.layerHeight - 50;

        if (this.gestureConfig.moveY >= 0) {
          this.gestureWrap.style.transform = `translateY(${Math.min(
            this.gestureConfig.moveY,
            this.gestureConfig.maxGestureDistance
          )}px)`;

          this.overlay.style.opacity = invlerp(this.gestureConfig.layerHeight, 0, this.gestureConfig.moveY);
        }
        else {
          this.gestureWrap.style.transform = 'translateY(0)';
          this.gestureWrap.style.transition = '';
          this.overlay.style.opacity = '1';
        }
      }

      this.gestureConfig.animationFrame = null;
    });
  }

  onPanEnd() {
    this.gestureConfig.animationFrame === null || cancelAnimationFrame(this.gestureConfig.animationFrame);
    this.gestureConfig.animationFrame = null;
    this.gestureWrap.style.transition = 'transform .1s linear';

    if (this.gestureConfig.layerHeight === null) {
      this.gestureConfig.layerHeight = this.gestureWrap.getBoundingClientRect().height;
    }

    this.gestureConfig.endPoint = this.gestureConfig.layerHeight * 0.3;

    if (this.hasAttribute('open')) {
      this.gestureConfig.moveY = this.gesture.touchMoveY;

      if (this.gestureConfig.moveY < this.gestureConfig.endPoint) {
        this.gestureWrap.style.transform = 'translateY(0)';
      }
      else {
        this.gestureWrap.style.transform = `translateY(${this.gestureConfig.layerHeight}px)`;

        this.gestureWrap.addEventListener(
          this.whichTransitionEvent(),
          () => {
            this.hide();
          },
          { once: true }
        );
      }
    }
  }

  removeTransition(node, transition) {
    const match = node.style.transition.match(new RegExp('(?:^|,)\\s*' + transition + '(?:$|\\s|,)[^,]*', 'i'));
    if (match) {
      const transitionArray = node.style.transition.split('');
      transitionArray.splice(match.index, match[0].length);
      node.style.transition = transitionArray.join('');
    }
  }

  whichTransitionEvent() {
    let t;
    const el = document.createElement('fakeelement');
    const transitions = {
      WebkitTransition: 'webkitTransitionEnd',
      MozTransition: 'transitionend',
      MSTransition: 'msTransitionEnd',
      OTransition: 'oTransitionEnd',
      transition: 'transitionEnd',
    };

    for (t in transitions) {
      if (el.style[t] !== undefined) {
        return transitions[t];
      }
    }
  }
}
customElements.define('modal-element', ModalElement);
lockLayerCount.set(ModalElement, 0);

class DrawerElement extends ModalElement {
  constructor() {
    super();

    this.events = {
      afterHide: 'drawer:afterHide',
      afterShow: 'drawer:afterShow'
    };
  }

  get shouldLock() {
    return true;
  }

  get shouldAppendToBody() {
    return true;
  }
}
customElements.define('drawer-element', DrawerElement);

class MenuDrawer extends DrawerElement {
  constructor() {
    super();
  }

  get menuItems() {
    return this._menuItems = this._menuItems || this.querySelectorAll('.drawer__menu:not(.active)>li');
  }

  beforeShow() {
    super.beforeShow();
    setTimeout(() => {
      Motion.animate(this.menuItems, { transform: ['translateX(-20px)', 'translateX(0)'], opacity: [0, 1] }, { duration: 0.6, easing: [.075, .82, .165, 1], delay: Motion.stagger(0.1) }).finished.then(() => {
        this.menuItems.forEach((item) => item.removeAttribute('style'));
      });
    }, 300);
  }

  beforeHide() {
    super.beforeHide();

    setTimeout(() => {
      this.querySelectorAll('details[is=menu-details]').forEach((subMenu) => {
        subMenu.onCloseButtonClick();
      });
    }, 300);
  }
}
customElements.define('menu-drawer', MenuDrawer);

class SampleProductDrawer extends DrawerElement {
  constructor() {
    super();
  }

  shouldLock(){
    return true;
  }

  beforeShow() {
    super.beforeShow();
    setTimeout(() => {
 
    }, 300);
  }

  hide(){
    super.hide();
    setTimeout(() => {
      document.body.classList.remove('has-modal-open');
    },400);
  }

  beforeHide() {
    super.beforeHide();

  }
}

customElements.define('sample-product-drawer', SampleProductDrawer);

class ShowMoreDrawer extends DrawerElement {
  constructor() {
    super();
  }
  shouldLock(){
    return true;
  }

  beforeShow() {
    super.beforeShow();
    setTimeout(() => {
 
    }, 300);
  }
  afterHide(){
    super.afterHide();
  }

  beforeHide() {
    super.beforeHide();

  }
  hide(){
    super.hide();
    setTimeout(() => {
      document.body.classList.remove('has-modal-open');
    },400);
  }
}

customElements.define('show-more-drawer', ShowMoreDrawer);

class ShareDrawer extends DrawerElement {
  constructor() {
    super();
  }

  get menuItems() {
    return this._menuItems = this._menuItems || this.querySelectorAll('.share-buttons>li');
  }

  get urlToShare() {
    const urlInput = this.querySelector('input[type=hidden]');
    return urlInput ? urlInput.value : document.location.href;
  }

  connectedCallback() {
    if (navigator.share) {
      this.controls.forEach((button) => button.addEventListener('click', this.shareTo.bind(this)));
    }
    else {
      super.connectedCallback();
    }
  }

  async shareTo(event) {
    event.preventDefault();
    try {
      await navigator.share({ url: this.urlToShare, title: document.title });
    }
    catch (error) {
      if (error.name === 'AbortError') {
        console.log('Share canceled by user');
      }
      else {
        console.error(error);
      }
    }
  }

  beforeShow() {
    super.beforeShow();
    setTimeout(() => {
      Motion.animate(this.menuItems, { transform: ['translateY(2.5rem)', 'translateY(0)'], opacity: [0, 1] }, { duration: 0.6, easing: [.075, .82, .165, 1], delay: Motion.stagger(0.1) }).finished.then(() => {
        this.menuItems.forEach((item) => item.removeAttribute('style'));
      });
    }, 300);
  }
}
customElements.define('share-drawer', ShareDrawer);

class BackInStockDrawer extends DrawerElement {
  constructor() {
    super();

    if (!theme.config.isTouch || Shopify.designMode) {
      this.init();
    }
    else {
      new theme.initWhenVisible(this.init.bind(this));
    }
  }

  get submited() {
    return this.querySelector('.alert') !== null;
  }

  init() {
    if (this.initialized) return;
    this.initialized = true;

    // Open modal if errors or success message exist
    if (this.submited) {
      setTimeout(() => {
        this.show();
      }, 1000);
    }
  }
}
customElements.define('back-in-stock-drawer', BackInStockDrawer);

class MenuDetails extends HTMLDetailsElement {
  constructor() {
    super();

    this.summary.addEventListener('click', this.onSummaryClick.bind(this));
    this.closeButton.addEventListener('click', this.onCloseButtonClick.bind(this));
  }

  get parent() {
    return this.closest('[data-parent]');
  }

  get summary() {
    return this.querySelector('summary');
  }

  get closeButton() {
    return this.querySelector('[data-close]');
  }

  get focusElement() {
    return this.summary.nextElementSibling.querySelector('button');
  }

  onSummaryClick(event) {
    event.preventDefault();
    this.setAttribute('open', '');
    this.activeElement = event.currentTarget;

    setTimeout(() => {
      this.parent.classList.add('active');
      this.classList.add('active');
      this.summary.setAttribute('aria-expanded', true);
      this.summary.setAttribute('tabindex', '-1');

      setTimeout(() => {
        theme.a11y.trapFocus(this, this.focusElement);
      }, 100);
    }, 100);
  }

  onCloseButtonClick() {
    this.parent.classList.remove('active');
    this.classList.remove('active');
    this.summary.setAttribute('aria-expanded', false);
    this.summary.removeAttribute('tabindex');

    this.closeAnimation();
  }

  closeAnimation() {
    let animationStart;

    const handleAnimation = (time) => {
      if (animationStart === undefined) {
        animationStart = time;
      }

      const elapsedTime = time - animationStart;

      if (elapsedTime < 400) {
        requestAnimationFrame(handleAnimation);
      }
      else {
        this.removeAttribute('open');
        theme.a11y.removeTrapFocus(this.activeElement);
      }
    }

    requestAnimationFrame(handleAnimation);
  }
}
customElements.define('menu-details', MenuDetails, { extends: 'details' });

class QuantityLabel extends HTMLLabelElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.querySelector('.quantity__rules-cart').append(this.animationElement);
  }

  static get observedAttributes() {
    return ['aria-busy'];
  }

  get contentElement() {
    return this._contentElement = this._contentElement || this.querySelector('.quantity-cart');
  }

  get animationElement() {
    return this._animationElement = this._animationElement || document.createRange().createContextualFragment(`
      <span class="btn-loader">
        <span></span>
        <span></span>
        <span></span>
      </span>
    `).firstElementChild;
  }

  async attributeChangedCallback(name, oldValue, newValue) {
    if (newValue === 'true') {
      Motion.timeline([
        [this.contentElement, { opacity: 0 }, { duration: 0.15 }],
        [this.animationElement, { opacity: 1 }, { duration: 0.15 }]
      ]);
      Motion.animate(this.animationElement.children, { transform: ['scale(1.6)', 'scale(0.6)'] }, { duration: 0.35, delay: Motion.stagger(0.35 / 2), direction: 'alternate', repeat: Infinity });
    }
    else {
      Motion.timeline([
        [this.animationElement, { opacity: 0 }, { duration: 0.15 }],
        [this.contentElement, { opacity: 1 }, { duration: 0.15 }]
      ]);
    }
  }
}
customElements.define('quantity-label', QuantityLabel, { extends: 'label' });

class QuantityInput extends HTMLElement {
  quantityUpdateUnsubscriber = undefined;
  quantityBoundriesUnsubscriber = undefined;
  quantityRulesUnsubscriber = undefined;

  constructor() {
    super();
  }

  get sectionId() {
    return this.getAttribute('data-section-id');
  }

  get productId() {
    return this.getAttribute('data-product-id');
  }

  get input() {
    return this.querySelector('input');
  }

  get value() {
    return this.input.value;
  }

  connectedCallback() {
    this.abortController = new AbortController();

    this.buttons = Array.from(this.querySelectorAll('button'));
    this.changeEvent = new Event('change', { bubbles: true });
    this.input.addEventListener('change', this.onInputChange.bind(this));
    this.input.addEventListener('focus', () => setTimeout(() => this.input.select()));

    this.buttons.forEach((button) => button.addEventListener('click', this.onButtonClick.bind(this)), { signal: this.abortController.signal });

    this.validateQtyRules();
    this.quantityUpdateUnsubscriber = theme.pubsub.subscribe(theme.pubsub.PUB_SUB_EVENTS.quantityUpdate, this.validateQtyRules.bind(this));
    this.quantityBoundriesUnsubscriber = theme.pubsub.subscribe(theme.pubsub.PUB_SUB_EVENTS.quantityBoundries, this.setQuantityBoundries.bind(this));
    this.quantityRulesUnsubscriber = theme.pubsub.subscribe(theme.pubsub.PUB_SUB_EVENTS.quantityRules, this.updateQuantityRules.bind(this));
  }

  disconnectedCallback() {
    this.abortController.abort();

    if (this.quantityUpdateUnsubscriber) {
      this.quantityUpdateUnsubscriber();
    }
    if (this.quantityBoundriesUnsubscriber) {
      this.quantityBoundriesUnsubscriber();
    }
    if (this.quantityRulesUnsubscriber) {
      this.quantityRulesUnsubscriber();
    }
  }

  onButtonClick(event) {
    event.preventDefault();
    const previousValue = this.input.value;

    if (event.currentTarget.name === 'plus') {
      if (parseInt(this.input.getAttribute('data-min')) > parseInt(this.input.step) && this.input.value == 0) {
        this.input.value = this.input.getAttribute('data-min');
      }
      else {
        this.input.stepUp();
      }
    } else {
      this.input.stepDown();
    }

    if (previousValue !== this.input.value) this.input.dispatchEvent(this.changeEvent);

    if (this.input.getAttribute('data-min') === previousValue && event.currentTarget.name === 'minus') {
      this.input.value = parseInt(this.input.min);
    }
  }

  onInputChange() {
    if (this.input.value === '') {
      this.input.value = parseInt(this.input.min);
    }
    this.validateQtyRules();
  }

  validateQtyRules() {
    const value = parseInt(this.input.value);
    if (this.input.min) {
      const buttonMinus = this.querySelector('button[name="minus"]');
      if (buttonMinus) buttonMinus.toggleAttribute('disabled', parseInt(value) <= parseInt(this.input.min));
    }
    if (this.input.max) {
      const buttonPlus = this.querySelector('button[name="plus"]');
      if (buttonPlus) buttonPlus.toggleAttribute('disabled', parseInt(value) >= parseInt(this.input.max));
    }
  }

  updateQuantityRules({ data: { sectionId, productId, parsedHTML } }) {
    if (sectionId !== this.sectionId || productId !== this.productId) return;

    const selectors = ['.quantity__input', '.quantity__rules', '.quantity__label'];
    const quantityFormUpdated = parsedHTML.getElementById(`QuantityForm-${sectionId}-${this.productId}`);
    const quantityForm = this.closest(`#QuantityForm-${sectionId}-${this.productId}`);
    for (let selector of selectors) {
      const current = quantityForm.querySelector(selector);
      const updated = quantityFormUpdated.querySelector(selector);
      if (!current || !updated) continue;

      if (selector === '.quantity__input') {
        const attributes = ['data-cart-quantity', 'data-min', 'data-max', 'step'];
        for (let attribute of attributes) {
          const valueUpdated = updated.getAttribute(attribute);
          if (valueUpdated !== null) {
            current.setAttribute(attribute, valueUpdated);
          }
          else {
            current.removeAttribute(attribute);
          }
        }
      }
      else {
        current.innerHTML = updated.innerHTML;
      }
    }
  }

  setQuantityBoundries({ data: { sectionId, productId } }) {
    if (sectionId !== this.sectionId || productId !== this.productId) return;

    const data = {
      cartQuantity: this.input.hasAttribute('data-cart-quantity') ? parseInt(this.input.getAttribute('data-cart-quantity')) : 0,
      min: this.input.hasAttribute('data-min') ? parseInt(this.input.getAttribute('data-min')) : 1,
      max: this.input.hasAttribute('data-max') ? parseInt(this.input.getAttribute('data-max')) : null,
      step: this.input.hasAttribute('step') ? parseInt(this.input.getAttribute('step')) : 1
    };

    let min = data.min;
    const max = data.max === null ? data.max : data.max - data.cartQuantity;
    if (max !== null) min = Math.min(min, max);
    if (data.cartQuantity >= data.min) min = Math.min(min, data.step);

    this.input.min = min;

    if (max) {
      this.input.max = max;
    }
    else {
      this.input.removeAttribute('max');
    }
    this.input.value = min;

    theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.quantityUpdate, undefined);
  }

  reset() {
    this.input.value = this.input.defaultValue;
  }
}
customElements.define('quantity-input', QuantityInput);

class CartCount extends HTMLElement {
  constructor() {
    super();
  }

  cartUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.cartUpdateUnsubscriber = theme.pubsub.subscribe(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, this.onCartUpdate.bind(this));
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  get itemCount() {
    return parseInt(this.innerText);
  }

  onCartUpdate(event) {
    if (event.cart.errors) return;

    this.innerText = event.cart.item_count;
    this.hidden = this.itemCount === 0;
  }
}
customElements.define('cart-count', CartCount);

class RecentlyViewed extends HTMLElement {
  constructor() {
    super();

    if (!theme.config.hasLocalStorage) {
      this.hidden = true;
      return;
    }

    Motion.inView(this, this.init.bind(this), { margin: '600px 0px 600px 0px' });
  }

  init() {
    fetch(this.getAttribute('data-url') + this.getQueryString())
      .then(response => response.text())
      .then(responseText => {
        const sectionInnerHTML = new DOMParser()
          .parseFromString(responseText, 'text/html')
          .querySelector('.shopify-section');

        const recommendations = sectionInnerHTML.querySelector('recently-viewed');
        if (recommendations && recommendations.innerHTML.trim().length) {
          this.innerHTML = recommendations.innerHTML;
          this.dispatchEvent(new CustomEvent('recentlyViewed:loaded'));
        }
        else {
          this.closest('.recently-section')?.remove();
          this.dispatchEvent(new CustomEvent('is-empty'));
        }
      })
      .catch(e => {
        console.error(e);
      });
  }

  getQueryString() {
    let recentlyViewed = '[]';
    if (theme.config.hasLocalStorage) {
      recentlyViewed = window.localStorage.getItem(`${theme.settings.themeName}:recently-viewed`) || '[]';
    }

    const items = new Set(JSON.parse(recentlyViewed));
    if (this.hasAttribute('data-product-id')) {
      items.delete(parseInt(this.getAttribute('data-product-id')));
    }
    return Array.from(items.values(), (item) => `id:${item}`).slice(0, this.hasAttribute('data-limit') ? parseInt(this.getAttribute('data-limit')) : 4).join(' OR ');
  }
}
customElements.define('recently-viewed', RecentlyViewed);

class ProductRecommendations extends HTMLElement {
  constructor() {
    super();

    Motion.inView(this, this.init.bind(this), { margin: '600px 0px 600px 0px' });
  }

  init() {
    fetch(this.getAttribute('data-url'))
      .then(response => response.text())
      .then(responseText => {
        const sectionInnerHTML = new DOMParser()
          .parseFromString(responseText, 'text/html')
          .querySelector('.shopify-section');

        if (sectionInnerHTML === null) return;

        const recommendations = sectionInnerHTML.querySelector('product-recommendations');
        if (recommendations && recommendations.innerHTML.trim().length) {
          this.innerHTML = recommendations.innerHTML;
          this.dispatchEvent(new CustomEvent('recommendations:loaded'));
        }
        else {
          this.setAttribute('hidden', '');
          this.closest('.recommendations-section')?.remove();
          this.dispatchEvent(new CustomEvent('is-empty'));
        }
      })
      .catch(e => {
        console.error(e);
      });
  }
}
customElements.define('product-recommendations', ProductRecommendations);

class ProductComplementary extends HTMLElement {
  constructor() {
    super();

    this.selectedIndex = this.selectedIndex;
  }

  static get observedAttributes() {
    return ['selected-index'];
  }

  get selectedIndex() {
    return parseInt(this.getAttribute('selected-index')) || 0;
  }

  set selectedIndex(index) {
    this.setAttribute('selected-index', Math.min(Math.max(index, 0), this.items.length - 1).toString());
  }

  get items() {
    return this._items = this._items || Array.from(this.children);
  }

  get container() {
    return this.closest('product-recommendations');
  }

  get prevButton() {
    return this.container.querySelector('[data-prev-button]');
  }

  get nextButton() {
    return this.container.querySelector('[data-next-button]');
  }

  connectedCallback() {
    if (this.innerHTML.trim().length) {
      if (this.classList.contains('flickity')) {
        this.carousel = new theme.Carousel(this, {
          accessibility: false,
          prevNextButtons: false,
          adaptiveHeight: true,
          pageDots: false,
          contain: true,
          cellAlign: 'center',
          rightToLeft: theme.config.rtl,
        }, {
          previous: this.prevButton,
          next: this.nextButton
        });

        this.carousel.load();
        this.carousel.slider.on('change', this.onChange.bind(this));
      }
    }
    else {
      this.container.hidden = true;
    }
  }

  disconnectedCallback() {
    if (this.carousel) {
      this.carousel.unload();
    }
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (name === 'selected-index' && oldValue !== null && oldValue !== newValue) {
      const focusableEvents = 'button, [href]';

      const fromElement = this.items[parseInt(oldValue)];
      const toElement = this.items[parseInt(newValue)];

      fromElement.querySelectorAll(focusableEvents).forEach((el) => {
        el.setAttribute('tabindex', '-1');
      });
      toElement.querySelectorAll(focusableEvents).forEach((el) => {
        el.removeAttribute('tabindex');
      });
    }
  }

  onChange() {
    this.selectedIndex = this.carousel.slider.selectedIndex;
  }
}
customElements.define('product-complementary', ProductComplementary);

class MediaElement extends HTMLElement {
  constructor() {
    super();
  }

  get parallax() {
    return this.hasAttribute('data-parallax') ? parseFloat(this.getAttribute('data-parallax')) : false;
  }

  get direction() {
    return this.hasAttribute('data-parallax-dir') ? this.getAttribute('data-parallax-dir') : 'vertical';
  }

  get media() {
    return Array.from(this.querySelectorAll('picture>img, video, iframe, svg, video-media, g-map'));
  }

  connectedCallback() {
    if (theme.config.motionReduced || !this.parallax) return;
    this.setupParallax();
  }

  setupParallax() {
    const [scale, translate] = [1 + this.parallax, this.parallax * 100 / (1 + this.parallax)];

    if (this.direction === 'vertical') {
      Motion.scroll(
        Motion.animate(this.media, { transform: [`scale(${scale}) translateY(0)`, `scale(${scale}) translateY(${translate}%)`], transformOrigin: ['bottom', 'bottom'] }, { easing: 'linear' }),
        { target: this, offset: ['start end', 'end start'] }
      );
    }
    else if (this.direction === 'horizontal') {
      Motion.scroll(
        Motion.animate(this.media, { transform: [`scale(${scale}) translateX(0)`, `scale(${scale}) translateX(${translate}%)`], transformOrigin: ['right', 'right'] }, { easing: 'linear' }),
        { target: this, offset: ['start end', 'end start'] }
      );
    }
    else {
      Motion.scroll(
        Motion.animate(this.media, { transform: [`scale(1)`, `scale(${scale})`], transformOrigin: ['center', 'center'] }, { easing: 'linear' }),
        { target: this, offset: ['start end', 'end start'] }
      );
    }
  }
}
customElements.define('media-element', MediaElement);

class SplitWords extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    if (theme.config.motionReduced) return;
    if (!document.body.hasAttribute('data-title-animation')) return;

    const splitting = Splitting({ target: this, by: 'words' });

    splitting[0].words.forEach((item, index) => {
      const wrapper = document.createElement('animate-element');
      wrapper.className = 'block';
      wrapper.setAttribute('data-animate', this.getAttribute('data-animate'));
      wrapper.setAttribute('data-animate-delay', (this.hasAttribute('data-animate-delay') ? parseInt(this.getAttribute('data-animate-delay')) : 0) + (index * 30));

      for (const itemContent of item.childNodes) {
        wrapper.appendChild(itemContent);
      }

      item.appendChild(wrapper);
    });
    this.classList.remove('js-invisible');
  }
}
customElements.define('split-words', SplitWords);

class HighlightedText extends HTMLElement {
  constructor() {
    super();

    Motion.inView(this, this.init.bind(this));
  }

  init() {
    this.classList.add('animate');
  }
}
customElements.define('highlighted-text', HighlightedText, { extends: 'em' });

class MarqueeElement extends HTMLElement {
  constructor() {
    super();

    if (theme.config.motionReduced) return;

    if (!theme.config.isTouch || Shopify.designMode) {
      Motion.inView(this, this.init.bind(this), { margin: '200px 0px 200px 0px' });
    }
    else {
      new theme.initWhenVisible(this.init.bind(this));
    }
  }

  get childElement() {
    return this.firstElementChild;
  }

  get speed() {
    return this.hasAttribute('data-speed') ? parseInt(this.getAttribute('data-speed')) : 16;
  }

  get maximum() {
    return this.hasAttribute('data-maximum') ? parseInt(this.getAttribute('data-maximum')) : Math.ceil(this.parentWidth / this.childElementWidth) + 2;
  }

  get direction() {
    return this.hasAttribute('data-direction') ? this.getAttribute('data-direction') : 'left';
  }

  get parallax() {
    return !theme.config.isTouch && this.hasAttribute('data-parallax') ? parseFloat(this.getAttribute('data-parallax')) : false;
  }

  get parentWidth() {
    return this.getWidth(this);
  }

  get childElementWidth() {
    return this.getWidth(this.childElement);
  }

  init() {
    if (this.initialized) return;
    this.initialized = true;

    if (this.childElementCount === 1) {
      this.childElement.classList.add('animate');

      for (let index = 0; index < this.maximum; index++) {
        this.clone = this.childElement.cloneNode(true);
        this.clone.setAttribute('aria-hidden', true);
        this.appendChild(this.clone);
        this.clone.querySelectorAll('.media').forEach((media) => media.classList.remove('loading'));
      }

      this.style.setProperty('--duration', `${(33 - this.speed) * Math.min(2.5, Math.ceil(this.childElementWidth / this.parentWidth))}s`);
    }

    if (this.parallax) {
      let translate = this.parallax * 100 / (1 + this.parallax);
      if (this.direction === 'right') {
        translate = translate * -1;
      }
      if (theme.config.rtl) {
        translate = translate * -1;
      }

      Motion.scroll(
        Motion.animate(this, { transform: [`translateX(${translate}%)`, `translateX(0)`] }, { easing: 'linear' }),
        { target: this, offset: ['start end', 'end start'] }
      );
    }
    else {
      // pause when out of view
      const observer = new IntersectionObserver((entries, _observer) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            this.classList.remove('paused');
          }
          else {
            this.classList.add('paused');
          }
        });
      }, { rootMargin: '0px 0px 50px 0px' });
      observer.observe(this);
    }
  }

  getWidth(element) {
    const rect = element.getBoundingClientRect();
    return rect.right - rect.left;
  }
}
customElements.define('marquee-element', MarqueeElement);

class ScrolledImages extends HTMLElement {
  constructor() {
    super();

    Motion.inView(this, this.init.bind(this), { margin: '200px 0px 200px 0px' });
  }

  get parallax() {
    return this.hasAttribute('data-parallax') ? parseFloat(this.getAttribute('data-parallax')) : false;
  }

  get template() {
    return this.querySelector('template');
  }

  get images() {
    return Array.from(this.template.content.querySelectorAll('.scrolled-images__item'));
  }

  init() {
    this.beforeInit();

    if (theme.config.motionReduced || !this.parallax) return;
    this.setupParallax();
  }

  beforeInit() {
    const main = this.querySelector('.scrolled-images__main');

    for (let i = 0; i < 3; i++) {
      let images = this.shuffle(this.images);

      if (images.length < 8) {
        let start = 0;
        while (images.length < 8) {
          images.push(images[start].cloneNode(true));
          start++;
        }
      }

      const row = document.createElement('div');
      row.classList = 'scrolled-images__row';
      images.forEach((item) => row.appendChild(item.cloneNode(true)));

      main.appendChild(row);
    }
  }

  setupParallax() {
    Array.from(this.querySelectorAll('.scrolled-images__row')).forEach((element, index) => {
      let translate = -1 * this.parallax * 100 / (1 + this.parallax);
      if (theme.config.rtl) {
        translate = translate * -1;
      }

      if (index % 2 === 0) {
        Motion.scroll(
          Motion.animate(element, { transform: [`translateX(${translate}%)`, 'translateX(0)'] }, { easing: 'linear' }),
          { target: this, offset: Motion.ScrollOffset.Any }
        );
      }
      else {
        Motion.scroll(
          Motion.animate(element, { transform: ['translateX(0)', `translateX(${translate}%)`] }, { easing: 'linear' }),
          { target: this, offset: Motion.ScrollOffset.Any }
        );
      }
    });
  }

  shuffle(arr) {
    return arr.sort(() => Math.random() - 0.5);
  }
}
customElements.define('scrolled-images', ScrolledImages);

class DropdownElement extends HTMLElement {
  constructor() {
    super();

    this.classes = {
      bodyClass: 'has-dropdown'
    };

    this.events = {
      afterHide: 'dropdown:afterHide',
      afterShow: 'dropdown:afterShow'
    };

    this.detectHoverListener = this.detectHover.bind(this);
    this.controls.forEach((button) => {
      button.addEventListener('click', this.onToggleClicked.bind(this));
      button.addEventListener('mouseenter', this.detectHoverListener.bind(this));
      button.addEventListener('mouseleave', this.detectHoverListener.bind(this));
    });

    this.detectClickOutsideListener = this.detectClickOutside.bind(this);
    this.detectEscKeyboardListener = this.detectEscKeyboard.bind(this);
    this.detectFocusOutListener = this.detectFocusOut.bind(this);
  }

  static get observedAttributes() {
    return ["id", 'open'];
  }

  get open() {
    return this.hasAttribute('open');
  }

  get controls() {
    return Array.from(document.querySelectorAll(`[aria-controls="${this.id}"]`));
  }

  get container() {
    return this.querySelector('*:first-child');
  }

  attributeChangedCallback(name, oldValue, newValue) {
    switch (name) {
      case 'open':
        this.controls.forEach((button) => button.setAttribute('aria-expanded', newValue === null ? 'false' : 'true'));
        break;
    }
  }

  show() {
    document.body.classList.add(this.classes.bodyClass);
    this.setAttribute('open', '');

    document.addEventListener('click', this.detectClickOutsideListener);
    document.addEventListener('keyup', this.detectEscKeyboardListener);
    document.addEventListener('focusout', this.detectFocusOutListener);

    this.afterShow();

    return theme.utils.waitForEvent(this, this.events.afterShow);
  }

  hide() {
    document.body.classList.remove(this.classes.bodyClass);
    this.removeAttribute('open');

    document.removeEventListener('click', this.detectClickOutsideListener);
    document.removeEventListener('keyup', this.detectEscKeyboardListener);
    document.removeEventListener('focusout', this.detectFocusOutListener);

    this.afterHide();

    return theme.utils.waitForEvent(this, this.events.afterHide);
  }

  onToggleClicked(event) {
    event?.preventDefault();
    this.open ? this.hide() : this.show();
  }

  afterShow() {
    Motion.animate(this, { opacity: [0, 1], visibility: 'visible' }, { duration: theme.config.motionReduced ? 0 : 0.6, easing: [.7, 0, .2, 1] });
    Motion.animate(this.container, { transform: ['translateY(-105%)', 'translateY(0)'] }, { duration: theme.config.motionReduced ? 0 : 0.6, easing: [.7, 0, .2, 1] });
  }

  afterHide() {
    Motion.animate(this, { opacity: 0, visibility: 'hidden' }, { duration: theme.config.motionReduced ? 0 : 0.3, easing: [.7, 0, .2, 1] });
    Motion.animate(this.container, { transform: 'translateY(-105%)' }, { duration: theme.config.motionReduced ? 0 : 0.6, easing: [.7, 0, .2, 1] });
  }

  detectClickOutside(event) {
    if (!this.parentElement.contains(event.target)) {
      this.hide();
    }
  }

  detectEscKeyboard(event) {
    if (event.code === 'Escape') {
      this.hide();
    }
  }

  detectFocusOut(event) {
    if (event.relatedTarget && !this.contains(event.relatedTarget)) {
      this.hide();
    }
  }

  detectHover(event) {
    if (theme.config.isTouch) return;

    if (event.type === 'mouseenter') {
      this.show();
    }
    else {
      this.hide();
    }
  }
}
customElements.define('dropdown-element', DropdownElement);

class DropdownLocalization extends DropdownElement {
  constructor() {
    super();
  }

  afterShow() {
    Motion.animate(this, { opacity: [0, 1], visibility: 'visible' }, { duration: theme.config.motionReduced ? 0 : 0.6, easing: [.7, 0, .2, 1] });
  }

  afterHide() {
    Motion.animate(this, { opacity: 0, visibility: 'hidden' }, { duration: theme.config.motionReduced ? 0 : 0.3, easing: [.7, 0, .2, 1] });
  }
}
customElements.define('dropdown-localization', DropdownLocalization);

const lockDropdownCount = new WeakMap();
class DetailsDropdown extends HTMLDetailsElement {
  constructor() {
    super();

    this.classes = {
      bodyClass: 'has-dropdown-menu'
    };

    this.events = {
      afterHide: 'menu:afterHide',
      afterShow: 'menu:afterShow'
    };

    this._open = this.hasAttribute('open');
    this.summaryElement.addEventListener('click', this.onSummaryClicked.bind(this));

    this.detectClickOutsideListener = this.detectClickOutside.bind(this);
    this.detectEscKeyboardListener = this.detectEscKeyboard.bind(this);
    this.detectFocusOutListener = this.detectFocusOut.bind(this);

    this.hoverTimer = null;
    this.detectHoverListener = this.detectHover.bind(this);
    this.addEventListener('mouseenter', this.detectHoverListener.bind(this));
    this.addEventListener('mouseleave', this.detectHoverListener.bind(this));
  }

  set open(value) {
    if (value !== this._open) {
      this._open = value;

      if (this.isConnected) {
        this.transition(value);
      }
      else {
        value ? this.setAttribute('open', '') : this.removeAttribute('open');
      }
    }
  }

  get summaryElement() {
    return this.firstElementChild;
  }

  get contentElement() {
    return this.lastElementChild;
  }

  get open() {
    return this._open;
  }

  get trigger() {
    return this.hasAttribute('trigger') ? this.getAttribute('trigger') : 'click';
  }

  get level() {
    return this.hasAttribute('level') ? this.getAttribute('level') : 'top';
  }

  get controlledElement() {
    return this.hasAttribute('aria-controls') ? document.getElementById(this.getAttribute('aria-controls')) : null;
  }

  onSummaryClicked(event) {
    event.preventDefault();

    if (!theme.config.isTouch && this.trigger === 'hover' && this.summaryElement.hasAttribute('data-link') && this.summaryElement.getAttribute('data-link').length > 0) {
      window.location.href = this.summaryElement.getAttribute('data-link');
    }
    else {
      this.open = !this.open;
    }
  }

  async transition(value) {
    if (value) {
      lockDropdownCount.set(DetailsDropdown, lockDropdownCount.get(DetailsDropdown) + 1);
      document.body.classList.add(this.classes.bodyClass);

      this.setAttribute('open', '');
      this.summaryElement.setAttribute('open', '');
      if (theme.config.motionReduced) {
        this.contentElement.setAttribute('open', '');
      } else {
        setTimeout(() => this.contentElement.setAttribute('open', ''), 100);
      }
      document.addEventListener('click', this.detectClickOutsideListener);
      document.addEventListener('keyup', this.detectEscKeyboardListener);
      document.addEventListener('focusout', this.detectFocusOutListener);
      await this.transitionIn();
      this.shouldReverse();

      return theme.utils.waitForEvent(this, this.events.afterShow);
    }
    else {
      lockDropdownCount.set(DetailsDropdown, lockDropdownCount.get(DetailsDropdown) - 1);
      document.body.classList.toggle(this.classes.bodyClass, lockDropdownCount.get(DetailsDropdown) > 0);

      this.summaryElement.removeAttribute('open');
      this.contentElement.removeAttribute('open');
      document.removeEventListener('click', this.detectClickOutsideListener);
      document.removeEventListener('keyup', this.detectEscKeyboardListener);
      document.removeEventListener('focusout', this.detectFocusOutListener);
      await this.transitionOut();
      if (!this.open) this.removeAttribute('open');

      return theme.utils.waitForEvent(this, this.events.afterHide);
    }
  }

  async transitionIn() {
    Motion.animate(this.contentElement, { opacity: [0, 1], visibility: 'visible' }, { duration: theme.config.motionReduced ? 0 : 0.6, easing: [.7, 0, .2, 1], delay: theme.config.motionReduced ? 0 : 0.2 });
    const translateY = this.level === 'top' ? '-105%' : '2rem';
    return Motion.animate(this.contentElement.firstElementChild, { transform: [`translateY(${translateY})`, 'translateY(0)'] }, { duration: theme.config.motionReduced ? 0 : 0.6, easing: [.7, 0, .2, 1] }).finished;
  }

  async transitionOut() {
    Motion.animate(this.contentElement, { opacity: 0, visibility: 'hidden' }, { duration: theme.config.motionReduced ? 0 : 0.3, easing: [.7, 0, .2, 1] });
    const translateY = this.level === 'top' ? '-105%' : '2rem';
    return Motion.animate(this.contentElement.firstElementChild, { transform: `translateY(${translateY})` }, { duration: theme.config.motionReduced ? 0 : 0.6, easing: [.7, 0, .2, 1] }).finished;
  }

  detectClickOutside(event) {
    if (!this.contains(event.target) && !(event.target.closest('details') instanceof DetailsDropdown)) {
      this.open = false;
    }
  }

  detectEscKeyboard(event) {
    if (event.code === 'Escape') {
      const targetMenu = event.target.closest('details[open]');
      if (targetMenu) {
        targetMenu.open = false;
      }
    }
  }

  detectFocusOut(event) {
    if (event.relatedTarget && !this.contains(event.relatedTarget)) {
      this.open = false;
    }
  }

  detectHover(event) {
    if (this.trigger !== 'hover' || theme.config.isTouch) return;

    if (event.type === 'mouseenter') {
      this.open = true;
    }
    else {
      this.open = false;
    }
  }

  shouldReverse() {
    const maxWidth = this.contentElement.offsetLeft + this.contentElement.clientWidth * 2;
    if (maxWidth > window.innerWidth) {
      this.contentElement.classList.add('should-reverse');
    }
  }
}
customElements.define('details-dropdown', DetailsDropdown, { extends: 'details' });
lockDropdownCount.set(DetailsDropdown, 0);

class DetailsMega extends DetailsDropdown {
  constructor() {
    super();

    if (Shopify.designMode) {
      this.addEventListener('shopify:block:select', () => this.open = true);
      this.addEventListener('shopify:block:deselect', () => this.open = false);
    }
  }

  async transitionIn() {
    this.contentElement.querySelector('tabs-element')?.unload();
    setTimeout(() => {
      this.contentElement.querySelector('tabs-element')?.load();
    }, theme.config.motionReduced ? 0 : 450);

    document.body.classList.add('with-mega');
    return Motion.animate(this.contentElement.firstElementChild, { visibility: 'visible', transform: ['translateY(-105%)', 'translateY(0)'] }, { duration: theme.config.motionReduced ? 0 : 0.6, easing: [.7, 0, .2, 1] }).finished;
  }

  async transitionOut() {
    document.body.classList.remove('with-mega');
    return Motion.animate(this.contentElement.firstElementChild, { visibility: 'hidden', transform: 'translateY(-105%)' }, { duration: theme.config.motionReduced ? 0 : 0.6, easing: [.7, 0, .2, 1] }).finished;
  }
}
customElements.define('details-mega', DetailsMega, { extends: 'details' });

class LocalizationListbox extends HTMLFormElement {
  constructor() {
    super();

    this.items.forEach((item) => item.addEventListener('click', this.onItemClick.bind(this)));
  }

  get items() {
    return this._items = this._items || Array.from(this.querySelectorAll('a'));
  }

  get input() {
    return this.querySelector('input[name="locale_code"], input[name="country_code"]');
  }

  get returnTo() {
    return this.querySelector('input[name="return_to"]');
  }

  onItemClick(event) {
    event.preventDefault();

    this.input.value = event.currentTarget.getAttribute('data-value');
    this.returnTo.value = window.location.pathname;
    this.submit();
  }
}
customElements.define('localization-listbox', LocalizationListbox, { extends: 'form' });

class LocalizationForm extends HTMLFormElement {
  constructor() {
    super();

    if (!theme.config.isTouch || Shopify.designMode) {
      Motion.inView(this, this.init.bind(this));
    }
    else {
      new theme.initWhenVisible(this.init.bind(this));
    }
  }

  init() {
    if (this.initialized) return;
    this.initialized = true;

    this.addEventListener('change', this.submit);
  }
}
customElements.define('localization-form', LocalizationForm, { extends: 'form' });

const cachedSectionsRenderingAPI = new Map();
class APIButton extends HTMLElement {
  constructor() {
    super();

    Motion.inView(this, this.init.bind(this));
  }

  get sectionId() {
    return this.getAttribute('data-section-id');
  }

  get sectionRenderingId() {
    return this.getAttribute('data-id');
  }

  init() {
    if (this.initialized) return;
    this.initialized = true;

    if (document.getElementById(this.sectionRenderingId) === null || document.getElementById(this.sectionRenderingId).hasAttribute('loaded')) return;

    const url = `${theme.routes.root_url}?section_id=${this.sectionId}`;
    cachedSectionsRenderingAPI.has(url)
      ? this.renderSectionFromCache(url)
      : this.renderSectionFromFetch(url);

    this.sectionsRenderingAPIListener = this.afterSectionsRenderingAPI.bind(this);
    document.addEventListener('sectionsRenderingAPI:cached', this.sectionsRenderingAPIListener);
  }

  renderSectionFromCache(url) {
    const responseText = cachedSectionsRenderingAPI.get(url);
    if (responseText.length) {
      this.updateSectionRendering(responseText);
    }
  }

  renderSectionFromFetch(url) {
    cachedSectionsRenderingAPI.set(url, '');
    fetch(url)
      .then(response => response.text())
      .then(responseText => {
        this.updateSectionRendering(responseText);
        cachedSectionsRenderingAPI.set(url, responseText);

        document.dispatchEvent(new CustomEvent('sectionsRenderingAPI:cached', {
          detail: {
            url,
            sectionId: this.sectionId
          }
        }));
      })
      .catch(e => {
        console.error(e);
      });
  }

  updateSectionRendering(responseText) {
    const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');
    document.getElementById(this.sectionRenderingId).replaceWith(parsedHTML.getElementById(this.sectionRenderingId));

    // remove listener
    document.removeEventListener('sectionsRenderingAPI:cached', this.sectionsRenderingAPIListener);
  }

  afterSectionsRenderingAPI(event) {
    if (event.detail.sectionId === this.sectionId) {
      this.renderSectionFromCache(event.detail.url);
    }
  }
}
customElements.define('api-button', APIButton);

class StickyElement extends HTMLElement {
  constructor() {
    super();

    this.endScroll = window.innerHeight - this.offsetHeight - 500;
    this.currPos = window.scrollY;
    this.screenHeight = window.innerHeight;
    this.stickyElementHeight = this.offsetHeight;
    this.bottomGap = this.offsetHeight < window.innerHeight ? 20 : 0;

    window.addEventListener('scroll', theme.utils.throttle(this.onScrollHandler.bind(this)), {
      capture: true,
      passive: true,
    });
  }

  get headerHeight() {
    const header = document.querySelector('.header-section header[is="sticky-header"]');
    return header ? Math.round(header.clientHeight) + 20 : (this.offsetHeight < window.innerHeight ? 20 : 0);
  }

  get inset() {
    return this.style.getPropertyValue('--inset') || 0;
  }

  onScrollHandler() {
    this.screenHeight = window.innerHeight;
    this.stickyElementHeight = this.offsetHeight;

    this.positionStickySidebar();
  }

  positionStickySidebar() {
    this.endScroll = window.innerHeight - this.offsetHeight - this.bottomGap;
    const style = window.getComputedStyle(this);
    const stickyElementTop = parseInt(style.insetBlockStart.replace('px', ''));

    if (this.stickyElementHeight + this.headerHeight + this.bottomGap > this.screenHeight) {
      if (window.scrollY < this.currPos) {
        // Scroll up
        if (stickyElementTop < this.headerHeight) {
          this.style.insetBlockStart = `${stickyElementTop + this.currPos - window.scrollY}px`;
          this.style.setProperty('--inset', `${stickyElementTop + this.currPos - window.scrollY}px`);
        }
        else if (stickyElementTop >= this.headerHeight && stickyElementTop !== this.headerHeight) {
          this.style.insetBlockStart = `${this.headerHeight}px`;
          this.style.setProperty('--inset', `${this.headerHeight}px`);
        }
      }
      else {
        // Scroll down
        if (stickyElementTop > this.endScroll) {
          this.style.insetBlockStart = `${stickyElementTop + this.currPos - window.scrollY}px`;
          this.style.setProperty('--inset', `${stickyElementTop + this.currPos - window.scrollY}px`);
        }
        else if (stickyElementTop < this.endScroll && stickyElementTop !== this.endScroll) {
          this.style.insetBlockStart = `${this.endScroll}px`;
          this.style.setProperty('--inset', `${this.endScroll}px`);
        }
      }
    }
    else {
      this.style.insetBlockStart = `${this.headerHeight}px`;
      this.style.setProperty('--inset', `${this.headerHeight}px`);
    }

    this.currPos = window.scrollY;
  }
}
customElements.define('sticky-element', StickyElement);

class ParallaxElement extends HTMLDivElement {
  constructor() {
    super();

    this.load();

    document.addEventListener('matchSmall', this.unload.bind(this));
    document.addEventListener('unmatchSmall', this.load.bind(this));
  }

  get mobileDisabled() {
    return false;
  }

  load() {
    if (theme.config.motionReduced) return;
    if (this.mobileDisabled && (theme.config.isTouch || theme.config.mqlSmall)) return;

    this.motion();
  }

  motion() {
    this.animation = Motion.scroll(
      Motion.animate(this, { transform: [`translateY(${this.getAttribute('data-start')})`, `translateY(${this.getAttribute('data-stop')})`] }),
      { target: this, offset: Motion.ScrollOffset.Any }
    );
  }

  unload() {
    if (this.animation) {
      this.animation();
      this.style.transform = null;
    }
  }
}
customElements.define('parallax-element', ParallaxElement, { extends: 'div' });

class ParallaxOverlay extends HTMLElement {
  constructor() {
    super();

    this.load();

    if (Shopify.designMode) {
      document.addEventListener('shopify:section:unload', this.refresh.bind(this));
    }
  }

  refresh() {
    let options = {};
    options[this.getAttribute('data-target')] = this.getAttribute('data-stop');

    Motion.animate(this, options, { duration: 0 });
  }

  load() {
    let options = {};
    options[this.getAttribute('data-target')] = [this.getAttribute('data-start'), this.getAttribute('data-stop')];

    this.animation = Motion.scroll(
      Motion.animate(this, options),
      { target: this.parentElement, offset: Motion.ScrollOffset.Enter }
    );
  }
}
customElements.define('parallax-overlay', ParallaxOverlay);

class FooterParallax extends ParallaxElement {
  constructor() {
    super();

    if (Shopify.designMode) {
      document.addEventListener('shopify:section:unload', this.refresh.bind(this));
    }
  }

  get mobileDisabled() {
    return true;
  }

  motion() {
    this.animation = Motion.scroll(
      Motion.animate(this, { transform: ['translateY(-50%)', 'translateY(0)'] }),
      { target: this, offset: Motion.ScrollOffset.Enter }
    );
  }

  refresh() {
    if (theme.config.motionReduced) return;
    if (this.mobileDisabled && (theme.config.isTouch || theme.config.mqlSmall)) return;

    setTimeout(() => {
      Motion.animate(this, { transform: 'translateY(0)' }, { duration: 0 });
    });
  }
}
customElements.define('footer-parallax', FooterParallax, { extends: 'div' });

class FooterGroup extends HTMLElement {
  constructor() {
    super();

    this.init();

    if (Shopify.designMode) {
      document.addEventListener('shopify:section:load', this.init.bind(this));
    }
  }

  get rounded() {
    return Array.from(this.querySelectorAll('.section--rounded'));
  }

  get sections() {
    return Array.from(this.querySelectorAll('.shopify-section'));
  }

  init() {
    this.sections.forEach((shopifySection, index) => {
      const section = shopifySection.querySelector('.section');
      if (section) {
        section.classList.remove('section--next-rounded');
        section.style.zIndex = this.sections.length - index;
      }
    });

    this.rounded.forEach((section) => {
      const shopifySection = section.closest('.shopify-section');
      let previousShopifySection = shopifySection.previousElementSibling;

      if (previousShopifySection === null) {
        previousShopifySection = document.querySelector('.main-content .shopify-section:last-child');
      }

      if (previousShopifySection) {
        const previousSection = previousShopifySection.querySelector('.section');
        if (previousSection) {
          previousSection.classList.add('section--next-rounded');
        }
      }
    });
  }
}
customElements.define('footer-group', FooterGroup, { extends: 'footer' });

class CarouselElement extends HTMLElement {
  constructor() {
    super();

    Motion.inView(this, this.init.bind(this), { margin: '200px 0px 200px 0px' });
  }

  get items() {
    return this._items = this._items || Array.from(this.children);
  }

  get watchCSS() {
    return this.hasAttribute('watch-css');
  }

  get initialIndex() {
    return parseInt(this.getAttribute('initial-index') || 0);
  }

  init() {
    if (this.items.length > 1) {
      this.carousel = new Flickity(this, {
        watchCSS: this.watchCSS,
        prevNextButtons: false,
        adaptiveHeight: true,
        wrapAround: true,
        rightToLeft: theme.config.rtl,
        initialIndex: this.initialIndex,
      });

      this.addEventListener('control:select', (event) => this.select(event.detail.index));
      this.carousel.on('change', this.onChange.bind(this));

      if (Shopify.designMode) {
        this.addEventListener('shopify:block:select', (event) => this.carousel.select(this.items.indexOf(event.target)));
      }
    }
  }

  disconnectedCallback() {
    if (this.carousel) this.carousel.destroy();
  }

  select(index = 0, immediate = false) {
    if (!immediate) {
      const { selectedIndex, slides } = this.carousel;

      immediate = Math.abs(index - selectedIndex) > 3;

      if (index === 0 && selectedIndex === slides.length - 1) {
        immediate = false;
      }

      if (index === slides.length - 1 && selectedIndex === 0) {
        immediate = false;
      }
    }
    this.carousel.select(index, false, immediate);
  }

  onChange(index) {
    this.dispatchEvent(new CustomEvent('carousel:change', { bubbles: true, detail: { index } }));
  }
}
customElements.define('carousel-element', CarouselElement);

class TestimonialsElement extends CarouselElement {
  constructor() {
    super();

    Motion.inView(this, () => {
      setTimeout(() => this.update(), 600);
    });
  }

  update() {
    if (this.carousel) this.carousel.select(0);
  }
}
customElements.define('testimonials-element', TestimonialsElement);

class SecondaryMedia extends HTMLElement {
  constructor() {
    super();

    Motion.inView(this, () => {
      setTimeout(() => this.init());
    }, { margin: '200px 0px 200px 0px' });
  }

  get template() {
    return this.previousElementSibling;
  }

  disconnectedCallback() {
    if (this.carousel) this.carousel.destroy();
  }

  init() {
    this.appendChild(this.template.content.cloneNode(true));
    this.mediaCount = this.querySelectorAll('.media').length;

    if (this.mediaCount > 1) {
      this.carousel = new Flickity(this, {
        accessibility: false,
        draggable: false,
        pageDots: true,
        prevNextButtons: false,
        wrapAround: true,
        rightToLeft: theme.config.rtl,
      });

      if (this.mediaCount === 2) {
        this.classList.add('without-dots');
      }

      this.addEventListener('mousemove', this.onMoveHandler);
      this.addEventListener('mouseleave', this.onLeaveHandler);
    }
  }

  onMoveHandler(event) {
    if (this.mediaCount === 2) {
      return this.carousel.select(1);
    }

    const { width } = this.carousel.size;
    const mouseX = event.clientX - this.getBoundingClientRect().left;

    if (this.mediaCount === 3) {
      if (mouseX < width / 2) {
        return this.carousel.select(1);
      }
      return this.carousel.select(2);
    }

    if (this.mediaCount === 4) {
      if (mouseX < width / 3) {
        return this.carousel.select(1);
      }
      if (mouseX < (2 * width) / 3) {
        return this.carousel.select(2);
      }
      return this.carousel.select(3);
    }
  }

  onLeaveHandler() {
    this.carousel.select(0);
  }
}
customElements.define('secondary-media', SecondaryMedia);

class MotionList extends HTMLElement {
  constructor() {
    super();

    this.isMobile = theme.config.mqlSmall || theme.config.isTouch;
    this.motionReduced = theme.config.motionReduced || this.hasAttribute('motion-reduced');

    if (this.motionReduced || this.initialized) return;

    this.unload();
    Motion.inView(this, this.load.bind(this));
  }

  get items() {
    return Array.from(this.querySelectorAll('.card'));
  }

  get itemsToShow() {
    return Array.from(this.querySelectorAll('.card:not([style])'));
  }

  get initialized() {
    return this.hasAttribute('initialized');
  }

  getAnimationParams() {
    return {
      distance: this.isMobile ? 30 : 50,
      duration: this.motionReduced ? 0 : (this.isMobile ? 0.3 : 0.5),
      staggerDelay: this.motionReduced ? 0 : (this.isMobile ? 0.05 : 0.1)
    };
  }

  getVisibleItems(items) {
    const viewportHeight = window.innerHeight + window.scrollY;
    const visible = [];
    const invisible = [];

    items.forEach(item => {
      const rect = item.getBoundingClientRect();
      const isVisible = rect.top < viewportHeight && rect.bottom > 0;

      if (isVisible) {
        visible.push(item);
      } else {
        invisible.push(item);
      }
    });

    return { visible, invisible };
  }

  unload() {
    const { distance } = this.getAnimationParams();
    const visibleItems = this.items;

    Motion.animate(visibleItems, { y: distance, opacity: 0, visibility: 'hidden' }, { duration: 0 });
  }

  async load() {
    const { distance, duration, staggerDelay } = this.getAnimationParams();
    const { visible: visibleItems, invisible: inVisibleItems } = this.getVisibleItems(this.items);

    await Motion.animate(visibleItems, { y: [distance, 0], opacity: [0, 1], visibility: ['hidden', 'visible'] }, { duration: duration, delay: Motion.stagger(staggerDelay) }).finished;
    Motion.animate(inVisibleItems, { y: [distance, 0], opacity: [0, 1], visibility: ['hidden', 'visible'] }, { duration: duration });

    this.items.forEach(item => item.style.removeProperty('transform'));
    this.setAttribute('initialized', '');
  }

  async reload() {
    const { distance, duration, staggerDelay } = this.getAnimationParams();
    const { visible: visibleItems, invisible: inVisibleItems } = this.getVisibleItems(this.itemsToShow);

    await Motion.animate(visibleItems, { y: [distance, 0], opacity: [0, 1], visibility: ['hidden', 'visible'] }, { duration: duration, delay: Motion.stagger(staggerDelay) }).finished;
    Motion.animate(inVisibleItems, { y: [distance, 0], opacity: [0, 1], visibility: ['hidden', 'visible'] }, { duration: duration });

    this.items.forEach(item => item.style.removeProperty('transform'));
  }
}
customElements.define('motion-list', MotionList);

class LazyBackground extends HTMLElement {
  constructor() {
    super();

    this.init();
  }

  get image() {
    const style = window.getComputedStyle(this);
    return style.backgroundImage ? style.backgroundImage.slice(5, -2).replace('width=1', 'width=720') : false;
  }

  async init() {
    if (!this.image) return;

    const img = document.createElement('img');
    img.src = this.image;
    img.style.visibility = 'hidden';
    img.style.position = 'absolute';

    await theme.utils.imageLoaded(img);
    this.style.backgroundImage = `url(${this.image})`;
    img.remove();
  }
}
customElements.define('lazy-background', LazyBackground);

class MenuToggle extends MagnetButton {
  constructor() {
    super();

    this.addEventListener('click', this.onClick);
  }

  get controlledElement() {
    return this.hasAttribute('aria-controls') ? document.getElementById(this.getAttribute('aria-controls')) : null;
  }

  get expanded() {
    return this.getAttribute('aria-expanded') === 'true';
  }

  onClick() {
    this.setAttribute('aria-expanded', this.expanded ? 'false' : 'true');
    if (this.controlledElement) this.controlledElement.classList.toggle('active');
  }
}
customElements.define('menu-toggle', MenuToggle, { extends: 'button' });

class ScrollShadow extends HTMLElement {
  constructor() {
    super();

    Motion.inView(this, this.init.bind(this));
  }

  get template() {
    return this.querySelector('template');
  }

  init() {
    this.attachShadow({ mode: 'open' });
    this.shadowRoot.appendChild(this.template.content.cloneNode(true));
    this.shadowRoot.addEventListener('slotchange', () => this.load());
    this.updater = new theme.scrollShadow.Updater(this.shadowRoot.children[1]);

    this.load();
  }

  load() {
    this.updater.on(this.children[0]);
  }

  disconnectedCallback() {
    this.updater?.on();
  }
}
customElements.define('scroll-shadow', ScrollShadow);

class CustomSelect extends HTMLSelectElement {
  constructor() {
    super();

    this.onChange();
    this.addEventListener('change', this.onChange);
  }

  onChange() {
    this.value !== '' || this.selectedIndex === -1 ? this.setAttribute('selected', '') : this.removeAttribute('selected');
  }
}
customElements.define('custom-select', CustomSelect, { extends: 'select' });

class GMap extends HTMLElement {
  constructor() {
    super();

    if (!this.hasAttribute('data-api-key') || !this.hasAttribute('data-map-address')) {
      return;
    }

    Motion.inView(this, this.prepMapApi.bind(this), { margin: '600px 0px 600px 0px' });

    // Global function called by Google on auth errors.
    // Show an auto error message on all map instances.
    window.gm_authFailure = () => {

      // Show errors only to merchant in the editor.
      if (Shopify.designMode) {
        window.mapError(theme.strings.authError);
      }
    };

    window.mapError = (error, element) => {
      const container = element ? element.closest('.shopify-section') : document;
      container.querySelectorAll('.gmap--error').forEach((error) => {
        error.remove();
      });

      container.querySelectorAll('g-map').forEach((map) => {
        const message = document.createElement('div');
        message.classList.add('rte', 'alert', 'alert--error', 'gmap--error');
        message.innerHTML = error;
        map.closest('.with-map').prepend(message);
      });
    };

    window.gmNoop = () => { };
  }

  prepMapApi() {
    this.loadScript()
      .then(this.initMap.bind(this))
      .then(() => {
        setTimeout(() => {
          const container = this.closest('.banner__map');
          if (container && container.previousElementSibling) {
            container.previousElementSibling.classList.remove('opacity-0');
          }
        }, 1e3);
      });
  }

  loadScript() {
    return new Promise((resolve, reject) => {
      const script = document.createElement('script');
      document.body.appendChild(script);
      script.onload = resolve;
      script.onerror = reject;
      script.async = true;
      script.src = `https://maps.googleapis.com/maps/api/js?key=${this.getAttribute('data-api-key')}&callback=gmNoop`;
    });
  }

  initMap() {
    const geocoder = new google.maps.Geocoder();
    geocoder.geocode({ address: this.getAttribute('data-map-address') }, (results, status) => {
      if (status !== google.maps.GeocoderStatus.OK) {

        // Show errors only to merchant in the editor.
        if (Shopify.designMode) {
          let errorMessage;

          switch (status) {
            case 'ZERO_RESULTS':
              errorMessage = theme.strings.addressNoResults;
              break;
            case 'OVER_QUERY_LIMIT':
              errorMessage = theme.strings.addressQueryLimit;
              break;
            case 'REQUEST_DENIED':
              errorMessage = theme.strings.authError;
              break;
            default:
              errorMessage = theme.strings.addressError;
              break;
          }
          window.mapError(errorMessage, this);
        }
      }
      else {
        const mapOptions = {
          zoom: parseInt(this.getAttribute('data-zoom')),
          center: results[0].geometry.location,
          draggable: true,
          clickableIcons: false,
          scrollwheel: false,
          disableDoubleClickZoom: true,
          disableDefaultUI: true
        };

        const map = new google.maps.Map(this, mapOptions), center = map.getCenter();

        map.setCenter(center);

        const icon = {
          path: "M22.6746 0C10.2174 0 0 8.79169 0 21.5118C0 31.2116 4.33864 38.333 9.26606 42.998C11.7232 45.3243 14.3387 47.0534 16.6674 48.2077C18.9384 49.3333 21.1148 50 22.6746 50C24.2345 50 26.4108 49.3333 28.6818 48.2077C31.0105 47.0534 33.626 45.3243 36.0832 42.998C41.0106 38.333 45.3492 31.2116 45.3492 21.5118C45.3492 8.79169 35.1318 0 22.6746 0ZM29.6514 22.6746C29.6514 26.5278 26.5278 29.6514 22.6746 29.6514C18.8214 29.6514 15.6978 26.5278 15.6978 22.6746C15.6978 18.8214 18.8214 15.6978 22.6746 15.6978C26.5278 15.6978 29.6514 18.8214 29.6514 22.6746Z",
          fillColor: this.getAttribute('data-marker-color'),
          fillOpacity: 1,
          anchor: new google.maps.Point(15, 55),
          strokeWeight: 0,
          scale: 0.7
        };

        new google.maps.Marker({
          map: map,
          position: map.getCenter(),
          icon: icon
        });

        const styledMapType = new google.maps.StyledMapType(JSON.parse(this.parentNode.querySelector('[data-gmap-style]').innerHTML));

        map.mapTypes.set('styled_map', styledMapType);
        map.setMapTypeId('styled_map');
      }
    });
  }
}
customElements.define('g-map', GMap);

class GMapLocations extends HTMLUListElement {
  constructor() {
    super();

    this.selectedIndex = this.selectedIndex;
    this.buttons.forEach((button, index) => button.addEventListener('click', () => this.selectedIndex = index));

    if (Shopify.designMode) {
      this.addEventListener('shopify:block:select', (event) => this.selectedIndex = this.buttons.indexOf(event.target));
    }
  }

  static get observedAttributes() {
    return ['selected-index'];
  }

  get selectedIndex() {
    return parseInt(this.getAttribute('selected-index')) || 0;
  }

  set selectedIndex(index) {
    this.setAttribute('selected-index', Math.min(Math.max(index, 0), this.buttons.length - 1).toString());
  }

  get buttons() {
    return this._buttons = this._buttons || Array.from(this.hasAttribute('selector') ? this.querySelectorAll(this.getAttribute('selector')) : this.children);
  }

  get controlledElement() {
    return this.hasAttribute('aria-controls') ? document.getElementById(this.getAttribute('aria-controls')) : null;
  }

  attributeChangedCallback(name, oldValue, newValue) {
    this.buttons.forEach((button, index) => {
      button.classList.toggle('active', index === parseInt(newValue));
      button.querySelector('[data-map-button]')?.classList.toggle('button--secondary', index !== parseInt(newValue));
    });

    if (name === 'selected-index' && oldValue !== null && oldValue !== newValue) {
      if (this.controlledElement) {
        const button = this.buttons[parseInt(newValue)];
        this.controlledElement.setAttribute('data-map-address', button.getAttribute('data-map-address'));
        this.controlledElement.initMap();
      }
    }
  }
}
customElements.define('g-map-locations', GMapLocations, { extends: 'ul' });

class PreviousButton extends HoverButton {
  constructor() {
    super();

    this.load();
  }

  get controlledElement() {
    return this.hasAttribute('aria-controls') ? document.getElementById(this.getAttribute('aria-controls')) : null;
  }

  load() {
    this.onClickListener = this.onClick.bind(this);
    this.addEventListener('click', this.onClickListener);

    if (this.controlledElement) {
      this.updateStatusListener = this.updateStatus.bind(this);
      this.controlledElement.addEventListener('slider:previousStatus', this.updateStatusListener);
    }
  }

  unload() {
    this.removeEventListener('click', this.onClickListener);
    if (this.controlledElement) {
      this.controlledElement.removeEventListener('slider:previousStatus', this.updateStatusListener);
    }
  }

  onClick() {
    (this.controlledElement ?? this).dispatchEvent(new CustomEvent('slider:previous', { bubbles: true, cancelable: true }));
  }

  updateStatus(event) {
    switch (event.detail.status) {
      case 'hidden':
        this.hidden = true;
        break;

      case 'disabled':
        this.disabled = true;
        break;

      default:
        this.hidden = false;
        this.disabled = false;
    }
  }
}
customElements.define('previous-button', PreviousButton, { extends: 'button' });

class NextButton extends HoverButton {
  constructor() {
    super();

    this.load();
  }

  get controlledElement() {
    return this.hasAttribute('aria-controls') ? document.getElementById(this.getAttribute('aria-controls')) : null;
  }

  load() {
    this.onClickListener = this.onClick.bind(this);
    this.addEventListener('click', this.onClickListener);

    if (this.controlledElement) {
      this.updateStatusListener = this.updateStatus.bind(this);
      this.controlledElement.addEventListener('slider:nextStatus', this.updateStatusListener);
    }
  }

  unload() {
    this.removeEventListener('click', this.onClickListener);
    if (this.controlledElement) {
      this.controlledElement.removeEventListener('slider:nextStatus', this.updateStatusListener);
    }
  }

  onClick() {
    (this.controlledElement ?? this).dispatchEvent(new CustomEvent('slider:next', { bubbles: true, cancelable: true }));
  }

  updateStatus(event) {
    switch (event.detail.status) {
      case 'hidden':
        this.hidden = true;
        break;

      case 'disabled':
        this.disabled = true;
        break;

      default:
        this.hidden = false;
        this.disabled = false;
    }
  }
}
customElements.define('next-button', NextButton, { extends: 'button' });

class SliderElement extends HTMLElement {
  constructor() {
    super();

    Motion.inView(this, this.init.bind(this), { margin: '200px 0px 200px 0px' });
  }

  get looping() {
    return false;
  }

  get items() {
    return this._items = this._items || Array.from(this.hasAttribute('selector') ? this.querySelectorAll(this.getAttribute('selector')) : this.children);
  }

  get itemsToShow() {
    return Array.from(this.items).filter(element => element.clientWidth > 0);
  }

  get itemOffset() {
    if (theme.config.rtl) {
      return this.itemsToShow.length > 1 ? this.itemsToShow[0].offsetLeft - this.itemsToShow[1].offsetLeft : 0;
    }

    return this.itemsToShow.length > 1 ? this.itemsToShow[1].offsetLeft - this.itemsToShow[0].offsetLeft : 0;
  }

  get perPage() {
    let elementWidth = this.clientWidth;
    const mql = window.matchMedia('screen and (min-width: 1280px)');
    if (mql.matches) {
      const styles = window.getComputedStyle(this);
      elementWidth = this.clientWidth - parseFloat(styles.paddingLeft) - parseFloat(styles.paddingRight);
    }

    return Math.floor(elementWidth / this.itemOffset);
  }

  get isScrollable() {
    const style = window.getComputedStyle(this);
    const hasScrollableOverflow = style.overflow === 'scroll' || style.overflow === 'auto' || style.overflowY === 'scroll' || style.overflowY === 'auto' || style.overflowX === 'scroll' || style.overflowX === 'auto';
    const hasScrollableContent = this.scrollHeight > this.clientHeight || this.scrollWidth > this.clientWidth;

    return hasScrollableOverflow && hasScrollableContent;
  }

  reset() {
    this._items = Array.from(this.hasAttribute('selector') ? this.querySelectorAll(this.getAttribute('selector')) : this.children);
  }

  init() {
    this.hasPendingOnScroll = false;
    this.currentPage = 1;
    this.updateButtons();
    this.updateTabindex();

    this.addEventListener('scroll', theme.utils.debounce(this.update.bind(this), 50));
    this.addEventListener('scrollend', this.scrollend);
    this.addEventListener('slider:previous', this.previous);
    this.addEventListener('slider:next', this.next);
    document.addEventListener('matchSmall', this.updateTabindex.bind(this));
    document.addEventListener('unmatchSmall', this.updateTabindex.bind(this));

    if (Shopify.designMode) {
      this.addEventListener('shopify:block:select', (event) => event.target.scrollIntoView({ behavior: 'smooth' }));
    }
  }

  previous() {
    this.scrollPosition = this.scrollLeft - this.perPage * this.itemOffset * (theme.config.rtl ? -1 : 1);
    this.scrollToPosition(this.scrollPosition);
  }

  next() {
    this.scrollPosition = this.scrollLeft + this.perPage * this.itemOffset * (theme.config.rtl ? -1 : 1);
    this.scrollToPosition(this.scrollPosition);
  }

  select(selectedIndex, immediate = false) {
    this.scrollPosition = this.scrollLeft - (this.currentPage - selectedIndex) * this.itemOffset * (theme.config.rtl ? -1 : 1);
    this.scrollToPosition(this.scrollPosition, immediate);
  }

  selected(selectedIndex) {
    return this.itemsToShow[selectedIndex];
  }

  scrollend() {
    this.hasPendingOnScroll = false;
    this.dispatchEventHandler();
  }

  update() {
    if (window.onscrollend === void 0) {
      clearTimeout(this.scrollendTimeout);
    }

    const previousPage = this.currentPage;
    this.currentPage = Math.round(Math.abs(this.scrollLeft) / this.itemOffset) + 1;

    if (this.currentPage !== previousPage) {
      if (!this.hasPendingOnScroll) {
        this.dispatchEventHandler();
      }

      this.itemsToShow.forEach((sliderItem, index) => {
        sliderItem.classList.toggle('selected', index + 1 === this.currentPage);
      });
    }

    if (window.onscrollend === void 0) {
      this.scrollendTimeout = setTimeout(() => {
        this.dispatchEvent(new CustomEvent('scrollend', { bubbles: true, composed: true }));
      }, 75);
    }

    if (this.looping) return;

    this.updateButtons();
  }

  updateButtons() {
    let previousDisabled = this.currentPage === 1;
    let nextDisabled = this.currentPage === this.itemsToShow.length;

    if (this.perPage > 1) {
      previousDisabled = previousDisabled || this.itemsToShow.length > 0 && this.isVisible(this.itemsToShow[0]) && this.scrollLeft === 0;
      nextDisabled = nextDisabled || this.itemsToShow.length > 0 && this.isVisible(this.itemsToShow[this.itemsToShow.length - 1]);
    }

    this.dispatchEvent(
      new CustomEvent('slider:previousStatus', {
        bubbles: true,
        detail: {
          status: previousDisabled ? (nextDisabled ? 'hidden' : 'disabled') : 'visible'
        },
      })
    );
    this.dispatchEvent(
      new CustomEvent('slider:nextStatus', {
        bubbles: true,
        detail: {
          status: nextDisabled ? (previousDisabled ? 'hidden' : 'disabled') : 'visible'
        },
      })
    );
  }

  updateTabindex() {
    this.removeAttribute('tabindex');

    if (this.isScrollable) {
      this.setAttribute('tabindex', '0');
    }
  }

  isVisible(element, offset = 0) {
    const lastVisibleSlide = this.clientWidth + this.scrollLeft - offset;
    return element.offsetLeft + element.clientWidth <= lastVisibleSlide && element.offsetLeft >= this.scrollLeft;

    /*
    const lastVisibleSlide = this.clientWidth + this.scrollLeft - offset;
    const offsetLeft = element.getBoundingClientRect().left;
    return offsetLeft + element.clientWidth <= lastVisibleSlide && offsetLeft >= this.scrollLeft;
    */
  }

  scrollToPosition(position, immediate = false) {
    this.hasPendingOnScroll = !immediate;

    this.scrollTo({
      left: position,
      behavior: immediate ? 'instant' : theme.config.motionReduced ? 'auto' : 'smooth'
    });
  }

  dispatchEventHandler() {
    this.dispatchEvent(
      new CustomEvent('slider:change', {
        detail: {
          currentPage: this.currentPage,
          currentElement: this.itemsToShow[this.currentPage - 1],
        },
      })
    );
  }
}
customElements.define('slider-element', SliderElement);

class SliderDots extends HTMLElement {
  constructor() {
    super();

    new theme.initWhenVisible(this.init.bind(this));
  }

  get controlledElement() {
    return this.hasAttribute('aria-controls') ? document.getElementById(this.getAttribute('aria-controls')) : null;
  }

  get items() {
    return this._items = this._items || Array.from(this.children);
  }

  get itemsToShow() {
    return Array.from(this.items).filter(element => element.clientWidth > 0);
  }

  init() {
    if (this.initialized) return;
    this.initialized = true;

    if (this.controlledElement) {
      this.controlledElement.addEventListener('slider:change', this.onChange.bind(this));

      this.items.forEach((item) => {
        item.addEventListener('click', this.onButtonClick.bind(this));
      });
    }
  }

  reset() {
    this._items = Array.from(this.children);
  }

  onChange(event) {
    this.transitionTo(parseInt(event.detail.currentPage) - 1);

    this.itemsToShow.forEach((item) => {
      item.setAttribute('aria-current', parseInt(item.getAttribute('data-index')) === parseInt(event.detail.currentPage) ? 'true' : 'false');
    });
  }

  transitionTo(selectedIndex, immediate = false) {
    if (this.itemsToShow[selectedIndex]) {
      const scrollElement = this.hasAttribute('align-selected') ? this.closest(this.getAttribute('align-selected')) : this;
      scrollElement.scrollTo({
        left: this.itemsToShow[selectedIndex].offsetLeft - scrollElement.clientWidth / 2 + this.itemsToShow[selectedIndex].clientWidth / 2,
        top: this.itemsToShow[selectedIndex].offsetTop - scrollElement.clientHeight / 2 - this.itemsToShow[selectedIndex].clientHeight / 2,
        behavior: immediate ? 'instant' : theme.config.motionReduced ? 'auto' : 'smooth'
      });
    }
  }

  onButtonClick(event) {
    event.preventDefault();
    const target = event.currentTarget;

    this.itemsToShow.forEach((item) => {
      item.setAttribute('aria-current', item.getAttribute('data-index') === target.getAttribute('data-index') ? 'true' : 'false');
    });

    this.controlledElement.select(parseInt(target.getAttribute('data-index')));
  }
}
customElements.define('slider-dots', SliderDots);

class ProgressBar extends HTMLElement {
  constructor() {
    super();

    if (this.hasAttribute('style')) return;

    Motion.inView(this, this.init.bind(this));
  }

  init() {
    this.style.setProperty('--progress', `${parseInt(this.getAttribute('data-value')) * 0.75 * 100 / parseInt(this.getAttribute('data-max'))}%`);
  }
}
customElements.define('progress-bar', ProgressBar);

const onYouTubePromise = new Promise((resolve) => {
  window.onYouTubeIframeAPIReady = () => resolve();
});
class DeferredMedia extends HTMLElement {
  constructor() {
    super();

    if (this.posterElement) {
      this.posterElement.addEventListener('click', this.onPosterClick.bind(this));
    }

    if (this.autoplay) {
      Motion.inView(this, this.init.bind(this));
    }
  }

  get posterElement() {
    return this.querySelector('[id^="DeferredPoster-"]');
  }

  get controlledElement() {
    return this.hasAttribute('aria-controls') ? document.getElementById(this.getAttribute('aria-controls')) : null;
  }

  get autoplay() {
    return this.hasAttribute('autoplay');
  }

  get playing() {
    return this.hasAttribute('playing');
  }

  get player() {
    return this.playerProxy = this.playerProxy || new Proxy(this.playerTarget(), {
      get: (target, prop) => {
        return async () => {
          target = await target;
          this.playerHandler(target, prop);
        };
      }
    });
  }

  static get observedAttributes() {
    return ['playing'];
  }

  onPosterClick(event) {
    event.preventDefault();
    event.stopPropagation();

    if (this.playing) {
      this.paused = true;
      this.pause();
    }
    else {
      this.paused = false;
      this.play();
    }
  }

  init() {
    if (!this.paused) {
      this.play();
    }
    return () => {
      this.pause();
    };
  }

  play() {
    if (!this.playing) {
      this.player.play();
    }
  }

  pause() {
    if (this.playing) {
      this.player.pause();
    }
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (name === 'playing') {
      if (oldValue === null && newValue === '') {
        return this.dispatchEvent(new CustomEvent('video:play', { bubbles: true }));
      }

      if (newValue === null) {
        return this.dispatchEvent(new CustomEvent('video:pause', { bubbles: true }));
      }
    }
  }
}

class VideoMedia extends DeferredMedia {
  constructor() {
    super();
  }

  playerTarget() {
    if (this.hasAttribute('host')) {
      this.setAttribute('loaded', '');
      this.closest('.media')?.classList.remove('loading');

      return new Promise(async (resolve) => {
        const templateElement = this.querySelector('template');
        if (templateElement) {
          templateElement.replaceWith(templateElement.content.firstElementChild.cloneNode(true));
        }
        const muteVideo = this.hasAttribute('autoplay') || window.matchMedia('screen and (max-width: 1023px)').matches;
        const script = document.createElement('script');
        script.type = 'text/javascript';
        if (this.getAttribute('host') === 'youtube') {
          if (!window.YT || !window.YT.Player) {
            script.src = 'https://www.youtube.com/iframe_api';
            document.head.appendChild(script);
            await new Promise((resolve2) => {
              script.onload = resolve2;
            });
          }
          await onYouTubePromise;
          const player = new YT.Player(this.querySelector('iframe'), {
            events: {
              onReady: () => {
                if (muteVideo) {
                  player.mute();
                }
                resolve(player);
              },
              onStateChange: (event) => {
                if (event.data === YT.PlayerState.PLAYING) {
                  this.setAttribute('playing', '');
                }
                else if (event.data === YT.PlayerState.ENDED || event.data === YT.PlayerState.PAUSED) {
                  this.removeAttribute('playing');
                }
              }
            }
          });
        }
        if (this.getAttribute('host') === 'vimeo') {
          if (!window.Vimeo || !window.Vimeo.Player) {
            script.src = 'https://player.vimeo.com/api/player.js';
            document.head.appendChild(script);
            await new Promise((resolve2) => {
              script.onload = resolve2;
            });
          }
          const player = new Vimeo.Player(this.querySelector('iframe'));
          if (muteVideo) {
            player.setMuted(true);
          }
          player.on('play', () => {
            this.setAttribute('playing', '');
          });
          player.on('pause', () => this.removeAttribute('playing'));
          player.on('ended', () => this.removeAttribute('playing'));
          resolve(player);
        }
      });
    }
    else {
      const templateElement = this.querySelector('template');
      if (templateElement) {
        templateElement.replaceWith(templateElement.content.firstElementChild.cloneNode(true));
      }
      this.setAttribute('loaded', '');
      this.closest('.media')?.classList.remove('loading');

      const player = this.querySelector('video');
      player.addEventListener('play', () => {
        this.setAttribute('playing', '');
        this.removeAttribute('suspended');
      });
      player.addEventListener('pause', () => {
        if (!player.seeking && player.paused) {
          this.removeAttribute('playing');
        }
      });
      return player;
    }
  }

  playerHandler(target, prop) {
    if (this.getAttribute('host') === 'youtube') {
      prop === 'play' ? target.playVideo() : target.pauseVideo();
    }
    else {
      if (prop === 'play' && !this.hasAttribute('host')) {
        target.play().catch((error) => {
          if (error.name === 'NotAllowedError') {
            this.setAttribute('suspended', '');
            target.controls = true;
            const replacementImageSrc = target.previousElementSibling?.currentSrc;
            if (replacementImageSrc) {
              target.poster = replacementImageSrc;
            }
          }
        });
      }
      else {
        target[prop]();
      }
    }
  }
}
customElements.define('video-media', VideoMedia);

class ModelMedia extends DeferredMedia {
  constructor() {
    super();

    this.player;

    if (this.closeElement) {
      this.closeElement.addEventListener('click', this.onCloseClick.bind(this));
    }
  }

  get closeElement() {
    return this.querySelector('[id^="DeferredPosterClose-"]');
  }

  onCloseClick(event) {
    event.preventDefault();
    event.stopPropagation();

    if (this.modelViewerUI) {
      this.modelViewerUI.pause();
    }
  }

  playerTarget() {
    return new Promise(() => {
      this.setAttribute('loaded', '');

      Shopify.loadFeatures([
        {
          name: 'shopify-xr',
          version: '1.0',
          onLoad: this.setupShopifyXR.bind(this),
        },
        {
          name: 'model-viewer-ui',
          version: '1.0',
          onLoad: this.setupModelViewerUI.bind(this),
        },
      ]);
    });
  }

  playerHandler(target, prop) {
    target[prop]();
  }

  async setupShopifyXR() {
    if (!window.ShopifyXR) {
      document.addEventListener('shopify_xr_initialized', this.setupShopifyXR.bind(this));
      return;
    }

    document.querySelectorAll('[id^="ProductJSON-"]').forEach((modelJSON) => {
      window.ShopifyXR.addModels(JSON.parse(modelJSON.textContent));
      modelJSON.remove();
    });
    window.ShopifyXR.setupXRElements();
  }

  setupModelViewerUI(errors) {
    if (errors) return;

    const modelViewer = this.querySelector('model-viewer');
    if (modelViewer && !modelViewer.hasAttribute('loaded')) {
      modelViewer.setAttribute('loaded', '');
      modelViewer.addEventListener('shopify_model_viewer_ui_toggle_play', this.modelViewerPlayed.bind(this));
      modelViewer.addEventListener('shopify_model_viewer_ui_toggle_pause', this.modelViewerPaused.bind(this));

      this.modelViewerUI = new Shopify.ModelViewerUI(modelViewer);
    }
  }

  modelViewerPlayed() {
    this.setAttribute('playing', '');
    this.closeElement.removeAttribute('hidden');
    (this.controlledElement ?? this).dispatchEvent(new CustomEvent('modelViewer:play', { bubbles: true }));
  }

  modelViewerPaused() {
    this.removeAttribute('playing');
    this.closeElement.setAttribute('hidden', '');
    (this.controlledElement ?? this).dispatchEvent(new CustomEvent('modelViewer:pause', { bubbles: true }));
  }
}
customElements.define('model-media', ModelMedia);

class VariantPicker extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.addEventListener('change', (event) => {
      const target = this.getInputForEventTarget(event.target);
      this.updateSelectionMetadata(event.target);

      theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.optionValueSelectionChange, {
        data: {
          event,
          target,
          selectedOptionValues: this.selectedOptionValues
        }
      });
    });
  }

  updateSelectionMetadata(target) {
    if (target.tagName === 'SELECT' && target.selectedOptions.length) {
      Array.from(target.options).find((option) => option.hasAttribute('selected')).removeAttribute('selected');
      target.selectedOptions[0].setAttribute('selected', '');
    }
  }

  getInputForEventTarget(target) {
    return target.tagName === 'SELECT' ? target.selectedOptions[0] : target;
  }

  get selectedOptionValues() {
    return Array.from(this.querySelectorAll('select option[selected], fieldset input:checked')).map((selector) => selector.getAttribute('data-option-value-id'));
  }
}
customElements.define('variant-picker', VariantPicker);

class ProductInfo extends HTMLElement {
  onVariantChangeUnsubscriber = undefined;
  cartUpdateUnsubscriber = undefined;
  abortController = undefined;
  pendingRequestUrl = null;
  preProcessHtmlCallbacks = [];
  postProcessHtmlCallbacks = [];

  constructor() {
    super();
  }

  get sectionId() {
    return this.hasAttribute('data-original-section-id') ? this.getAttribute('data-original-section-id') : this.getAttribute('data-section-id');
  }

  get productId() {
    return this.getAttribute('data-product-id');
  }

  get productForm() {
    return document.forms[this.getAttribute('form')];
  }

  get productStickyForm() {
    return document.getElementById(`ProductStickyForm-${this.sectionId}-${this.productId}`);
  }

  get pickupAvailability() {
    return this.querySelector('pickup-availability');
  }

  get variantSelectors() {
    return this.querySelector('variant-picker');
  }

  get quantityInput() {
    return this.querySelector('quantity-input input');
  }

  connectedCallback() {
    this.initProductAnimation();

    this.onVariantChangeUnsubscriber = theme.pubsub.subscribe(
      theme.pubsub.PUB_SUB_EVENTS.optionValueSelectionChange,
      this.handleOptionValueChange.bind(this)
    );

    this.initQuantityHandlers();
    this.dispatchEvent(new CustomEvent('product-info:loaded', { bubbles: true }));
  }

  disconnectedCallback() {
    this.onVariantChangeUnsubscriber();
    this.cartUpdateUnsubscriber?.();
  }

  initProductAnimation() {
    if (theme.config.motionReduced) return;

    this.animation = new theme.Animation(this);
    this.animation.beforeLoad();

    Motion.inView(this, async () => {
      if (!this.immediate && this.media) await theme.utils.imageLoaded(this.media);
      this.animation.load();
    });
  }

  handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
    if (!this.contains(event.target)) return;

    this.resetProductFormState();

    const productUrl = target.getAttribute('data-product-url') || this.pendingRequestUrl || this.getAttribute('data-product-url');
    this.pendingRequestUrl = productUrl;
    const shouldSwapProduct = this.getAttribute('data-product-url') !== productUrl;

    if (shouldSwapProduct) {
      window.location.href = productUrl;
      return;
    }

    this.renderProductInfo({
      requestUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValues),
      targetId: target.tagName === 'OPTION' ? target.parentElement.id : target.id,
      callback: this.handleUpdateProductInfo()
    });
  }

  resetProductFormState() {
    this.productForm?.handleErrorMessage();
    this.productForm?.toggleSubmitButton(true, '');
    this.productStickyForm?.handleErrorMessage();
    this.productStickyForm?.toggleSubmitButton(true, '');
  }

  renderProductInfo({ requestUrl, targetId, callback }) {
    this.abortController?.abort();
    this.abortController = new AbortController();

    fetch(requestUrl, { signal: this.abortController.signal })
      .then((response) => response.text())
      .then((responseText) => {
        this.pendingRequestUrl = null;
        if (callback !== undefined) {
          const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');
          callback(parsedHTML);
        }
      })
      .then(() => {
        // set focus to last clicked option value
        const activeElement = document.getElementById(targetId);
        if (activeElement === null) return;

        if (activeElement.hasAttribute('align-selected')) {
          const scrollElement = activeElement.closest(activeElement.getAttribute('align-selected'));
          if(scrollElement) {
          scrollElement.scrollTo({
            left: activeElement.offsetLeft,
            behavior: 'instant'
          });
          }
        }
        setTimeout(() => {
          activeElement.focus({ preventScroll: true });
        }, 100);
      })
      .catch((error) => {
        if (error.name === 'AbortError') {
          console.log('Fetch aborted by user');
        } else {
          console.error(error);
        }
      });
  }

  buildRequestUrlWithParams(url, optionValues) {
    const params = [];
    params.push(`section_id=${this.sectionId}`);

    if (optionValues.length) {
      params.push(`option_values=${optionValues.join(',')}`);
    }

    return `${url}?${params.join('&')}`;
  }

  handleUpdateProductInfo() {
    return (parsedHTML) => {
      const variant = this.getSelectedVariant(parsedHTML);

      this.pickupAvailability?.update(variant);
      this.updateOptionValues(parsedHTML);
      this.updateURL(variant?.id);
      this.updateVariantInputs(variant?.id);

      if (!variant) {
        this.setUnavailable();
        return;
      }

      this.updateSourceFromDestination(parsedHTML, 'ProductGallery');
      this.updateSourceFromDestination(parsedHTML, 'Price');
      this.updateSourceFromDestination(parsedHTML, 'BuyButtonPrice');
      this.updateSourceFromDestination(parsedHTML, 'StickyPrice');
      this.updateSourceFromDestination(parsedHTML, 'Sku');
      this.updateSourceFromDestination(parsedHTML, 'Inventory');
      this.updateSourceFromDestination(parsedHTML, 'Volume');
      this.updateSourceFromDestination(parsedHTML, 'PricePerItem');
      this.updateSourceFromDestination(parsedHTML, 'BackInStock');
      this.updateSourceFromDestination(parsedHTML, 'ProductBundle');

      this.updateQuantityRules(parsedHTML);
      this.updateSourceFromDestination(parsedHTML, 'QuantityRules');
      this.updateSourceFromDestination(parsedHTML, 'QuantityRulesCart');
      this.updateSourceFromDestination(parsedHTML, 'VolumeNote');

      this.productForm?.toggleSubmitButton(!variant.available, theme.variantStrings.soldOut);
      this.productStickyForm?.toggleSubmitButton(!variant.available, theme.variantStrings.soldOut);

      theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.variantChange, {
        data: {
          sectionId: this.sectionId,
          productId: this.productId,
          parsedHTML: parsedHTML,
          variant: variant
        }
      });
      (this.productForm ?? document).dispatchEvent(new CustomEvent('variant:change', {
        detail: {
          variant: variant
        }
      }));
      document.dispatchEvent(new CustomEvent('variant:change', {
        detail: {
          variant: variant
        }
      }));
    };
  }

  getSelectedVariant(productInfoNode) {
    const selectedVariant = productInfoNode.querySelector('[data-selected-variant]')?.textContent;
    return !!selectedVariant ? JSON.parse(selectedVariant) : null;
  }

  updateOptionValues(parsedHTML) {
    const variantPicker = parsedHTML.getElementById(`VariantPicker-${this.sectionId}-${this.productId}`);
    if (variantPicker) {
      theme.HTMLUpdateUtility.viewTransition(this.variantSelectors, variantPicker, this.preProcessHtmlCallbacks);
    }
  }

  updateURL(variantId) {
    if (!variantId || this.getAttribute('data-update-url') === 'false') return;

    const newUrl = new URL(window.location.href);
    newUrl.searchParams.set('variant', variantId);
    window.history.replaceState({ path: newUrl.toString() }, '', newUrl.toString());
  }

  updateVariantInputs(variantId) {
    if (!variantId) return;

    const productForms = document.querySelectorAll(`#ProductForm-${this.sectionId}-${this.productId}, #ProductFormInstallment-${this.sectionId}-${this.productId}`);
    productForms.forEach((productForm) => {
      const input = productForm.querySelector('input[name="id"]');
      input.value = variantId ?? '';
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
  }

  updateSourceFromDestination(parsedHTML, id) {
    const source = parsedHTML.getElementById(`${id}-${this.sectionId}-${this.productId}`);
    const destination = document.querySelector(`#${id}-${this.sectionId}-${this.productId}`);
    if (source && destination) {
      destination.innerHTML = source.innerHTML;
      destination.removeAttribute('hidden');
    }
  }

  setUnavailable() {
    this.productForm?.toggleSubmitButton(true, theme.variantStrings.unavailable, true);
    this.productStickyForm?.toggleSubmitButton(true, theme.variantStrings.unavailable, true);

    const selectors = ['Price', 'BuyButtonPrice', 'StickyPrice', 'Inventory', 'Sku', 'PricePerItem', 'BackInStock', 'ProductBundle', 'VolumeNote', 'Volume', 'QuantityRules', 'QuantityRulesCart']
      .map((id) => `#${id}-${this.sectionId}-${this.productId}`)
      .join(', ');
    document.querySelectorAll(selectors).forEach((selector) => selector.setAttribute('hidden', ''));
  }

  initQuantityHandlers() {
    if (!this.quantityInput) return;

    this.setQuantityBoundries();
    if (!this.hasAttribute('data-original-section-id')) {
      this.cartUpdateUnsubscriber = theme.pubsub.subscribe(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, this.fetchQuantityRules.bind(this));
    }
  }

  setQuantityBoundries() {
    theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.quantityBoundries, {
      data: {
        sectionId: this.sectionId,
        productId: this.productId
      }
    });
  }

  fetchQuantityRules() {
    const currentVariantId = this.productForm?.variantIdInput?.value;
    if (!currentVariantId) return;

    this.querySelector('label[is="quantity-label"]')?.setAttribute('aria-busy', 'true');
    fetch(`${this.getAttribute('data-product-url')}?variant=${currentVariantId}&section_id=${this.sectionId}`)
      .then((response) => response.text())
      .then((responseText) => {
        const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');
        this.updateQuantityRules(parsedHTML);
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        this.querySelector('label[is="quantity-label"]')?.removeAttribute('aria-busy');
      });
  }

  updateQuantityRules(parsedHTML) {
    if (!this.quantityInput) return;

    theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.quantityRules, {
      data: {
        sectionId: this.sectionId,
        productId: this.productId,
        parsedHTML
      }
    });

    this.setQuantityBoundries();
  }
}
customElements.define('product-info', ProductInfo);

class ProductForm extends HTMLFormElement {
  constructor() {
    super();

    this.variantIdInput.disabled = false;
    this.addEventListener('submit', this.onSubmitHandler);
  }

  get cartDrawer() {
    return document.querySelector('cart-drawer');
  }

  get submitButton() {
    return this._submitButton = this._submitButton || this.querySelector('[type="submit"]');
  }

  get subscribeButton() {
    return this._subscribeButton = this._subscribeButton || this.querySelector('[data-subscribe-button]') || null;
  }

  get submitButtons() {
    return this._submitButtons = this._submitButtons || document.querySelectorAll(`[type="submit"][form="${this.getAttribute('id')}"]`);
  }

  get hideErrors() {
    return this.getAttribute('data-hide-errors') === 'true';
  }

  get variantIdInput() {
    return this.querySelector('[name="id"]');
  }

  get bundles() {
    return Array.from(document.querySelectorAll(`[form="${this.getAttribute('id')}"] input[name="bundles"]:checked`));
  }

  prepareFormData(formData) {
    const bundlesLength = this.bundles.length;
    const itemsArray = new Array(bundlesLength + 1);

    for (let i = 0; i < bundlesLength; i++) {
      const reverseIndex = bundlesLength - 1 - i;
      itemsArray[i] = {
        id: this.bundles[reverseIndex].value,
        quantity: 1
      };
    }

    const allFormData = { items: itemsArray.slice(0, bundlesLength) };

    const json = {};
    const formEntries = Array.from(formData.entries());

    for (const [name, value] of formEntries) {
      if (name === 'id' || name === 'quantity' || name.includes('properties')) {
        if (name === 'id' || name === 'quantity') {
          json[name] = value;
        } else {
          const regex = /(?:^(properties\[))(.*?)(?:\])/;
          const property = regex.exec(name)[2];
          json.properties = json.properties || {};
          json.properties[property] = value;
        }
      } else {
        allFormData[name] = value;
      }
    }

    allFormData.items.push(json);
    return allFormData;
  }

  onSubmitHandler(event) {
    const hasBundles = this.bundles.length > 0;

    if (!Shopify.designMode) {
      if (document.body.classList.contains('template-cart') || theme.settings.cartType === 'page') {
        if (hasBundles) {
          theme.utils.postLink2(theme.routes.cart_add_url, {
            parameters: {
              ...this.prepareFormData(new FormData(this))
            }
          });
          event.preventDefault();
        }
        return;
      }
    }

    event.preventDefault();
    console.log(event.target, "event.target");
    if (this.submitButton.hasAttribute('aria-disabled')) return;
    if (this.subscribeButton) {
      if (this.submitButton.hasAttribute('aria-disabled')) return;
    }
    this.activeElement = event.submitter || event.currentTarget;

    this.handleErrorMessage();

    let sectionsToBundle = [];
    document.documentElement.dispatchEvent(new CustomEvent('cart:bundled-sections', { bubbles: true, detail: { sections: sectionsToBundle } }));

    const config = theme.utils.fetchConfig('javascript');
    config.headers['X-Requested-With'] = 'XMLHttpRequest';
    delete config.headers['Content-Type'];

    const formData = new FormData(this);
    formData.append('sections', sectionsToBundle);
    formData.append('sections_url', window.location.pathname);

    config.body = formData;

    if (hasBundles) {
      const allFormData = this.prepareFormData(formData);
      config.body = JSON.stringify(allFormData);
      config.headers['Content-Type'] = 'application/json';
    }

    this.submitButton.setAttribute('aria-disabled', 'true');
    this.submitButton.setAttribute('aria-busy', 'true');

    if (this.subscribeButton) {
      this.subscribeButton.setAttribute('aria-disabled', 'true');
      this.subscribeButton.setAttribute('aria-busy', 'true');
    }

    fetch(`${theme.routes.cart_add_url}`, config)
      .then((response) => response.json())
      .then(async (parsedState) => {
        if (parsedState.status) {
          theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartError, {
            source: 'product-form',
            productVariantId: formData.get('id'),
            errors: parsedState.errors || parsedState.description,
            message: parsedState.message
          });
          this.handleErrorMessage(parsedState.description);
          document.dispatchEvent(new CustomEvent('ajaxProduct:error', {
            detail: {
              errorMessage: parsedState.description
            }
          }));

          const submitButtonText = this.submitButton.querySelector('.btn-text>span');
          if (!submitButtonText || !submitButtonText.hasAttribute('data-sold-out')) return;
          submitButtonText.innerText = submitButtonText.getAttribute('data-sold-out');
          this.submitButton.setAttribute('aria-disabled', 'true');
          if (this.subscribeButton) {
            this.subscribeButton.setAttribute('aria-disabled', 'true');
          }
          this.error = true;
          return;
        }

        if (Shopify.designMode) {
          if (document.body.classList.contains('template-cart') || theme.settings.cartType === 'page') {
            window.location.href = theme.routes.cart_url;
            return;
          }
        }

        const cartJson = await (await fetch(theme.routes.cart_url, { ...theme.utils.fetchConfig('json', 'GET') })).json();
        cartJson['sections'] = parsedState['sections'];

        theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, { source: 'product-form', productVariantId: formData.get('id'), cart: cartJson });
        document.dispatchEvent(new CustomEvent('ajaxProduct:added', {
          detail: {
            product: parsedState,
            target: event.target
          }
        }));

        this.cartDrawer?.show(this.activeElement);
      })
      .catch((error) => {
        console.log(error);
      })
      .finally(() => {
        this.submitButton.removeAttribute('aria-busy');
        if (this.subscribeButton) {
          this.subscribeButton.removeAttribute('aria-busy');
        }
        this.submitButtons.forEach(submitButton => submitButton.removeAttribute('aria-busy'));

        if (!this.error) {
          this.submitButton.removeAttribute('aria-disabled');
          this.submitButtons.forEach(submitButton => submitButton.removeAttribute('aria-disabled'));
          if (this.subscribeButton) {
            this.subscribeButton.removeAttribute('aria-disabled');
          }
        }
      });
  }

  handleErrorMessage(errorMessage = false) {
    if (this.hideErrors) return;

    this.errorMessage = this.errorMessage || this.querySelector('.product-form__error-message');
    if (!this.errorMessage) return;

    this.errorMessage.toggleAttribute('hidden', !errorMessage);
    this.errorMessage.innerText = errorMessage;
  }

  toggleSubmitButton(disable = true, text, unavailable = false) {
    if (!this.submitButton) return;

    this.submitButton.removeAttribute('loading');
    this.submitButton.removeAttribute('unavailable');

    const submitButtonText = this.submitButton.querySelector('.btn-text');
    const submitButtonTextChild = this.submitButton.querySelector('.btn-text>span');

    if (disable) {
      this.submitButton.setAttribute('disabled', '');
      if (unavailable) this.submitButton.setAttribute('unavailable', '');
      if (text) {
        (submitButtonTextChild || submitButtonText).textContent = text;
      }
      else {
        this.submitButton.setAttribute('loading', '');
      }
    }
    else {
      this.submitButton.removeAttribute('disabled');
      (submitButtonTextChild || submitButtonText).textContent = this.submitButton.hasAttribute('data-pre-order') ? theme.variantStrings.preOrder : theme.variantStrings.addToCart;
    }
  }
}
customElements.define('product-form', ProductForm, { extends: 'form' });

class ProductStickyForm extends HTMLElement {
  constructor() {
    super();

    this.scopeFrom = document.querySelector('.quick-order-list') || document.getElementById(this.getAttribute('form'));
    this.scopeTo = document.querySelector('.footer-group');

    if (!this.scopeFrom || !this.scopeTo) {
      return;
    }

    const intersectionObserver = new IntersectionObserver(this.handleIntersection.bind(this));
    intersectionObserver.observe(this.scopeFrom);
    intersectionObserver.observe(this.scopeTo);

    this.onVariantChangedListener = this.onVariantChanged.bind(this);
  }

  get productForm() {
    return document.forms[this.getAttribute('form')];
  }

  get submitButton() {
    return this._submitButton = this._submitButton || this.querySelector('[type="submit"]');
  }

  get productOptions() {
    return this._productOptions = this._productOptions || this.querySelector('[data-sticky-product-options]');
  }

  get productMedia() {
    return this._productMedia = this._productMedia || this.querySelector('[data-sticky-product-media]');
  }

  handleIntersection(entries) {
    entries.forEach((entry) => {
      if (entry.target === this.scopeFrom) {
        this.scopeFromPassed = entry.boundingClientRect.bottom < 0;
      }
      if (entry.target === this.scopeTo) {
        this.scopeToReached = entry.isIntersecting;
      }
    });

    if (this.scopeFromPassed && !this.scopeToReached) {
      Motion.animate(this.firstElementChild, { opacity: 1, visibility: 'visible', transform: ['translateY(15px)', 'translateY(0)'] }, { duration: 1, easing: [0.16, 1, 0.3, 1] });
    }
    else {
      Motion.animate(this.firstElementChild, { opacity: 0, visibility: 'hidden', transform: ['translateY(0)', 'translateY(15px)'] }, { duration: 1, easing: [0.16, 1, 0.3, 1] });
    }
  }

  connectedCallback() {
    (this.productForm ?? document).addEventListener('variant:change', this.onVariantChangedListener);
  }

  disconnectedCallback() {
    (this.productForm ?? document).removeEventListener('variant:change', this.onVariantChangedListener);
  }

  onVariantChanged(event) {
    this.updateProductMedia(event.detail.variant);
    this.updateProductOptions(event.detail.variant);
  }

  updateProductMedia(currentVariant) {
    if (!currentVariant || !currentVariant.featured_media) return;

    const image = this.productMedia.querySelector('img');
    const newImage = new Image(currentVariant.featured_media.preview_image.width, currentVariant.featured_media.preview_image.height);

    newImage.alt = currentVariant.featured_media.alt;
    newImage.src = currentVariant.featured_media.preview_image.src;
    newImage.srcset = this.generateSrcset(currentVariant.featured_media.preview_image);
    newImage.sizes = image.sizes;

    this.productMedia.replaceChildren(newImage);
  }

  updateProductOptions(currentVariant) {
    if (!currentVariant) return;

    this.productOptions.innerText = currentVariant.title;
  }

  handleErrorMessage(errorMessage = false) {
    if (this.productForm?.hideErrors) return;

    this.errorMessage = this.errorMessage || this.querySelector('.product-form__error-message');
    if (!this.errorMessage) return;

    this.errorMessage.toggleAttribute('hidden', !errorMessage);
    this.errorMessage.innerText = errorMessage;
  }

  toggleSubmitButton(disable = true, text) {
    if (!this.submitButton) return;

    const submitButtonText = this.submitButton.querySelector('.btn-text');
    const submitButtonTextChild = this.submitButton.querySelector('.btn-text>span');

    if (disable) {
      this.submitButton.setAttribute('disabled', '');
      if (text) {
        (submitButtonTextChild || submitButtonText).textContent = text;
      }
    }
    else {
      this.submitButton.removeAttribute('disabled');
      (submitButtonTextChild || submitButtonText).textContent = this.submitButton.hasAttribute('data-pre-order') ? theme.variantStrings.preOrder : theme.variantStrings.addToCart;
    }
  }

  generateSrcset(image) {
    const widths = this.productMedia.getAttribute('data-widths').split(',').map((width) => parseInt(width));
    return widths.filter((width) => width <= image.width).map((width) => {
      return `${image.src}&width=${width} ${width}w`;
    }).join(', ');
  }
}
customElements.define('product-sticky-form', ProductStickyForm);

class ProductBundleDetails extends AccordionDetails {
  constructor() {
    super();
  }

  cartUpdateUnsubscriber = undefined;

  get productForm() {
    return document.forms[this.getAttribute('form')];
  }

  get bundles() {
    return Array.from(this.querySelectorAll('input[name="bundles"]:checked'));
  }

  connectedCallback() {
    this.cartUpdateUnsubscriber = theme.pubsub.subscribe(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, this.onCartUpdate.bind(this));
    this.onBundleChangeListener = this.onBundleChange.bind(this);
    this.addEventListener('change', this.onBundleChangeListener);
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
    this.removeEventListener('change', this.onBundleChangeListener);
  }

  onCartUpdate(event) {
    if (this.bundles.length === 0) return;

    if (event.source === 'product-form') {
      this.bundles.forEach((input) => {
        if (!input.disabled) input.checked = false;
      });
      this.onBundleChange();
    }
  }

  onBundleChange() {
    (this.productForm ?? document).dispatchEvent(new CustomEvent('bundle:change', {
      detail: {
        items: this.bundles.map(input => ({
          id: input.value,
          quantity: 1,
          price: input.getAttribute('data-price')
        }))
      }
    }));
  }
}
customElements.define('product-bundle-details', ProductBundleDetails, { extends: 'details' });

class ProductBuyPrice extends HTMLElement {
  constructor() {
    super();
  }

  get productForm() {
    return document.forms[this.getAttribute('form')];
  }

  connectedCallback() {
    this.onBundleChangedListener = this.onBundleChanged.bind(this);
    (this.productForm ?? document).addEventListener('bundle:change', this.onBundleChangedListener);
  }

  disconnectedCallback() {
    (this.productForm ?? document).removeEventListener('bundle:change', this.onBundleChangedListener);
  }

  onBundleChanged(event) {
    const { items } = event.detail;
    let subtotal = parseInt(this.getAttribute('data-price'));

    items.forEach(({ price, quantity }) => {
      subtotal += parseInt(price) * parseInt(quantity);
    });

    this.innerHTML = theme.Currency.formatMoney(subtotal, theme.settings.currencyCodeEnabled ? theme.settings.moneyWithCurrencyFormat : theme.settings.moneyFormat);
  }
}
customElements.define('product-buy-price', ProductBuyPrice);

class MediaGallery extends HTMLElement {
  constructor() {
    super();

    Motion.inView(this, () => {
      requestAnimationFrame(() => this.pauseAllMedia());
      //setTimeout(() => this.pauseAllMedia(), 500);
    });

    this.addEventListener('lightbox:open', (event) => this.openZoom(event.detail.index));
    this.sliderGallery.addEventListener('slider:change', this.onSlideChange.bind(this));

    this.countMediaGallery();
  }

  get viewInSpaceButton() {
    return this.querySelector('[data-shopify-xr]');
  }

  get sliderGallery() {
    return this.querySelector('slider-element');
  }

  get mediaPreview() {
    return this.querySelector('.product__preview .product__media');
  }

  get photoswipe() {
    if (this._photoswipe) return this._photoswipe;

    const lightbox = new PhotoSwipeLightbox({
      arrowPrevSVG: '<svg class="pswp__icn icon" stroke="currentColor" fill="none" viewBox="0 0 30 30"><path d="M17.5 7.5L10 15L17.5 22.5"/></svg>',
      arrowNextSVG: '<svg class="pswp__icn icon" stroke="currentColor" fill="none" viewBox="0 0 30 30"><path d="M17.5 7.5L10 15L17.5 22.5"/></svg>',
      closeSVG: '<svg class="pswp__icn icon" stroke="currentColor" fill="none" viewBox="0 0 30 30"><path d="m7.5 22.5 15-15m-15 0 15 15"/></svg>',
      bgOpacity: 1,
      pswpModule: () => import(theme.settings.pswpModule),

      arrowPrevTitle: theme.strings.previous,
      arrowNextTitle: theme.strings.next,
      closeTitle: theme.strings.close,

      // Recommended options for this plugin
      allowPanToNext: false,
      allowMouseDrag: true,
      wheelToZoom: false,
      returnFocus: true,
      zoom: false,
    });

    lightbox.addFilter('thumbEl', (_thumbEl, data) => data['thumbnailElement']);

    lightbox.on('contentLoad', (event) => {
      const { content } = event;

      if (content.type === 'video' || content.type === 'external_video' || content.type === 'model') {
        event.preventDefault();

        // Create a container for video and assign it to the `content.element` property
        content.element = document.createElement('div');
        content.element.className = 'pswp__video-container';
        content.element.appendChild(content.data.domElement.cloneNode(true));
      }
    });

    lightbox.on('change', () => {
      this.sliderGallery.select(lightbox.pswp.currIndex + 1, true);
    });

    lightbox.on('close', () => {
      const slideSelected = this.sliderGallery.selected(lightbox.pswp.currIndex);
      lightbox.pswp._lastActiveElement = slideSelected ? slideSelected.querySelector('button') : document.activeElement;
    });

    lightbox.init();
    return this._photoswipe = lightbox;
  }

  onSlideChange(event) {
    const activeMedia = event.detail.currentElement;
    this.playActiveMedia(activeMedia);

    if (this.viewInSpaceButton) {
      if (activeMedia.getAttribute('data-media-type') === 'model') {
        this.viewInSpaceButton.setAttribute('data-shopify-model3d-id', activeMedia.getAttribute('data-media-id'));
      } else {
        this.viewInSpaceButton.setAttribute('data-shopify-model3d-id', this.viewInSpaceButton.getAttribute('data-shopify-model3d-default-id'));
      }
    }
  }

  playActiveMedia(activeMedia) {
    if (typeof activeMedia === 'undefined') return;

    const deferredMedia = activeMedia.querySelector('.deferred-media');

    this.sliderGallery.querySelectorAll('.deferred-media').forEach((media) => {
      deferredMedia === media ? media.play() : media.pause();
    });
  }

  pauseAllMedia() {
    this.sliderGallery.querySelectorAll('[data-media-id]').forEach((media, index) => {
      if (index > 0) {
        const deferredMedia = media.querySelector('.deferred-media');
        if (deferredMedia && typeof deferredMedia.pause === 'function') deferredMedia.pause();
      }
    });
  }

  openZoom(index = 0) {
    let dataSource = this.sliderGallery.itemsToShow.map((media) => {
      const image = media.querySelector('img');

      if (media.getAttribute('data-media-type') === 'image') {
        return {
          thumbnailElement: image,
          src: image.src,
          srcset: image.srcset,
          msrc: image.currentSrc || image.src,
          width: parseInt(image.getAttribute('width')),
          height: parseInt(image.getAttribute('height')),
          alt: image.alt,
          thumbCropped: true
        };
      }

      if (media.getAttribute('data-media-type') === 'video' || media.getAttribute('data-media-type') === 'external_video' || media.getAttribute('data-media-type') === 'model') {
        const video = media.querySelector('.deferred-media');
        return {
          thumbnailElement: image,
          domElement: video,
          type: media.getAttribute('data-media-type'),
          src: image.src,
          srcset: image.srcset,
          msrc: image.currentSrc || image.src,
          width: 800,
          height: 800 / video.getAttribute('data-aspect-ratio'),
          alt: image.alt,
          thumbCropped: true
        };
      }
    });

    if (this.mediaPreview && this.mediaPreview.offsetParent) {
      if (this.mediaPreview.getAttribute('data-media-type') === 'image') {
        const image = this.mediaPreview.querySelector('img');
        dataSource.push({
          thumbnailElement: image,
          src: image.src,
          srcset: image.srcset,
          msrc: image.currentSrc || image.src,
          width: parseInt(image.getAttribute('width')),
          height: parseInt(image.getAttribute('height')),
          alt: image.alt,
          thumbCropped: true
        });

        if (index === -1) {
          index = dataSource.length - 1;
        }
      }
    }

    this.photoswipe.loadAndOpen(index, dataSource);
  }

  countMediaGallery() {
    let mediaCount = 0;
    this.sliderGallery.querySelectorAll('[data-media-id]').forEach((media) => {
      if (!media.classList.contains('xl:hidden') && !media.hasAttribute('hidden')) {
        mediaCount++;
      }
    });

    if (this.mediaPreview) {
      mediaCount++;
    }

    mediaCount > 1 ? this.classList.remove('with-only1') : this.classList.add('with-only1');
  }
}
customElements.define('media-gallery', MediaGallery);

class MediaLightboxButton extends HTMLButtonElement {
  constructor() {
    super();

    this.addEventListener('click', this.onButtonClick);
  }

  onButtonClick() {
    const media = this.closest('[data-media-id]');
    const sliderGallery = this.closest('slider-element');
    const openIndex = sliderGallery ? sliderGallery.itemsToShow.indexOf(media) : -1;

    this.dispatchEvent(new CustomEvent('lightbox:open', {
      bubbles: true,
      detail: {
        index: openIndex
      }
    }));
  }
}
customElements.define('media-lightbox-button', MediaLightboxButton, { extends: 'button' });

class MediaHoverButton extends MediaLightboxButton {
  constructor() {
    super();
  }

  get zoomRatio() {
    return 2;
  }

  onButtonClick(event) {
    if (theme.config.isTouch) {
      super.onButtonClick();
    }
    else {
      const media = this.closest('[data-media-type="image"]');
      if (media) {
        const image = media.querySelector('img');
        this.gallery = this.closest('slider-element');

        this.magnify(image);
        this.moveWithHover(image, event);
      }
    }
  }

  createOverlay(image) {
    const overlayImage = document.createElement('img');
    overlayImage.setAttribute('src', `${image.src}`);
    const overlay = document.createElement('media-hover-overlay');
    this.prepareOverlay(overlay, overlayImage);

    this.toggleLoadingSpinner(image);

    overlayImage.onload = () => {
      this.toggleLoadingSpinner(image);
      image.parentElement.insertBefore(overlay, image);
    };

    if (this.gallery) this.gallery.classList.add('magnify');

    return overlay;
  }

  prepareOverlay(container, image) {
    container.setAttribute('class', 'media z-10 absolute top-0 left-0 w-full h-full');
    container.setAttribute('aria-hidden', 'true');
    container.style.backgroundImage = `url('${image.src}')`;
    container.style.cursor = 'zoom-out';
  }

  toggleLoadingSpinner(image) {
    const loadingSpinner = image.parentElement;
    loadingSpinner.classList.toggle('loading');
    loadingSpinner.classList.toggle('pointer-events-none');
  }

  moveWithHover(image, event) {
    // calculate mouse position
    const ratio = image.height / image.width;
    const container = event.target.getBoundingClientRect();
    const xPosition = event.clientX - container.left;
    const yPosition = event.clientY - container.top;
    const xPercent = `${xPosition / (image.clientWidth / 100)}%`;
    const yPercent = `${yPosition / ((image.clientWidth * ratio) / 100)}%`;

    // determine what to show in the frame
    this.overlay.style.backgroundPosition = `${xPercent} ${yPercent}`;
    this.overlay.style.backgroundSize = `${image.width * this.zoomRatio}px`;
  }

  magnify(image) {
    this.overlay = this.createOverlay(image);
    this.overlay.onclick = () => this.reset();
    this.overlay.onmousemove = (event) => this.moveWithHover(image, event);
    this.overlay.onmouseleave = () => this.reset();
  }

  reset() {
    this.overlay.remove();

    if (this.gallery) this.gallery.classList.remove('magnify');
  }
}
customElements.define('media-hover-button', MediaHoverButton, { extends: 'button' });

class MediaDots extends SliderDots {
  constructor() {
    super();
  }

  init() {
    super.init();
    this.resetIndexes();
  }

  resetIndexes() {
    let newIndex = 1;
    this.itemsToShow.forEach((item, index) => {
      item.setAttribute('data-index', newIndex);
      item.setAttribute('aria-current', index === 0 ? 'true' : 'false');

      newIndex++;
    });
  }
}
customElements.define('media-dots', MediaDots);

class XModal extends ModalElement {
  constructor() {
    super();
  }

  get shouldLock() {
    return true;
  }

  get shouldAppendToBody() {
    return true;
  }
}
customElements.define('x-modal', XModal);

class LogoList extends HTMLElement {
  constructor() {
    super();

    Motion.inView(this, this.init.bind(this), { margin: '200px 0px 200px 0px' });
  }

  get childElement() {
    return this.firstElementChild;
  }

  get speed() {
    return this.hasAttribute('data-speed') ? parseInt(this.getAttribute('data-speed')) : 16;
  }

  get maximum() {
    return this.hasAttribute('data-maximum') ? parseInt(this.getAttribute('data-maximum')) : Math.ceil(this.parentWidth / this.childElementWidth) + 2;
  }

  get direction() {
    return this.hasAttribute('data-direction') ? this.getAttribute('data-direction') : 'left';
  }

  get parentWidth() {
    return this.getWidth(this);
  }

  get childElementWidth() {
    return this.getWidth(this.childElement);
  }

  disconnectedCallback() {
    if (this.marquee) {
      this.pause();
      this.marquee.destroy();
    }
  }

  init() {
    if (this.childElementCount === 1) {
      this.childElement.classList.add('animate');

      for (let index = 0; index < this.maximum; index++) {
        this.clone = this.childElement.cloneNode(true);
        this.clone.setAttribute('aria-hidden', true);
        this.appendChild(this.clone);
        this.clone.querySelectorAll('.media').forEach((media) => media.classList.remove('loading'));
      }

      if (theme.config.isTouch) {
        this.style.setProperty('--duration', `${33 - this.speed}s`);
      }
      else {
        this.marquee = new Flickity(this, {
          prevNextButtons: false,
          pageDots: false,
          wrapAround: true,
          freeScroll: true,
          rightToLeft: this.direction === 'right',
        });

        // Set initial position to be 0
        this.marquee.x = 0;

        // Start the marquee animation
        this.play();

        this.addEventListener('mouseenter', this.pause);
        this.addEventListener('mouseleave', this.play);
      }
    }
  }

  play() {
    // Set the decrement of position x
    this.marquee.x -= this.speed * 0.1;

    // Settle position into the slider
    this.marquee.settle(this.marquee.x);

    // Set the requestId to the local variable
    this.requestId = requestAnimationFrame(this.play.bind(this));
  }

  pause() {
    if (this.requestId) {
      // Cancel the animation
      cancelAnimationFrame(this.requestId);

      // Reset the requestId for the next animation.
      this.requestId = undefined;
    }
  }

  getWidth(element) {
    const rect = element.getBoundingClientRect();
    return rect.right - rect.left;
  }
}
customElements.define('logo-list', LogoList);

class TextScrolling extends HTMLElement {
  constructor() {
    super();

    this.items.forEach((item) => {
      const header = item.querySelector('.heading');

      Motion.scroll(
        Motion.animate(header, { opacity: [0, 0, 1, 1, 1, 0, 0] }),
        { target: header, offsets: ['33vh', '66vh'] }
      );
    });
  }

  get items() {
    return this._items = this._items || Array.from(this.children);
  }
}
customElements.define('text-scrolling', TextScrolling);

class TabsElement extends HTMLElement {
  constructor() {
    super();

    this.selectedIndex = this.selectedIndex;
    this.buttons.forEach((button, index) => button.addEventListener('click', () => {
      this.selectedIndex = index;
      this.dispatchEvent(new CustomEvent('tab:change', { bubbles: true, detail: { index: index } }));
    }));

    if (Shopify.designMode) {
      this.addEventListener('shopify:block:select', (event) => this.selectedIndex = this.buttons.indexOf(event.target));
    }
  }

  static get observedAttributes() {
    return ['selected-index'];
  }

  get selectedIndex() {
    return parseInt(this.getAttribute('selected-index')) || 0;
  }

  set selectedIndex(index) {
    this.setAttribute('selected-index', Math.min(Math.max(index, 0), this.buttons.length - 1).toString());
  }

  get buttons() {
    return this._buttons = this._buttons || Array.from(this.querySelectorAll('button[role="tab"]'));
  }

  get indicators() {
    return this._indicators = this._indicators || Array.from(this.querySelectorAll('.indicators'));
  }

  load() {
    const toButton = this.buttons[parseInt(this.selectedIndex)];
    if (toButton === undefined) return;

    const toPanel = document.getElementById(toButton.getAttribute('aria-controls'));
    Motion.animate(toPanel, { transform: ['translateY(2rem)', 'translateY(0)'], opacity: [0, 1] }, { duration: theme.config.motionReduced ? 0 : 0.15 });

    const motionList = toPanel.querySelector('motion-list');
    if (motionList && motionList.initialized) {
      motionList.load();
    }
  }

  unload() {
    const fromButton = this.buttons[parseInt(this.selectedIndex)];
    if (fromButton === undefined) return;

    const fromPanel = document.getElementById(fromButton.getAttribute('aria-controls'));
    Motion.animate(fromPanel, { transform: ['translateY(0)', 'translateY(2rem)'], opacity: [1, 0] }, { duration: theme.config.motionReduced ? 0 : 0.15 });

    const motionList = fromPanel.querySelector('motion-list');
    if (motionList && motionList.initialized) {
      motionList.unload();
    }
  }

  attributeChangedCallback(name, oldValue, newValue) {
    this.buttons.forEach((button, index) => {
      button.classList.toggle('button--primary', index === parseInt(newValue));
      button.classList.toggle('button--secondary', index !== parseInt(newValue));
      button.disabled = index === parseInt(newValue);
    });

    this.indicators.forEach((indicators, index) => {
      indicators.hidden = index !== parseInt(newValue);
      if (index === parseInt(newValue)) {
        indicators.querySelectorAll('button').forEach((button) => {
          button.unload();
          button.load();
        });
      }
    });

    if (name === 'selected-index' && oldValue !== null && oldValue !== newValue) {
      const fromButton = this.buttons[parseInt(oldValue)];
      const toButton = this.buttons[parseInt(newValue)];
      this.transition(document.getElementById(fromButton.getAttribute('aria-controls')), document.getElementById(toButton.getAttribute('aria-controls')));
    }
  }

  async transition(fromPanel, toPanel) {
    if (fromPanel) {
      await Motion.animate(fromPanel, { transform: ['translateY(0)', 'translateY(2rem)'], opacity: [1, 0] }, { duration: theme.config.motionReduced ? 0 : 0.15 }).finished;
    }

    fromPanel.hidden = true;
    toPanel.hidden = false;
    Motion.animate(toPanel, { transform: ['translateY(2rem)', 'translateY(0)'], opacity: [0, 1] }, { duration: theme.config.motionReduced ? 0 : 0.15 });

    const motionList = toPanel.querySelector('motion-list');
    if (motionList && motionList.initialized) {
      motionList.load();
    }
  }
}
customElements.define('tabs-element', TabsElement);

class CountdownTimer extends HTMLElement {
  constructor() {
    super();

    Motion.inView(this, this.init.bind(this), { margin: '200px 0px 200px 0px' });
  }

  get date() {
    return this._date = this._date || new Date(this.getAttribute('data-expires'));
  }

  get isCompact() {
    return this.getAttribute('data-compact') === 'true' || (this.getAttribute('data-compact') === 'mobile' && theme.config.mqlSmall);
  }

  init() {
    this.calculate();
    this.timerInterval = setInterval(this.calculate.bind(this), 1000);
  }

  calculate() {
    const now = new Date(),
      countTo = new Date(this.date),
      timeDifference = (countTo - now);

    if (timeDifference < 0) {
      this.complete();
      return;
    }

    const secondsInADay = 60 * 60 * 1000 * 24,
      secondsInAHour = 60 * 60 * 1000;

    const days = Math.floor(timeDifference / (secondsInADay) * 1);
    const hours = Math.floor((timeDifference % (secondsInADay)) / (secondsInAHour) * 1);
    const mins = Math.floor(((timeDifference % (secondsInADay)) % (secondsInAHour)) / (60 * 1000) * 1);
    const secs = Math.floor((((timeDifference % (secondsInADay)) % (secondsInAHour)) % (60 * 1000)) / 1000 * 1);

    if (this.isCompact) {
      const dayHTML = days > 0 ? `<div class="countdown__item"><p>${days}${theme.dateStrings.d}</p></div>` : '';
      const hourHTML = `<div class="countdown__item"><p>${hours}${theme.dateStrings.h}</p></div>`;
      const minHTML = `<div class="countdown__item"><p>${mins}${theme.dateStrings.m}</p></div>`;
      const secHTML = `<div class="countdown__item"><p>${secs}${theme.dateStrings.s}</p></div>`;

      this.innerHTML = dayHTML + hourHTML + minHTML + secHTML;
    }
    else {
      const dayHTML = days > 0 ? `<div class="countdown__item"><p>${days}</p><span>${days === 1 ? theme.dateStrings.day : theme.dateStrings.days}</span></div>` : '';
      const hourHTML = `<div class="countdown__item"><p>${hours}</p><span>${hours === 1 ? theme.dateStrings.hour : theme.dateStrings.hours}</span></div>`;
      const minHTML = `<div class="countdown__item"><p>${mins}</p><span>${mins === 1 ? theme.dateStrings.minute : theme.dateStrings.minutes}</span></div>`;
      const secHTML = `<div class="countdown__item"><p>${secs}</p><span>${secs === 1 ? theme.dateStrings.second : theme.dateStrings.seconds}</span></div>`;

      this.innerHTML = dayHTML + hourHTML + minHTML + secHTML;
    }
  }

  complete() {
    if (this.timerInterval) {
      clearInterval(this.timerInterval);
    }

    this.hidden = true;
  }
}
customElements.define('countdown-timer', CountdownTimer);

class ImageComparison extends HTMLElement {
  constructor() {
    super();

    Motion.inView(this, async () => {
      await theme.utils.imageLoaded(this.media);
      this.init();
    });
  }

  get button() {
    return this.querySelector('button');
  }

  get bounding() {
    return this.getBoundingClientRect();
  }

  get horizontal() {
    return this.getAttribute('data-layout') === 'horizontal';
  }

  get media() {
    return Array.from(this.querySelectorAll('img, svg'));
  }

  init() {
    this.active = false;

    this.button.addEventListener('touchstart', this.startHandler.bind(this), theme.supportsPassive ? { passive: false } : false);
    document.body.addEventListener('touchend', this.endHandler.bind(this), theme.supportsPassive ? { passive: true } : false);
    document.body.addEventListener('touchmove', this.onHandler.bind(this), theme.supportsPassive ? { passive: true } : false);

    this.button.addEventListener('mousedown', this.startHandler.bind(this));
    document.body.addEventListener('mouseup', this.endHandler.bind(this));
    document.body.addEventListener('mousemove', this.onHandler.bind(this));

    setTimeout(() => this.animate(), 1e3);
  }

  animate() {
    this.setAttribute('animate', '');

    this.classList.add('animating');
    setTimeout(() => {
      this.classList.remove('animating');
    }, 1e3);
  }

  startHandler(event) {
    event.preventDefault();
    this.active = true;
    this.classList.add('scrolling');
  }

  endHandler() {
    this.active = false;
    this.classList.remove('scrolling');
  }

  onHandler(e) {
    if (!this.active) return;

    const event = (e.touches && e.touches[0]) || e;
    let x = this.horizontal
      ? event.pageX - (this.bounding.left + window.scrollX)
      : event.pageY - (this.bounding.top + window.scrollY);

    this.scrollIt(x);
  }

  scrollIt(x) {
    const distance = this.horizontal ? this.clientWidth : this.clientHeight;

    const max = distance - 20;
    const min = 20;
    const mouseX = Math.max(min, (Math.min(x, max)));
    const mousePercent = (mouseX * 100) / distance;
    this.style.setProperty('--percent', mousePercent + '%');
  }
}
customElements.define('image-comparison', ImageComparison);

class LookbookElement extends HTMLElement {
  constructor() {
    super();

    Motion.inView(this, async () => {
      await theme.utils.imageLoaded(this.media);
      this.init();
    });
  }

  get media() {
    return Array.from(this.querySelectorAll('img, svg'));
  }

  get items() {
    return this._items = this._items || Array.from(this.querySelectorAll('.hotspot'));
  }

  init() {
    this.items.forEach((item) => item.addEventListener('mouseenter', (event) => this.select(this.items.indexOf(event.target))));

    if (Shopify.designMode) {
      const section = this.closest('.shopify-section');
      section.addEventListener('shopify:section:select', this.animate.bind(this));
      section.addEventListener('shopify:section:deselect', this.closeAll.bind(this));
      this.addEventListener('shopify:block:select', (event) => this.open(this.items.indexOf(event.target)));
    }

    setTimeout(() => this.animate(), 1e3);
  }

  animate() {
    this.openAll();

    this.timer = setTimeout(() => this.closeAll(), 3e3);
  }

  open(selectedIndex) {
    this.items.forEach((item, index) => item.classList.toggle('active', selectedIndex === index));
  }

  openAll() {
    this.items.forEach((item) => item.classList.add('active'));
  }

  closeAll() {
    this.items.forEach((item) => item.classList.remove('active'));
    clearTimeout(this.timer);
  }

  select(selectedIndex) {
    this.items.forEach((item, index) => item.setAttribute('aria-current', selectedIndex === index ? 'true' : 'false'));
    this.dispatchEvent(new CustomEvent('lookbook:change', { bubbles: true, detail: { index: selectedIndex } }));
  }
}
customElements.define('lookbook-element', LookbookElement);

class ShopTheLook extends HTMLElement {
  constructor() {
    super();

    this.lookbook.addEventListener('lookbook:change', (event) => this.carousel.select(event.detail.index));
    this.carousel.addEventListener('carousel:change', (event) => this.lookbook.select(event.detail.index));
  }

  get lookbook() {
    return this.querySelector('lookbook-element');
  }

  get carousel() {
    return this.querySelector('carousel-element');
  }
}
customElements.define('shop-the-look', ShopTheLook);

class SpinningText extends HTMLElement {
  constructor() {
    super();

    if (!theme.config.isTouch || Shopify.designMode) {
      Motion.inView(this, this.init.bind(this), { margin: '600px 0px 600px 0px' });
    }
    else {
      new theme.initWhenVisible(this.init.bind(this));
    }
  }

  get string() {
    let string = this.getAttribute('data-string');
    string = string.replace(/  +/g, ' ');
    return '•' + string.replace(/ +/g, '•')
  }

  init() {
    if (this.initialized) return;
    this.initialized = true;

    const canTrig = CSS.supports('(top: calc(sin(1) * 1px))');
    const OPTIONS = {
      TEXT: this.string,
      SIZE: 2,
      SPACING: 2,
    };

    const HEADING = document.createElement('div');
    const text = OPTIONS.TEXT;

    // Take the text and split it into spans...
    const chars = text.split('');
    this.style.setProperty('--char-count', chars.length);

    for (let c = 0; c < chars.length; c++) {
      HEADING.innerHTML += `<span aria-hidden="true" class="split-char" style="--char-index: ${c};">${chars[c]}</span>`;
    }
    HEADING.innerHTML += `<span class="sr-only">${OPTIONS.TEXT}</span>`;
    HEADING.classList = 'split-chars';

    // Set the styles
    this.style.setProperty('--font-size', OPTIONS.SIZE);
    this.style.setProperty('--character-width', OPTIONS.SPACING);
    this.style.setProperty(
      '--radius',
      canTrig
        ? 'calc((var(--character-width) / sin(var(--inner-angle))) * -1ch)'
        : `calc(
        (${OPTIONS.SPACING} / ${Math.sin(
          360 / this.children.length / (180 / Math.PI)
        )})
        * -1ch
      )`
    );

    // Append
    this.appendChild(HEADING);
  }
}
customElements.define('spinning-text', SpinningText);

class SlideshowElement extends HTMLElement {
  constructor() {
    super();

    this.selectedIndex = this.selectedIndex;

    if (!theme.config.isTouch || Shopify.designMode) {
      Motion.inView(this, this.init.bind(this), { margin: '200px 0px 200px 0px' });
    }
    else {
      new theme.initWhenVisible(theme.utils.throttle(this.init.bind(this)));
    }
  }

  static get observedAttributes() {
    return ['selected-index'];
  }

  get selectedIndex() {
    return parseInt(this.getAttribute('selected-index')) || 0;
  }

  set selectedIndex(index) {
    this.setAttribute('selected-index', Math.min(Math.max(index, 0), this.items.length - 1).toString());
  }

  get items() {
    return this._items = this._items || Array.from(this.children);
  }

  get autoplay() {
    return this.hasAttribute('autoplay');
  }

  get speed() {
    return this.hasAttribute('autoplay') ? parseInt(this.getAttribute('autoplay-speed')) * 1000 : 5000;
  }

  get adaptiveHeightProperty() {
    return false;
  }

  init() {
    if (this.initialized) return;
    this.initialized = true;

    const that = this;
    if (this.items.length > 1) {
      this.slider = new Flickity(this, {
        accessibility: false,
        pageDots: false,
        prevNextButtons: false,
        wrapAround: true,
        rightToLeft: theme.config.rtl,
        autoPlay: this.autoplay ? this.speed : false,
        adaptiveHeightProperty: this.adaptiveHeightProperty,
        on: {
          ready: function () {
            const { selectedElement } = this;
            that.onReady(selectedElement);
          }
        }
      });

      this.slider.on('change', this.onChange.bind(this));
      this.addEventListener('slider:previous', () => this.slider.previous());
      this.addEventListener('slider:next', () => this.slider.next());
      this.addEventListener('slider:play', () => this.slider.playPlayer());
      this.addEventListener('slider:pause', () => this.slider.pausePlayer());

      if (Shopify.designMode) {
        this.addEventListener('shopify:block:select', (event) => this.slider.select(this.items.indexOf(event.target)));
      }
    }
    else if (this.items.length === 1) {
      const selectedElement = this.firstChild;
      that.onReady(selectedElement);
    }
  }

  disconnectedCallback() {
    if (this.slider) this.slider.destroy();
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (name === 'selected-index' && oldValue !== null && oldValue !== newValue) {
      const fromElement = this.items[parseInt(oldValue)];
      const toElement = this.items[parseInt(newValue)];

      const animateElement = toElement.querySelector('animate-element');
      animateElement?.refresh();

      if (fromElement.getAttribute('data-type') === 'video') {
        const fromVideoElement = theme.utils.visibleMedia(fromElement.querySelectorAll('video-media'));
        fromVideoElement?.pause();
      }

      if (toElement.getAttribute('data-type') === 'video') {
        const toVideoElement = theme.utils.visibleMedia(toElement.querySelectorAll('video-media'));
        toVideoElement?.play();
      }
    }
  }

  onChange() {
    this.selectedIndex = this.slider.selectedIndex;
    this.dispatchEvent(new CustomEvent('slider:change', { bubbles: true, detail: { currentPage: this.slider.selectedIndex } }));
  }

  select(selectedIndex) {
    this.slider.select(selectedIndex);
  }

  onReady(selectedElement) {
    if (selectedElement.getAttribute('data-type') === 'video') {
      const videoElement = theme.utils.visibleMedia(selectedElement.querySelectorAll('video-media'));
      videoElement?.play();
    }

    if (!theme.config.isTouch && document.body.hasAttribute('data-page-transition')) {
      const animateElement = selectedElement.querySelector('animate-element');
      animateElement?.refresh();
    }
  }
}
customElements.define('slideshow-element', SlideshowElement);

class SlideshowHero extends SlideshowElement {
  constructor() {
    super();

    window.addEventListener('scroll', theme.utils.throttle(this.onScrollHandler.bind(this)), false);
  }

  get adaptiveHeightProperty() {
    return true;
  }

  get heroSection() {
    return this.closest('.hero-section');
  }

  get nextSection() {
    let nextSibling = this.heroSection.nextElementSibling;
    for (let i = 0; i < 20; i++) {
      if (nextSibling.classList.contains('shopify-section') && !nextSibling.classList.contains('header-section')) break;
      nextSibling = nextSibling.nextElementSibling;
    }

    if (nextSibling && nextSibling.nodeType != 1) {
      nextSibling = document.getElementById('PageContainer');
    }

    return nextSibling;
  }

  onScrollHandler() {
    const bounding = this.nextSection.getBoundingClientRect();
    this.heroSection.classList.toggle('hero-scrolled', bounding.y < -30);
  }
}
customElements.define('slideshow-hero', SlideshowHero);

class SlideshowWords extends HTMLElement {
  constructor() {
    super();

    this.selectedIndex = this.selectedIndex;

    if (this.controlledElement) {
      this.controlledElement.addEventListener('slider:change', (event) => this.selectedIndex = event.detail.currentPage);
    }
  }

  static get observedAttributes() {
    return ['selected-index'];
  }

  get selectedIndex() {
    return parseInt(this.getAttribute('selected-index')) || 0;
  }

  set selectedIndex(index) {
    this.setAttribute('selected-index', Math.min(Math.max(index, 0), this.items.length - 1).toString());
  }

  get controlledElement() {
    return this.hasAttribute('aria-controls') ? document.getElementById(this.getAttribute('aria-controls')) : null;
  }

  get items() {
    return this._items = this._items || Array.from(this.children);
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (name === 'selected-index' && oldValue !== null && oldValue !== newValue) {
      this.transition(this.items[parseInt(oldValue)], this.items[parseInt(newValue)]);
    }
  }

  transition(fromElement, toElement) {
    const fromWords = Array.from(fromElement.querySelectorAll('animate-element'));
    const toWords = Array.from(toElement.querySelectorAll('animate-element'));

    fromWords.forEach((element) => element.reset());

    setTimeout(() => {
      this.items.forEach((item) => {
        item.setAttribute('aria-current', parseInt(item.getAttribute('data-index')) === parseInt(this.selectedIndex) ? 'true' : 'false');
      });

      toWords.forEach((element) => element.refresh());
    }, 500 + (30 * fromWords.length));
  }
}
customElements.define('slideshow-words', SlideshowWords, { extends: 'nav' });

class SlideshowParallax extends HTMLDivElement {
  constructor() {
    super();

    this.load();
  }

  get controlledElement() {
    return this.hasAttribute('aria-controls') ? document.getElementById(this.getAttribute('aria-controls')) : null;
  }

  get mobileDisabled() {
    return false;
  }

  load() {
    if (theme.config.motionReduced) return;
    if (this.mobileDisabled && (theme.config.isTouch || theme.config.mqlSmall)) return;

    this.motion();
  }

  motion() {
    this.animation = Motion.scroll(
      Motion.animate(this, { transform: [`translateY(${this.getAttribute('data-start')})`, `translateY(${this.getAttribute('data-stop')})`] }),
      { target: this.controlledElement ?? document.body, offset: Motion.ScrollOffset.Exit }
    );
  }
}
customElements.define('slideshow-parallax', SlideshowParallax, { extends: 'div' });

class ControlButton extends HTMLButtonElement {
  constructor() {
    super();

    this.addEventListener('click', this.onClick);
  }

  get controlledElement() {
    return this.hasAttribute('aria-controls') ? document.getElementById(this.getAttribute('aria-controls')) : null;
  }

  get paused() {
    return this.hasAttribute('paused');
  }

  static get observedAttributes() {
    return ['paused'];
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (name === 'paused') {
      if (oldValue === null && newValue === '') {
        return (this.controlledElement ?? this).dispatchEvent(new CustomEvent('slider:pause', { bubbles: true }));
      }

      if (newValue === null) {
        return (this.controlledElement ?? this).dispatchEvent(new CustomEvent('slider:play', { bubbles: true }));
      }
    }
  }

  onClick() {
    this.paused ? this.removeAttribute('paused') : this.setAttribute('paused', '');
  }
}
customElements.define('control-button', ControlButton, { extends: 'button' });

class QuickView extends XModal {
  constructor() {
    super();
  }

  get selector() {
    return '.quick-view__content';
  }

  cartUpdateUnsubscriber = undefined;

  connectedCallback() {
    super.connectedCallback();

    this.cartUpdateUnsubscriber = theme.pubsub.subscribe(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, this.hide.bind(this));
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  beforeShow() {
    super.beforeShow();
    this.quickview();
  }

  afterShow() {
    super.afterShow();

    document.dispatchEvent(new CustomEvent('quickview:open', {
      detail: {
        productUrl: this.getAttribute('data-product-url')
      }
    }));
  }

  afterHide() {
    super.afterHide();

    const drawerContent = this.querySelector(this.selector);
    drawerContent.innerHTML = '';
  }

  quickview() {
    const drawerContent = this.querySelector(this.selector);
    const productUrl = this.getAttribute('data-product-url').split('?')[0];
    const sectionUrl = `${productUrl}?view=modal`;

    fetch(sectionUrl)
      .then(response => response.text())
      .then(responseText => {
        setTimeout(() => {
          const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');
          const productElement = parsedHTML.querySelector(this.selector);
          this.setInnerHTML(drawerContent, productElement.innerHTML);
          theme.a11y.trapFocus(this, this.focusElement);

          if (window.Shopify && Shopify.PaymentButton) {
            Shopify.PaymentButton.init();
          }

          document.dispatchEvent(new CustomEvent('quickview:loaded', {
            detail: {
              productUrl: this.getAttribute('data-product-url')
            }
          }));
        }, 200);
      })
      .catch(e => {
        console.error(e);
      });
  }

  setInnerHTML(element, innerHTML) {
    element.innerHTML = innerHTML;

    // Reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
    element.querySelectorAll('script').forEach(oldScriptTag => {
      const newScriptTag = document.createElement('script');
      Array.from(oldScriptTag.attributes).forEach(attribute => {
        newScriptTag.setAttribute(attribute.name, attribute.value)
      });
      newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
      oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
    });
  }
}
customElements.define('quick-view', QuickView);

class FiltersToggle extends HoverButton {
  constructor() {
    super();

    this.addEventListener('click', this.onClick);

    if (theme.config.hasLocalStorage) {
      const expanded = window.localStorage.getItem(`${theme.settings.themeName}:filters-toggle`) || 'false';
      this.onToggle(expanded === 'true');
    }
  }

  get controlledElement() {
    return this.hasAttribute('aria-controls') ? document.getElementById(this.getAttribute('aria-controls')) : null;
  }

  get expanded() {
    return this.getAttribute('aria-expanded') === 'true';
  }

  onClick() {
    this.onToggle(this.expanded);
  }

  onToggle(expanded) {
    this.setAttribute('aria-expanded', expanded ? 'false' : 'true');
    if (this.controlledElement) this.controlledElement.classList.toggle('xl:block', !expanded);

    if (theme.config.hasLocalStorage) {
      window.localStorage.setItem(`${theme.settings.themeName}:filters-toggle`, expanded);
    }
  }
}
customElements.define('filters-toggle', FiltersToggle, { extends: 'button' });

class RevealBanner extends HTMLElement {
  constructor() {
    super();

    Motion.inView(this, this.init.bind(this), { margin: '600px 0px 600px 0px' });
  }

  init() {
    const tracker = this.querySelector('.reveal-banner__tracker');
    Motion.scroll(
      Motion.animate(this.querySelectorAll('.banner__media'), { clipPath: ['inset(20% 35% 0% 35% round var(--rounded-block))', 'inset(0% 0%)'] }, { easing: 'linear' }),
      { target: tracker, offset: Motion.ScrollOffset.Enter }
    );

    const tracker2nd = this.querySelector('.reveal-banner__tracker2nd');
    if (tracker2nd) {
      Motion.scroll(Motion.timeline([
        [this.querySelector('.banner__overlay'), { opacity: [0, 1] }, { easing: 'linear' }],
        [this.querySelector('.banner__content'), { opacity: [0, 1], visibility: ['hidden', 'visible'], transform: ['translateY(10%)', 'translateY(0)'] }, { easing: 'linear', at: '<' }],
      ]), { target: tracker2nd, offset: Motion.ScrollOffset.Enter });
    }
  }
}
customElements.define('reveal-banner', RevealBanner);

class TestimonialParallax extends ParallaxElement {
  constructor() {
    super();
  }

  get mobileDisabled() {
    return true;
  }
}
customElements.define('testimonial-parallax', TestimonialParallax, { extends: 'div' });

class SplittingBanner extends HTMLElement {
  constructor() {
    super();

    Motion.inView(this, this.init.bind(this), { margin: '600px 0px 600px 0px' });
  }

  init() {
    const splitting = Splitting({ target: this.querySelector('.splitting'), by: 'chars', whitespace: true });

    const wrapper = this.querySelector('.splitting-wrapper');
    const tracker = this.querySelector('.reveal-banner__tracker');
    Motion.scroll(Motion.timeline([
      [splitting[0].chars, { opacity: [0.3, 1] }, { delay: theme.config.motionReduced ? 0 : Motion.stagger(0.1) }],
      //[wrapper, { transform: ['translateY(-20vh)', 'translateY(0)'] }, { easing: 'linear', at: '<' }]
    ]), { target: tracker, offset: Motion.ScrollOffset.Enter });

    Motion.scroll(
      Motion.animate(wrapper, { opacity: theme.config.mqlSmall ? [1, 0, 0] : [1, 1, 0] }, { easing: 'linear' }),
      { target: tracker, offset: Motion.ScrollOffset.Exit }
    );

    const tracker2nd = this.querySelector('.reveal-banner__tracker2nd');
    if (tracker2nd) {
      Motion.scroll(
        Motion.animate(this.querySelector('.button-wrapper'), { opacity: [0, 1, 1], transform: ['translateY(100%)', 'translateY(0)'] }, { easing: 'linear' }),
        { target: tracker2nd, offset: Motion.ScrollOffset.Enter }
      );
    }
  }
}
customElements.define('splitting-banner', SplittingBanner);

class ProductCardInfo extends ProductInfo {
  constructor() {
    super();

    this._sectionId = 'ProductCard-template';
  }

  get pickerType() {
    return this.getAttribute('data-picker-type');
  }

  buildRequestUrlWithParams(url, optionValues) {
    const params = [];
    params.push('view=bundle-card');

    if (optionValues.length) {
      params.push(`option_values=${optionValues.join(',')}`);
    }

    return `${url}?${params.join('&')}`;
  }

  updateSourceFromDestination(parsedHTML, id) {
    const source = parsedHTML.getElementById(`${id}-${this._sectionId}-${this.productId}`);
    const destination = document.querySelector(`#${id}-${this.sectionId}-${this.productId}`);
    if (source && destination) {
      destination.innerHTML = source.innerHTML;
      destination.removeAttribute('hidden');
    }
  }

  updateOptionValues(parsedHTML) {
    const variantPicker = parsedHTML.getElementById(`VariantPicker-${this._sectionId}-${this.productId}-${this.pickerType}`);
    if (variantPicker) {
      theme.HTMLUpdateUtility.viewTransition(this.variantSelectors, variantPicker, this.preProcessHtmlCallbacks);
    }
  }

  getSelectedVariant(productInfoNode) {
    const selectedVariant = productInfoNode.querySelector(`product-card-info[data-product-id="${this.productId}"] [data-selected-variant]`)?.textContent;
    return !!selectedVariant ? JSON.parse(selectedVariant) : null;
  }
}
customElements.define('product-card-info', ProductCardInfo);

class ProductBundleForm extends HTMLFormElement {
  constructor() {
    super();

    this.querySelector('[name="id"]').disabled = false;
    this.addEventListener('submit', this.onSubmitHandler);
  }

  get controlledElement() {
    return this.hasAttribute('aria-controls') ? document.getElementById(this.getAttribute('aria-controls')) : null;
  }

  get submitButton() {
    return this._submitButton = this._submitButton || this.querySelector('[type="submit"]');
  }

  getSelectedVariant() {
    const selectedVariant = this.querySelector('[data-selected-variant]')?.textContent;
    return !!selectedVariant ? JSON.parse(selectedVariant) : null;
  }

  onSubmitHandler(event) {
    event.preventDefault();
    if (this.submitButton.hasAttribute('aria-disabled')) return;

    const product = this.closest('.product-card');
    const variant = this.getSelectedVariant();
    variant.default_featured_media = this.hasAttribute('data-product-image') ? this.getAttribute('data-product-image') : '';

    this.submitButton.removeAttribute('aria-busy');
    (this.controlledElement ?? this).dispatchEvent(new CustomEvent('bundle:added', { bubbles: true, detail: { product: product, variant: variant } }));
  }

  handleErrorMessage() {
    // todo
  }

  toggleSubmitButton(disable = true, text) {
    if (!this.submitButton) return;

    const submitButtonText = this.submitButton.querySelector('.btn-text');
    const submitButtonTextChild = this.submitButton.querySelector('.btn-text>span');

    if (disable) {
      this.submitButton.setAttribute('disabled', '');
      if (text) {
        (submitButtonTextChild || submitButtonText).textContent = text;
      }
    }
    else {
      this.submitButton.removeAttribute('disabled');
      (submitButtonTextChild || submitButtonText).textContent = theme.variantStrings.addToBundle;
    }
  }
}
customElements.define('product-bundle-form', ProductBundleForm, { extends: 'form' });

class ProductBundleRemoveButton extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('click', (event) => {
      const variant = event.target.closest('[data-product-bundle-variant]');
      (this.controlledElement ?? this).dispatchEvent(new CustomEvent('bundle:removed', { bubbles: true, detail: { variant: variant } }));
    });
  }

  get controlledElement() {
    return this.hasAttribute('aria-controls') ? document.getElementById(this.getAttribute('aria-controls')) : null;
  }
}
customElements.define('product-bundle-remove-button', ProductBundleRemoveButton);

class ProductBundleToggleButton extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('click', () => {
      this.controlledElement.classList.toggle('active');
    });
  }

  get controlledElement() {
    return this.hasAttribute('aria-controls') ? document.getElementById(this.getAttribute('aria-controls')) : null;
  }
}
customElements.define('product-bundle-toggle-button', ProductBundleToggleButton);

class ProductBundle extends HTMLElement {
  constructor() {
    super();

    this.template;
    this.bundleCount = 0;

    this.addEventListener('bundle:added', this.onAddHandler.bind(this));
    this.addEventListener('bundle:removed', this.onRemoveHandler.bind(this));
    this.addEventListener('change', theme.utils.debounce(this.onChangeHandler.bind(this), 300));
    this.submitButton.addEventListener('click', this.onSubmitHandler.bind(this));
  }

  get bundleMin() {
    return this.hasAttribute('data-minimum') ? parseInt(this.getAttribute('data-minimum')) : 3;
  }

  get bundleMax() {
    const minimum = this.bundleMin;
    const maximum = this.hasAttribute('data-maximum') ? parseInt(this.getAttribute('data-maximum')) : 3;
    return maximum > minimum ? maximum : minimum;
  }

  get variants() {
    return Array.from(this.querySelectorAll('[data-product-bundle-variant]'));
  }

  get submitButton() {
    return this.querySelector('[data-product-bundle-submit]');
  }

  get cartDrawer() {
    return document.querySelector('cart-drawer');
  }

  get preventDuplicate() {
    return this.hasAttribute('data-prevent-duplicate');
  }

  get template() {
    return this._template = this._template || this.querySelector('[data-product-bundle-variant][available]').cloneNode(true);
  }

  onChangeHandler() {
    this.updateTotal();
    this.updateTotalWithCurrency();
    this.updateProgressBar();
  }

  onSubmitHandler(event) {
    const validateQuantity = this.variants.every((variant) => {
      const target = variant.querySelector('quantity-input').input;
      const inputValue = parseInt(target.value);
      let message = '';

      if (inputValue < parseInt(target.getAttribute('data-min'))) {
        message = theme.quickOrderListStrings.minError.replace('[min]', target.getAttribute('data-min'));
      }
      else if (inputValue > parseInt(target.max)) {
        message = theme.quickOrderListStrings.maxError.replace('[max]', target.max);
      }
      else if (inputValue % parseInt(target.step) !== 0) {
        message = theme.quickOrderListStrings.stepError.replace('[step]', target.step);
      }

      if (message) {
        target.setCustomValidity(message);
        target.reportValidity();
        target.value = target.defaultValue;
        target.select();

        return false;
      }

      return true;
    });
    if (!validateQuantity) return;

    const data = {
      items: this.variants.map(variant => ({
        id: variant.getAttribute('data-variant-id'),
        quantity: variant.querySelector('quantity-input').value || 1
      }))
    };

    if (document.body.classList.contains('template-cart') || theme.settings.cartType === 'page') {
      theme.utils.postLink2(theme.routes.cart_add_url, {
        parameters: {
          ...data
        }
      });
      return;
    }

    event.preventDefault();
    if (this.submitButton.hasAttribute('aria-disabled')) return;
    this.activeElement = event.submitter || event.currentTarget;

    this.handleErrorMessage();

    let sectionsToBundle = [];
    document.documentElement.dispatchEvent(new CustomEvent('cart:bundled-sections', { bubbles: true, detail: { sections: sectionsToBundle } }));

    const body = JSON.stringify({
      ...data,
      sections: sectionsToBundle,
      sections_url: window.location.pathname
    });

    this.submitButton.setAttribute('aria-disabled', 'true');
    this.submitButton.setAttribute('aria-busy', 'true');

    fetch(`${theme.routes.cart_add_url}`, { ...theme.utils.fetchConfig('javascript'), body })
      .then((response) => response.json())
      .then(async (parsedState) => {
        if (parsedState.status) {
          this.handleErrorMessage(parsedState.description);
          document.dispatchEvent(new CustomEvent('ajaxProduct:error', {
            detail: {
              errorMessage: parsedState.description
            }
          }));

          const submitButtonText = this.submitButton.querySelector('.btn-text>span');
          if (!submitButtonText || !submitButtonText.hasAttribute('data-sold-out')) return;
          submitButtonText.innerText = submitButtonText.getAttribute('data-sold-out');
          this.submitButton.setAttribute('aria-disabled', 'true');
          this.error = true;
          return;
        }

        const cartJson = await (await fetch(theme.routes.cart_url, { ...theme.utils.fetchConfig('json', 'GET') })).json();
        cartJson['sections'] = parsedState['sections'];

        theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, { source: 'product-bundle', cart: cartJson });
        document.dispatchEvent(new CustomEvent('ajaxProduct:added', {
          detail: {
            product: parsedState
          }
        }));

        this.cartDrawer?.show(this.activeElement);
        this.resetVariants();
      })
      .catch((error) => {
        console.log(error);
      })
      .finally(() => {
        this.submitButton.removeAttribute('aria-busy');

        if (!this.error) {
          this.submitButton.removeAttribute('aria-disabled');
        }
      });
  }

  handleErrorMessage(errorMessage = false) {
    if (this.hideErrors) return;

    this.errorMessage = this.errorMessage || this.querySelector('.product-form__error-message');
    if (!this.errorMessage) return;

    this.errorMessage.toggleAttribute('hidden', !errorMessage);
    this.errorMessage.innerText = errorMessage;
  }

  onAddHandler(event) {
    this.addVariant(event.detail.product, event.detail.variant);
    this.updateTotal();
    this.updateTotalWithCurrency();
    this.updateProgressBar();
  }

  onRemoveHandler(event) {
    this.clearVariant(event.detail.variant);
    this.reorderVariants();
    this.updateTotal();
    this.updateTotalWithCurrency();
    this.updateProgressBar();
  }

  updateTotal() {
    let subtotal = 0;
    this.variants.forEach((variant) => {
      if (!variant.hasAttribute('available')) {
        const price = variant.querySelector('[data-product-bundle-variant-price]');
        const quantityInput = variant.querySelector('quantity-input');
        subtotal += parseInt(price.getAttribute('data-price')) * parseInt(quantityInput.value);
      }
    });

    const element = this.querySelector('[data-product-bundle-total]');
    element.innerHTML = theme.Currency.formatMoney(subtotal, theme.settings.moneyFormat);
  }

  updateTotalWithCurrency() {
    let subtotal = 0;
    this.variants.forEach((variant) => {
      if (!variant.hasAttribute('available')) {
        const price = variant.querySelector('[data-product-bundle-variant-price]');
        const quantityInput = variant.querySelector('quantity-input');
        subtotal += parseInt(price.getAttribute('data-price')) * parseInt(quantityInput.value);
      }
    });

    const element = this.querySelector('[data-product-bundle-total-with-currency]');
    element.innerHTML = theme.Currency.formatMoney(subtotal, theme.settings.moneyWithCurrencyFormat);
  }

  updateProgressBar() {
    const percent = 100 * this.bundleCount / this.bundleMin;
    const element = this.querySelector('[data-product-bundle-progress-bar]');
    element.style.setProperty('--progress', `${percent}%`);
  }

  addVariant(product, variant) {
    this.bundleCount++;

    if (this.bundleCount >= this.bundleMin) {
      this.submitButton.disabled = false;
    }
    if (this.bundleCount >= this.bundleMax) {
      this.closest('.product-bundle-wrapper').setAttribute('locked', '');
    }

    let variantElement = this.getAvailableAvriant();
    if (variantElement === null) {
      variantElement = this.template.cloneNode(true);
      variantElement.style.order = this.bundleCount;
      this.querySelector('.product-bundle__body').appendChild(variantElement);
    }

    if (variantElement) {
      const preview_image = variant.featured_media ? variant.featured_media.preview_image.src : variant.default_featured_media;
      const media = variantElement.querySelector('[data-product-bundle-variant-media]');
      if (preview_image.length > 0) {
        media.innerHTML = `<img src="${this.getResizedImageSrc(preview_image, 180)}" srcset="${this.getResizedImageSrc(preview_image, 180)} 180w, ${this.getResizedImageSrc(preview_image, 360)} 360w, ${this.getResizedImageSrc(preview_image, 540)} 540w" loading="lazy" is="lazy-image" />`;
      }

      const content = variantElement.querySelector('[data-product-bundle-variant-content]');
      content.innerHTML = `
        <div class="flex flex-col gap-1">
          <p class="horizontal-product__title font-medium text-base leading-tight">${product.querySelector('[data-product-bundle-title]').textContent}</p>
          ${variant.options.length > 0 ? `
            <ul class="grid gap-1d5">
              ${variant.options.map(option => {
        if (option !== 'Default Title') {
          return `<li class="text-xs text-opacity leading-tight">${option}</li>`;
        }
      }).join('')}
            </ul>
          ` : ''}
        </div>
        <div class="price text-sm flex flex-wrap gap-1d5" data-price="${variant.price}" data-product-bundle-variant-price>
          ${theme.Currency.formatMoney(variant.price, theme.settings.moneyFormat)}
        </div>`;

      variantElement.product = product;
      variantElement.setAttribute('data-variant-id', variant.id);
      variantElement.removeAttribute('available');
      if (this.preventDuplicate) {
        product.setAttribute('locked', '');
      }
    }
  }

  clearVariant(variant) {
    if (this.preventDuplicate) {
      variant.product.removeAttribute('locked');
    }
    variant.setAttribute('available', '');
    variant.removeAttribute('data-variant-id');
    variant.querySelector('[data-product-bundle-variant-media]').innerHTML = '';
    variant.querySelector('[data-product-bundle-variant-media]').classList.remove('loading');
    variant.querySelector('[data-product-bundle-variant-content]').innerHTML = '<span class="horizontal-product__skeleton"></span><span class="horizontal-product__skeleton"></span>';
    variant.querySelector('quantity-input').reset();

    this.bundleCount--;

    if (this.bundleCount < this.bundleMin) {
      this.submitButton.disabled = true;
    }
    else {
      variant.remove();
    }
    if (this.bundleCount < this.bundleMax) {
      this.closest('.product-bundle-wrapper').removeAttribute('locked');
    }
  }

  reorderVariants() {
    let order = 0;
    let minimum = Math.max(this.bundleMin, this.bundleCount) * -1;
    this.variants.forEach((variant) => {
      variant.style.order = variant.hasAttribute('available') ? order++ : minimum++;
    });
  }

  resetVariants() {
    this.variants.forEach((variant) => {
      this.clearVariant(variant);
    });

    this.reorderVariants();
    this.updateTotal();
    this.updateTotalWithCurrency();
    this.updateProgressBar();
  }

  getAvailableAvriant() {
    return this.querySelector('[data-product-bundle-variant][available]');
  }

  getResizedImageSrc(src, size) {
    return `${src}${src.includes("?") ? "&" : "?"}width=${size}&height=${size}`.replace(/\n|\r|\s/g, "");
  }
}
customElements.define('product-bundle', ProductBundle);

class NumberCounter extends HTMLElement {
  constructor() {
    super();

    if (theme.config.motionReduced) return;

    if (!theme.config.isTouch || Shopify.designMode) {
      Motion.inView(this, this.init.bind(this), { margin: '200px 0px 200px 0px' });
    }
    else {
      new theme.initWhenVisible(this.init.bind(this));
    }
  }

  init() {
    if (this.initialized) return;
    this.initialized = true;

    const matches = this.textContent.trim().match(/\d+(?:[,. ]\d+)*/);
    const toReplace = matches[0].replace(/[,\. ]+/, "");

    Motion.animate((progress) => {
      const formattedString = Math.round(progress * parseInt(toReplace)).toString();
      this.textContent = progress === 1 ? matches[0] : formattedString;
    }, { duration: parseFloat(this.getAttribute('data-duration')), easing: [0.16, 1, 0.3, 1] });
  }
}
customElements.define('number-counter', NumberCounter);

class ScrollingBanner extends HTMLElement {
  constructor() {
    super();

    if (!theme.config.isTouch || Shopify.designMode) {
      Motion.inView(this, this.init.bind(this), { margin: '600px 0px 600px 0px' });
    }
    else {
      new theme.initWhenVisible(this.init.bind(this));
    }
  }

  get media() {
    return this._media = this._media || Array.from(this.querySelectorAll('.image-with-text__image>.media'));
  }

  get stickyElement() {
    return this.querySelector('sticky-element');
  }

  init() {
    if (this.initialized) return;
    this.initialized = true;

    this.detectScrollListener = theme.utils.throttle(this.onScrollHandler.bind(this));

    const mql = window.matchMedia('screen and (min-width: 1024px)');
    if (mql.matches) this.load();
    mql.onchange = (mql) => {
      mql.matches ? this.load() : this.unload();
    }
  }

  setMediaHeight() {
    let height = 0;
    this.media.forEach((media) => {
      height += media.getBoundingClientRect().height;
    });
    this.style.setProperty('--scrolling-height', `${height}px`);
  }

  onScrollHandler() {
    const scrollTop = window.scrollY;

    if (this.currentScrollTop === scrollTop) return;
    this.currentScrollTop = scrollTop;
    const offsetTop = this.getBoundingClientRect().top - parseFloat(this.stickyElement.inset);

    const mediaElements = this.media;
    const mediaCount = mediaElements.length;

    if (offsetTop < 0) {
      let activeIndex = 1;

      // Start from second element
      for (let i = 1; i < mediaCount; i++) {
        const media = mediaElements[i];
        const mediaHeight = media.getBoundingClientRect().height;
        const mediaScrollTop = mediaHeight * (i - 1);

        if (Math.abs(offsetTop) > mediaScrollTop) {
          activeIndex = i;
          let progress = 100 - Math.floor((Math.abs(offsetTop) - mediaScrollTop) / mediaHeight * 100);
          if (progress > 95) progress = 100;
          if (progress < 5) progress = 0;

          media.style.clipPath = `polygon(0 ${progress}%, 100% ${progress}%, 100% 100%, 0 100%)`;
          this.updateControlledElement(media, progress);
        }
        else {
          media.style.clipPath = '';
        }
      }

      // Update remaining elements
      for (let i = activeIndex + 1; i < mediaCount; i++) {
        const media = mediaElements[i];
        this.updateControlledElement(media, 100);
      }
    }
    else {
      // Reset all elements when scrolled to top
      for (let i = 1; i < mediaCount; i++) {
        const media = mediaElements[i];
        media.style.clipPath = '';
        this.updateControlledElement(media, 100);
      }
    }
  }

  updateControlledElement(media, progress) {
    const controlledElement = media.hasAttribute('aria-controls') ? document.getElementById(media.getAttribute('aria-controls')) : null;
    if (controlledElement) {
      if (progress === 100) controlledElement.classList.add('opacity-0', 'pointer-events-none');
      if (progress === 0) controlledElement.classList.remove('opacity-0', 'pointer-events-none');
    }
  }

  load() {
    this.setMediaHeight();
    window.addEventListener('scroll', this.detectScrollListener, false);
  }

  unload() {
    window.removeEventListener('scroll', this.detectScrollListener);
    this.style.removeProperty('--scrolling-height');
    this.media.forEach((media) => {
      media.style.clipPath = '';
    });
  }
}
customElements.define('scrolling-banner', ScrollingBanner);

class SecondaryVideo extends HTMLDivElement {
  constructor() {
    super();

    if (theme.config.motionReduced || theme.config.isTouch) return;

    if (this.videoMedia) {
      this.addEventListener('mouseenter', this.onEnterHandler);
      this.addEventListener('mouseleave', this.onLeaveHandler);
    }
  }

  get videoMedia() {
    return this.querySelector('video-media');
  }

  onEnterHandler() {
    const media = this.videoMedia;
    if (media && media.hasAttribute('autoplay')) {
      media.play();
    }
  }

  onLeaveHandler() {
    const media = this.videoMedia;
    if (media) {
      media.pause();
    }
  }
}
customElements.define('secondary-video', SecondaryVideo, { extends: 'div' });

class SocialFeed extends XModal {
  constructor() {
    super();

    this.onKeyup = this.onKeyup.bind(this);
  }

  get shouldCloseAll() {
    return true;
  }

  get lookbook() {
    return this.querySelector('lookbook-element');
  }

  get previousButton() {
    return this.querySelector('button[data-button="previous"]');
  }

  get nextButton() {
    return this.querySelector('button[data-button="next"]');
  }

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('keyup', this.onKeyup);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.onKeyup);
  }

  beforeShow() {
    super.beforeShow();
    this.lookbook.closeAll();
  }

  async afterShow() {
    super.afterShow();
    Motion.inView(this.lookbook, async () => {
      await theme.utils.imageLoaded(this.lookbook.media);
      this.lookbook.animate();
    });
  }

  onKeyup(event) {
    if (event.code === 'ArrowLeft' || event.code === 'ArrowRight') {
      const button = event.code === 'ArrowLeft' ? this.previousButton : this.nextButton;
      if (button && !button.disabled) {
        event.preventDefault();
        button.dispatchEvent(new Event('click'));
      }
    }
  }
}
customElements.define('social-feed', SocialFeed);

class SocialFeedButton extends HTMLButtonElement {
  constructor() {
    super();

    this.addEventListener('click', this.onButtonClick);
  }

  get controlledElement() {
    return this.hasAttribute('aria-controls') ? document.getElementById(this.getAttribute('aria-controls')) : null;
  }

  onButtonClick() {
    if (this.controlledElement) this.controlledElement.show();
  }
}
customElements.define('social-feed-button', SocialFeedButton, { extends: 'button' });

class IconsCarousel extends HTMLDivElement {
  constructor() {
    super();

    this.onUpdateTabindex();

    this.onUpdateTabindex = this.onUpdateTabindex.bind(this);
    this.onEnterListener = this.onEnterHandler.bind(this);
    this.onLeaveListener = this.onLeaveHandler.bind(this);
  }

  get isScrollable() {
    const style = window.getComputedStyle(this);
    const hasScrollableOverflow = style.overflow === 'scroll' || style.overflow === 'auto' || style.overflowY === 'scroll' || style.overflowY === 'auto' || style.overflowX === 'scroll' || style.overflowX === 'auto';
    const hasScrollableContent = this.scrollHeight > this.clientHeight || this.scrollWidth > this.clientWidth;

    return hasScrollableOverflow && hasScrollableContent;
  }

  connectedCallback() {
    if (theme.config.isTouch) return;

    this.addEventListener('mouseenter', this.onEnterListener);
    this.addEventListener('mouseleave', this.onLeaveListener);
    document.addEventListener('matchSmall', this.onUpdateTabindex);
    document.addEventListener('unmatchSmall', this.onUpdateTabindex);
  }

  disconnectedCallback() {
    if (theme.config.isTouch) return;

    this.removeEventListener('mouseenter', this.onEnterListener);
    this.removeEventListener('mouseleave', this.onLeaveListener);
    document.removeEventListener('matchSmall', this.onUpdateTabindex);
    document.removeEventListener('unmatchSmall', this.onUpdateTabindex);
  }

  onEnterHandler() {
    const scrollWidth = this.scrollWidth * (theme.config.rtl ? -1 : 1);
    this.scrollTo({
      left: scrollWidth,
      behavior: theme.config.motionReduced ? 'auto' : 'smooth'
    });
  }

  onLeaveHandler() {
    this.scrollTo({
      left: 0,
      behavior: theme.config.motionReduced ? 'auto' : 'smooth'
    });
  }

  onUpdateTabindex() {
    this.removeAttribute('tabindex');

    if (this.isScrollable) {
      this.setAttribute('tabindex', '0');
    }
  }
}
customElements.define('icons-carousel', IconsCarousel, { extends: 'div' });

class MarqueeImage extends HTMLElement {
  constructor() {
    super();
    this.speed = parseFloat(this.getAttribute('speed')) || 0.5;
    this.direction = 'left';
    this.animationId = null;
    this.flickityInstance = null;
  }

  static get observedAttributes() {
    return ['speed', 'direction'];
  }

  connectedCallback() {
    document.addEventListener('DOMContentLoaded', () => {
      this.setupFlickity();
      this.startAnimation();
    });
  }

  disconnectedCallback() {
    this.stopAnimation();
    if (this.flickityInstance) {
      this.flickityInstance.destroy();
    }
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue !== newValue) {
      if (name === 'speed') {
        this.speed = parseFloat(newValue) || 0.5;
      } else if (name === 'direction') {
        this.direction = newValue || 'left';
      }
    }
  }

  setupFlickity() {
    // Check if Flickity is available
    if (typeof Flickity === 'undefined') {
      console.warn('Flickity not found. Marquee will use CSS animation instead.');
      this.setupCSSAnimation();
      return;
    }

    const slider = this.querySelector('.marquee-image-slider');
    if (!slider) return;

    this.flickityInstance = new Flickity(slider, {
      accessibility: true,
      autoPlay: false,   // Disable auto sliding
      wrapAround: true,
      resize: true,
      contain: false,
      wrapAround: true,
      prevNextButtons: false,
      pageDots: false,
      percentPosition: false,
      setGallerySize: true,
      draggable: false,
      freeScroll: true,
      pauseAutoPlayOnHover: false
    });
    this.flickityInstance.x = 0;
    this.flickityInstance.on('settle', () => {
      this.classList.add('flickity-ready');
    });   

    // Set initial position
    this.addEventListener('mouseover', () => {
      this.stopAnimation()
      
    });
    this.addEventListener('mouseout', () => {
      this.animate()
    });

  }

  destroyFlickity() {
    if (this.flickityInstance) {
      this.flickityInstance.destroy();
      this.flickityInstance = null;
    }
  }
  

  setupCSSAnimation() {
    const slider = this.querySelector('.marquee-image-slider');
    if (!slider) return;

    const direction = this.direction === 'right' ? 'reverse' : 'normal';
    const duration = Math.max(10, 20 / this.speed); // Convert speed to duration

    // Add CSS keyframes if they don't exist
    if (!document.getElementById('marquee-keyframes')) {
      const style = document.createElement('style');
      style.id = 'marquee-keyframes';
      style.textContent = `
        @keyframes marquee {
          0% {
            transform: translateX(0%);
          }
          100% {
            transform: translateX(-100%);
          }
        }
        
        .marquee-image-slider.css-animation {
          display: flex;
          width: max-content;
          animation: marquee ${duration}s linear infinite ${direction};
        }
      `;
      document.head.appendChild(style);
    }

    slider.classList.add('css-animation');
    slider.style.animationDuration = `${duration}s`;
    slider.style.animationDirection = direction;
  }

  startAnimation() {
    if (this.flickityInstance) {
      this.animate();
    }
  }

  stopAnimation() {
    if (this.animationId) {
      cancelAnimationFrame(this.animationId);
      this.animationId = null;
    }
  }

  animate() {
    if (!this.flickityInstance) return;

    const speedMultiplier = this.speed;
    const directionMultiplier = this.direction === 'right' ? 1 : -1;
    // Update position based on direction and speed 
    this.flickityInstance.x += speedMultiplier * directionMultiplier;

    // Settle position into the slider
    this.flickityInstance.settle(this.flickityInstance.x);

    // Continue animation
    this.animationId = window.requestAnimationFrame(() => this.animate());
    
  }
}

// Register the custom element
customElements.define('marquee-image', MarqueeImage);


if (!customElements.get('subscription-modal')) {
  customElements.define(
    'subscription-modal',
    class SubscriptionModal extends DrawerElement {
      constructor() {
        super();

        if (!theme.config.isTouch || Shopify.designMode) {
          this.init();
        }
        else {
          new theme.initWhenVisible(theme.utils.throttle(this.init.bind(this)));
        }
      }
      get shouldLock() {
        return true;
      }

      beforeShow(){
        super.beforeHide();
      }

      afterHide(){
        super.afterHide();
      }

      show(){
        super.show();
        setTimeout(() => {
          this.querySelector('subscription-flow').classList.add('overflow-hidden');
        },100);
      }

      hide(){
        super.hide();
        setTimeout(() => {
          document.body.classList.remove('has-modal-open');
          this.querySelector('subscription-flow').classList.remove('overflow-hidden');
        },100);
      }

      init() {
        if (this.initialized) return;
        this.initialized = true;

      }

      load(delay) {
        if (Shopify.designMode) return;

        setTimeout(() => this.show(), delay * 1000);
      }
    }
  );
}

class CustomVerticalSlider extends HTMLElement {
  constructor() {
    super();
    document.addEventListener('resize',()=>{
      this.updateSlideMatrics.bind(this);
      
    });
    document.addEventListener('DOMContentLoaded',() => {
      this.init();
    });
  }

  init() {
    
    this.sliderContainer = this.querySelector('[data-slider-container]');
    this.slides = this.sliderContainer.querySelectorAll('[data-slide]');
    this.currentSlideIndex = 0;
    this.numberedNav = document.querySelector('[data-nav-for]').querySelectorAll('.numbered-nav-item');
    this.mobileNav = document.querySelector('[data-mobile-nav]');
    this.slideSelections = Array(this.slides.length).fill(null);
    this.renderNumberedNav();
    this.subscriptionFlow = this.closest('subscription-flow') ? this.closest('subscription-flow') : null;
    this.boundUpdateMetrics = () => {
    this.updateSlideMatrics();
    };
    window.addEventListener('resize', this.boundUpdateMetrics);
      // ✅ Add swipe listeners (for mobile)
    const isMobile = window.innerWidth < 768.98;
    if(isMobile){
      this.initTouchEvents();
    }
    //  // Add scroll event listener only to the sliderContainer for desktop
    //  if (window.innerWidth >= 1024 && this.sliderContainer) {
    //    this.sliderContainer.addEventListener('wheel', this.handleSliderScroll.bind(this), { passive: false });
    //  }
  }
  initTouchEvents() {
    if (!this.sliderContainer) return;
  
    let touchStartX = 0;
    let touchEndX = 0;
    let touchStartY = 0;
    let touchEndY = 0;
    const threshold = 50; // px swipe threshold
  
    this.sliderContainer.addEventListener('touchstart', (e) => {
      const touch = e.changedTouches[0];
      touchStartX = touch.screenX;
      touchStartY = touch.screenY;
    }, { passive: true });
  
    this.sliderContainer.addEventListener('touchend', (e) => {
      const touch = e.changedTouches[0];
      touchEndX = touch.screenX;
      touchEndY = touch.screenY;
      this.handleSwipe(touchStartX, touchEndX, touchStartY, touchEndY, threshold);
    }, { passive: true });
  }
  
  handleSwipe(startX, endX, startY, endY, threshold) {
    const diffX = endX - startX;
    const diffY = endY - startY;
  
    const isHorizontal = Math.abs(diffX) > Math.abs(diffY);
  
    if (isHorizontal && Math.abs(diffX) > threshold) {
      // Horizontal swipe (mobile)
      if (diffX < 0) {
        console.log("Swiped left → Next slide");
        this.handleNextSlide();
      } else {
        console.log("Swiped right → Previous slide");
        this.handlePreviousSlide();
      }
    } else if (!isHorizontal && window.innerWidth >= 1024 && Math.abs(diffY) > threshold) {
      // Optional: Vertical swipe support (desktop)
      if (diffY < 0) {
        console.log("Swiped up → Next slide");
        this.handleNextSlide();
      } else {
        console.log("Swiped down → Previous slide");
        this.handlePreviousSlide();
      }
    }
  }

  updateSlideMatrics() {
    // this.sliderContainer = this.querySelector('[data-slider-container]');
    // this.slides = this.sliderContainer.querySelectorAll('[data-slide]');
    this.goToSlide(this.currentSlideIndex); 
  }
  disconnectedCallback() {
    // Clean up scroll event listener
    if (this.sliderContainer) {
      this.sliderContainer.removeEventListener('wheel', this.handleSliderScroll.bind(this));
    }
    if (super.disconnectedCallback) super.disconnectedCallback();
  }


  handleNextSlide() {
    console.log("handle Next slide calling")
    if (this.subscriptionFlow) {
      if (!this.subscriptionFlow.completedSteps.includes(this.currentSlideIndex + 1)) {
        return
      }
    }
    if (this.currentSlideIndex < this.slides.length - 1) {
      this.goToSlide(this.currentSlideIndex + 1);
    }
  }
 
  handlePreviousSlide() {
    console.log("handle Previous slide calling")
    if (this.currentSlideIndex > 0) {
      this.goToSlide(this.currentSlideIndex - 1);
    }
  }

  goToSlide(index) {
    if (this.sliderContainer && this.slides[parseInt(index)]) {
      const isMobile = window.innerWidth < 768.98;
      let offset, currentOffset, property;
      
      if (isMobile) {
        // Horizontal sliding for mobile
        const slideWidth = this.slides[parseInt(index)].offsetWidth;
        offset = parseInt(index) * slideWidth;
        currentOffset = this.currentSlideIndex * slideWidth;
        property = 'translateX';
      } else {
        // Vertical sliding for desktop/tablet
        const slideHeight = this.slides[parseInt(index)].offsetHeight;
        offset = parseInt(index) * slideHeight;
        currentOffset = this.currentSlideIndex * slideHeight;
        property = 'translateY';
      }

      // Use Element.animate for smooth scrolling
      this.sliderContainer.animate(
        [
          { transform: `${property}(-${currentOffset}px)` },
          { transform: `${property}(-${offset}px)` }
        ],
        {
          duration: 1000,
          easing: 'ease-in-out',
          fill: 'forwards',
        }
      );
      this.currentSlideIndex = parseInt(index);
      this.updateNavigation();
    }
  }

  renderNumberedNav() {
    this.numberedNav.forEach(item => {
      item.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        console.log("numbered nav item clicked")
        const maxCompletedStep = Math.max(...this.subscriptionFlow.completedSteps);
        const eligibleForNextStep = maxCompletedStep + 1;
        if (this.subscriptionFlow) {
          if (!this.subscriptionFlow.completedSteps.includes(parseInt(item.getAttribute('data-step-index')))) {
            if (parseInt(item.getAttribute('data-step-index')) == eligibleForNextStep) {
              this.goToSlide(item.getAttribute('data-slide-index'));
            }
            return;
          }
        }
        this.goToSlide(item.getAttribute('data-slide-index'))
      });
    });
    this.mobileNav.querySelectorAll('.flickity-page-dot').forEach(button => {
      button.addEventListener('click', () => {
        const maxCompletedStep = Math.max(...this.subscriptionFlow.completedSteps);
        const eligibleForNextStep = maxCompletedStep + 1;
        if (this.subscriptionFlow) {
          if (!this.subscriptionFlow.completedSteps.includes(parseInt(button.getAttribute('data-step-index')))) {
            if (parseInt(button.getAttribute('data-step-index')) == eligibleForNextStep) {
              this.goToSlide(button.getAttribute('data-slide-index'));
            }
            return;
          }
        }
        this.goToSlide(button.getAttribute('data-slide-index'))
      });
    });
  }

  // Method to handle scroll on the sliderContainer only (not the whole component)
  handleSliderScroll(event) {
    // Only handle vertical scroll (desktop/tablet)
    if (window.innerWidth < 1024) return; // skip on mobile

    // Prevent default scroll behavior
    event.preventDefault();

    // Determine scroll direction
    const delta = event.deltaY || event.detail || event.wheelDelta;
    let direction = 0;
    if (delta > 0) direction = 1; // scroll down, next
    else if (delta < 0) direction = -1; // scroll up, previous

    if (direction === 0) return;

    const nextIndex = this.currentSlideIndex + direction;

    // Bounds check
    if (nextIndex < 0 || nextIndex >= this.slides.length) return;

    // Only allow if the nextIndex is in completedSteps (or is the next eligible step)
    if (this.subscriptionFlow) {
      const completed = this.subscriptionFlow.completedSteps;
      if (completed.includes(nextIndex)) {
        this.goToSlide(nextIndex);
      } else {
        // Optionally, allow moving to the next eligible step (maxCompletedStep + 1)
        const maxCompleted = Math.max(...completed);
        if (direction === 1 && nextIndex === maxCompleted + 1) {
          this.goToSlide(nextIndex);
        }
      }
    } else {
      // If no subscriptionFlow, just allow navigation
      this.goToSlide(nextIndex);
    }
  }  
 
  updateNavigation() {
    // Implement navigation UI update logic here
   
    if(this.currentSlideIndex == this.numberedNav.length - 1){
   
      const flowplaylistSlider = this.querySelector('flow-playlist-slider') ? this.querySelector('flow-playlist-slider') : null;
      if(flowplaylistSlider){
        flowplaylistSlider.playlistSlider?.resize()
      }
    }
    if (this.subscriptionFlow) {
      this.subscriptionFlow.completedSteps.forEach(step => {
        const button = Array.from(this.numberedNav).find(button => button.getAttribute('data-step-index') == step);
        if (button) {
          button.classList.add('completed-step');
        }
        else {
          button.classList.remove('completed-step');
        }
      });
       // Update active state for numbered navigation buttons
        this.mobileNav.querySelectorAll('.flickity-page-dot').forEach(button => {
          button.classList.remove('is-selected');
          button.setAttribute('aria-current', 'false');
        });
        this.mobileNav.querySelectorAll('.flickity-page-dot')[this.currentSlideIndex].classList.add('is-selected');
        Array.from(this.numberedNav).forEach((button, index) => {
          const step = index + 1;
          if (index == this.currentSlideIndex) {
            button.classList.add('active-nav-item');
            this.slides[index].classList.add('active-slide');
          } else {
            button.classList.remove('active-nav-item');
            this.slides[index].classList.remove('active-slide');

          }
        });
        if(this.currentSlideIndex == this.slides.length - 1){
          this.subscriptionFlow.classList.add('final-step-reached');
        }
        else {
          this.subscriptionFlow.classList.remove('final-step-reached');
        }
    }
    if(this.currentSlideIndex == 0){
      document.querySelector('[data-back-button]').classList.add('hidden');
    }
    else {
      document.querySelector('[data-back-button]').classList.remove('hidden');
    }
  }
}

customElements.define('custom-vertical-slider', CustomVerticalSlider);


class FlowPlaylistSlider extends HTMLElement {
  constructor() {
    super();
  }
  connectedCallback() {
    this.playlistSlider = new Flickity(this, {
      cellAlign: 'center',
      contain: true,
      wrapAround: true,
      draggable: true,
      autoPlay: false,
      prevNextButtons: true,
      pageDots: true,
      adaptiveHeight: true,
      setGallerySize: true,
      resize: true,
      resizeObserver: true,
      on: {
        ready: () => {
          const selectedIndex = 0;
          const totalSlides = this.querySelectorAll(`.playlist-slider-item`).length;
          const currentElement = this.querySelector(`.playlist-slider-item[data-slide-index="${selectedIndex}"]`);
          if(currentElement?.nextElementSibling){
            currentElement?.nextElementSibling.classList.add('nextSlide');
          }
          else{
            this.querySelector(`.playlist-slider-item[data-slide-index="0"]`)?.classList.add('nextSlide');
          }
          if(currentElement?.previousElementSibling){
            currentElement?.previousElementSibling.classList.add('prevSlide');
          }else{
            this.querySelector(`.playlist-slider-item[data-slide-index="${totalSlides - 1}"]`)?.classList.add('prevSlide');
          }
        },
        change: (index) => {
          const currentElement = this.querySelector(`.playlist-slider-item[data-slide-index="${index}"]`);
          this.querySelectorAll('.playlist-slider-item')?.forEach(item => {
            item.classList.remove('nextSlide');
            item.classList.remove('prevSlide');
          });
          
          const totalSlides = this.playlistSlider.cells.length;
          if(currentElement?.nextElementSibling){
            currentElement?.nextElementSibling.classList.add('nextSlide');
          }
          else{
            this.querySelector(`.playlist-slider-item[data-slide-index="0"]`)?.classList.add('nextSlide');
          }
          if(currentElement?.previousElementSibling){
            currentElement?.previousElementSibling.classList.add('prevSlide');
          }else{
            this.querySelector(`.playlist-slider-item[data-slide-index="${totalSlides - 1}"]`)?.classList.add('prevSlide');
          }
      
          if(totalSlides == 2 && index == 1){
            this.querySelector(`.playlist-slider-item[data-slide-index="0"]`)?.classList.remove('prevSlide');
            this.querySelector(`.playlist-slider-item[data-slide-index="0"]`)?.classList.add('nextSlide');
          }
        }
      }
    });
    this.playlistSlider.on('settle', () => {
      this.playlistSlider.resize();
    });
    setTimeout(() => {
      this.playlistSlider.resize();
    }, 1000);
  }
}
customElements.define('flow-playlist-slider', FlowPlaylistSlider);

document.addEventListener('DOMContentLoaded', () => {
  const variantPickers = document.querySelectorAll('[data-variant-picker]');
  if (variantPickers.length > 0) {
    variantPickers.forEach(item => {
      const variants = JSON.parse(item.querySelector('script').textContent);
      item.querySelectorAll('input').forEach(input => {
        input.addEventListener('change', (e) => {
          const variantId = e.target.value;
          const variant = variants.find(variant => variant.id == variantId);
          const modalElement = e.target.closest('sample-product-drawer');
          if (modalElement) {
            const form = modalElement.querySelector('form');
            form.querySelector('input[name="id"]').value = variant.id;
            if (variant.available) {
              form.querySelector('button[type="submit"]').disabled = false;
              form.querySelector('.btn-text>span').textContent = theme.variantStrings.addToCart;
              if (form.querySelectorAll('[data-price]').length > 0 && variant.price) {
                form.querySelectorAll('[data-price]').forEach(item => {
                  item.textContent = theme.Currency.formatMoney(variant.price, theme.settings.moneyFormat);
                });
              }
            }
            else {
              form.querySelector('button[type="submit"]').disabled = true;
              form.querySelector('.btn-text>span').textContent = theme.variantStrings.soldOut;
              if (form.querySelectorAll('[data-price]').length > 0 && variant.price) {
                form.querySelectorAll('[data-price]').forEach(item => {
                  item.textContent = theme.Currency.formatMoney(variant.price, theme.settings.moneyFormat);
                });
              }
            }
          }
        });
      });
    });
  }
});

document.addEventListener('ajaxProduct:added', function (evt) {
  if (evt.detail.target.classList.contains('sample-product-add-to-cart')) {
    Utility.hideAllModalElements();
  }
});
document.addEventListener('DOMContentLoaded',() => {
  const playlistTriggerButtons = document.querySelectorAll('[data-playlist-action-trigger]');
  if(playlistTriggerButtons.length > 0){
    playlistTriggerButtons.forEach(item => {
      if(item.querySelector('.playlist-trigger-button')){
        item.querySelector('.playlist-trigger-button').addEventListener('click',() => {
     
          const playlistHandle = item.getAttribute('data-playlist-action-trigger');
          const subscriptionModal = document.querySelector('subscription-modal');
          const subscriptionFlow = document.querySelector('subscription-flow');
          subscriptionFlow.setAttribute('data-playlist-handle',playlistHandle);
          subscriptionFlow.currentPlaylistHandle = playlistHandle;
          subscriptionFlow.verticalSlider.updateSlideMatrics();
          subscriptionFlow.prepareSummary();
          subscriptionModal.setAttribute('data-playlist-name',playlistHandle);
          subscriptionModal.show();
        });
      }
    });
  }
  const sampleListProductsElements = document.querySelectorAll('[data-enable-add-to-cart-sample-product]');
 
    sampleListProductsElements.forEach(sampleListProduct => {
      if(sampleListProduct){
       
        const sample_button = sampleListProduct.querySelector('.button');
        sample_button.addEventListener('click',() => {
          const list_sample_products = sampleListProduct.getAttribute('data-sample-products-ids').split(',');
          let samplProductItems = [];
          list_sample_products.forEach(item => {
            samplProductItems.push({
              id: item,
              quantity: 1
            });
          });
          sample_button.setAttribute('aria-disabled', 'true');
          sample_button.setAttribute('aria-busy', 'true');
          sample_button.disabled = true;
          
          fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              items: samplProductItems,
            }),
          }).then(response => {
            sample_button.setAttribute('aria-disabled', 'false');
            sample_button.setAttribute('aria-busy', 'false');
            sample_button.disabled = false;
            document.dispatchEvent(new CustomEvent('cart:refresh', {
              bubbles: true, detail: { open: true }
            }));
          }).catch(error => {
            console.log(error,"error");
            sample_button.setAttribute('aria-disabled', 'false');
            sample_button.setAttribute('aria-busy', 'false');
            sample_button.disabled = false;
          });
        });
      }
    });
});
class SubscriptionWidget extends HTMLElement {
  constructor() {
    super();
    this.selling_plan = null;
    this.elemQuantity = document.querySelector('volume-quantity-changer');
    this.formId = this.getAttribute('data-form-id');
    this.buttonWrapper = document.querySelector('product-info').querySelector('.buy-buttons');
    this.subscriptionInput = this.querySelector('input[purchase-type="subscription"]');
    this.subscriptionLabel = this.querySelector('label[for="subscription-widget-checkbox"]');
    this.subscriptionType = null;
    this.subscriptionFrequencySelector = this.querySelector('[data-subscription-frequency]');
    this.planDetails = { frequency: null, frequency_type: null };
    this.playlistHandle = this.getAttribute('data-playlist-handle');
  }

  connectedCallback() {
    // this.addEventListener('subscription:change', this.onSubscriptionChange.bind(this));
    this.elemQuantity.addEventListener('volume-quantity:change', this.onVolumeQuantityChange.bind(this));
    this.querySelectorAll('input[type="radio"]').forEach(item => {
      item.addEventListener('change', this.onSubscriptionChange.bind(this));
    });
    if(this.subscriptionFrequencySelector){
      this.subscriptionFrequencySelector.addEventListener('change', this.onSubscriptionFrequencyChange.bind(this));
    }
    this.prefillPlanDetails()
  }

  prefillPlanDetails() {
    const selectedOption = this.subscriptionFrequencySelector.querySelector('option:checked');
    this.selling_plan = parseInt(selectedOption.value);
    this.planDetails.frequency = parseInt(selectedOption.getAttribute('data-frequency'));
    this.planDetails.frequency_type = selectedOption.getAttribute('data-frequency-type');
  }

  
  onSubscriptionFrequencyChange(event) {
    this.selling_plan = parseInt(event.target.value);
    this.planDetails.frequency = parseInt(event.target.selectedOptions[0].getAttribute('data-frequency'));
    this.planDetails.frequency_type = event.target.selectedOptions[0].getAttribute('data-frequency-type');
  }
  onSubscriptionChange() {
    const checkedRadio = this.querySelector('input[type="radio"]:checked');
    const subscriptionType = checkedRadio.getAttribute('purchase-type');
    const subscriptionButton = document.querySelector('.subscribe-button');
    if(subscriptionButton){
      subscriptionButton.setAttribute('aria-disabled', 'true');
      subscriptionButton.setAttribute('aria-busy', 'false');
    }

    if(subscriptionType == 'onetime'){
      this.buttonWrapper.classList.add('one-time-purchase');
      this.buttonWrapper.classList.remove('subscription-purchase');
      this.subscriptionType = 'onetime';
    }
    else{
      this.buttonWrapper.classList.add('subscription-purchase');
      this.buttonWrapper.classList.remove('one-time-purchase');
      this.subscriptionType = 'subscription';
    }

    this.dispatchEvent(new CustomEvent('subscription:change', {
      bubbles: true, detail: { subscriptionType: this.subscriptionType }
    }));

    this.updatePrice();
  }
  onVolumeQuantityChange() {
    this.updatePrice();
  }
  triggerOneTimeSelection(){
    // this.querySelector('#subscription-one-time-purchase').checked = true;
    this.querySelector('#subscription-one-time-purchase').click();
  }
  updatePrice(){
    console.log("updatePrice");
    if(document.querySelector('[data-selected-variant]')){
      const onetTimeSalePriceElement = document.querySelectorAll('[data-onetime-sale-price]');
      const onetTimeComparePriceElement = document.querySelectorAll('[data-one-time-compare-at-price]');
      const subscriptionSalePriceElement = document.querySelectorAll('[data-subscription-sale-price]');
      const subscriptionComparePriceElement = document.querySelectorAll('[data-subscription-compare-at-price]');
      const variant = JSON.parse(document.querySelector('[data-selected-variant]').textContent);
      const oneTimePrice = variant.price * this.elemQuantity.quantity;
      if(!variant.public_title.includes('300')){
        this.validateSubscriptionInput(true);
        this.triggerOneTimeSelection();
        if(this.subscriptionLabel){
          this.subscriptionLabel.classList.add('disabled-label')
        }
      }
      else{
        if(this.elemQuantity.quantity == 1){
          this.validateSubscriptionInput(true);
          this.triggerOneTimeSelection();
          if(this.subscriptionLabel){
            this.subscriptionLabel.classList.add('disabled-label')
          }
        }
        else{
          this.validateSubscriptionInput(false);
          if(this.subscriptionLabel){
            this.subscriptionLabel.classList.remove('disabled-label')
          }
        }
      }
      let staticBundlePrice = 0;
      if(this.playlistHandle){
        staticBundlePrice = window.playlistUtilities.getStaticBundlePrice(this.playlistHandle,this.elemQuantity.quantity);
        console.log(staticBundlePrice,"staticBundlePrice");
      }
      
      // calculate price
      let subscriptionSalePrice = 0;
      let subscriptionCompareAtPrice = 0;
      let oneTimeSalePrice = 0;
      let oneTimeCompareAtPrice = 0;
      switch(this.elemQuantity.quantity){
        case 1:
          subscriptionSalePrice = variant.price * 1 * 1;
          subscriptionCompareAtPrice = variant.price * 1;
          oneTimeSalePrice = variant.price * 1;
          oneTimeCompareAtPrice = variant.price * 1;
          break;
        case 2:
          subscriptionSalePrice = staticBundlePrice;
          subscriptionCompareAtPrice = variant.price * 2;
          oneTimeSalePrice = variant.price * 2;
          oneTimeCompareAtPrice = variant.price * 2;
          break;
        case 3:
          subscriptionSalePrice = staticBundlePrice;
          subscriptionCompareAtPrice = variant.price * 3;
          oneTimeSalePrice = variant.price * 3;
          oneTimeCompareAtPrice = variant.price * 3;
          break;
        case 4:
          subscriptionSalePrice = staticBundlePrice;
          subscriptionCompareAtPrice = variant.price * 4;
          oneTimeSalePrice = variant.price * 4;
          oneTimeCompareAtPrice = variant.price * 4;
          break;
        case 5:
          subscriptionSalePrice = staticBundlePrice;
          subscriptionCompareAtPrice = variant.price * 5;
          oneTimeSalePrice = variant.price * 5;
          oneTimeCompareAtPrice = variant.price * 5;
          break;
      }
      if(onetTimeSalePriceElement.length > 0){
        onetTimeSalePriceElement.forEach(item => {
          item.textContent = theme.Currency.formatMoney(oneTimeSalePrice, theme.settings.currencyCodeEnabled ? theme.settings.moneyWithCurrencyFormat : theme.settings.moneyFormat);
        });
        document.querySelector(`form[id="${this.formId}"]`).querySelector('product-buy-price').textContent = theme.Currency.formatMoney(oneTimeSalePrice, theme.settings.currencyCodeEnabled ? theme.settings.moneyWithCurrencyFormat : theme.settings.moneyFormat);
      }
      if(onetTimeComparePriceElement.length > 0){
        onetTimeComparePriceElement.forEach(item => {
          item.textContent = theme.Currency.formatMoney(oneTimeCompareAtPrice, theme.settings.currencyCodeEnabled ? theme.settings.moneyWithCurrencyFormat : theme.settings.moneyFormat);
          if(oneTimeCompareAtPrice > oneTimeSalePrice){
            item.classList.remove('hidden');
          }
          else{
            item.classList.add('hidden');
          }
        });
      }
      if(subscriptionSalePriceElement.length > 0){
        subscriptionSalePriceElement.forEach(item => {
          item.textContent = theme.Currency.formatMoney(subscriptionSalePrice, theme.settings.currencyCodeEnabled ? theme.settings.moneyWithCurrencyFormat : theme.settings.moneyFormat);
        });
      }
      if(subscriptionComparePriceElement.length > 0){
        subscriptionComparePriceElement.forEach(item => {
          item.textContent = theme.Currency.formatMoney(subscriptionCompareAtPrice, theme.settings.currencyCodeEnabled ? theme.settings.moneyWithCurrencyFormat : theme.settings.moneyFormat);
          if(subscriptionCompareAtPrice > subscriptionSalePrice){
            item.classList.remove('hidden');
          }
          else{
            item.classList.add('hidden');
          }
        });
      }
      if(this.subscriptionType == 'subscription'){
        document.querySelector('[data-selected-sale-price]').textContent = theme.Currency.formatMoney(subscriptionSalePrice, theme.settings.currencyCodeEnabled ? theme.settings.moneyWithCurrencyFormat : theme.settings.moneyFormat);
        document.querySelector('[data-selected-compare-price]').textContent = theme.Currency.formatMoney(subscriptionCompareAtPrice, theme.settings.currencyCodeEnabled ? theme.settings.moneyWithCurrencyFormat : theme.settings.moneyFormat);
        if(subscriptionCompareAtPrice > subscriptionSalePrice){
          document.querySelector('[data-selected-compare-price]').classList.remove('hidden');
        }
        else{
          document.querySelector('[data-selected-compare-price]').classList.add('hidden');
        }
      }
      else{
        document.querySelector('[data-selected-sale-price]').textContent = theme.Currency.formatMoney(oneTimeSalePrice, theme.settings.currencyCodeEnabled ? theme.settings.moneyWithCurrencyFormat : theme.settings.moneyFormat);
        document.querySelector('[data-selected-compare-price]').textContent = theme.Currency.formatMoney(oneTimeCompareAtPrice, theme.settings.currencyCodeEnabled ? theme.settings.moneyWithCurrencyFormat : theme.settings.moneyFormat);
        if(oneTimeCompareAtPrice > oneTimeSalePrice){
          document.querySelector('[data-selected-compare-price]').classList.remove('hidden');
        }
        else{
          document.querySelector('[data-selected-compare-price]').classList.add('hidden');
        }
      }
    }
  }
  validateSubscriptionInput(restrictSubscription = false){
      if(restrictSubscription){
        this.subscriptionInput.disabled = true;
      }
      else{
        this.subscriptionInput.disabled = false;
      }
  }
}
customElements.define('subscription-widget', SubscriptionWidget);

class SingleSubscriptionWidget extends HTMLElement {
  constructor() {
    super();
    this.selling_plan = null;
    this.elemQuantity = document.querySelector('volume-quantity-changer');
    this.formId = this.getAttribute('data-form-id');
    this.form = document.querySelector(`form[id="${this.formId}"]`);
    this.buttonWrapper = document.querySelector('product-info').querySelector('.buy-buttons');
    this.subscriptionInput = this.querySelector('input[purchase-type="subscription"]');
    this.subscriptionType = null;
    this.planDetails = { frequency: null, frequency_type: null };
    this.playlistHandle = this.getAttribute('data-playlist-handle');
    this.subscriptionFrequencySelectors = this.querySelectorAll('[data-subscription-frequency]');
    this.currentVariantId = parseInt(this.getAttribute('data-variant-id'));
  }

  connectedCallback() {
    // this.addEventListener('subscription:change', this.onSubscriptionChange.bind(this));
    if(this.elemQuantity){
      this.elemQuantity.addEventListener('volume-quantity:change', this.onVolumeQuantityChange.bind(this));
    }
    this.querySelectorAll('input[type="radio"]').forEach(item => {
      item.addEventListener('change', this.onSubscriptionChange.bind(this));
    });
    if(this.subscriptionFrequencySelectors){
      this.subscriptionFrequencySelectors.forEach(item => {
        item.addEventListener('change', this.onSubscriptionFrequencyChange.bind(this));
      });
    }
    this.onload();
  }

  onload(){
    this.quantity = parseInt(this.elemQuantity.quantity || 1);
    this.currentVariant = JSON.parse(document.querySelector('[data-selected-variant]').textContent);
    this.updateSellingPlan(this.currentVariantId);
    this.updatePrice();
    this.triggerSubscriptionSelection();
  }
  triggerSubscriptionSelection(){
    this.querySelector('#subscription-widget-checkbox').click();
  }

  static get observedAttributes() {
    return ['data-variant-id'];
  }

  attributeChangedCallback(name, oldValue, newValue) {
    console.log('Attribute changed:', name, oldValue, newValue);
    if (name === 'data-variant-id') {
      this.currentVariantId = parseInt(newValue, 10);
      this.quantity = parseInt(this.elemQuantity.quantity || 1);
      this.currentVariant = JSON.parse(document.querySelector('[data-selected-variant]').textContent);
      this.updateSellingPlan(this.currentVariantId);
      this.updatePrice();
    }
  }
  updateSellingPlan(variantId){
      this.subscriptionFrequencySelectors.forEach(item => {
        if(item.getAttribute('data-variant-id') == variantId){
          console.log("item.selectedOptions[0]",item.selectedOptions[0]);
          this.selling_plan_id = parseInt(item.selectedOptions[0].getAttribute('data-selling-plan-id'));
          item.classList.remove('hidden');
          const form = document.querySelector(`#${this.formId}`);
          if(form.querySelector('[name="selling_plan"]')){
            form.querySelector('[name="selling_plan"]').value = this.selling_plan_id;
          }
          else{
            form.appendChild(document.createElement('input')).setAttribute('type', 'hidden').setAttribute('name', 'selling_plan').setAttribute('value', this.selling_plan_id);
          }
        }
        else{
          item.classList.add('hidden');
        }
      });
  }

  
  onSubscriptionFrequencyChange(event) {
    this.selling_plan_id = parseInt(event.target.selectedOptions[0].getAttribute('data-selling-plan-id'));
    if(this.form){
    if(this.form.querySelector('[name="selling_plan"]')){
      this.form.querySelector('[name="selling_plan"]').value = this.selling_plan_id;
    }
    else{
      this.form.appendChild(document.createElement('input')).setAttribute('type', 'hidden').setAttribute('name', 'selling_plan').setAttribute('value', this.selling_plan_id);
      }
    }
    this.updateSellingPlan(this.currentVariantId);
    this.updatePrice();
  }
  onSubscriptionChange() {
    console.log("onSubscriptionChange");
    const checkedRadio = this.querySelector('input[type="radio"]:checked');
    const subscriptionType = checkedRadio.getAttribute('purchase-type');
    const subscriptionButton = document.querySelector('.subscribe-button');
    if(subscriptionButton){
      subscriptionButton.setAttribute('aria-disabled', 'true');
      subscriptionButton.setAttribute('aria-busy', 'false');
    }

    if(subscriptionType == 'onetime'){
      this.buttonWrapper.classList.add('one-time-purchase');
      this.buttonWrapper.classList.remove('subscription-purchase');
      this.subscriptionType = 'onetime';
      if(this.form){
        this.form.querySelector('[name="selling_plan"]').value = null;
      }
    }
    else{
      this.buttonWrapper.classList.add('subscription-purchase');
      this.buttonWrapper.classList.remove('one-time-purchase');
      this.subscriptionType = 'subscription';
      if(this.form){
        this.form.querySelector('[name="selling_plan"]').value = this.selling_plan_id;
      }
    }

    this.dispatchEvent(new CustomEvent('subscription:change', {
      bubbles: true, detail: { subscriptionType: this.subscriptionType }
    }));

    this.updatePrice();
  }
  onVolumeQuantityChange(event) {
    this.quantity = parseInt(event.detail.quantity);
    this.updatePrice();
  }

  updatePrice(){

    console.log("updatePrice");
    if(document.querySelector('[data-selected-variant]')){
      const onetTimeSalePriceElement = document.querySelectorAll('[data-onetime-sale-price]');
      const onetTimeComparePriceElement = document.querySelectorAll('[data-one-time-compare-at-price]');
      const subscriptionSalePriceElement = document.querySelectorAll('[data-subscription-sale-price]');
      const subscriptionComparePriceElement = document.querySelectorAll('[data-subscription-compare-at-price]');
      const variant = this.currentVariant;
      const currentVariantPlanSelect = this.querySelector(`select[data-variant-id="${variant.id}"]`);
      if(currentVariantPlanSelect){
        this.selectedPlanOption = currentVariantPlanSelect.selectedOptions[0];
        console.log(this.selectedPlanOption,"this.selectedPlanOption");
      }
      const variantPrice = this.selectedPlanOption.getAttribute('data-variant-price');
      const variantCompareAtPrice = this.selectedPlanOption.getAttribute('data-variant-compare-at-price');
      // calculate price
      let subscriptionSalePrice = 0;
      let subscriptionCompareAtPrice = 0;
      let oneTimeSalePrice = 0;
      let oneTimeCompareAtPrice = 0;
      
      //subscription price
      subscriptionSalePrice = variantPrice * this.quantity;
      subscriptionCompareAtPrice = variantCompareAtPrice * this.quantity;

      //one time price
      oneTimeSalePrice = variant.price * this.quantity;
      oneTimeCompareAtPrice = variant.compare_at_price * this.quantity;
      if(onetTimeSalePriceElement.length > 0){
        onetTimeSalePriceElement.forEach(item => {
          item.textContent = theme.Currency.formatMoney(oneTimeSalePrice, theme.settings.currencyCodeEnabled ? theme.settings.moneyWithCurrencyFormat : theme.settings.moneyFormat);
        });
        document.querySelector(`form[id="${this.formId}"]`).querySelector('product-buy-price').textContent = theme.Currency.formatMoney(oneTimeSalePrice, theme.settings.currencyCodeEnabled ? theme.settings.moneyWithCurrencyFormat : theme.settings.moneyFormat);
      }
      if(onetTimeComparePriceElement.length > 0){
        onetTimeComparePriceElement.forEach(item => {
          item.textContent = theme.Currency.formatMoney(oneTimeCompareAtPrice, theme.settings.currencyCodeEnabled ? theme.settings.moneyWithCurrencyFormat : theme.settings.moneyFormat);
          if(oneTimeCompareAtPrice > oneTimeSalePrice){
            item.classList.remove('hidden');
          }
          else{
            item.classList.add('hidden');
          }
        });
      }

      
      if(subscriptionSalePriceElement.length > 0){
        subscriptionSalePriceElement.forEach(item => {
          item.textContent = theme.Currency.formatMoney(subscriptionSalePrice, theme.settings.currencyCodeEnabled ? theme.settings.moneyWithCurrencyFormat : theme.settings.moneyFormat);
        });
      }
      if(subscriptionComparePriceElement.length > 0){
        subscriptionComparePriceElement.forEach(item => {
          item.textContent = theme.Currency.formatMoney(subscriptionCompareAtPrice, theme.settings.currencyCodeEnabled ? theme.settings.moneyWithCurrencyFormat : theme.settings.moneyFormat);
          if(subscriptionCompareAtPrice > subscriptionSalePrice){
            item.classList.remove('hidden');
          }
          else{
            item.classList.add('hidden');
          }
        });
      }
      if(this.subscriptionType == 'subscription'){
        document.querySelector('[data-selected-sale-price]').textContent = theme.Currency.formatMoney(subscriptionSalePrice, theme.settings.currencyCodeEnabled ? theme.settings.moneyWithCurrencyFormat : theme.settings.moneyFormat);
        document.querySelector('[data-selected-compare-price]').textContent = theme.Currency.formatMoney(subscriptionCompareAtPrice, theme.settings.currencyCodeEnabled ? theme.settings.moneyWithCurrencyFormat : theme.settings.moneyFormat);
        if(subscriptionCompareAtPrice > subscriptionSalePrice){
          document.querySelector('[data-selected-compare-price]').classList.remove('hidden');
        }
        else{
          document.querySelector('[data-selected-compare-price]').classList.add('hidden');
        }
      }
      else{
        document.querySelector('[data-selected-sale-price]').textContent = theme.Currency.formatMoney(oneTimeSalePrice, theme.settings.currencyCodeEnabled ? theme.settings.moneyWithCurrencyFormat : theme.settings.moneyFormat);
        document.querySelector('[data-selected-compare-price]').textContent = theme.Currency.formatMoney(oneTimeCompareAtPrice, theme.settings.currencyCodeEnabled ? theme.settings.moneyWithCurrencyFormat : theme.settings.moneyFormat);
        if(oneTimeCompareAtPrice > oneTimeSalePrice){
          document.querySelector('[data-selected-compare-price]').classList.remove('hidden');
        }
        else{
          document.querySelector('[data-selected-compare-price]').classList.add('hidden');
        }
      }
    }
  }
}
customElements.define('single-subscription-widget', SingleSubscriptionWidget);

class VolumeQuantityChanger extends HTMLElement {
  constructor() {
    super();
    this.all_inputs = this.querySelectorAll('input');
    this.formId = this.getAttribute('data-form-id');
    this.playlistHandle = this.getAttribute('data-playlist-handle');
    
  }
  connectedCallback() {
    this.quantity = parseInt(this.querySelector('input[type="radio"]:checked').value);
    if(document.querySelector(`form[id="${this.formId}"]`)){
      document.querySelector(`form[id="${this.formId}"]`).insertAdjacentHTML('beforeend', `<input type="hidden" name="quantity" value="${this.quantity}">`);
    }
    this.all_inputs.forEach(item => {
      item.addEventListener('change', this.onInputChange.bind(this));
    });
  }
  onInputChange(event) {
    this.quantity = parseInt(event.target.value);
    if(document.querySelector(`form[id="${this.formId}"]`)){
      document.querySelector(`form[id="${this.formId}"]`).querySelector('input[name="quantity"]').value = this.quantity;
    }
    this.dispatchEvent(new CustomEvent('volume-quantity:change', {
      bubbles: true, detail: { quantity: this.quantity }
    }));
  }
}
customElements.define('volume-quantity-changer', VolumeQuantityChanger);

// document.addEventListener('DOMContentLoaded',() => {
//   if(document.body.classList.contains('product-playlist-template')){
//     const subscriptionFlow = document.querySelector('subscription-flow');
//     const subscriptionWidget = document.querySelector('subscription-widget');
//     const grindPreferenceSelector = document.querySelector('grind-preference-selector');
//     let attempt = 0;
//     const maxAttempts = 10;
//     const updatePriceInterval = setInterval(() => {
//       if(window.playlistLoaded){
//         if(subscriptionWidget){
//           subscriptionWidget.updatePrice();
//           subscriptionWidget.onSubscriptionChange();
//           document.querySelector('.product__price').classList.add('hidden');
//           const volumeQuantityChanger = document.querySelector('volume-quantity-changer');
//           if(volumeQuantityChanger){
//             volumeQuantityChanger.querySelector(`[name="quantity"][value="2"]`).click();
//           }
//           if(subscriptionWidget){
//             subscriptionWidget.querySelector('#subscription-widget-checkbox').click();
//           }
//           const variantPicker = document.querySelector('variant-picker');
//           if(variantPicker){
//             const selectedVariant = variantPicker.querySelector('input:checked').value;
//             if(selectedVariant.includes('300')){
//               if(volumeQuantityChanger){
//                 volumeQuantityChanger.classList.remove('hide-discount-lables');
//               }
//             }
//             else{
//               if(volumeQuantityChanger){
//                 volumeQuantityChanger.classList.add('hide-discount-lables');
//               }
//             }
//           }
//         }
//         attempt++;
//         if(attempt >= maxAttempts){
//           clearInterval(updatePriceInterval);
//         }
//         clearInterval(updatePriceInterval);
//       }
//     }, 1000);
    
//     const subscribeButton = document.querySelector('.subscribe-button');
    
//     if(subscribeButton){
//       subscribeButton.addEventListener('click', function(e) {
//         e.preventDefault();
//         const volumeQuantityChanger = document.querySelector('volume-quantity-changer');
//         subscriptionFlow.triggerPlaylistFromProduct(volumeQuantityChanger.quantity, subscriptionWidget.planDetails.frequency);
//         subscriptionFlow.roastType = grindPreferenceSelector.brewType;
//         if(grindPreferenceSelector.grindPreferenceValue == 'whole-beans'){
//           subscriptionFlow.grindPreferences.consent = 'no';
//           subscriptionFlow.grindPreferencesStepContainer.querySelector('input[value="no"]').click();
//           if(grindPreferenceSelector.brewType == 'filter'){
//             subscriptionFlow.grindPreferences.filter = 'whole-beans';
//             subscriptionFlow.grindPreferences.espresso = null
//             subscriptionFlow.filterCount = volumeQuantityChanger.quantity
//             subscriptionFlow.espressoCount = 0
            
//           }
//           else{
//             subscriptionFlow.grindPreferences.espresso = 'whole-beans';
//             subscriptionFlow.grindPreferences.filter = null
//             subscriptionFlow.filterCount = 0
//             subscriptionFlow.espressoCount = volumeQuantityChanger.quantity
//           }
//         }
//         else{
//           subscriptionFlow.grindPreferences.consent = 'yes';
//           subscriptionFlow.grindPreferencesStepContainer.querySelector('input[value="yes"]').click();
//           if(grindPreferenceSelector.brewType == 'filter'){
//             subscriptionFlow.grindPreferences.filter = grindPreferenceSelector.grindPreferenceValue;
//             subscriptionFlow.grindPreferences.espresso = null
//             subscriptionFlow.filterCount = volumeQuantityChanger.quantity
//             subscriptionFlow.espressoCount = 0
//             subscriptionFlow.brewTypeStepContainer.querySelector('input[value="filter"]').click();
//             subscriptionFlow.grindPreferencesStepContainer.querySelector('input[value="yes"]').click();
//              subscriptionFlow.grindPreferencesStepContainer.querySelector(`input[value="${grindPreferenceSelector.grindPreferenceValue}"]`).click();
//           }
//           else{
//             subscriptionFlow.grindPreferences.espresso = grindPreferenceSelector.grindPreferenceValue;
//             subscriptionFlow.grindPreferences.filter = null
//             subscriptionFlow.filterCount = 0
//             subscriptionFlow.espressoCount = volumeQuantityChanger.quantity
//             subscriptionFlow.brewTypeStepContainer.querySelector('input[value="espresso"]').click();
//             subscriptionFlow.grindPreferencesStepContainer.querySelector('input[value="yes"]').click();
//             subscriptionFlow.grindPreferencesStepContainer.querySelector(`input[value="${grindPreferenceSelector.grindPreferenceValue}"]`).click();
//           }
//         }
//         subscriptionFlow.completedSteps = [1,2,3,4];
//         const subscriptionModal = document.querySelector('subscription-modal');
//         subscriptionFlow.setAttribute('data-playlist-handle',subscribeButton.getAttribute('data-playlist-handle'));
//         subscriptionModal.setAttribute('data-playlist-name',subscribeButton.getAttribute('data-playlist-handle'));
//         subscriptionFlow.currentPlaylistHandle = subscribeButton.getAttribute('data-playlist-handle');
//         subscriptionFlow.prepareSummary()
//         subscriptionModal.show();
//         subscriptionFlow.verticalSlider.goToSlide(4)
        

        
//         // temp2.frequency.value = subscriptionWidget.querySelector('#subscription-frequency').value;
        
//       })
//     }
 
//     document.addEventListener('variant:change', function(evt) {
//       const variant = evt.detail.variant;
//       const subscriptionWidget = document.querySelector('subscription-widget');
//       const volumeQuantityBlock = document.querySelector('volume-quantity-changer');
//       if(subscriptionWidget){
//         subscriptionWidget.updatePrice();
        
//       }
//       if(subscriptionWidget){
//         if(!variant.public_title.includes('300')){
//           if(volumeQuantityBlock){
//            volumeQuantityBlock.classList.add('hide-discount-lables');
//           }
          
//         }
//         else{
//           if(volumeQuantityBlock){
//             volumeQuantityBlock.classList.remove('hide-discount-lables');
//            }
//         }
//       }
//     });
//   }
//   if(document.body.classList.contains('single-subscription')){
//     const singleSubscriptionWidget = document.querySelector('single-subscription-widget');
//     const subscribeButton = document.querySelector('.subscribe-button');
//     document.querySelector('.product__price').classList.add('hidden');
//     document.addEventListener('variant:change', function(evt) {
//       const variant = evt.detail.variant;
//       console.log(variant,"variant");
//       if(singleSubscriptionWidget){
//         singleSubscriptionWidget.setAttribute('data-variant-id', variant.id);
//       }
//       if(variant.available){
//         subscribeButton.classList.remove('hidden');
//         subscribeButton.querySelector('[data-subscribe-button-text]').textContent = 'Subscribe';
//         subscribeButton.setAttribute('aria-disabled', 'false');
//         subscribeButton.removeAttribute('disabled');
//       }
//       else{
//         subscribeButton.classList.add('hidden');
//         subscribeButton.querySelector('[data-subscribe-button-text]').textContent = 'Subscribe';
//         subscribeButton.setAttribute('aria-disabled', 'true');
//         subscribeButton.setAttribute('disabled', '');
//       }
//     });
//   }
// });
document.addEventListener('DOMContentLoaded',() => {
  if(window.innerWidth < 768.98){
    let scrollTimeout;
    const scrollContainer = document.querySelector('[data-summary-step]');
    const stickyCheckoutButton = document.querySelector('.sticky-checkout-button');
    scrollContainer.addEventListener('scroll', (e) => {
      // Immediate actions (lightweight)
      const scrollTop = e.target.scrollTop;
      scrollTimeout = setTimeout(() => {
        const element = e.target;
        const scrollPercentage = (element.scrollTop / (element.scrollHeight - element.clientHeight)) * 100;
        
        // Your heavy calculations here
        if(scrollPercentage > 90){
          stickyCheckoutButton.classList.add('hidden');
        }
        else{
          stickyCheckoutButton.classList.remove('hidden');
        }
      }, 100);
    }, { passive: true });
  }
});

class GrindPreferenceSelector extends HTMLElement {
  constructor() {
    super();
    this.querySelectorAll('input[type="radio"]').forEach(item => {
      item.addEventListener('change', this.onInputChange.bind(this));
    });
    this.brewType = this.getAttribute('data-brew-type');
    this.grindPreferenceSelector = this.querySelector('.grind-preference-selector-js');
    this.grindPreferenceSelector.addEventListener('change', this.onGrindPreferenceSelectorChange.bind(this));
    this.grindPreferenceValue = this.querySelector('input:checked')?.value;
    this.formId = this.getAttribute('data-form-id') || null;
  }
  connectedCallback(){
    if(this.formId){
      if(document.querySelector(`form[id="${this.formId}"]`)){
        
        let input = document.createElement('input');
        input.setAttribute('type', 'hidden');
        if(this.brewType == 'filter'){
          input.setAttribute('name', 'properties[grind_filter]');
        }
        else{
          input.setAttribute('name', 'properties[grind_espresso]');
        }
        input.value = this.grindPreferenceValue;
        document.querySelector(`form[id="${this.formId}"]`).appendChild(input);
      }
    }
  }
  onInputChange(event) {
   const selectedValue = event.target.value;
    if(selectedValue == 'whole-beans'){
      this.grindPreferenceValue = event.target.value;
    }
    else{
      this.grindPreferenceValue = this.grindPreferenceSelector.value;
    }
    if(this.formId){
      if(document.querySelector(`form[id="${this.formId}"]`)){
        if(this.brewType == 'filter'){
          document.querySelector(`form[id="${this.formId}"]`).querySelector('input[name="properties[grind_filter]"]').value = this.grindPreferenceValue;
        }
        else{
          document.querySelector(`form[id="${this.formId}"]`).querySelector('input[name="properties[grind_espresso]"]').value = this.grindPreferenceValue;
        }
      }
    }
    this.dispatchEvent(new CustomEvent('grind-preference:change', {
      bubbles: true, detail: { grindPreferenceValue: this.grindPreferenceValue }
    }));

  }
  
  onGrindPreferenceSelectorChange(event) {
  
    const selectedValue = event.target.value;
    this.grindPreferenceValue = selectedValue;
    if(this.formId){
      if(document.querySelector(`form[id="${this.formId}"]`)){
        if(this.brewType == 'filter'){
          document.querySelector(`form[id="${this.formId}"]`).querySelector('input[name="properties[grind_filter]"]').value = this.grindPreferenceValue;
        }
        else{
          document.querySelector(`form[id="${this.formId}"]`).querySelector('input[name="properties[grind_espresso]"]').value = this.grindPreferenceValue;
        }
      }
    }
    this.dispatchEvent(new CustomEvent('grind-preference:change', {
      bubbles: true, detail: { grindPreferenceValue: this.grindPreferenceValue }
    }));
  }
  
}
customElements.define('grind-preference-selector', GrindPreferenceSelector);



class AddressDrawer extends DrawerElement {
  constructor() {
    super();
    this.addressForm = this.querySelector('form');
    this.addressObject = null;
    this.updateAddressButton = this.querySelector('[data-update-address]');
    this.updateAddressButton.addEventListener('click', this.onUpdateAddressButtonClick.bind(this));
    this.addressForm.addEventListener('change', this.onAddressFormChange.bind(this));
    this.confirmationBox = this.querySelector('[data-address-confirmation-box]');
    this.footer = this.querySelector('.drawer__footer');
  }

  connectedCallback(){
    super.connectedCallback();
    this.querySelector('[data-address-country-select]')?.addEventListener('change', this.onCountryChange.bind(this));
  }

  onCountryChange(event){
    event.preventDefault();
    this.updateProvinces(event.target);
  }

  updateProvinces(countrySelect, selectedProvince){
    const selectedOption = countrySelect.options[countrySelect.selectedIndex];
    const provinces = selectedOption?.getAttribute('data-provinces');
    let provinceData = [];
    if (provinces) {
      try {
        provinceData = JSON.parse(provinces);
      } catch (e) {
        console.warn("Invalid province data", e);
      }
    }
    const provinceSelect = this.querySelector('[name="address[province]"]');
    provinceSelect.innerHTML = '';
    provinceData.forEach(item => {
      provinceSelect.options.add(new Option(item[1], item[0]));
    });
    if (selectedProvince) provinceSelect.value = selectedProvince;
    provinceSelect.closest('.field')?.classList.toggle('hidden', provinceData.length === 0);
  }
  async onUpdateAddressButtonClick(event){
    event.preventDefault();
    this.isLoading();
           let newAddress = this.getUpdatedAddressObject();
    console.log(newAddress,"updated address");
      if (
    newAddress.first_name &&
    newAddress.last_name &&
    newAddress.address1 &&
    newAddress.city &&
    newAddress.province &&
    newAddress.zip &&
    newAddress.country_code
  ){

    try {
        await recharge.address.updateAddress(window.rechargeSession, this.addressObject.id, this.getUpdatedAddressObject());
        // await rechargeHelper.updateAddress(this.addressObject.id, this.getUpdatedAddressObject());
        setTimeout(() => {
          document.querySelector('recharge-dashboard')?.storeSetupData();
          this.confirmationBox.classList.remove('hidden');
          this.addressForm.classList.add('hidden');
          this.footer.classList.add('hidden');
          document.querySelector('.MissingFieldsEdit').innerHTML = "";
          document.querySelector('.editDrawerHeader').classList.add('hidden');
          this.isLoaded();
        }, 1000);
    } catch (error) {
    if (error.status === 401) {
      await this.authorize();
      return await this.updateAddress(addressID, updates);
    } else {
      console.log("Error while updating address:", error);
      document.querySelector(".MissingFieldsEdit").innerHTML = JSON.parse(error.message).zip[0].replace("Invalid data: ", "");
      document.querySelector(".MissingFieldsEdit").classList.add("emptyErrorText");
      this.isLoaded();
      return { success: false, error };
    }
  }
  }else{
     const requiredFields = [
      "first_name",
      "last_name",
      "address1",
      "city",
      "province",
      "zip",
      "country_code",
    ];

    const missingFields = requiredFields.filter(
      (field) => !newAddress[field] || newAddress[field].toString().trim() === ""
    );

    console.error("Missing required address fields:", missingFields);

    const ul = document.createElement("ul");
    missingFields.forEach((field) => {
      const li = document.createElement("li");
      const formattedField = field.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
      li.textContent = `${formattedField} is required`;
      li.classList.add("emptyErrorText");
      ul.appendChild(li);
    });

    const container = document.querySelector(".MissingFieldsEdit");
    if (container) {
      container.innerHTML = ""; 
      container.appendChild(ul);
      this.isLoaded();
    }

    return { error: "Missing required fields", missingFields };
  }
   
  }
  onAddressFormChange(event){
   this.updateAddressButton.disabled = false;
  }

  getUpdatedAddressObject(){
    return {
      first_name: this.addressForm.querySelector('input[name="address[first_name]"]').value,
      last_name: this.addressForm.querySelector('input[name="address[last_name]"]').value,
      address1: this.addressForm.querySelector('input[name="address[address1]"]').value,
      address2: this.addressForm.querySelector('input[name="address[address2]"]').value,
      city: this.addressForm.querySelector('input[name="address[city]"]').value,
      province: this.addressForm.querySelector('select[name="address[province]"]').value,
      country_code: getCountryCodeByName(this.addressForm.querySelector('select[name="address[country]"]').value),
      phone: this.addressForm.querySelector('input[name="address[phone]"]').value,
      zip: this.addressForm.querySelector('input[name="address[zip]"]').value,
    }
  }
  
  isLoading(){
    this.updateAddressButton.setAttribute('aria-busy', 'true');
    this.updateAddressButton.disabled = true;
    this.updateAddressButton.setAttribute('aria-disabled', 'true');
  }
  isLoaded(){
    this.updateAddressButton.setAttribute('aria-busy', 'false');
    this.updateAddressButton.disabled = false;
    this.updateAddressButton.setAttribute('aria-disabled', 'false');
  }

  renderAddressData(){
    if(this.addressObject){
      this.addressForm.querySelector('input[name="address[first_name]"]').value = this.addressObject.first_name;
      this.addressForm.querySelector('input[name="address[last_name]"]').value = this.addressObject.last_name;
      this.addressForm.querySelector('input[name="address[address1]"]').value = this.addressObject.address1;
      this.addressForm.querySelector('input[name="address[address2]"]').value = this.addressObject.address2;
      this.addressForm.querySelector('input[name="address[city]"]').value = this.addressObject.city;
      const provinceElement = this.addressForm.querySelector('select[name="address[province]"]');
      const countryElement = this.addressForm.querySelector('select[name="address[country]"]');
      provinceElement.setAttribute('data-default', this.addressObject.province);
      countryElement.setAttribute('data-default', getCountryNameByCode(this.addressObject.country_code));
      this.addressForm.querySelector('input[name="address[phone]"]').value = this.addressObject.phone;
      this.addressForm.querySelector('input[name="address[zip]"]').value = this.addressObject.zip;
      const countryName = getCountryNameByCode(this.addressObject.country_code);
      const countryOption = Array.from(countryElement.options).find(o => o.value === countryName || o.text === countryName);
      if (countryOption) countryElement.value = countryOption.value;
      this.updateProvinces(countryElement, this.addressObject.province);
      countryElement.onChange?.();
      this.querySelector('[name="address[province]"]').onChange?.();
    }
  }


  beforeShow() {
    super.beforeShow();
    setTimeout(() => {
      // Motion.animate(this.menuItems, { transform: ['translateX(-20px)', 'translateX(0)'], opacity: [0, 1] }, { duration: 0.6, easing: [.075, .82, .165, 1], delay: Motion.stagger(0.1) }).finished.then(() => {
      //   this.menuItems.forEach((item) => item.removeAttribute('style'));
      // });
    }, 300);
  }
  afterHide() {
    super.afterHide();
    setTimeout(() => {
      this.confirmationBox.classList.add('hidden');
      this.addressForm.classList.remove('hidden');
      this.footer.classList.remove('hidden');
    }, 300);
  }

  beforeHide() {
    super.beforeHide();

    setTimeout(() => {
      // this.querySelectorAll('details[is=menu-details]').forEach((subMenu) => {
      //   subMenu.onCloseButtonClick();
      // });
    }, 300);
  }
}
customElements.define('address-drawer', AddressDrawer);

class AddAddressDrawer extends DrawerElement {
  constructor() {
    super();
    this.addressForm = this.querySelector('form');
    this.addressObject = null;
    this.addNewAddressButton = this.querySelector('[data-add-new-address]');
    this.addNewAddressButton.addEventListener('click', this.onUpdateAddressButtonClick.bind(this));
    this.addressForm.addEventListener('change', this.onAddressFormChange.bind(this));
    this.confirmationBox = this.querySelector('[data-address-confirmation-box]');
    this.footer = this.querySelector('.drawer__footer');
  }

  connectedCallback(){
    super.connectedCallback();
    this.querySelector('[data-address-country-select]')?.addEventListener('change', this.onCountryChange.bind(this));
  }
  onCountryChange(event){
    event.preventDefault();
    this.updateProvinces(event.target);
  }

  updateProvinces(countrySelect, selectedProvince){
    const selectedOption = countrySelect.options[countrySelect.selectedIndex];
    const provinces = selectedOption?.getAttribute('data-provinces');
    let provinceData = [];
    if (provinces) {
      try {
        provinceData = JSON.parse(provinces);
      } catch (e) {
        console.warn("Invalid province data", e);
      }
    }
    const provinceSelect = this.querySelector('[name="address[province]"]');
    provinceSelect.innerHTML = '';
    provinceData.forEach(item => {
      provinceSelect.options.add(new Option(item[1], item[0]));
    });
    if (selectedProvince) provinceSelect.value = selectedProvince;
    provinceSelect.onChange?.();
  }

  async onUpdateAddressButtonClick(event){
    event.preventDefault();
    this.isLoading();
        let newAddress = this.getUpdatedAddressObject();
    console.log(newAddress,"updated address");
      if (
    newAddress.first_name &&
    newAddress.last_name &&
    newAddress.address1 &&
    newAddress.city &&
    newAddress.province &&
    newAddress.zip &&
    newAddress.country_code
  ){

    try {
        const response = await recharge.address.createAddress(window.rechargeSession, this.getUpdatedAddressObject() );
        console.log(response, "response");
          setTimeout(() => {
            document.querySelector('recharge-dashboard')?.storeSetupData();
            this.confirmationBox.classList.remove('hidden');
            this.addressForm.classList.add('hidden');
            this.footer.classList.add('hidden');
            this.isLoaded();
          }, 1000);
        

       
    } catch (error) {
    if (error.status === 401) {
      await this.authorize();
      return await this.updateAddress(addressID, updates);
    } else {
      console.log("Error while updating address:", error);
      document.querySelector(".MissingFields").innerHTML = JSON.parse(error.message).zip[0].replace("Invalid data: ", "");
      document.querySelector(".MissingFields").classList.add("emptyErrorText");
      this.isLoaded();
      return { success: false, error };
    }
  }
  }else{
    const requiredFields = [
      "first_name",
      "last_name",
      "address1",
      "city",
      "province",
      "zip",
      "country_code",
    ];

    const missingFields = requiredFields.filter(
      (field) => !newAddress[field] || newAddress[field].toString().trim() === ""
    );

    console.error("Missing required address fields:", missingFields);

    const ul = document.createElement("ul");
    missingFields.forEach((field) => {
      const li = document.createElement("li");
      const formattedField = field.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
      li.textContent = `${formattedField} is required`;
      li.classList.add("emptyErrorText");
      ul.appendChild(li);
    });

    const container = document.querySelector(".MissingFields");
    if (container) {
      container.innerHTML = ""; 
      container.appendChild(ul);
      this.isLoaded();
    }

    return { error: "Missing required fields", missingFields };
  }
   
  }
  onAddressFormChange(event){
   this.addNewAddressButton.disabled = false;
  }

  getUpdatedAddressObject(){
    return {
      first_name: this.addressForm.querySelector('input[name="address[first_name]"]').value,
      last_name: this.addressForm.querySelector('input[name="address[last_name]"]').value,
      address1: this.addressForm.querySelector('input[name="address[address1]"]').value,
      address2: this.addressForm.querySelector('input[name="address[address2]"]').value,
      city: this.addressForm.querySelector('input[name="address[city]"]').value,
      province: this.addressForm.querySelector('select[name="address[province]"]').value,
      country_code: getCountryCodeByName(this.addressForm.querySelector('select[name="address[country]"]').value),
      phone: this.addressForm.querySelector('input[name="address[phone]"]').value,
      zip: this.addressForm.querySelector('input[name="address[zip]"]').value,
    }
  }
  
  isLoading(){
    this.addNewAddressButton.setAttribute('aria-busy', 'true');
    this.addNewAddressButton.disabled = true;
    this.addNewAddressButton.setAttribute('aria-disabled', 'true');
  }
  isLoaded(){
    this.addNewAddressButton.setAttribute('aria-busy', 'false');
    this.addNewAddressButton.disabled = false;
    this.addNewAddressButton.setAttribute('aria-disabled', 'false');
  }

  beforeShow() {
    super.beforeShow();
    const countrySelect = this.querySelector('[data-address-country-select]');
    const provinceSelect = this.querySelector('[name="address[province]"]');
    // Populate provinces for the preselected country when the drawer opens
    if (!provinceSelect.options.length) {
      this.updateProvinces(countrySelect, provinceSelect.getAttribute('data-default'));
    }
    setTimeout(() => {

      // Motion.animate(this.menuItems, { transform: ['translateX(-20px)', 'translateX(0)'], opacity: [0, 1] }, { duration: 0.6, easing: [.075, .82, .165, 1], delay: Motion.stagger(0.1) }).finished.then(() => {
      //   this.menuItems.forEach((item) => item.removeAttribute('style'));
      // });
    }, 300);
  }
  afterHide() {
    super.afterHide();
    setTimeout(() => {
      this.confirmationBox.classList.add('hidden');
      this.addressForm.classList.remove('hidden');
      this.footer.classList.remove('hidden');
    }, 300);
  }

  beforeHide() {
    super.beforeHide();

    setTimeout(() => {
   
    }, 300);
  }
}
customElements.define('add-address-drawer', AddAddressDrawer);

class DiscountDrawer extends DrawerElement {
  constructor() {
    super();
    const triggerBTN = this.querySelector('[data-discount]');
    triggerBTN.addEventListener('click', this.addDiscount.bind(this));
  }
  
  beforeShow() {
    super.beforeShow();
    setTimeout(() => {
  
    }, 300);
  }

  beforeHide() {
    super.beforeHide();
    setTimeout(() => {
   
    }, 300);
  }

async addDiscount(event) {
  const discountCode = this.querySelector('#DiscountCode').value;
  if (!discountCode) {
    this.querySelector('#DiscountMessage').textContent = "Please enter a discount code.";
    return;
  }
  event.preventDefault();
  try {

    const response = await recharge.address.applyDiscountToAddress(
      window.rechargeSession,
      this.address_id,
      discountCode
    );
   
    this.querySelector('#DiscountMessage').textContent = "Discount applied successfully!";
    this.querySelector('#DiscountCode').value = "";
  } catch (error) {
    console.log(error, "error");
    this.querySelector('#DiscountMessage').textContent = "Error applying discount.";
  }
}

} 
customElements.define('discount-drawer', DiscountDrawer);

class OneTimeProductDrawer extends DrawerElement {
  constructor() {
    super();
  }
  connectedCallback() {
    super.connectedCallback();
    this.addToOneButton = this.querySelector('[data-add-to-one-time]');
    this.addToOneButton.addEventListener('click', this.onAddToOneButtonClick.bind(this));
  }
  initEvents() {
    this.querySelectorAll('input[type="radio"]').forEach(item => {
      item.addEventListener('change', this.onRadioChange.bind(this));
    });

  }
  async onAddToOneButtonClick(event) {
    event.preventDefault();

    this.addToOneButton.setAttribute('aria-busy', 'true');
    this.addToOneButton.classList.add('disabled');
    this.addToOneButton.setAttribute('disabled', 'true');
    try {
      await recharge.onetime.createOnetime(window.rechargeSession, {
        address_id: this.subscriptionData.address_id,
        quantity: 1,
        external_variant_id: {
          ecommerce: String(this.selectedVariantId),
        },
        external_product_id: {
          ecommerce: String(this.productObject.id),
        },
        add_to_next_charge:true,
        properties:[{name:'one_time_product',value:'true'}]
      });
      this.hide();
      document.dispatchEvent(new CustomEvent('update:subscriptionDashboard'));
    } catch (error) {
      console.log(error,"error");
    }
    finally {
      this.addToOneButton.removeAttribute('aria-busy');
      this.addToOneButton.classList.remove('disabled');
      this.addToOneButton.removeAttribute('disabled');
    }
  }
  renderProductDetails() {
    if(this.productObject){
      this.querySelector('[data-product-image]').src = this.productObject.featured_image;
      this.querySelectorAll('[data-product-title]').forEach(item => {
        item.textContent = this.productObject.title;
      });
      this.querySelectorAll('[data-product-price]').forEach(item => {
        item.textContent = theme.Currency.formatMoney(this.productObject.price, theme.settings.moneyFormat);
      });
      this.querySelector('[data-product-variant]').textContent = this.productObject.variants[0].title;
      if(this.productObject.origin !=""){
        this.querySelector('[data-product-origin-value]').textContent = this.productObject.origin;
      }
      else{
        this.querySelector('[data-product-origin-value]').textContent = 'N/A';
      }
      if(this.productObject.variety !=""){
        this.querySelector('[data-product-variety-value]').textContent = this.productObject.variety;
      }
      else{
        this.querySelector('[data-product-variety-value]').textContent = 'N/A';
      }
      if(this.productObject.testing_notes.length > 0){
        this.querySelector('[data-product-tasting-notes-value]').textContent = this.productObject.testing_notes.join(' | ');
      }
      else{
        this.querySelector('[data-product-tasting-notes-value]').textContent = 'N/A';
      }
      this.selectedVariantId = this.productObject.variants[0].id;
      this.querySelector('[data-product-description]').textContent = this.productObject.description;
      this.querySelector('[data-product-variants]').innerHTML = this.renderVariantSwatches(this.productObject.variants, this.productObject.id, this.productObject.id, this.productObject.variants[0].id);
      this.initEvents();
    }
  }

  onRadioChange(event) {
    this.selectedVariantId = event.target.getAttribute('data-option-value-id');
    console.log(this.selectedVariantId,"this.selectedVariantId");
  }

  renderVariantSwatches(variants, sectionId, productId, selectedVariantId) {
    return `
        ${variants.map((variant, index) => `
          <li>
            <magnet-element class="flex h-full">
              <input
                type="radio"
                class="sr-only"
                id="size-${sectionId}-${productId}-${index}"
                name="variant"
                value="${variant.title}"
                data-option-value="${variant.title}"
                data-option-value-id="${variant.id}"
                data-variant-price="${variant.price}"
                align-selected=".scroll-area"
                ${variant.id == selectedVariantId ? 'checked' : ''}
              />
              <label
                for="size-${sectionId}-${productId}-${index}"
                class="label-swatch text-sm font-formal leading-none cursor-pointer relative"
                title="${variant.title}">
                ${variant.title}
              </label>
            </magnet-element>
          </li>
        `).join("")}
    `;
  }

  beforeShow() {
    super.beforeShow();
    setTimeout(() => {
  
    }, 300);
  }

  beforeHide() {
    super.beforeHide();
    setTimeout(() => {
    
    }, 300);
  }
}
customElements.define('one-time-product-drawer', OneTimeProductDrawer);

class SkipOrderDrawer extends DrawerElement {
  constructor() {
    super();
  }
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener('DOMContentLoaded',() => {
      this.initEvents();
    });
  }

  initEvents() {
    this.dataProcessSkipOrder = this.querySelector('[data-process-skip-order]');
    this.concentBlock = this.querySelector('[data-skip-order-concent-block]');
    this.successBlock = this.querySelector('[data-skip-order-success-block]');
    this.dataProcessSkipOrder.addEventListener('click', this.onProcessSkipOrderClick.bind(this));
    
  }


  renderData() {
    if(this.subscriptionData && typeof this.subscriptionData != undefined){
      this.querySelector('[data-skip-order-date]').textContent = Utility.formatDate(this.subscriptionData.next_charge_scheduled_at,"MMM DD, YYYY");
      const nextOrderData = window.subscriptionHelper.getSkipChargeDates(this.subscriptionData);
      this.nextScheduleDate = Utility.formatDate(nextOrderData[0],"YYYY-MM-DD");
      this.querySelector('[data-skip-order-next-date]').textContent = Utility.formatDate(nextOrderData[0],"MMM DD, YYYY");
    }
  }
  async onProcessSkipOrderClick(event) {
    event.preventDefault();
    this.dataProcessSkipOrder.setAttribute('aria-busy', 'true');
    this.dataProcessSkipOrder.classList.add('disabled');
    this.dataProcessSkipOrder.setAttribute('disabled', 'true');
    try {
      console.log(this.subscriptionData.id,"this.subscriptionData.id");
      console.log(this.chargeData,"this.chargeData");
      console.log(this.chargeData.id,"this.chargeData.id");
      let oneTimeProducts = this.chargeData.line_items.filter(item => item.purchase_item_type == 'onetime').map(item => {
        return {
          purchase_item_id: parseInt(item.purchase_item_id),
          scheduled_at: this.nextScheduleDate
        }
      });
      await recharge.charge.skipCharge(window.rechargeSession, this.chargeData.id,[this.subscriptionData.id]);
      await this.updateOneTimeProductsScheduledOrders(oneTimeProducts);
      document.dispatchEvent(new CustomEvent('update:subscriptionDashboard'));
      this.concentBlock.classList.add('hidden');
      this.successBlock.classList.remove('hidden');
      // setTimeout(() => {
      //   this.hide();
      // }, 300);
    } catch (error) {
      console.log(error,"error");
    }
    finally {
      this.dataProcessSkipOrder.removeAttribute('aria-busy');
      this.dataProcessSkipOrder.classList.remove('disabled');
      this.dataProcessSkipOrder.removeAttribute('disabled');
    }
  }
  async updateOneTimeProductsScheduledOrders(items) {
    if (!Array.isArray(items) || items.length === 0) return;
    const promises = items.map((item) => {
      const { purchase_item_id, scheduled_at } = item;
      return recharge.onetime.updateOnetime(window.rechargeSession, purchase_item_id, {
        next_charge_scheduled_at: scheduled_at,
      },{commit: true});
    });
  }
  beforeShow() {
    super.beforeShow();
    setTimeout(() => {
  
    }, 300);
  }

  afterHide() {
    super.afterHide();
    setTimeout(() => {
      this.concentBlock.classList.remove('hidden');
      this.successBlock.classList.add('hidden');
    }, 300);
  }

  beforeHide() {
    super.beforeHide();
    setTimeout(() => {
    
    }, 300);
  }
}
customElements.define('skip-order-drawer', SkipOrderDrawer); 

class AddOnOneTimeDrawer extends DrawerElement {
  constructor() {
    super();
    this.dataSwapProductListingModel = this.querySelector('[data-swap-product-listing-model]');
    this.dataSwapProductItemModal = this.querySelector('[data-swap-product-item-modal]');
    this.dataProductItemSelector = this.querySelectorAll('[data-product-item-selector]');
    this.dataBackButton = this.querySelectorAll('[data-swap-modal-back-button]');
    this.grindPreferenceSelector = this.querySelector('grind-preference-selector');
    this.saveSubscriptionButton = this.querySelector('[data-save-subscription-button]');
    this.cancelSubscriptionButton = this.querySelector('[data-cancel-subscription-button]');
    this.inputOneTime = this.querySelector('#product-search-account-dashboard');
  }
  connectedCallback() {
    super.connectedCallback();
    this.dataProductItemSelector.forEach(item => {
      item.addEventListener('click', this.onProductItemClick.bind(this));
    });
    this.dataBackButton.forEach(item => {
      item.addEventListener('click', this.onBackButtonClick.bind(this));
    });
    this.inputOneTime.addEventListener('input', this.applyOneTimeSearch.bind(this));
    this.initButtonsEvent();
  }

  initButtonsEvent(){
    document.addEventListener('DOMContentLoaded',() => {
      this.saveSubscriptionButton.addEventListener('click', this.onSaveSubscriptionButtonClick.bind(this));
    });
  }
  
  applyOneTimeSearch(event){
    const cards = document.querySelectorAll("[data-product-item-selector]");
        const query = event.target.value.toLowerCase();
            let matchFound = false;
  const noResults = document.getElementById("noResults");

     cards.forEach(card => {
      const title = card.querySelector(".cartTitleOneTime").textContent.toLowerCase();
      if (title.includes(query)) {
        card.style.display = "block"; 
        matchFound = true;
      } else {
        card.style.display = "none"; 
      }
    });

    if (!matchFound && query !== "") {
      noResults.style.display = "block";
    } else {
      noResults.style.display = "none";
    }
  }

  renderData() {
    this.dataSwapProductListingModel.innerHTML = this.renderProductListingRow();
  }
  renderProductListingRow() {
    return `
    `;
  }
  async onProductItemClick(event) {
    event.preventDefault();
  
    this.productObject = window.getProductById(event.target.closest('[data-product-id]').getAttribute('data-product-id'));
    this.plans = [];
    this.planId = null;
    const plansResponse = await recharge.plan.listPlans(window.rechargeSession, {
      limit: 5,
      external_product_id:this.productObject.id,
      type:"onetime"
    });
    this.plans = plansResponse.plans;
    let currentPlan = this.plans[0];
    if(this.plans.length > 0){
      this.planId = currentPlan.id;
    }
    this.dataSwapProductListingModel.classList.remove('active');
    this.dataSwapProductItemModal.classList.add('active');
    await this.renderDataOnModal();
  }
  onBackButtonClick(event) {
    event.preventDefault();
    this.dataSwapProductListingModel.classList.add('active');
    this.dataSwapProductItemModal.classList.remove('active');
  }

  async onCancelSubscriptionButtonClick(event) {
    event.preventDefault();
    this.cancelSubscriptionButton.setAttribute('aria-busy', 'true');
    this.cancelSubscriptionButton.classList.add('disabled');
    this.cancelSubscriptionButton.setAttribute('disabled', 'true');
    try {
      await recharge.subscription.cancelSubscription(window.rechargeSession, parseInt(this.subscriptionData.id),{cancellation_reason:'Customer requested'});
      this.hide();
      document.dispatchEvent(new CustomEvent('update:subscriptionDashboard'));
    } catch (error) {
      console.log(error,"error");
    }
    finally {
      this.cancelSubscriptionButton.removeAttribute('aria-busy');
      this.cancelSubscriptionButton.classList.remove('disabled');
      this.cancelSubscriptionButton.removeAttribute('disabled');
    }
  }

  initEvents() {
    this.querySelectorAll('input[type="radio"]').forEach(item => {
      item.addEventListener('change', this.onRadioChange.bind(this));
    });
    

  }
  async onSaveSubscriptionButtonClick(event) {
    this.saveSubscriptionButton.setAttribute('aria-busy', 'true');
    this.saveSubscriptionButton.classList.add('disabled');
    this.saveSubscriptionButton.setAttribute('disabled', 'true');
    const variant_id = this.currentVariant.id;
    const quantity = this.querySelector('.quantity__input').value;
    const grind_preferences = this.grindPreferenceSelector.grindPreferenceValue;
    let one_time_properties = [];
    if(this.coffeeBrew == 'filter'){
      one_time_properties.push({name:'grind_filter',value:grind_preferences});
    }
    else{
      one_time_properties.push({name:'grind_espresso',value:grind_preferences});
    }
    try {
        await recharge.onetime.createOnetime(window.rechargeSession, {
        address_id: this.subscriptionData.address_id,
        next_charge_scheduled_at:this.subscriptionData.next_charge_scheduled_at,
        quantity: quantity,
        plan_id: this.planId || null,
        external_variant_id: {
          ecommerce: String(variant_id),
        },
        external_product_id: {
          ecommerce: String(this.productObject.id),
        },
        properties: one_time_properties
      });
      this.hide(); 
      document.dispatchEvent(new CustomEvent('update:subscriptionDashboard'));
    }
    catch(error) {
      console.log(error,"error");
    }
    finally {
      this.saveSubscriptionButton.removeAttribute('aria-busy');
      this.saveSubscriptionButton.classList.remove('disabled');
      this.saveSubscriptionButton.removeAttribute('disabled');
    }
  }
  onRadioChange(event) {
    const selectedVariantId = this.querySelector('[data-subscription-variant-list]').querySelector('input[type="radio"]:checked').getAttribute('data-option-value-id');
    this.currentVariant = this.productObject.variants.find(item => {
      if(item.id == selectedVariantId){
        return item;
      }
    });
    this.querySelector('[data-swape-total-order]').textContent = theme.Currency.formatMoney(this.currentVariant.price, theme.settings.moneyFormat);
    this.querySelector('[data-subscription-price]').textContent = `1 x ${theme.Currency.formatMoney(this.currentVariant.price, theme.settings.moneyFormat)}`;
    this.querySelector('[data-onetime-product-price]').textContent = theme.Currency.formatMoney(this.currentVariant.price + parseInt(this.chargeData.total_price*100), theme.settings.moneyFormat);
  }
  async renderDataOnModal() {
    const productData = this.productObject;
    const plans = this.plans;
    const currentPlan = this.plans[0];
 
    
    const variant_id = productData.variants[0].id;
    const variant_title = productData.variants[0].title;
    const variant_price = productData.variants[0].price;
    const quantity = 1;
    this.currentVariant = productData.variants[0];
    console.log(this.subscriptionData,"this.subscriptionData.total_price");
    console.log(variant_price,"variant_price");
    console.log(parseInt(parseInt(this.chargeData.total_price*100)),"this.subscriptionData.price");
    const totalPrice = parseFloat(variant_price) * quantity + parseInt(parseInt(this.chargeData.total_price*100));
    console.log(totalPrice,"totalPrice");
    if(productData){
      this.querySelectorAll('[data-subscription-title]').forEach(item => {
        item.textContent = this.productObject.title;
      });
      if(plans.length > 0){
        if(currentPlan.discount_type == "percentage"){
            const discountPrice = parseFloat(variant_price) * (parseInt(100 - currentPlan.discount_amount) / 100) * quantity;
          console.log(discountPrice,"discountPrice");
          this.querySelector('[data-subscription-price]').innerHTML = `1 x <s>${theme.Currency.formatMoney(variant_price, theme.settings.moneyFormat)} </s> ${theme.Currency.formatMoney(parseInt(discountPrice), theme.settings.moneyFormat)}`;
        }
      }
      else{
        this.querySelector('[data-subscription-price]').textContent = `1 x ${theme.Currency.formatMoney(variant_price, theme.settings.moneyFormat)}`;
      }
      
      this.querySelector('[data-subscription-variant]').textContent = variant_title;
    }
    this.querySelector('[data-subscription-variant-list]').innerHTML = this.renderVariantSwatches(productData.variants, productData.id,productData.id, variant_id );
    this.querySelector('[data-onetime-product-title]').textContent = productData.title;
    this.querySelector('[data-onetime-product-price]').textContent = theme.Currency.formatMoney(totalPrice, theme.settings.moneyFormat);
    if(productData){
      this.querySelector('[data-subscription-image]').src = productData.featured_image;
      if(plans.length > 0){
        if(currentPlan.discount_type == "percentage"){
            const discountPrice = parseFloat(variant_price) * (parseInt(100 - currentPlan.discount_amount) / 100) * quantity;
          console.log(discountPrice,"discountPrice");
          this.querySelector('[data-swape-total-order]').innerHTML = `<s>${theme.Currency.formatMoney(variant_price, theme.settings.moneyFormat)} </s> ${theme.Currency.formatMoney(parseInt(discountPrice), theme.settings.moneyFormat)}`;
        }
      }
      else{
        this.querySelector('[data-swape-total-order]').textContent = theme.Currency.formatMoney(variant_price, theme.settings.moneyFormat);
      }
    }
    let coffeeBrew = null
    if(productData.tags.includes('filter')){
      coffeeBrew = 'filter';
      this.coffeeBrew = 'filter';
      this.grindPreferenceSelector.setAttribute('data-brew-type', 'filter');
    }
    else if(productData.tags.includes('espresso')){
      coffeeBrew = 'espresso';
      this.coffeeBrew = 'espresso';
      this.grindPreferenceSelector.setAttribute('data-brew-type', 'espresso');
    }
    else{
      coffeeBrew = null;
    }
    if(coffeeBrew == 'filter'){
      this.groundOptionList = "French Press,Pour Over,Drip,Aeropress".split(',');
    }
    else{
      this.groundOptionList = "Espresso".split(',');
    }

    console.log(coffeeBrew,"coffeeBrew");
    
    this.grindPreferenceSelector.querySelector('select').innerHTML = this.groundOptionList.map((option,index) => `<option value="${option.toLowerCase().replace(' ', '-')}" ${index == 0 ? 'selected' : ''}>${option}</option>`).join('');
    this.grindPreferenceSelector.querySelector(`[name="grind_preferences"][value="whole-beans"]`).click();
    this.initEvents();
  }

  renderVariantSwatches(variants, sectionId, productId, selectedVariantId) {
    return `
        ${variants.map((variant, index) => `
          <li>
            <magnet-element class="flex h-full">
              <input
                type="radio"
                class="sr-only"
                id="size-${sectionId}-${productId}-${index}"
                name="variant"
                value="${variant.title}"
                data-option-value="${variant.title}"
                data-option-value-id="${variant.id}"
                data-variant-price="${variant.price}"
                align-selected=".scroll-area"
                ${variant.id == selectedVariantId ? 'checked' : ''}
              />
              <label
                for="size-${sectionId}-${productId}-${index}"
                class="label-swatch text-sm font-formal leading-none cursor-pointer relative"
                title="${variant.title}">
                ${variant.title}
              </label>
            </magnet-element>
          </li>
        `).join("")}
    `;
  }

  
  beforeShow() {
    super.beforeShow();
    setTimeout(() => {
  
    }, 300);
  }

  beforeHide() {
    super.beforeHide();
    setTimeout(() => {
    
    }, 300);
  }
}
customElements.define('add-on-one-time-drawer', AddOnOneTimeDrawer); 

class AddOnOneTimeEditDrawer extends DrawerElement {
  constructor() {
    super();
    this.grindPreferenceSelector = this.querySelector('grind-preference-selector');
    this.saveSubscriptionButton = this.querySelector('[data-save-subscription-button]');
    this.cancelSubscriptionButton = this.querySelector('[data-cancel-subscription-button]');
  }
  connectedCallback() {
    super.connectedCallback();
  }

  async onCancelSubscriptionButtonClick(event) {
    event.preventDefault();
    this.cancelSubscriptionButton.setAttribute('aria-busy', 'true');
    this.cancelSubscriptionButton.classList.add('disabled');
    this.cancelSubscriptionButton.setAttribute('disabled', 'true');
    try {
      await recharge.subscription.cancelSubscription(window.rechargeSession, parseInt(this.subscriptionData.id),{cancellation_reason:'Customer requested'});
      this.hide();
      document.dispatchEvent(new CustomEvent('update:subscriptionDashboard'));
    } catch (error) {
      console.log(error,"error");
    }
    finally {
      this.cancelSubscriptionButton.removeAttribute('aria-busy');
      this.cancelSubscriptionButton.classList.remove('disabled');
      this.cancelSubscriptionButton.removeAttribute('disabled');
    }
  }

  initEvents() {
    this.querySelectorAll('input[type="radio"]').forEach(item => {
      item.addEventListener('change', this.onRadioChange.bind(this));
    });
    this.saveSubscriptionButton.addEventListener('click', this.onSaveSubscriptionButtonClick.bind(this));

  }
  async onSaveSubscriptionButtonClick(event) {
    this.saveSubscriptionButton.setAttribute('aria-busy', 'true');
    this.saveSubscriptionButton.classList.add('disabled');
    this.saveSubscriptionButton.setAttribute('disabled', 'true');
    const variant_id = this.currentVariant.id;
    const quantity = this.querySelector('.quantity__input').value;
    const grind_preferences = this.grindPreferenceSelector.grindPreferenceValue;
    let one_time_properties = [];
    if(this.coffeeBrew == 'filter'){
      one_time_properties.push({name:'grind_filter',value:grind_preferences});
    }
    else{
      one_time_properties.push({name:'grind_espresso',value:grind_preferences});
    }
    try {
        await recharge.onetime.updateOnetime(window.rechargeSession,this.purchaseItemId, {
        external_variant_id: {
          ecommerce: String(variant_id),
        },
        quantity: quantity,
        properties: one_time_properties
      });
      this.hide(); 
      document.dispatchEvent(new CustomEvent('update:subscriptionDashboard'));
    }
    catch(error) {
      console.log(error,"error");
    }
    finally {
      this.saveSubscriptionButton.removeAttribute('aria-busy');
      this.saveSubscriptionButton.classList.remove('disabled');
      this.saveSubscriptionButton.removeAttribute('disabled');
    }
  }
  onRadioChange(event) {
    const selectedVariantId = this.querySelector('[data-subscription-variant-list]').querySelector('input[type="radio"]:checked').getAttribute('data-option-value-id');
    this.currentVariant = this.productObject.variants.find(item => {
      if(item.id == selectedVariantId){
        return item;
      }
    });
    this.querySelector('[data-swape-total-order]').textContent = theme.Currency.formatMoney(this.currentVariant.price, theme.settings.moneyFormat);
    this.querySelector('[data-subscription-price]').textContent = `1 x ${theme.Currency.formatMoney(this.currentVariant.price, theme.settings.moneyFormat)}`;
    this.querySelector('[data-onetime-product-price]').textContent = theme.Currency.formatMoney(this.currentVariant.price, theme.settings.moneyFormat);
  }
  async renderDataOnModal() {
    const productData = this.productObject;

    const variant_id = productData.variants[0].id;
    const variant_title = productData.variants[0].title;
    const variant_price = productData.variants[0].price;
    const quantity = 1;
    this.currentVariant = productData.variants[0];
    const totalPrice = parseFloat(variant_price) * quantity;
    console.log(totalPrice,"totalPrice");
    if(productData){
      this.querySelectorAll('[data-subscription-title]').forEach(item => {
        item.textContent = this.productObject.title;
      });
      this.querySelector('[data-subscription-price]').textContent = `1 x ${theme.Currency.formatMoney(variant_price, theme.settings.moneyFormat)}`;
      this.querySelector('[data-subscription-variant]').textContent = variant_title;
    }
    this.querySelector('[data-subscription-variant-list]').innerHTML = this.renderVariantSwatches(productData.variants, productData.id,productData.id, variant_id );
    this.querySelector('[data-onetime-product-title]').textContent = productData.title;
    this.querySelector('[data-onetime-product-price]').textContent = theme.Currency.formatMoney(totalPrice, theme.settings.moneyFormat);
    if(productData){
      this.querySelector('[data-subscription-image]').src = productData.featured_image;
      this.querySelector('[data-swape-total-order]').textContent = theme.Currency.formatMoney(variant_price, theme.settings.moneyFormat);
    }
    let coffeeBrew = null
    if(productData.tags.includes('filter')){
      coffeeBrew = 'filter';
      this.coffeeBrew = 'filter';
      this.grindPreferenceSelector.setAttribute('data-brew-type', 'filter');
    }
    else if(productData.tags.includes('espresso')){
      coffeeBrew = 'espresso';
      this.coffeeBrew = 'espresso';
      this.grindPreferenceSelector.setAttribute('data-brew-type', 'espresso');
    }
    else{
      coffeeBrew = null;
    }
    if(coffeeBrew == 'filter'){
      this.groundOptionList = "French Press,Pour Over,Drip,Aeropress".split(',');
    }
    else{
      this.groundOptionList = "Espresso".split(',');
    }

    console.log(coffeeBrew,"coffeeBrew");
    
    this.grindPreferenceSelector.querySelector('select').innerHTML = this.groundOptionList.map((option,index) => `<option value="${option.toLowerCase().replace(' ', '-')}" ${index == 0 ? 'selected' : ''}>${option}</option>`).join('');
    this.grindPreferenceSelector.querySelector(`[name="grind_preferences"][value="whole-beans"]`).click();
    this.initEvents();
  }

  renderVariantSwatches(variants, sectionId, productId, selectedVariantId) {
    return `
        ${variants.map((variant, index) => `
          <li>
            <magnet-element class="flex h-full">
              <input
                type="radio"
                class="sr-only"
                id="size-${sectionId}-${productId}-${index}"
                name="variant"
                value="${variant.title}"
                data-option-value="${variant.title}"
                data-option-value-id="${variant.id}"
                data-variant-price="${variant.price}"
                align-selected=".scroll-area"
                ${variant.id == selectedVariantId ? 'checked' : ''}
              />
              <label
                for="size-${sectionId}-${productId}-${index}"
                class="label-swatch text-sm font-formal leading-none cursor-pointer relative"
                title="${variant.title}">
                ${variant.title}
              </label>
            </magnet-element>
          </li>
        `).join("")}
    `;
  }

  
  beforeShow() {
    super.beforeShow();
    setTimeout(() => {
  
    }, 300);
  }

  beforeHide() {
    super.beforeHide();
    setTimeout(() => {
    
    }, 300);
  }
}
customElements.define('add-on-one-time-edit-drawer', AddOnOneTimeEditDrawer); 


class SwapCoffeeSubscriptionModal extends ModalElement {
  constructor() {
    super();
    this.swapProductItems = this.querySelectorAll('[data-swap-modal-product-item]');
    this.swapProductListingModel = this.querySelector('[data-swap-product-listing-model]');
    this.swapProductItemModal = this.querySelector('[data-swap-product-item-modal]');
    this.swapModalBackButton = this.querySelectorAll('[data-swap-modal-back-button]');
    this.swapProductListingRow = this.querySelector('.swap-product-listing-row');
    this.grindPreferenceSelector = this.querySelector('grind-preference-selector');
    this.excludeDecafProducts = false;
    this.swapModalBackButton.forEach(item => {
      item.addEventListener('click', this.onSwapModalBackButtonClick.bind(this));
    });
    // this.grindPreferenceSelector.addEventListener('grind-preference:change', (e) => {
    //   this.grindPreferenceValue = e.detail.grindPreferenceValue;
    //   console.log(this.grindPreferenceValue,"this.grindPreferenceValue");
    // });
    this.swapModalSaveButton = this.querySelector('[data-swap-modal-save-button]');
    this.swapModalSaveButton.addEventListener('click', this.onSwapModalSaveButtonClick.bind(this));
  }

  initEvents() {
    this.querySelectorAll('[data-swap-modal-product-item]').forEach(item => {
      item.addEventListener('click', this.onSwapProductItemClick.bind(this));
    });

  }
  
  shouldLock() {
    return true;
  }

  resetToListingView() {
    this.swapProductListingModel.classList.add('active');
    this.swapProductItemModal.classList.remove('active');
    this.currentProductItemId = null;
  }

  renderDataOnModal() {
    this.resetToListingView();
    const excludeProducts = [this.productObject.id];
    console.log(excludeProducts,"excludeProducts");
    this.swapProductListingRow.innerHTML = '';
    const productListing = this.playlistData.playlist_products;
    this.decaf_properties = this.subscriptionData.properties.find(property => property.name == 'decaf')?.value ? this.subscriptionData.properties.find(property => property.name == 'decaf').value : 'true';
      if(this.playlistData.playlist_handle == 'mixtape'){
        const excludeDecafProducts = this.decaf_properties == 'false' ? true : false;
        this.excludeDecafProducts = excludeDecafProducts;
      }
    this.swapProductListingRow.innerHTML = this.renderProductListingRow(productListing,excludeProducts,this.excludeDecafProducts);
    this.initEvents();
  }
  renderProductListingRow(productListing,excludeProducts,excludeDecafProducts = false) {
   
    return productListing.map(item => {
      if(excludeDecafProducts && item.tags.includes('decaf')){
        return '';
      }
      if(excludeProducts.includes(item.id)){
        return '';
      }
      if(item.available == false){
        return '';
      }
      let found300g = false;
      if(item.variants.find(variant => variant.title == "300g" || variant.title == "300G" || variant.title == "300 G" || variant.title == "300 g")){
        found300g = true;
      }
      if(found300g == false){
        return '';
      }
      return `
      <div data-swap-modal-product-item="${item.id}" class="flex flex-col next-swap-playlist">
          <img src="${item.featured_image}?width=200" alt="${item.title}" height="150" width="150" class="mt-0 mb-2 w-full">
          <div class="lg:text-base text-2sm font-bold leading-tight tracking-none heading mb-1"> ${item.title} </div>
          <p class="text-xs mt-0 mb-2"> ${item.variants[0].title} </p>
          <button  class="button button--primary button--sm gap-3 w-full lg:w-auto lg:max-w-max current-order-desktop-button icon-with-text" role="link" aria-disabled="true" is="hover-button">
              <span class="btn-fill" data-fill="" style="transform: translateY(var(--motion-translateY)); --motion-translateY: -76%;"></span>
              <span class="btn-text max-w-max">
                <svg class="icon icon-swap-playlist icon-sm transform" viewBox="0 0 18 18" stroke="currentColor" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M16.5295 4.22357H3.35306C2.31776 4.22357 1.4707 5.07063 1.4707 6.10592V8.92945" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"></path>
                  <path d="M13.7051 1.40002L16.5286 4.22355L13.7051 7.04708" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"></path>
                  <path d="M1.4707 13.6353H14.6472C15.6825 13.6353 16.5295 12.7883 16.5295 11.753V8.92944" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"></path>
                  <path d="M4.29423 16.4588L1.4707 13.6353L4.29423 10.8118" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"></path>
                </svg>
                Swap
              </span>
              <span class="btn-loader">
              <span></span>
              <span></span>
              <span></span>
            </span>
          </button>
      </div>
      `;
    }).join('');
  }

  onSwapProductItemClick(event) {
    event.preventDefault();
    this.swapProductListingModel.classList.remove('active');
    this.swapProductItemModal.classList.add('active');
    this.currentProductItemId = event.target.closest('[data-swap-modal-product-item]')? event.target.closest('[data-swap-modal-product-item]').getAttribute('data-swap-modal-product-item') : event.target.getAttribute('data-swap-modal-product-item');
    this.renderProductItem(this.currentProductItemId);
  }
  renderProductItem(productItemId) {
   
    
    const productItem = this.playlistData.playlist_products.find(item => item.id == productItemId);
    this.swapProductObject = productItem;
    this.querySelector('[data-selected-swap-product-image]').src = productItem.featured_image;
    this.querySelector('[data-selected-swap-product-title]').textContent = productItem.title;
    this.querySelector('.nextProduct').textContent = productItem.title; 
    this.querySelector('.previousProduct').textContent = sessionStorage.getItem('swapCoffeeModalTitle');
    if(this.querySelector('[data-subscription-metafiled-details]')){
      this.querySelector('[data-subscription-metafiled-details]').innerHTML = this.renderMetafiledDetails(productItem);
    }
    let coffeeBrew = null
    if(productItem.tags.includes('filter')){
      coffeeBrew = 'filter';
      this.coffeeBrew = 'filter';
      this.grindPreferenceSelector.setAttribute('data-brew-type', 'filter');
    }
    else if(productItem.tags.includes('espresso')){
      coffeeBrew = 'espresso';
      this.coffeeBrew = 'espresso';
      this.grindPreferenceSelector.setAttribute('data-brew-type', 'espresso');
    }
    else{
      coffeeBrew = null;
    }
    if(coffeeBrew == 'filter'){
      this.groundOptionList = "French Press,Pour Over,Drip,Aeropress".split(',');
    }
    else{
      this.groundOptionList = "Espresso".split(',');
    }
    
    this.swapProductItemModal.querySelector('select').innerHTML = this.groundOptionList.map(option => `<option value="${option.toLowerCase().replace(' ', '-')}">${option}</option>`).join('');
  
    
  }
  renderMetafiledDetails(productItem) {
    return `
    ${productItem.description != "" ? `
      <p class="mt-0 mb-2 lg:text-sm text-2sm">
        ${productItem.description.length > 100 ? productItem.description.substring(0, 100) + '...' : productItem.description}
      </p>
      ` : ''}
    ${productItem.origin != "" ? `
      <div class="leading-normal gap-3 mb-2 pb-2 next-order-info-box flex justify-between">
        <span class="lg:text-sm text-2sm font-bold tracking-none min-w-max"> Origin </span>
        <span class="lg:text-sm text-2sm"> ${productItem.origin} </span>
      </div>
      `:''}
      ${productItem.variety != "" ? `
      <div class="leading-normal gap-3 mb-2 pb-2 next-order-info-box flex justify-between">
        <span class="lg:text-sm text-2sm font-bold tracking-none min-w-max"> Varietal </span>
        <span class="lg:text-sm text-2sm"> ${productItem.variety} </span>
      </div>
      ` : ''}
      ${productItem.processing_method != "" ? `
      <div class="leading-normal gap-3 mb-2 pb-2 next-order-info-box flex justify-between">
        <span class="lg:text-sm text-2sm font-bold tracking-none min-w-max"> Processing Method </span>
        <span class="lg:text-sm text-2sm"> ${productItem.processing_method} </span>
      </div>
      ` : ''}
      ${productItem.testing_notes.length > 0 ? `
      <div class="leading-normal gap-3 next-order-info-box flex justify-between">
        <span class="lg:text-sm text-2sm font-bold tracking-none min-w-max"> Tasting Notes </span>
        <span class="lg:text-sm text-2sm"> ${productItem.testing_notes.join(', ')} </span>
      </div>
      ` : ''}
    `;
  }
  onSwapModalBackButtonClick(event) {
    event.preventDefault();
    this.swapProductListingModel.classList.add('active');
    this.swapProductItemModal.classList.remove('active');
  }
  async onSwapModalSaveButtonClick(event) {
    event.preventDefault();
    this.swapModalSaveButton.setAttribute('aria-busy', 'true');
    this.swapModalSaveButton.setAttribute('disabled', 'true');
    const quantity = parseInt(this.quantity);

    

    const subscriptionProperties = this.subscriptionData.properties;
    let newProperties = subscriptionProperties.map(item => {
      if(item.name == 'grind_filter' && this.coffeeBrew == 'filter'){
        return {
          ...item,
          value: this.grindPreferenceSelector.grindPreferenceValue
        }
      }
      if(item.name == 'grind_espresso' && this.coffeeBrew == 'espresso'){
        return {
          ...item,
          value: this.grindPreferenceSelector.grindPreferenceValue
        }
      }
      return item;
    });
  
    const oldBundleSelection = this.subscriptionData.include.bundle_selections.items;
    const flatBundleSelection = oldBundleSelection.flatMap(item =>
      Array.from({ length: item.quantity }, () => ({ ...item, quantity: 1 }))
    );

    const collectionId = this.playlistData.collection_id;
    const bundleSelectionId = this.subscriptionData.include.bundle_selections.id;
    // Remove only one item with this.productObject.id from the bundle selections,
    // keeping all other items. Only the FIRST occurrence is removed.
    let removed = false;
    let newBundleSelection = [];
    flatBundleSelection.forEach(item => {
      if (!removed && item.external_product_id == this.productObject.id) {
        removed = true; // skip only the first match
        return;
      }
      newBundleSelection.push({
        collection_id: collectionId,
        collection_source: 'shopify',
        external_product_id: item.external_product_id,
        external_variant_id: item.external_variant_id,
        quantity: item.quantity
      });
    });

    newBundleSelection = newBundleSelection.filter(item => item !== undefined);
    newBundleSelection.push({
      collection_id: collectionId,
      collection_source: 'shopify',
      external_product_id: String(this.swapProductObject.id),
      external_variant_id: String(this.swapProductObject.variants.find(variant => variant.title == '300g' || variant.title == '300G').id),
      quantity: quantity
    });
    let multipleOldVariants = false;
    let grindPreferenceArray = this.subscriptionData.properties.find(property => property.name == 'grind_preference_array')?.value ? JSON.parse(this.subscriptionData.properties.find(property => property.name == 'grind_preference_array')?.value) : [];
    let products_sku = this.subscriptionData.properties.find(property => property.name == 'products_sku')?.value ? JSON.parse(this.subscriptionData.properties.find(property => property.name == 'products_sku')?.value) : [];
    let oldSKU = this.productObject.variants.find(item => item.sku == this.productObject.variants.find(variant => variant.title == '300g' || variant.title == '300G').sku)?.sku;
    let oldVariantId = this.productObject.variants.find(item => item.id == this.productObject.variants.find(variant => variant.title == '300g' || variant.title == '300G').id)?.id;
    const nextSubscriptionCard = document.querySelector(`next-subscription-order[data-subscription-id="${this.subscriptionData.id}"]`);
    let varaintOldVariantCount = 0;
    if(nextSubscriptionCard){
      const productLineItems = nextSubscriptionCard.querySelectorAll('product-line-item');
      let variantIds = [];
      productLineItems.forEach(item => {
        const variantId = item.getAttribute('data-variant-id');
        variantIds.push(variantId);
      });
        variantIds.forEach(item => {
          if(item == oldVariantId){
            varaintOldVariantCount++;
          }
        });
    }

    if(varaintOldVariantCount == 1){
      if(oldSKU){
        products_sku = products_sku.filter(item => item.sku != oldSKU);
      }
      if(oldVariantId){
        grindPreferenceArray = grindPreferenceArray.filter(item => item.variantId != oldVariantId);
      }
    }
    grindPreferenceArray.push({
      variantId: this.swapProductObject.variants.find(variant => variant.title == '300g' || variant.title == '300G').id,
      grind_Value: this.grindPreferenceSelector.grindPreferenceValue
    });
    products_sku.push({
      sku: this.swapProductObject.variants.find(variant => variant.title == '300g' || variant.title == '300G').sku,
      grindType: this.grindPreferenceSelector.grindPreferenceValue
    });
    if(this.grindPreferenceSelector.grindPreferenceValue){
      grindPreferenceArray.forEach(item => {
        if(item.variantId == this.swapProductObject.variants.find(variant => variant.title == '300g' || variant.title == '300G').id){
          item.grind_Value = this.grindPreferenceSelector.grindPreferenceValue;
        }
      });
      products_sku.forEach(item => {
        if(item.sku == this.swapProductObject.variants.find(variant => variant.title == '300g' || variant.title == '300G').sku){
          item.grindType = this.grindPreferenceSelector.grindPreferenceValue;
        }
      });
    }
    
    let filterCount = 0;
    let espressoCount = 0;
    newBundleSelection.forEach(item => {
      const prObject = window.getProductById(item.external_product_id);
      if(prObject.tags.includes('filter')){
        filterCount = filterCount + item.quantity;
      }
      else if(prObject.tags.includes('espresso')){
        espressoCount = espressoCount + item.quantity;
      }
    });
   
    const updateNewProperties = window.subscriptionHelper.updateProperties(newProperties, {filter: String(filterCount), espresso: String(espressoCount), grind_preference_array: JSON.stringify(window.removeDuplicates(grindPreferenceArray,'variantId')),products_sku: JSON.stringify(window.removeDuplicates(products_sku,'sku'))});
    
    try {
      const response =  await recharge.bundle.updateBundleSelection(window.rechargeSession,bundleSelectionId,{
        items: newBundleSelection,
      });
      const response2 = await recharge.subscription.updateSubscription(window.rechargeSession, parseInt(this.subscriptionData.id), { properties: updateNewProperties });
      const response3 = await recharge.address.updateAddress(window.rechargeSession, this.subscriptionData.address_id, {
        order_attributes:
        [{name:'_subscriptionData',value:JSON.stringify(updateNewProperties)}]
      });

      this.hide();
      document.dispatchEvent(new CustomEvent('update:subscriptionDashboard'));
    } catch (error) {
      console.log(error,"error");
      window.notificationEle.updateNotification('Swap Product', 'Something went wrong', {
        type: 'error',
        timeout: 10000
      });
    }
    finally{
      this.swapModalSaveButton.removeAttribute('aria-busy');
      this.swapModalSaveButton.removeAttribute('disabled');
    }

  }
  beforeShow() {
    super.beforeShow();
    setTimeout(() => {
  
    }, 300);
  }

  beforeHide() {
    super.beforeHide();
    this.resetToListingView();
    setTimeout(() => {
    
    }, 300);
  }
}
customElements.define('swap-coffee-subscription-modal', SwapCoffeeSubscriptionModal);

class ManageSingleSubscription extends ModalElement {
  constructor() {
    super();
    this.grindPreferenceSelector = this.querySelector('grind-preference-selector');
    this.saveSubscriptionButton = this.querySelector('[data-save-subscription-button]');
    this.cancelSubscriptionButton = this.querySelector('[data-cancel-subscription-button]');
  }

  connectedCallback() {
    super.connectedCallback();
    this.cancelSubscriptionButton.addEventListener('click', this.onCancelSubscriptionButtonClick.bind(this));
  }
  async onCancelSubscriptionButtonClick(event) {
    event.preventDefault();
    
    try {
      let response = await recharge.customer.getActiveChurnLandingPageURL(window.rechargeSession,this.subscriptionData.id, window.location.href);
      window.location.href = response;
      // let userConsent = confirm("Are you sure you want to cancel subscription?");
      // if(userConsent){
      //   await recharge.subscription.cancelSubscription(window.rechargeSession, parseInt(this.subscriptionData.id),{cancellation_reason:'Customer requested'});
      //   this.hide();
      //   document.dispatchEvent(new CustomEvent('update:subscriptionDashboard'));
      // }
    } catch (error) {
      console.log(error,"error");
    }
    
  }

  initEvents() {
    this.querySelectorAll('input[type="radio"]').forEach(item => {
      item.addEventListener('change', this.onRadioChange.bind(this));
    });
    

  }
  async onSaveSubscriptionButtonClick(event) {
    this.saveSubscriptionButton.setAttribute('aria-busy', 'true');
    this.saveSubscriptionButton.classList.add('disabled');
    this.saveSubscriptionButton.setAttribute('disabled', 'true');
    const variant_id = this.selectedValueObject.variant.variant_id;
    const order_interval_frequency = this.selectedValueObject.order_interval_frequency.order_interval_frequency;
    const orderIntervalUnit = this.selectedValueObject.order_interval_frequency.orderIntervalUnit;
    const properties = this.subscriptionProperties.map(item => {
      return {
        ...item,
        value: this.grindPreferenceSelector.grindPreferenceValue
      }
    });
    try {
      await recharge.subscription.updateSubscription(window.rechargeSession, parseInt(this.subscriptionData.id), {
        external_variant_id: {ecommerce: String(variant_id)},
        order_interval_frequency: order_interval_frequency,
        charge_interval_frequency: order_interval_frequency,
        order_interval_unit: orderIntervalUnit
      },{commit: true});
      this.hide(); 
      document.dispatchEvent(new CustomEvent('update:subscriptionDashboard'));
    }
    catch(error) {
      console.log(error,"error");
    }
    finally {
      this.saveSubscriptionButton.removeAttribute('aria-busy');
      this.saveSubscriptionButton.classList.remove('disabled');
      this.saveSubscriptionButton.removeAttribute('disabled');
    }
  }
  onRadioChange(event) {
    console.log(this.onLoadsSelectedValueObject,"this.onLoadsSelectedValueObject");
    const variant_id = parseInt(this.querySelector('[data-subscription-variant-list]').querySelector('input[type="radio"]:checked').getAttribute('data-option-value-id'));
    const variant_title = this.querySelector('[data-subscription-variant-list]').querySelector('input[type="radio"]:checked').value;
    const variant_price = this.querySelector('[data-subscription-variant-list]').querySelector('input[type="radio"]:checked').getAttribute('data-variant-price');
    const order_interval_frequency = parseInt(this.querySelector('[data-subscription-frequency-list]').querySelector('input[type="radio"]:checked').value);
    const orderIntervalPlanId = this.querySelector('[data-subscription-frequency-list]').querySelector('input[type="radio"]:checked').getAttribute('data-option-value');
    const orderIntervalUnit = this.querySelector('[data-subscription-frequency-list]').querySelector('input[type="radio"]:checked').getAttribute('data-plan-unit');
    this.querySelector('[data-swape-total-order]').textContent = theme.Currency.formatMoney(variant_price, theme.settings.moneyFormat);
    this.selectedValueObject = {
      variant:{variant_id,variant_title},
      order_interval_frequency:{order_interval_frequency,orderIntervalPlanId,orderIntervalUnit}
    }
    if(this.selectedValueObject.variant.variant_title == this.onLoadsSelectedValueObject.variant_title && this.selectedValueObject.order_interval_frequency.order_interval_frequency == this.onLoadsSelectedValueObject.order_interval_frequency){
      this.saveSubscriptionButton.setAttribute('disabled', 'true');
      this.saveSubscriptionButton.classList.add('disabled');
    }
    else{
      this.saveSubscriptionButton.removeAttribute('disabled');
      this.saveSubscriptionButton.classList.remove('disabled');
    }
    
    console.log(this.selectedValueObject,"this.selectedValueObject");
  }
  renderDataOnModal() {
    const { price ,product_title ,variant_title ,order_interval_frequency,order_interval_unit , propet } = this.subscriptionData;
    this.subscriptionProperties = this.subscriptionData.properties;
    this.onLoadsSelectedValueObject = {
      variant_title,
      order_interval_frequency
    }
    const externalProductId = this.subscriptionData.external_product_id.ecommerce;
    const selectedVariantId = this.subscriptionData.external_variant_id.ecommerce;
 
 
    if(this.subscriptionData){
      // this.querySelector('[data-subscription-image]').src = `${this.productObject.images[0].original}?width=350`;
      this.querySelectorAll('[data-subscription-title]').forEach(item => {
        item.textContent = product_title;
      });
      this.querySelector('[data-subscription-price]').textContent = theme.Currency.formatMoney(price, theme.settings.moneyFormat);
      this.querySelector('[data-subscription-variant]').textContent = variant_title;
      this.querySelector('[data-subscription-frequency]').textContent = 'Every ' + order_interval_frequency + ' ' + order_interval_unit;
    }
    this.querySelector('[data-subscription-variant-list]').innerHTML = this.renderVariantSwatches(this.productObject.variants, this.subscriptionData.id,externalProductId, selectedVariantId );
    this.querySelector('[data-subscription-frequency-list]').innerHTML = this.renderFrequencySwatches(this.productObject.plans, this.subscriptionData.id,externalProductId, order_interval_frequency );
    if(this.productObject){
      this.querySelector('[data-subscription-image]').src = `${this.productObject.images[0].original}?width=350`;
      this.querySelector('[data-swape-total-order]').textContent = theme.Currency.formatMoney(this.subscriptionData.price, theme.settings.moneyFormat);
    }
    let coffeeBrew = null
    if(this.productObject.tags.includes('filter')){
      coffeeBrew = 'filter';
      this.coffeeBrew = 'filter';
      this.grindPreferenceSelector.setAttribute('data-brew-type', 'filter');
    }
    else if(this.productObject.tags.includes('espresso')){
      coffeeBrew = 'espresso';
      this.coffeeBrew = 'espresso';
      this.grindPreferenceSelector.setAttribute('data-brew-type', 'espresso');
    }
    else{
      coffeeBrew = null;
    }
    if(coffeeBrew == 'filter'){
      this.groundOptionList = "French Press,Pour Over,Drip,Aeropress".split(',');
    }
    else{
      this.groundOptionList = "Espresso".split(',');
    }
    this.saveSubscriptionButton.setAttribute('disabled', 'true');
    this.saveSubscriptionButton.classList.add('disabled');
    this.grindPreferenceSelector.querySelector('select').innerHTML = this.groundOptionList.map(option => `<option value="${option.toLowerCase().replace(' ', '-')}">${option}</option>`).join('');
    this.initEvents();
  }

  renderVariantSwatches(variants, sectionId, productId, selectedVariantId) {
    return `
        ${variants.map((variant, index) => `
          <li>
            <magnet-element class="flex h-full">
              <input
                type="radio"
                class="sr-only"
                id="size-${sectionId}-${productId}-${index}"
                name="variant"
                value="${variant.title}"
                data-option-value="${variant.title}"
                data-option-value-id="${variant.external_variant_id}"
                data-variant-price="${variant.prices[0].unit_price}"
                align-selected=".scroll-area"
                ${variant.external_variant_id == selectedVariantId ? 'checked' : ''}
              />
              <label
                for="size-${sectionId}-${productId}-${index}"
                class="label-swatch text-sm font-formal leading-none cursor-pointer relative"
                title="${variant.title}">
                ${variant.title}
              </label>
            </magnet-element>
          </li>
        `).join("")}
    `;
  }
  renderFrequencySwatches(plans, sectionId, productId, order_interval_frequency) {
    // Sort by sort_order ASC
    const sortedPlans = [...plans].sort((a, b) => a.sort_order - b.sort_order);
  
    return `
      ${sortedPlans.map((plan, index) => { 
        if(plan.external_plan_group_id == null){
          return '';
        }
        return `<li>
            <magnet-element class="flex h-full">
              <input
                type="radio"
                class="sr-only"
                id="frequency-${sectionId}-${productId}-${index}"
                name="frequency"
                value="${plan.subscription_preferences.charge_interval_frequency}"
                data-option-value="${plan.external_plan_id}"
                data-option-value-id="${plan.external_plan_id}"
                data-plan-unit="${plan.subscription_preferences.interval_unit}"
                align-selected=".scroll-area"
                ${plan.subscription_preferences.charge_interval_frequency == order_interval_frequency ? "checked" : ""}
              />
              <label
                for="frequency-${sectionId}-${productId}-${index}"
                class="label-swatch text-sm font-formal leading-none cursor-pointer relative"
                title="${plan.title}">
                ${plan.title}
              </label>
            </magnet-element>
          </li>
        `}).join("")}
    `;
  }

  beforeShow() {
    super.beforeShow();
    setTimeout(() => {
  
    }, 300);
  }

  shouldLock() {
    return true;
  }

  beforeHide() {
    super.beforeHide();
    setTimeout(() => {
    
    }, 300);
  }
}
customElements.define('manage-single-subscription', ManageSingleSubscription);

if (!customElements.get('subscription-edit-modal')) {
  customElements.define(
    'subscription-edit-modal',
    class SubscriptionEditModal extends ModalElement {
      constructor() {
        super();

        if (!theme.config.isTouch || Shopify.designMode) {
          this.init();
        }
        else {
          new theme.initWhenVisible(theme.utils.throttle(this.init.bind(this)));
        }
        this.accountEditWrapper = this.querySelector('[data-account-edit-wrapper]')?this.querySelector('[data-account-edit-wrapper]'):null;
        this.accountSwapPlaylistWrapper = this.querySelector('[data-account-swap-playlist-wrapper]')?this.querySelector('[data-account-swap-playlist-wrapper]'):null;
        this.switchToSwapPlaylistButton = this.querySelector('[data-swap-playlist-block]')?this.querySelector('[data-swap-playlist-block]'):null;
        this.swapPlaylistBackButton = this.querySelector('[data-swap-playlist-back-button]')?this.querySelector('[data-swap-playlist-back-button]'):null;
        this.swapSubscriptionButton = this.querySelector('[data-swap-subscription-button]')?this.querySelector('[data-swap-subscription-button]'):null;
        this.playlistData = null;
        this.defaultSelection = {
          quantity: 2,
          frequency: 2,
          espresso: 0,
          filter: 0,
        }
        this.onChangeSelection = {...this.defaultSelection};

        this.quantityBlockSelector = this.querySelector('volume-quantity-changer');
        this.frequencyBlockSelector = this.querySelector('.frequency-radio-list');
        this.quantityBlockSelector.addEventListener('volume-quantity:change', this.onQuantityChange.bind(this));
        // this.accountEditWrapper.querySelectorAll('input[type="radio"]').forEach(item => {
        //   item.addEventListener('change', this.onRadioChange.bind(this));
        // });
        this.cancelSubscriptionButton = this.querySelector('[data-cancel-subscription-button]')?this.querySelector('[data-cancel-subscription-button]'):null;
        if(this.cancelSubscriptionButton){
          this.cancelSubscriptionButton.addEventListener('click', this.onCancelSubscriptionButtonClick.bind(this));
        }
        
        this.querySelectorAll('.ca-playlist-listing-item input[type="radio"]').forEach(item => {
          item.addEventListener('change', this.onPlaylistItemClick.bind(this));
        });
        this.frequencyBlockSelector.addEventListener('change', this.onFrequencyChange.bind(this));
        this.saveSwapPlaylistButton = this.querySelector('[data-save-swap-playlist]')?this.querySelector('[data-save-swap-playlist]'):null;
        if(this.saveSwapPlaylistButton){
          this.saveSwapPlaylistButton.addEventListener('click', this.onSaveSwapPlaylistButtonClick.bind(this));
        }
        // range input
        this.rangeInput = this.querySelector('[data-range-input]');
        this.espresoTextBlock = this.querySelector('[data-espresso-count]');
        this.filterTextBlock = this.querySelector('[data-filter-count]');
        this.max = parseInt(this.rangeInput.getAttribute('max'));
        this.rangeInput.addEventListener('change', (e) => {
          const filterValue = parseInt(e.target.value);
          const espressoValue = parseInt(this.max - filterValue);
          this.espresoTextBlock.textContent = espressoValue;
          this.filterTextBlock.textContent = filterValue;
          this.espressoCount = espressoValue;
          this.filterCount = filterValue;
          this.onChangeSelection = {...this.onChangeSelection,espresso:espressoValue,filter:filterValue,quantity:this.max};
          this.updateChanges()
        });
      }
      onFrequencyChange(event) {
        this.frequency = parseInt(event.target.value);
        this.onChangeSelection = {...this.onChangeSelection,frequency:this.frequency};
        this.updateChanges()
      }
      onQuantityChange(event) {
        this.max = parseInt(event.detail.quantity);
        this.rangeInput.setAttribute('max', this.max);
        this.rangeInput.dispatchEvent(new Event('change')); 
        this.quantity = parseInt(event.detail.quantity);
        this.onChangeSelection = {...this.onChangeSelection,quantity:this.quantity};
        this.updateChanges()
      }
      connectedCallback() {
        super.connectedCallback();
        this.switchToSwapPlaylistButton.addEventListener('click', this.onSwitchToSwapPlaylistButtonClick.bind(this));
        this.swapPlaylistBackButton.addEventListener('click', this.onSwapPlaylistBackButtonClick.bind(this));
        this.swapSubscriptionButton.addEventListener('click', this.onSwapSubscriptionButtonClick.bind(this));
      }
      updateChanges(event) {
        if(this.playlistData.playlist_handle == "mixtape"){
          try{
            const {bundleSelections,bundleObject,playlistData,totalProductPrice,selectionProductListing,discountedPrice,bundleProduct,sellingPlan,externalCollectionId} = window.playlistUtilities.getDynamicMultiPlaylistBundleSelection(this.playlistData.playlist_handle,this.onChangeSelection.quantity,{espresso:this.espressoCount,filter:this.filterCount},`${this.frequency} week`,this.noDecaf);
            this.updateSubscriptionDataCard(bundleObject,discountedPrice,selectionProductListing,externalCollectionId);
          }
          catch(error){
            console.log(error,"error");
            const {bundleSelections,bundleObject,playlistData,totalProductPrice,selectionProductListing,discountedPrice,bundleProduct,sellingPlan,externalCollectionId} = window.playlistUtilities.getDynamicBundleSelection(this.playlistData.playlist_handle,this.onChangeSelection.quantity,{espresso:this.espressoCount,filter:this.filterCount},`${this.frequency} week`,this.noDecaf);
            this.updateSubscriptionDataCard(bundleObject,discountedPrice,selectionProductListing,externalCollectionId);
          }
        
        }
        else{
          const {bundleSelections,bundleObject,playlistData,totalProductPrice,selectionProductListing,discountedPrice,bundleProduct,sellingPlan,externalCollectionId} = window.playlistUtilities.getStaticBundleSelection(this.playlistData.playlist_handle,this.onChangeSelection.quantity,{espresso:this.espressoCount,filter:this.filterCount},`${this.frequency} week`);
          this.updateSubscriptionDataCard(bundleObject,discountedPrice,selectionProductListing,externalCollectionId);
        }
       
      }
      updateSubscriptionDataCard(bundleObject,discountedPrice,selectionProductListing,externalCollectionId){
        this.selectedBundleObject = bundleObject;
        this.selectionProductListing = selectionProductListing;
        this.externalCollectionId = externalCollectionId;
        this.accountEditWrapper.querySelector('[data-swape-total-order]').textContent = theme.Currency.formatMoney(discountedPrice, theme.settings.moneyFormat);
        this.accountEditWrapper.querySelector('[data-edit-playlist-price]').textContent = theme.Currency.formatMoney(discountedPrice, theme.settings.moneyFormat);
        this.accountEditWrapper.querySelector('[data-edit-product-frequency]').textContent = 'Every ' + this.frequency + ' Weeks';
        this.accountEditWrapper.querySelector('[data-swape-total-order]').textContent = theme.Currency.formatMoney(discountedPrice, theme.settings.moneyFormat);
        this.accountEditWrapper.querySelector('[data-edit-product-variant]').textContent = this.quantity + ' bags (300g)';
        // this.accountEditWrapper.querySelector('[data-shipping-type]').textContent = this.subscriptionCharges.shipping_lines[0].code;
        this.accountEditWrapper.querySelector('[data-edit-product-frequency').textContent = 'Every ' + this.frequency + ' Weeks';
        if(this.defaultSelection.quantity == this.quantity && this.defaultSelection.frequency == this.frequency && this.defaultSelection.espresso == this.espressoCount && this.defaultSelection.filter == this.filterCount){
          this.swapSubscriptionButton.setAttribute('disabled', 'true');
        }
        else{
          this.swapSubscriptionButton.removeAttribute('disabled');
          
        }
      }
      async onCancelSubscriptionButtonClick(event) {
        event.preventDefault();
        this.cancelSubscriptionButton.setAttribute('aria-busy', 'true');
        this.cancelSubscriptionButton.classList.add('disabled');
        this.cancelSubscriptionButton.setAttribute('disabled', 'true');
        try{
          let response = await recharge.customer.getActiveChurnLandingPageURL(window.rechargeSession,this.subscriptionData.id, window.location.href);
          window.location.href = response;
          // let userConsent = confirm("Are you sure you want to cancel subscription?");
          // if(userConsent){
          //   await recharge.subscription.cancelSubscription(window.rechargeSession,parseInt(this.subscriptionData.id),{cancellation_reason:'Customer requested'});
          //   document.dispatchEvent(new CustomEvent('update:subscriptionDashboard'));
          //   this.hide();
          // }
        }
        catch(error){
          console.log(error,"error");
        }
        finally{
          this.cancelSubscriptionButton.setAttribute('aria-busy', 'false');
          this.cancelSubscriptionButton.classList.remove('disabled');
          this.cancelSubscriptionButton.removeAttribute('disabled');
        }
      }
      updateSwapPlaylistCard(){
        const subscriptionProperties = this.getOnloadProperties();
        const isCuratedPlaylist = subscriptionProperties.find(item => item.name == 'seasonal_order')?.value;
        const espressoCount = parseInt(subscriptionProperties.find(item => item.name == 'espresso')?.value);
        const filterCount = parseInt(subscriptionProperties.find(item => item.name == 'filter')?.value);
        const quantity = espressoCount + filterCount;
        if(quantity == 0){
          return;
        }

        if(this.accountSwapPlaylistWrapper){
          window.all_playlists.forEach(item => {
            if(item.is_curated_playlist == false){
              if(item.playlist_handle == 'mixtape'){
                const {totalProductPrice,discountedPrice} = window.playlistUtilities.getDynamicBundleSelection(item.playlist_handle,String(quantity),{espresso:espressoCount,filter:filterCount},`${this.subscriptionData.order_interval_frequency} week`);
                const itemPlaylist =this.accountSwapPlaylistWrapper.querySelector(`[data-playlist-item="${item.playlist_handle}"]`);
              
                itemPlaylist.setAttribute('data-dicount-price',parseInt(discountedPrice));
                itemPlaylist.querySelector('[data-item-playlist-discounted-price]').textContent = theme.Currency.formatMoney(parseInt(discountedPrice), theme.settings.moneyFormat);
                itemPlaylist.querySelector('[data-item-playlist-price]').textContent = theme.Currency.formatMoney(totalProductPrice, theme.settings.moneyFormat);
              } 
              else{
                const {totalProductPrice,discountedPrice} = window.playlistUtilities.getStaticBundleSelection(item.playlist_handle,String(quantity),{espresso:espressoCount,filter:filterCount},`${this.subscriptionData.order_interval_frequency} week`);
                const itemPlaylist =this.accountSwapPlaylistWrapper.querySelector(`[data-playlist-item="${item.playlist_handle}"]`);
                itemPlaylist.setAttribute('data-dicount-price',discountedPrice);
                itemPlaylist.querySelector('[data-item-playlist-discounted-price]').textContent = theme.Currency.formatMoney(discountedPrice, theme.settings.moneyFormat);
                itemPlaylist.querySelector('[data-item-playlist-price]').textContent = theme.Currency.formatMoney(totalProductPrice, theme.settings.moneyFormat);
              }
            }
          });
        }
      }
      getRandomBrewData(quantity) {
        const types = ["filter", "espresso"];
        const result = { filter: 0, espresso: 0 };
      
        for (let i = 0; i < quantity; i++) {
          // pick random type each time
          const randomType = types[Math.floor(Math.random() * types.length)];
          result[randomType] += 1;
        }
      
        return result;
      }

      getOnloadProperties(){
           // subscriptionProperties Logic
           let subscriptionProperties = null;
           if( this.subscriptionData.properties.find(item => (item.name == '_subscriptionData' || item.name == 'subscriptionData'))){
             subscriptionProperties = JSON.parse(this.subscriptionData.properties.find(item => item.name == '_subscriptionData').value);
           }
           else{
             subscriptionProperties = this.subscriptionData.properties;
           }
           if(subscriptionProperties == null){
             let defCount = 0;
             this.subscriptionData.include.bundle_selections.items.forEach(item => {
               defCount = defCount + item.quantity;
             });
             const espressoCount = defCount;
             const filterCount = defCount - espressoCount;
            
             const playlistData = window.playlistUtilities.getPlaylistDataByBundleProductId(this.subscriptionData.include.bundle_product.external_product_id);
          
             
              subscriptionProperties = [
               { name: 'espresso', value: String(espressoCount) },
               { name: 'filter', value: String(filterCount) },
               { name: 'playlist_handle', value: playlistData.playlist_handle },
               { name: 'grind_filter', value: 'whole-beans' },
               { name: 'grind_espresso', value: 'whole-beans' },
               { name: 'weight', value: '300g' }
             ];
             this.subscriptionProperties = subscriptionProperties;
           }
           return subscriptionProperties;
      }
      
      renderPlaylistData(){
        // update playlist input data
        //======== Prepare Data for Update Subscription =========
        const subscriptionProperties = this.getOnloadProperties();
     
        //======== End of Prepare Data for Update Subscription =========

        // data for update subscription
        let espressoCount = parseInt(subscriptionProperties.find(item => item.name == 'espresso')?.value);
        let filterCount = parseInt(subscriptionProperties.find(item => item.name == 'filter')?.value);
        const isCuratedPlaylist = subscriptionProperties.find(item => item.name == 'is_curated_playlist')?.value;
        if(espressoCount == 0 && filterCount == 0){
          const {filter:filterCountFromSubscription,espresso:espressoCountFromSubscription} = window.subscriptionHelper.getFilterAndEspressoCountFromSubscription(this.subscriptionData);
          filterCount = filterCountFromSubscription;
          espressoCount = espressoCountFromSubscription;
        }

        this.espressoCount = espressoCount;
        this.filterCount = filterCount;
        this.rangeInput.value = filterCount; 
        
        this.espresoTextBlock.textContent = espressoCount;
        this.filterTextBlock.textContent = filterCount;

        this.max = parseInt(filterCount + espressoCount);
        this.rangeInput.setAttribute('max', this.max);
        
        this.quantityBlockSelector.querySelector(`input[value="${filterCount + espressoCount}"]`)?.click(); 
        this.frequencyBlockSelector.querySelector(`input[value="${this.subscriptionData.order_interval_frequency}"]`)?.click();
        this.frequency = this.subscriptionData.order_interval_frequency;

        this.defaultSelection = {
          quantity: this.subscriptionData.include.bundle_selections.items_count,
          frequency: this.subscriptionData.order_interval_frequency,
          espresso: espressoCount,
          filter: filterCount,
        }
        this.quantity = filterCount + espressoCount;
        this.onChangeSelection = {...this.defaultSelection};
        //======== End of data for update subscription =========
        
        // update subscription block
        this.querySelector('[data-swape-total-order]').textContent = theme.Currency.formatMoney(this.subscriptionCharges.total_price, theme.settings.moneyFormat);

        //======== End of update subscription block =========

        // Update Playlist Block
        if(this.playlistData){
          this.setAttribute('data-playlist-name',this.playlistData.playlist_handle);
          // update edit subscripiton block
          this.accountEditWrapper.querySelector('[data-edit-playlist-image]').src = this.playlistData.playlist_default_image;
          this.accountEditWrapper.querySelectorAll('[data-edit-playlist-title]').forEach(item => {
            item.textContent = this.playlistData.playlist_title;
          });
          this.updateChanges();
          if(this.accountSwapPlaylistWrapper.querySelector(`input[value="${this.playlistData.playlist_handle}"]`)){
            this.accountSwapPlaylistWrapper.querySelector(`input[value="${this.playlistData.playlist_handle}"]`).click();
          }
          if(this.querySelector('[data-playlist-frequency]')){
            this.querySelector('[data-playlist-frequency]').textContent =  this.subscriptionData.order_interval_frequency;
          }
            this.updateSwapPlaylistCard();
          if(this.accountSwapPlaylistWrapper.querySelector('.ca-playlist-listing-item[data-playlist-item="' + this.playlistData.playlist_handle + '"]')){
            this.accountSwapPlaylistWrapper.querySelector('.ca-playlist-listing-item[data-playlist-item="' + this.playlistData.playlist_handle + '"]').click();
          }
          setTimeout(() => {
            document.querySelector('[data-save-swap-playlist]').setAttribute('disabled', '');
          }, 100);
          this.accountSwapPlaylistWrapper.querySelector('[data-playlist-discounted-price]').textContent = theme.Currency.formatMoney(this.subscriptionCharges.total_price, theme.settings.moneyFormat);
        }
      }
      onPlaylistItemClick(event) {
        const playlistItem = event.target.closest('.ca-playlist-listing-item');
        const discountedPrice = playlistItem.getAttribute('data-dicount-price');
        this.selectedPlaylistItem = playlistItem.getAttribute('data-playlist-item');
        if(this.playlistData.playlist_handle == this.selectedPlaylistItem){
          document.querySelector('[data-save-swap-playlist]').setAttribute('disabled', '');
        }
        else{
          document.querySelector('[data-save-swap-playlist]').removeAttribute('disabled');
        }
        this.accountSwapPlaylistWrapper.querySelector('[data-playlist-discounted-price]').textContent = theme.Currency.formatMoney(discountedPrice, theme.settings.moneyFormat);
      }
      async onSwapSubscriptionButtonClick(event) {
        const subscriptionProperties = this.getOnloadProperties();
        this.swapSubscriptionButton.setAttribute('aria-busy', 'true');
        this.swapSubscriptionButton.classList.add('disabled');
        this.swapSubscriptionButton.removeAttribute('disabled');
      
        const frequency = this.frequencyBlockSelector.querySelector('input[type="radio"]:checked').value;
        
        const collectionId = this.externalCollectionId;
        const externalVariantId = this.selectedBundleObject.externalVariantId;
        // if subscritpionDataProperties Found
        subscriptionProperties.find((item)=>{
          if(item.name == '_subscriptionData'){
            this.subscriptionProperties = JSON.parse(item.value);
            subscriptionProperties = JSON.parse(item.value);
          }
        })
        const newProperties = window.subscriptionHelper.updateProperties(subscriptionProperties, {external_collection_id: String(collectionId), external_variant_id: String(externalVariantId),seasonal_order:"false", espresso: JSON.stringify(this.espressoCount), filter: JSON.stringify(this.filterCount)});

        let grindPreferenceArray = [];
        let products_sku = [];
        const grindEspressoValue = subscriptionProperties.find(property => property.name == 'grind_espresso')?.value;
        const grindFilterValue = subscriptionProperties.find(property => property.name == 'grind_filter')?.value;

        this.selectionProductListing.forEach(item => {
          if(item.tags.includes('espresso')){
            item.variants.forEach(variant => {
              if(variant.title == "300g" || variant.title == "300G" || variant.title == "300 G" || variant.title == "300 g"){
                grindPreferenceArray.push({variantId:variant.id,grind_Value:grindEspressoValue});
                if(variant.sku){
                  products_sku.push({sku:variant.sku,grindType:grindEspressoValue});
                }
              }
            });
          }
          else{
            item.variants.forEach(variant => {
              if(variant.title == "300g" || variant.title == "300G" || variant.title == "300 G" || variant.title == "300 g"){
                grindPreferenceArray.push({variantId:variant.id,grind_Value:grindFilterValue});
                if(variant.sku){
                  products_sku.push({sku:variant.sku,grindType:grindFilterValue});
                }
              }
            });
          }
        });
        
        const UpdateNewProperties =  window.subscriptionHelper.updateProperties(newProperties, {grind_preference_array: JSON.stringify(grindPreferenceArray),products_sku: JSON.stringify(products_sku)});
       
        const isValid = await recharge.bundle.validateBundle(this.selectedBundleObject);
        if(!isValid){
          this.querySelector('[data-swap-playlist-error-message]').textContent = 'Invalid Bundle';
          this.querySelector('[data-swap-playlist-error-message]').removeAttribute('hidden');
          this.swapSubscriptionButton.setAttribute('aria-busy', 'false');
          this.swapSubscriptionButton.classList.remove('disabled');
          this.swapSubscriptionButton.removeAttribute('disabled');
          alert('Invalid Bundle');
          return;
        }
        try{
          const response = await recharge.bundle.updateBundle(
            window.rechargeSession,
            this.subscriptionData.id,
            window.playlistUtilities.convertBundleObjectToRechargeBundleObject(this.selectedBundleObject),
           { order_interval_frequency: parseInt(frequency) },
            { commit: true }
          );
          console.log(response,"update Bundle Response");
          const response2 = await recharge.subscription.updateSubscription(window.rechargeSession, this.subscriptionData.id, {charge_interval_frequency:parseInt(frequency), order_interval_frequency: parseInt(frequency),order_interval_unit: 'week', properties: UpdateNewProperties },{ commit: true });
          console.log(response2,"response2");
    
          const response3 = await recharge.address.updateAddress(window.rechargeSession, this.subscriptionData.address_id, {
            order_attributes:
            [{name:'_subscriptionData',value:JSON.stringify(UpdateNewProperties)}]
          }); 
          const oneTimeProducts = await this.getOneTimeProductsScheduledOrders(response2.next_charge_scheduled_at);
          const response4 = await this.updateAllOneTimeProductsScheduledOrders(oneTimeProducts);
          console.log(response4,"response4");
          console.log(response3,"response3");
          // window.location.reload();
          document.dispatchEvent(new CustomEvent('update:subscriptionDashboard'));
          this.hide();
        }
        catch(error){
          console.log(error,"error");
          this.querySelector('[data-swap-playlist-error-message]').textContent = error.message;
          this.querySelector('[data-swap-playlist-error-message]').classList.remove('hidden');
        }
        finally{
          this.swapSubscriptionButton.setAttribute('aria-busy', 'false');
          this.swapSubscriptionButton.classList.remove('disabled');
          this.swapSubscriptionButton.removeAttribute('disabled');
        }
      }
      async onSaveSwapPlaylistButtonClick(event) {
        const subscriptionProperties = this.getOnloadProperties();
        console.log("testingtinkgnsk")
        this.saveSwapPlaylistButton.setAttribute('aria-busy', 'true');
        this.saveSwapPlaylistButton.classList.add('disabled');
        this.saveSwapPlaylistButton.removeAttribute('disabled');

        let espressoCount = parseInt(subscriptionProperties.find(item => item.name == 'espresso')?.value);
        let filterCount = parseInt(subscriptionProperties.find(item => item.name == 'filter')?.value);
     
        if(espressoCount == 0 && filterCount == 0){
          
          const {filter:filterCountFromSubscription,espresso:espressoCountFromSubscription} = window.subscriptionHelper.getFilterAndEspressoCountFromSubscription(this.subscriptionData);
          espressoCount = espressoCountFromSubscription;
          filterCount = filterCountFromSubscription;
        }
        const totalCount = filterCount + espressoCount;
        const newProperties = window.subscriptionHelper.updateProperties(subscriptionProperties, {playlist_handle: this.selectedPlaylistItem});

        let bundleObjectFinal = null;
        let productListingFinal = null;
        let playlistExternalCollectionId = null;
       if(this.selectedPlaylistItem == 'mixtape'){
        try{
          const {bundleObject,selectionProductListing,externalCollectionId} = window.playlistUtilities.getDynamicMultiPlaylistBundleSelection(this.selectedPlaylistItem,totalCount,{filter: filterCount, espresso: espressoCount},parseInt(this.subscriptionData.order_interval_frequency) + ' week',this.noDecaf);
          bundleObjectFinal = bundleObject;
          productListingFinal = selectionProductListing;
          playlistExternalCollectionId = String(externalCollectionId);
          console.log("Dynamic Multi Playlist Bundle Selection Success");
        }
        catch(error){
          console.log("Dynamic Multi Playlist Bundle Selection Error");
          const {bundleObject,selectionProductListing,externalCollectionId} = window.playlistUtilities.getDynamicBundleSelection(this.selectedPlaylistItem,totalCount,{filter: filterCount, espresso: espressoCount},parseInt(this.subscriptionData.order_interval_frequency) + ' week',this.noDecaf);
          bundleObjectFinal = bundleObject;
          productListingFinal = selectionProductListing;
          playlistExternalCollectionId = String(externalCollectionId);
          console.log(error,"error");
        }
        
       }
       else{
        const {bundleObject,selectionProductListing ,externalCollectionId} = window.playlistUtilities.getStaticBundleSelection(this.selectedPlaylistItem,totalCount,{filter: filterCount, espresso: espressoCount},parseInt(this.subscriptionData.order_interval_frequency) + ' week');
        bundleObjectFinal = bundleObject;
        productListingFinal = selectionProductListing;
        playlistExternalCollectionId = String(externalCollectionId);
       }
        let rechargeBundleObject = window.playlistUtilities.convertBundleObjectToRechargeBundleObject(bundleObjectFinal);
        let totalProductCount = rechargeBundleObject.items.reduce((acc,item) => acc + item.quantity, 0);
       

        let grindPreferenceArray = [];
        let products_sku = [];
        const grindEspressoValue = subscriptionProperties.find(property => property.name == 'grind_espresso')?.value;
        const grindFilterValue = subscriptionProperties.find(property => property.name == 'grind_filter')?.value;
        productListingFinal.forEach(item => {
          if(item.tags.includes('espresso')){
            item.variants.forEach(variant => {
              if(variant.title == "300g" || variant.title == "300G" || variant.title == "300 G" || variant.title == "300 g"){
                grindPreferenceArray.push({variantId:variant.id,grind_Value:grindEspressoValue});
                if(variant.sku){
                  products_sku.push({sku:variant.sku,grindType:grindEspressoValue});
                }
              }
            });
          }
          else{
            item.variants.forEach(variant => {
              if(variant.title == "300g" || variant.title == "300G" || variant.title == "300 G" || variant.title == "300 g"){
                grindPreferenceArray.push({variantId:variant.id,grind_Value:grindFilterValue});
                if(variant.sku){
                  products_sku.push({sku:variant.sku,grindType:grindFilterValue});
                }
              }
            });
          }
        });
        const UpdateNewProperties =  window.subscriptionHelper.updateProperties(newProperties, {espresso: String(espressoCount), filter: String(filterCount), external_variant_id:rechargeBundleObject.external_variant_id.ecommerce,external_collection_id: playlistExternalCollectionId,grind_preference_array: JSON.stringify(grindPreferenceArray),products_sku: JSON.stringify(products_sku)});
        
        if(totalProductCount < this.subscriptionData.include.bundle_selections.items_count){
          this.querySelector('[data-swap-playlist-error-message]').textContent = 'No Product Found Based on the your selection';
          this.querySelector('[data-swap-playlist-error-message]').removeAttribute('hidden');
          this.saveSwapPlaylistButton.setAttribute('aria-busy', 'false');
          this.saveSwapPlaylistButton.classList.remove('disabled');
          this.saveSwapPlaylistButton.removeAttribute('disabled');
          return;
        }
        try{
          const response = await recharge.bundle.updateBundle(window.rechargeSession,this.subscriptionData.id,rechargeBundleObject,{ order_interval_frequency: parseInt(this.subscriptionData.order_interval_frequency) },{ properties: UpdateNewProperties },{ commit: true });
          const response2 = await recharge.subscription.updateSubscription(window.rechargeSession, this.subscriptionData.id, { properties: UpdateNewProperties },{ commit: true });

          await recharge.address.updateAddress(window.rechargeSession, this.subscriptionData.address_id, {
            order_attributes:
            [{name:'_subscriptionData',value:JSON.stringify(UpdateNewProperties)}]
          }); 
          
          // window.location.reload();
          document.dispatchEvent(new CustomEvent('update:subscriptionDashboard'));
          this.hide();
          this.querySelector('[data-swap-playlist-error-message]').setAttribute('hidden', 'true');
          // document.dispatchEvent(new CustomEvent('update:subscriptionDashboard'));
          // this.hide();
        }
        catch(error){
          const parsedError = JSON.parse(error);
          
          this.querySelector('[data-swap-playlist-error-message]').textContent = parsedError.message;
          this.querySelector('[data-swap-playlist-error-message]').removeAttribute('hidden');

        }
        finally{
          this.saveSwapPlaylistButton.setAttribute('aria-busy', 'false');
          this.saveSwapPlaylistButton.classList.remove('disabled');
          this.saveSwapPlaylistButton.removeAttribute('disabled');
          
        }
      }

      async getOneTimeProductsScheduledOrders(scheduledDate) {
        return this.subscriptionCharges.line_items.filter(item => item.purchase_item_type == 'onetime').map(item => {
          return {
            purchase_item_id: parseInt(item.purchase_item_id),
            scheduled_at: scheduledDate
          }
        });
      }

      async updateAllOneTimeProductsScheduledOrders(items) {
        if (!Array.isArray(items) || items.length === 0) return;
        let promises = [];
        for(let item of items){
          const { purchase_item_id, scheduled_at } = item;
          promises.push(await recharge.onetime.updateOnetime(window.rechargeSession, purchase_item_id, {
            next_charge_scheduled_at: scheduled_at,
          },{commit: true}));
        }
      
        try {
          const results = await Promise.all(promises);
          console.log('✅ All updates completed successfully:', results);
          return results;
        } catch (error) {
          console.error('❌ One or more updates failed:', error);
          return null;
        }
      }

      onSwitchToSwapPlaylistButtonClick(event) {
        this.accountEditWrapper.classList.remove('active');
        this.accountSwapPlaylistWrapper.classList.add('active');
        this.updateSwapPlaylistCard();
      }
      onSwapPlaylistBackButtonClick(event) {
        this.accountEditWrapper.classList.add('active');
        this.accountSwapPlaylistWrapper.classList.remove('active');
      }

      get shouldLock() {
        return true;
      }

      beforeShow() {
        super.beforeShow();
        setTimeout(() => {
      
        }, 300);
      }

      beforeHide(){
        super.beforeHide();
        setTimeout(() => {
          document.body.classList.remove(this.classes.open);
        },100);
      }

      init() {
        if (this.initialized) return;
        this.initialized = true;

      }

      

      load(delay) {
        if (Shopify.designMode) return;

        setTimeout(() => this.show(), delay * 1000);
      }
    }
  );
}


if (!customElements.get('edit-grind-type-modal')) {
  customElements.define(
    'edit-grind-type-modal',
    class EditGrindTypeModal extends ModalElement {
      constructor() {
        super();

        if (!theme.config.isTouch || Shopify.designMode) {
          this.init();
        }
        else {
          new theme.initWhenVisible(theme.utils.throttle(this.init.bind(this)));
        }
        this.defaultSelection = {
          grindType: 'whole-beans'
        }
      
      }
      connectedCallback() {
        super.connectedCallback();
        this.saveGrindPreferenceButton = this.querySelector('[data-save-grind-preference]');
        this.grindPreferenceSelector = this.querySelector('grind-preference-selector');
        this.grindPreferenceSelector.addEventListener('grind-preference:change', (e) => {
         
          if(e.detail.grindPreferenceValue == this.defaultSelection.grindType){
            this.saveGrindPreferenceButton.setAttribute('disabled', 'true');
          }
          else{
            this.saveGrindPreferenceButton.removeAttribute('disabled');
          }
        })
        this.saveGrindPreferenceButton.addEventListener('click', () => {
            this.saveGrindPreference();
        });
      }

      onLoadGrindType() {
        this.grindPreferenceSelector.setAttribute('data-brew-type', this.grindType.toLowerCase());
    
        this.defaultSelection = {
          grindType: this.grindType.toLowerCase(),
        }
        if(this.grindType != 'whole-beans'){
          this.grindPreferenceSelector.querySelector(`select`).value = this.grindType;
        }
      }

      get shouldLock() {
        return true;
      }

      beforeShow() {
        super.beforeShow();
        setTimeout(() => {
      
        }, 300);
      }

      beforeHide(){
        super.beforeHide();
        setTimeout(() => {
          document.body.classList.remove(this.classes.open);
        },100);
      }

      init() {
        if (this.initialized) return;
        this.initialized = true;

      }

      load(delay) {
        if (Shopify.designMode) return;

        setTimeout(() => this.show(), delay * 1000);
      }
      async saveGrindPreference() {
        this.saveGrindPreferenceButton.setAttribute('aria-busy', 'true');
        this.saveGrindPreferenceButton.setAttribute('disabled', 'true');
        let subscriptionProperties = null;
        this.subscriptionData.properties.forEach(item => {
          if(item.name == 'subscriptionData' || item.name == '_subscriptionData'){
            subscriptionProperties = JSON.parse(this.subscriptionData.properties);
          }
        });
        if(!subscriptionProperties){
          subscriptionProperties = this.subscriptionData.properties;
        }

        const brew = this.coffeeBrew.toLowerCase();
        const grindPreferenceValue = this.querySelector('grind-preference-selector').grindPreferenceValue.toLowerCase();
        
        let grindPreferenceArray = [];
        // grind Preference Array Logic
        if(this.subscriptionData.properties.find(property => property.name == 'grind_preference_array')){
          grindPreferenceArray = JSON.parse(this.subscriptionData.properties.find(property => property.name == 'grind_preference_array')?.value);
        }
      
        if(grindPreferenceArray.length == 0){
          grindPreferenceArray.push({variantId:this.variantId,grind_Value:grindPreferenceValue});
        }
        else{
          if(grindPreferenceArray.find(item => item.variantId == this.variantId)){
            grindPreferenceArray.forEach(item => {
              if(item.variantId == this.variantId) {
                item.grind_Value = grindPreferenceValue;
              }
            });
          }
          else{
            grindPreferenceArray.push({variantId:this.variantId,grind_Value:grindPreferenceValue});
          }
        }
        // If property not found, add it
        let newProperties = subscriptionProperties.map(prop => {
          if (brew === "filter" && prop.name === "grind_filter") {
            return { ...prop, value: grindPreferenceValue }; // update existing
          }
          if (brew === "espresso" && prop.name === "grind_espresso") {
            return { ...prop, value: grindPreferenceValue }; // update existing
          }
          return prop; // keep other properties unchanged
        });
        // Products SKU Logic
        let products_sku = [];
        if(this.subscriptionData.properties.find(property => property.name == 'products_sku')){
          products_sku = JSON.parse(this.subscriptionData.properties.find(property => property.name == 'products_sku')?.value);
        }
        else{
          products_sku = [];
        }
        const variant_300g = this.productObject.variants.find(variant => variant.title == "300g" || variant.title == "300G" || variant.title == "300 G" || variant.title == "300 g");
        if(products_sku.find(item => item.sku == variant_300g.sku)){
          products_sku.forEach(item => {
            if(item.sku == variant_300g.sku){
              item.grindType = grindPreferenceValue;
            }
          });
        }
        else{
          products_sku.push({sku:variant_300g.sku,grindType:grindPreferenceValue});
        }
        //
   

        newProperties = newProperties.filter(item => item.name != 'grind_preference_array');
        newProperties = newProperties.filter(item => item.name != 'products_sku');
        newProperties.push({name:'grind_preference_array',value:JSON.stringify(window.removeDuplicates(grindPreferenceArray,'variantId'))});
        newProperties.push({name:'products_sku',value:JSON.stringify(window.removeDuplicates(products_sku,'sku'))});

        try {
          const response = await recharge.subscription.updateSubscription(window.rechargeSession, this.subscriptionId, { properties: newProperties });
          await recharge.address.updateAddress(window.rechargeSession, this.subscriptionData.address_id, {
            order_attributes:
            [{name:'_subscriptionData',value:JSON.stringify(newProperties)}]
          }); 
          document.dispatchEvent(new CustomEvent('update:subscriptionDashboard'));
          this.saveGrindPreferenceButton.setAttribute('aria-busy', 'false');
          this.saveGrindPreferenceButton.removeAttribute('disabled');
          
          this.hide();
        } catch (error) {
          console.log(error,"error");
          window.notificationEle.updateNotification('Save Grind Preference', 'Something went wrong', {
            type: 'error',
            timeout: 10000
          });
        }
        finally{
          this.saveGrindPreferenceButton.setAttribute('aria-busy', 'false');
          this.saveGrindPreferenceButton.removeAttribute('disabled');
        }
      }
    }
  );
}


if (!customElements.get('playslist-swipe-element')) {
  customElements.define(
    'playslist-swipe-element',
    class PlayslistSwipeElement extends HTMLElement {
      constructor() {
        super();
        this._scrollEndTimer = null;
        this.dataNext = this.getAttribute('data-next');
        this.dataPrev = this.getAttribute('data-prev');
        this.nextButton = document.querySelector(this.dataNext);
        this.prevButton = document.querySelector(this.dataPrev);
      }

      connectedCallback() {
        // Ensure horizontal scroll
        this.style.overflowX = "auto";
        this.style.display = "flex";
        this.style.scrollBehavior = "smooth"; // important for smooth scroll

        // --- scroll listener ---
        this.addEventListener("scroll", () => {
          const scrolled = this.scrollLeft;
          const maxScroll = this.scrollWidth - this.clientWidth;
          const remaining = maxScroll - scrolled;

          if (remaining <= 0) {
            this.nextButton.classList.add("hidden");
            this.prevButton.classList.remove("hidden");
          } else if (scrolled <= 0) {
            this.prevButton.classList.add("hidden");
            this.nextButton.classList.remove("hidden");
          } else {
            this.nextButton.classList.remove("hidden");
            this.prevButton.classList.remove("hidden");
          }

          // debounce for "scroll ended"
          clearTimeout(this._scrollEndTimer);
          this._scrollEndTimer = setTimeout(() => {
            console.log("✅ Scroll ended");
          }, 150);
        });

        // --- next / prev buttons ---
        if (this.nextButton) {
          this.nextButton.addEventListener("click", () => {
            this.scrollBy({
              left: this.clientWidth, // scroll one viewport width
              behavior: "smooth",
            });
          });
        }
        if (this.prevButton) {
          this.prevButton.addEventListener("click", () => {
            this.scrollBy({
              left: -this.clientWidth, // scroll one viewport width backward
              behavior: "smooth",
            });
          });
        }
      }
    }
  );
}


class CustomerFlavourProfile extends HTMLElement {
  constructor() {
    super();
  }
  connectedCallback() {
   this.initEvents();
  }
  initEvents(){
    this.querySelector('[data-profile-playlist-swap-playlist]').addEventListener('click', ()=> {
    const playlistHandle = this.querySelector('[data-profile-playlist-swap-playlist]').getAttribute('data-profile-playlist-swap-playlist');
    const playlistData = window.playlistUtilities.getPlaylistData(playlistHandle);
    const nextSubscriptionOrder = document.querySelector('next-subscription-order');
    if(nextSubscriptionOrder){
      nextSubscriptionOrder.querySelector(`[aria-controls="swap-playlist-trigger"]`).click();
      document.querySelector(`.ca-playlist-listing-item[data-playlist-item="${playlistHandle}"]`)?.click();
    }
    });
  }

  async renderFlavourProfile(){
    await this.fetchFlavourNotes();
    await this.fetchProfileData();
    await this.fetchUniqueTriedCoffee();
    await this.renderProductRecommendations();
    await this.renderPlaylistBasedOnProfileData();
    await this.coffeeStats();
  }

  async fetchFlavourNotes() {
    try{
      const response = await fetch(`/apps/proxy/customer/flavour-preference/${window.customer.id}/customer-tasting-notes`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'},
      });
      const responseData = await response.json();
      this.flavourNotesObject = responseData;
      const flavourNotes = Object.entries(this.flavourNotesObject.customerTastingNotes) // convert to [key, value] pairs
      .sort((a, b) => b[1] - a[1])            // sort by value (descending)
      .slice(0, 3)                   // take first 3
      .map(([key]) => key)           // extract only keys
      .join(", ");
      if(flavourNotes){
        this.querySelector('[data-tasting-notes]').textContent = flavourNotes;
      }
      else{
        this.querySelector('[data-tasting-notes]').textContent = '---';
      }
    }
    catch(error){
      this.querySelector('[data-tasting-notes]').textContent = '---';
    }
  }
  async fetchUniqueTriedCoffee() {
    try{
      const response = await fetch(`/apps/proxy/customer/unique-coffee-tried/${window.customer.id}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'},
      });
      const responseData = await response.json();
      this.uniqueTriedCoffee = responseData.coffee;
      this.querySelector('[data-total-unique-tried-coffee]').textContent = this.uniqueTriedCoffee.length;
    }
    catch(error){
      this.querySelector('[data-total-unique-tried-coffee]').textContent = '---';
    }
  }

  async fetchProfileData() {
    try{
      const response = await fetch(`/apps/proxy/customer/flavour-preference/${window.customer.id}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'},
      });
      const responseData = await response.json();
      const rechargeDashboard = document.querySelector('recharge-dashboard');
      if(rechargeDashboard.activeBundleSubscriptions.length == 0){
        this.querySelector('[data-mood-row]').classList.add('hidden');
      }
      this.profileData = responseData.customerFlavourPreference;
      this.groupedProfileData = this.getGroupedValues(this.profileData);
      if(this.groupedProfileData.archetype.length > 0){
        this.querySelector('[data-favourite-archetypes]').textContent = this.groupedProfileData.archetype.join(", ");
        // current mood logic
        const currentVibePlaylist = this.groupedProfileData.archetype[0];
        const handleisPlaylist = currentVibePlaylist.toLowerCase().replace(/ /g, "-");
        this.flavourProfileMoode = '';
        
        switch(handleisPlaylist){
          case "chocolatey-comforts":
            this.showingPlaylistHandle = "chocolatey-comforts";
            this.flavourProfileMoode = "Smooth, cozy, and reliable — your coffee vibe is all about comfort in every sip.";
            break;
          case "super-funky-adventures":
            this.showingPlaylistHandle = "super-funky-adventures";
            this.flavourProfileMoode = "Bold, vibrant, and unapologetically fruity — you’re here for coffees that make a statement.";
            break;
          case "fruity-classics":
            this.showingPlaylistHandle = "fruity-classics";
            this.flavourProfileMoode = "Bright, juicy, and balanced — your taste buds love a little everyday adventure.";
            break;
          case "dreamy-low-no-cafe":
            this.showingPlaylistHandle = "dreamy-low-no-cafe";
            this.flavourProfileMoode = "Chill, relaxed, and easy-going — you’re all about flavour without the jitters.";
            break;
        }
        this.querySelector('[data-mood]').textContent = this.flavourProfileMoode;
      }
      else{
        this.querySelector('[data-favourite-archetypes]').textContent = '---';
        this.querySelector('[data-mood]').textContent = '---';
      }
    }
    catch(error){
      this.querySelector('[data-favourite-archetypes]').textContent = '---';
    }
  }
  
  renderPlaylistBasedOnProfileData() {
    const rechargeDashboard = document.querySelector('recharge-dashboard');
    if(rechargeDashboard.noSubscriptionAccount){
      this.querySelector('[data-account-playlist-proposed]')?.classList.add('hidden');
      this.classList.add('no-playlist-suggestions');
      return;
    }
    let currentPlaylistHandle = '';
    // let currentPlaylistHandle = rechargeDashboard.currentlyEnjoyingPlaylistData.playlist_handle;

    const nextOrder = document.querySelector('next-subscription-order');
    if(nextOrder){
      currentPlaylistHandle = nextOrder.playlistData?.playlist_handle;
    }

    if(rechargeDashboard.currentlyEnjoyingPlaylistData){
      let recommendPlaylistHandle = '';
      switch(currentPlaylistHandle){
        case "chocolatey-comforts":
          recommendPlaylistHandle = "fruity-classics";
          break;
        case "fruity-classics":
          recommendPlaylistHandle = "super-funky-adventures";
            break;
        case "super-funky-adventures":
          recommendPlaylistHandle = "mixtape";
          break;
        
        case "dreamy-low-no-cafe":
          recommendPlaylistHandle = '';
          break;
        case "mixtape":
          recommendPlaylistHandle = '';
          break;
      }
      if(recommendPlaylistHandle != ''){

        const recommendPlaylistData = window.playlistUtilities.getPlaylistData(recommendPlaylistHandle);
        this.querySelector('[data-account-playlist-proposed]')?.setAttribute('data-playlist-name',recommendPlaylistHandle);
        if(this.querySelector('[data-account-playlist-proposed]')?.classList.contains('hidden')){
          this.querySelector('[data-account-playlist-proposed]')?.classList.remove('hidden');
        }
        this.querySelector('[data-profile-playlist-name]').textContent = recommendPlaylistData.playlist_title;
        this.querySelector('[data-profile-playlist-description]')?.classList.remove('hidden');
        this.querySelector('[data-profile-playlist-description]').innerHTML = recommendPlaylistData.playlist_description;
        this.querySelector('[data-profile-playlist-swap-playlist]')?.classList.remove('hidden');
        this.querySelector('[data-profile-playlist-swap-playlist]').setAttribute('data-profile-playlist-swap-playlist',recommendPlaylistHandle);
        this.querySelector('[data-profile-playlist-swap-playlist-icon]').innerHTML = `<img class="image-fit" src="${recommendPlaylistData.playlist_icon}" alt="${recommendPlaylistData.playlist_title}" />`;
        this.classList.remove('no-playlist-suggestions');
      }
      else{
        this.querySelector('[data-account-playlist-proposed]')?.classList.add('hidden');
        this.classList.add('no-playlist-suggestions');
      }
    }
    else{
      this.querySelector('[data-account-playlist-proposed]')?.classList.add('hidden');
      this.classList.add('no-playlist-suggestions');
    }
    // if(this.groupedProfileData.archetype.length > 0){
    // const currentVibePlaylist = this.groupedProfileData.archetype[0];
    // const handleisPlaylist = currentVibePlaylist.toLowerCase().replace(/ /g, "-");
    // this.showingPlaylistHandle = '';
    // this.flavourProfileMoode = '';
    //   switch(handleisPlaylist){
    //     case "chocolatey-comforts":
    //       this.showingPlaylistHandle = "chocolatey-comforts";
    //       this.flavourProfileMoode = "Smooth, cozy, and reliable — your coffee vibe is all about comfort in every sip.";
    //       break;
    //     case "super-funky-adventures":
    //       this.showingPlaylistHandle = "super-funky-adventures";
    //       this.flavourProfileMoode = "Bold, vibrant, and unapologetically fruity — you’re here for coffees that make a statement.";
    //       break;
    //     case "fruity-classics":
    //       this.showingPlaylistHandle = "fruity-classics";
    //       this.flavourProfileMoode = "Bright, juicy, and balanced — your taste buds love a little everyday adventure.";
    //       break;
    //     case "dreamy-low-no-cafe":
    //       this.showingPlaylistHandle = "dreamy-low-no-cafe";
    //       this.flavourProfileMoode = "Chill, relaxed, and easy-going — you’re all about flavour without the jitters.";
    //       break;
    //   }
    // }
    // if(this.showingPlaylistHandle){
    //   if(this.querySelector('[data-account-playlist-proposed]')){
    //     this.playlistData = window.playlistUtilities.getPlaylistData(this.showingPlaylistHandle);
        
    //   this.querySelector('[data-account-playlist-proposed]').setAttribute('data-playlist-name',this.playlistData.playlist_handle);
    //   this.querySelector('[data-profile-playlist-name]').textContent = this.playlistData.playlist_title;
    //   this.querySelector('[data-profile-playlist-description]').textContent = this.playlistData.playlist_description;
    //   this.querySelector('[data-profile-playlist-swap-playlist]').setAttribute('data-profile-playlist-swap-playlist',this.playlistData.playlist_handle);
    //   this.querySelector('[data-profile-playlist-swap-playlist-icon]').innerHTML = `<img class="image-fit" src="${this.playlistData.playlist_icon}" alt="${this.playlistData.playlist_title}" />`;
    //   this.querySelector('[data-mood').innerHTML = this.flavourProfileMoode;
    //   // this.querySelector('[data-profile-playlist-try-a-sample]').textContent = this.playlistData.;
    //   }
    // }
    // this.querySelector('[data-favourite-archetypes]').textContent = this.groupedProfileData.archetype.join(", ");
  }

  async coffeeStats() {
    const rechargeDashboard = document.querySelector('recharge-dashboard');
    if(rechargeDashboard.noSubscriptionAccount){
      if(this.querySelector('[data-subscription-streak-wrapper]')){
        this.querySelector('[data-subscription-streak-wrapper]').classList.add('hidden');
      }
      if(this.querySelector('[data-total-orders-wrapper]')){
        this.querySelector('[data-total-orders-wrapper]').classList.remove('hidden');
      }
      return;
    }
    else{
      if(rechargeDashboard.customerData){
        rechargeDashboard.customerData.subscription_related_charge_streak;
        if(this.querySelector('[data-subscription-streak-wrapper]')){
          this.querySelector('[data-subscription-streak-wrapper]').classList.remove('hidden');
          this.querySelector('[data-subscription-streak]').textContent = rechargeDashboard.customerData.subscription_related_charge_streak;
        }
      }
    }
  }
  renderFlavourNotes() {
    const flavourNotes = Object.entries(this.flavourNotesObject.customerTastingNotes) // convert to [key, value] pairs
      .sort((a, b) => b[1] - a[1])            // sort by value (descending)
      .slice(0, 3)                   // take first 3
      .map(([key]) => key)           // extract only keys
      .join(", ");
    
    this.querySelector('[data-tasting-notes]').textContent = flavourNotes
    
  }

  getGroupedValues(data) {
    // keys we want to ignore
    const excludeKeys = ["tasting_note_1", "tasting_note_2", "tasting_note_3"];
  
    // group items by key
    const grouped = {};
  
    data.forEach(item => {
      if (excludeKeys.includes(item.key)) return; // skip excluded keys
  
      if (!grouped[item.key]) {
        grouped[item.key] = [];
      }
      grouped[item.key].push(item);
    });
  
    // sort each group by points (desc) and map to values
    for (const key in grouped) {
      grouped[key] = grouped[key]
        .sort((a, b) => b.points - a.points)
        .map(item => item.value);
    }
  
    return grouped;
  }
  async renderProductRecommendations() {
    try {
      const rechargeDashboard = document.querySelector('recharge-dashboard');
      const bundleSubscription = rechargeDashboard.activeBundleSubscriptions[0];
      if(bundleSubscription){
      this.subscriptionData = bundleSubscription;
      }
      else{
        this.subscriptionData = null;
      }
      const response = await fetch(`/apps/proxy/customer/recommended-coffees/${window.customer.id}`)
      const data = await response.json();
      this.recommendationProducts = data.coffee;
      if(this.recommendationProducts.length > 0){
        await this.renderHTMLProductRecommendations(this.recommendationProducts);
      }
      else{
        this.querySelector('[data-product-recommendations-wrapper]').classList.add('hidden');
      }
    } catch (error) {
        console.error("🚨 Error sending request:", error.message);
        this.recommendationProducts = [];
        await this.renderHTMLProductRecommendations(this.recommendationProducts);
    }
  }

  async renderHTMLProductRecommendations(recommendationProducts){
    this.querySelector('[data-product-recommendations]').innerHTML = `
      <hearted-product-recommendations class="product-recommendations-box block relative" ${this.subscriptionData ? `data-subscription-id="${this.subscriptionData.id}"` : ''}>
      <div class="w-full" data-heart-recommendation-list>
        ${this.getProductListingHTML(recommendationProducts)}
      </div>
      <button class="arrow arrow-next">
      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
        <path d="M9.24406 12.3556L13.5996 8.00011M13.5996 8.00011L9.24406 3.64453M13.5996 8.00011L2.39961 8.00011" stroke="#6C6D6D" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>
      </button>
      <button class="arrow arrow-prev disabled" disabled>
      <svg xmlns="http://www.w3.org/2000/svg" width="14" height="12" viewBox="0 0 14 12" fill="none">
        <path d="M5.75594 1.64453L1.40039 6.00004M1.40039 6.00004L5.75594 10.3556M1.40039 6.00004H12.6004" stroke="#6C6D6D" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>
      </button>
      </hearted-product-recommendations>
    `;
      const heartedProductRecommendations = document.querySelector('hearted-product-recommendations');
      if(heartedProductRecommendations){
        heartedProductRecommendations.initSlider();
        setTimeout(() => {
          heartedProductRecommendations.flickity.resize();
        }, 1000);
      }
  }
  getProductListingHTML(products) {
    const productListingHTML = products.slice(0, 5).map((product,index) => {
      const productObject = window.playlistUtilities.getProductObjectByVariantId(product.variantId);
      if(productObject == null){
        return '';
      }
        return `
          <div class="account-dashboard-product-content" data-product-id="${productObject.id}" data-product-index="${index}">
            <div class="w-full card-product-image-wrap relative mt-0 mb-2 w-full">
              <img src="${productObject.featured_image}" alt="${productObject.title}" class="mt-0 mb-0"/>
            </div>
            <div class="text-sm font-bold leading-tight tracking-none heading mb-1 recommend-account-product-title"> ${productObject.title} </div>
              <p class="text-xs mt-0 mb-0"> ${productObject.variants[0].title} </p>
          </div>
        `;
      }).join('');
    return productListingHTML;
  }

}
customElements.define('customer-flavour-profile', CustomerFlavourProfile);

document.addEventListener('update:subscriptionDashboard',() => {
  const rechargeDashboard = document.querySelector('recharge-dashboard');
  rechargeDashboard.storeSetupData();
});

class HeartedProductRecommendations extends HTMLElement {
  constructor() {
    super();
  }
  connectedCallback() {
    const rechargeDashboard = document.querySelector('recharge-dashboard');
    this.oneTimeProductDrawer = document.querySelector('one-time-product-drawer');
    this.subscriptionData = rechargeDashboard.activeBundleSubscriptions[0];
    
    this.querySelector('.arrow-next').addEventListener('click', ()=> {
      this.flickity.next();
    });
    this.querySelector('.arrow-prev').addEventListener('click', ()=> {
      this.flickity.previous();
    });
  }
  initSlider(){
    this.flickity = new Flickity(this.querySelector('[data-heart-recommendation-list]'), {
      accessibility: true,
      draggable: true,
      pageDots: true,
      prevNextButtons: false,
      cellAlign: 'left',
      groupCells: true,
      contain: true,
      resize: true,
      on: {
        ready: ()=> {
          this.querySelectorAll('[data-product-id]').forEach(item => {
            item.addEventListener('click', ()=> {
              const subscriptionId = this.subscriptioId;
              const productId = item.getAttribute('data-product-id');
              const productObject = window.playlistUtilities.getProductObjectById(productId);
              this.oneTimeProductDrawer.productObject = productObject;
              this.oneTimeProductDrawer.subscriptionData = this.subscriptionData;
              this.oneTimeProductDrawer.renderProductDetails();
              this.oneTimeProductDrawer.show();
              this.flickity.resize();
            });
          });
        },
        change:( index ) => {
          const total_slides = this.flickity.slides.length;
        
          if(index == 0){
            this.querySelector('.arrow-prev').classList.add('disabled');
            this.querySelector('.arrow-prev').setAttribute('disabled', 'true');
          }
          else{
            this.querySelector('.arrow-prev').classList.remove('disabled');
            this.querySelector('.arrow-prev').removeAttribute('disabled');
          }
          if(index == total_slides - 1){
            this.querySelector('.arrow-next').classList.add('disabled');
            this.querySelector('.arrow-next').setAttribute('disabled', 'true');
          }
          else{
            this.querySelector('.arrow-next').classList.remove('disabled');
            this.querySelector('.arrow-next').removeAttribute('disabled');
          }
          console.log( 'Slide changed to' + index );
        },
        settle: ()=> {
          this.flickity.resize();
        }
      }
    });
  }
}

customElements.define('hearted-product-recommendations', HeartedProductRecommendations);

class PlaylistCarousel extends HTMLElement {
  constructor() {
    super();
  }
  connectedCallback() {
    if(typeof  this.flickity !== 'undefined'){
      this.flickity.resize();
    }
  }
  triggerPlaylistCarousel(){
    const rechargeDashboard = document.querySelector('recharge-dashboard');
    this.oneTimeProductDrawer = document.querySelector('one-time-product-drawer');
    this.subscriptionData = rechargeDashboard.activeBundleSubscriptions[0];
    this.initSlider();
    this.querySelector('.arrow-next').addEventListener('click', ()=> {
      this.flickity.next();
    });
    this.querySelector('.arrow-prev').addEventListener('click', ()=> {
      this.flickity.previous();
    });
  }
  initSlider(){
    this.flickity = new Flickity(this.querySelector('[data-playlist-carousel]'), {
      accessibility: true,
      draggable: true,
      pageDots: true,
      prevNextButtons: false,
      cellAlign: 'left',
      groupCells: true,
      contain: true,
      resize: true,
      on: {
        ready: ()=> {
          this.querySelectorAll('[data-product-id]')?.forEach(item => {
            item.addEventListener('click', ()=> {
              const productId = item.getAttribute('data-product-id');
              const productObject = window.playlistUtilities.getProductObjectById(productId);
              this.oneTimeProductDrawer.productObject = productObject;
              this.oneTimeProductDrawer.subscriptionData = this.subscriptionData;
              this.oneTimeProductDrawer.renderProductDetails();
              this.oneTimeProductDrawer.show();
            });
          });
          this.flickity.resize();
        },
        settle: ()=> {
          this.flickity.resize();
        }
      }
    });
  }
}

customElements.define('playlist-carousel', PlaylistCarousel);

document.addEventListener('DOMContentLoaded',() => {
  const dashboardTabSelect = document.querySelector('#dashboard-tab-select');
  const accountNavTabsDesktop = document.querySelector('.account-nav-tabs-desktop');
  const accountDashboardContent = document.querySelector('.account-dashboard-content');
  const maitabList = document.querySelector('.main-tab-list');
  if(dashboardTabSelect){
    dashboardTabSelect.addEventListener('change',function() {
      let selectedValue = this.value;
      if(maitabList){
        maitabList.querySelector(`.tab__item[data-index="${selectedValue}"]`).click();
      }
   
      
    });
  }
  if(accountNavTabsDesktop){
    accountNavTabsDesktop.addEventListener('tab:change',(event) => {
      let selectedValue = event.detail.index;
      
      if(selectedValue == 3 || selectedValue == 4){ 
        if(accountDashboardContent){
        accountDashboardContent.classList.add('hide-right-nav');
        accountDashboardContent.querySelector('#TabPanel-1')?.removeAttribute('hidden')
        }
      }
      else{
        if(accountDashboardContent){
          accountDashboardContent.classList.remove('hide-right-nav')
        }
      }
      setTimeout(() => {
        window.dispatchEvent(new Event('resize'));
      }, 300);
    });
  }
});
class OneTimeProductItem extends HTMLElement {
  constructor() {
    super();
  }
  connectedCallback() {
    this.dataRemoveOnetimeProduct = this.querySelector('[data-remove-onetime-product]');
    this.dataEditOnetimeProduct = this.querySelector('[data-edit-onetime-product]');
    this.dataMobileRemoveOnetimeProduct = this.querySelector('[data-mobile-remove-onetime-product]');
    this.dataMobileEditOnetimeProduct = this.querySelector('[data-mobile-edit-onetime-product]');
    this.dataMobileRemoveOnetimeProduct.addEventListener('click', this.mobileRemoveOnetimeProductClick.bind(this));
    this.dataRemoveOnetimeProduct.addEventListener('click', this.onRemoveOnetimeProductClick.bind(this));
    this.subscriptionData = null;
    this.subscriptionId = this.getAttribute('data-subscription-id');
    this.productId = this.getAttribute('data-product-id');
    this.purchaseItemId = this.getAttribute('data-purchase-item-id');
    this.productObject = window.playlistUtilities.getProductObjectById(this.productId);
 
    this.dataEditOnetimeProduct.addEventListener('click', this.onEditOnetimeProductClick.bind(this));
    this.dataMobileEditOnetimeProduct.addEventListener('click', this.onMobileEditOnetimeProductClick.bind(this));
  }
  async onMobileEditOnetimeProductClick(event) {
    event.preventDefault();
    const addOnOneTimeEditDrawer = document.querySelector('add-on-one-time-edit-drawer');
    const productId = this.dataMobileEditOnetimeProduct.getAttribute('data-product-id');
    this.purchaseItemId = this.dataMobileEditOnetimeProduct.getAttribute('data-purchase-item-id');
    this.dataMobileEditOnetimeProduct.setAttribute('aria-busy','true');
    this.dataMobileEditOnetimeProduct.classList.add('disabled');
    this.dataMobileEditOnetimeProduct.setAttribute('disabled', 'true');
    try{
      const response = await recharge.onetime.getOnetime(window.rechargeSession, this.purchaseItemId);
      addOnOneTimeEditDrawer.productObject = window.getProductById(productId);
      addOnOneTimeEditDrawer.subscriptionData = response;
      addOnOneTimeEditDrawer.purchaseItemId = event.target.getAttribute('data-purchase-item-id');
      addOnOneTimeEditDrawer.renderDataOnModal();
      addOnOneTimeEditDrawer.show();
    }
    catch(error) {
      console.log(error,"error");
    }
    finally {
      this.dataMobileEditOnetimeProduct.removeAttribute('aria-busy');
      this.dataMobileEditOnetimeProduct.classList.remove('disabled');
      this.dataMobileEditOnetimeProduct.removeAttribute('disabled');
    }
  }
  async onEditOnetimeProductClick(event) {
    event.preventDefault();
    if(this.subscriptionData == null){
      const response = await recharge.onetime.getOnetime(window.rechargeSession, this.purchaseItemId);
      this.subscriptionData = response;
    }

    const prouctId = this.dataEditOnetimeProduct.getAttribute('data-product-id');
    this.productObject = window.getProductById(prouctId);
    this.dataEditOnetimeProduct.setAttribute('aria-busy','true');
    this.dataEditOnetimeProduct.classList.add('disabled');
    this.dataEditOnetimeProduct.setAttribute('disabled', 'true');
    const addOnOneTimeEditDrawer = document.querySelector('add-on-one-time-edit-drawer');
    addOnOneTimeEditDrawer.productObject = this.productObject;
    addOnOneTimeEditDrawer.subscriptionData = this.subscriptionData;
    addOnOneTimeEditDrawer.purchaseItemId = this.purchaseItemId;
    addOnOneTimeEditDrawer.renderDataOnModal();
    addOnOneTimeEditDrawer.show();
  }
  renderProductDetails() {
    if(this.productObject){
      this.querySelector('[data-product-image]').src = this.productObject.featured_image;
      this.querySelector('[data-product-title]').textContent = this.productObject.title;
      this.querySelector('[data-product-price]').textContent = theme.Currency.formatMoney(this.productObject.price, theme.settings.moneyFormat);
      this.querySelector('[data-product-variant]').textContent = this.productObject.variants[0].title;
    }
  }
  async mobileRemoveOnetimeProductClick(event) {
    event.preventDefault();
    this.dataMobileRemoveOnetimeProduct.setAttribute('aria-busy','true');
    this.dataMobileRemoveOnetimeProduct.classList.add('disabled');
    this.dataMobileRemoveOnetimeProduct.setAttribute('disabled', 'true');
    try{
      await recharge.onetime.deleteOnetime(window.rechargeSession, this.purchaseItemId);
      document.dispatchEvent(new CustomEvent('update:subscriptionDashboard'));
    }
    catch(error) {
      console.log(error,"error");
    }
    finally {
      this.dataMobileRemoveOnetimeProduct.removeAttribute('aria-busy');
      this.dataMobileRemoveOnetimeProduct.classList.remove('disabled');
      this.dataMobileRemoveOnetimeProduct.removeAttribute('disabled');
    }
  }
  async onRemoveOnetimeProductClick(event) {
    try{
      this.dataRemoveOnetimeProduct.setAttribute('data-loading-close','true');
      this.dataRemoveOnetimeProduct.classList.add('disabled');
      this.dataRemoveOnetimeProduct.setAttribute('disabled', 'true');
      await recharge.onetime.deleteOnetime(window.rechargeSession, this.purchaseItemId);
      document.dispatchEvent(new CustomEvent('update:subscriptionDashboard'));
    }
    catch(error) {
      console.log(error,"error");
    }
    finally {
      this.dataRemoveOnetimeProduct.removeAttribute('data-loading-close');
      this.dataRemoveOnetimeProduct.classList.remove('disabled');
      this.dataRemoveOnetimeProduct.removeAttribute('disabled');
    }
    
  }
}
customElements.define('one-time-product-item', OneTimeProductItem);


// document.addEventListener('DOMContentLoaded',() => {
//   const defaultSwapPlaylist = document.querySelectorAll('[data-default-swap-playlist]');
//   defaultSwapPlaylist.forEach(item => {
//     item.addEventListener('click',async () => {
//       item.setAttribute('aria-busy', 'true');
//       item.classList.add('disabled');
//       item.setAttribute('disabled', 'true');
//       const nextSubscriptionOrder = document.querySelectorAll('next-subscription-order');
//       const nextSubscriptionFirstOrder = nextSubscriptionOrder[0];
//       console.log(nextSubscriptionFirstOrder,"nextSubscriptionFirstOrder");
//       const playlistHandle = item.getAttribute('data-playlist-handle');
//       const oldProperties = nextSubscriptionFirstOrder.subscription.properties;
//       const playlistData = window.playlistUtilities.getPlaylistData(playlistHandle);
     
//       if(nextSubscriptionFirstOrder){
//         const properties = nextSubscriptionFirstOrder.subscription.properties;
//         const filterCount = properties.find(item => item.name == 'filter')?.value;
//         const espressoCount = properties.find(item => item.name == 'espresso')?.value;
//         let newProperties = null;
//         if(properties.find(item => item.name == 'subscriptionData')){
//           const oldSubscriptionData = window.subscriptionHelper.rubyHashToObject(properties.find(item => item.name == 'subscriptionData').value);
//           newProperties = window.subscriptionHelper.updateProperties(oldSubscriptionData,{playlist_handle:playlistHandle,external_collection_id:playlistData.collection_id});
//         }
//         else{
//           newProperties = window.subscriptionHelper.updateProperties(oldProperties,{playlist_handle:playlistHandle,external_collection_id:playlistData.collection_id});
//         }
//         const bagCount = nextSubscriptionFirstOrder.subscription.include.bundle_selections.items_count;
        
//         try{
//         const {bundleSelections,bundleObject,playlistData,totalProductPrice,selectionProductListing,discountedPrice,bundleProduct,sellingPlan} = window.playlistUtilities.getStaticBundleSelection(playlistHandle,bagCount,{filter:filterCount,espresso:espressoCount},'4 week');
//         console.log(bundleSelections,"bundleSelections");
//           if(bundleSelections.length == 0){
//             item.removeAttribute('aria-busy');
//             item.classList.remove('disabled');
//             item.removeAttribute('disabled');
//             alert('No Product Found Based on the your selection');
//             return;
//           }
//           console.log(bundleObject,"bundleObject");
//           console.log(newProperties,"newProperties");
//           const rechargeBundleObject = window.playlistUtilities.convertBundleObjectToRechargeBundleObject(bundleObject);
//           await recharge.bundle.updateBundle(window.rechargeSession,nextSubscriptionFirstOrder.subscription.id,rechargeBundleObject,{ commit: true });
//           await recharge.subscription.updateSubscription(window.rechargeSession,nextSubscriptionFirstOrder.subscription.id,{properties:newProperties});
          
//           console.log(rechargeBundleObject,"rechargeBundleObject");
//           document.dispatchEvent(new CustomEvent('update:subscriptionDashboard'));
        
//         }
//         catch(error){
//           console.log(error,"error");
//         }
//         finally{
//           item.removeAttribute('aria-busy');
//           item.classList.remove('disabled');
//           item.removeAttribute('disabled');
//         }
//         //  await recharge.bundle.updateBundleSelection(window.rechargeSession,nextSubscriptionFirstOrder.subscription.include.bundle_selections.id,bundleSelections);
//         //  await recharge.subscription.updateSubscription(window.rechargeSession,nextSubscriptionFirstOrder.subscription.id,{properties:newProperties});
//         //  document.dispatchEvent(new CustomEvent('update:subscriptionDashboard'));
//       }
//     });
   
//   });
// });

document.addEventListener('DOMContentLoaded',() => {
  const getStartedButtons = document.querySelectorAll('[data-get-started-button]');
  if(getStartedButtons){
    getStartedButtons.forEach(item => {
      item.addEventListener('click',() => {
        const showMoreDrawer = item.closest('show-more-drawer');
        if(showMoreDrawer){
          showMoreDrawer.hide();
        }
        const playlistHandle = item.getAttribute('data-show-more-playlist-handle');
        const subscriptionModal = document.querySelector('subscription-modal');
        const subscriptionFlow = document.querySelector('subscription-flow');
        subscriptionModal.setAttribute('data-playlist-name',playlistHandle);
        subscriptionFlow.setAttribute('data-playlist-handle',playlistHandle);
        subscriptionFlow.currentPlaylistHandle = playlistHandle;
        const playlistData = window.playlistUtilities.getPlaylistData(playlistHandle);
        subscriptionFlow.setAttribute('data-collection-id',playlistData.collection_id);
        subscriptionModal.show();
      });
    });
  }
  const showMoreSampleTrigger = document.querySelectorAll('[data-show-more-sample-trigger]');
  if(showMoreSampleTrigger){
    showMoreSampleTrigger.forEach(item => {
      item.addEventListener('click',() => {
        const showMoreDrawer = item.closest('show-more-drawer');
        if(showMoreDrawer){
          showMoreDrawer.hide();
        }
        const sampleProductDrawer = document.querySelector('#Sample-Product-Common-Drawer-version-2');
        sampleProductDrawer.show()
      });
    });
  }

  
});

class OneTimeQuantityInput extends HTMLElement {
  quantityUpdateUnsubscriber = undefined;
  quantityBoundriesUnsubscriber = undefined;
  quantityRulesUnsubscriber = undefined;

  constructor() {
    super();
  }

  get sectionId() {
    return this.getAttribute('data-section-id');
  }

  get productId() {
    return this.getAttribute('data-product-id');
  }

  get input() {
    return this.querySelector('input');
  }

  get value() {
    return this.input.value;
  }

  init() {
    this.abortController = new AbortController();

    this.buttons = Array.from(this.querySelectorAll('button'));
    this.changeEvent = new Event('change', { bubbles: true });
    this.input.addEventListener('change', this.onInputChange.bind(this));
    this.input.addEventListener('focus', () => setTimeout(() => this.input.select()));

    this.buttons.forEach((button) => button.addEventListener('click', this.onButtonClick.bind(this)), { signal: this.abortController.signal });

    this.validateQtyRules();
    this.quantityUpdateUnsubscriber = theme.pubsub.subscribe(theme.pubsub.PUB_SUB_EVENTS.quantityUpdate, this.validateQtyRules.bind(this));
    this.quantityBoundriesUnsubscriber = theme.pubsub.subscribe(theme.pubsub.PUB_SUB_EVENTS.quantityBoundries, this.setQuantityBoundries.bind(this));
    this.quantityRulesUnsubscriber = theme.pubsub.subscribe(theme.pubsub.PUB_SUB_EVENTS.quantityRules, this.updateQuantityRules.bind(this));
  }
  connectedCallback() {
    document.addEventListener('DOMContentLoaded', this.init.bind(this));
  }

  disconnectedCallback() {
    this.abortController.abort();

    if (this.quantityUpdateUnsubscriber) {
      this.quantityUpdateUnsubscriber();
    }
    if (this.quantityBoundriesUnsubscriber) {
      this.quantityBoundriesUnsubscriber();
    }
    if (this.quantityRulesUnsubscriber) {
      this.quantityRulesUnsubscriber();
    }
  }

  onButtonClick(event) {
    event.preventDefault();
    const previousValue = this.input.value;

    if (event.currentTarget.name === 'plus') {
      if (parseInt(this.input.getAttribute('data-min')) > parseInt(this.input.step) && this.input.value == 0) {
        this.input.value = this.input.getAttribute('data-min');
      }
      else {
        this.input.stepUp();
      }
    } else {
      this.input.stepDown();
    }

    if (previousValue !== this.input.value) this.input.dispatchEvent(this.changeEvent);

    if (this.input.getAttribute('data-min') === previousValue && event.currentTarget.name === 'minus') {
      this.input.value = parseInt(this.input.min);
    }
  }

  onInputChange() {
    if (this.input.value === '') {
      this.input.value = parseInt(this.input.min);
    }
    this.validateQtyRules();
  }

  validateQtyRules() {
    const value = parseInt(this.input.value);
    if (this.input.min) {
      const buttonMinus = this.querySelector('button[name="minus"]');
      if (buttonMinus) buttonMinus.toggleAttribute('disabled', parseInt(value) <= parseInt(this.input.min));
    }
    if (this.input.max) {
      const buttonPlus = this.querySelector('button[name="plus"]');
      if (buttonPlus) buttonPlus.toggleAttribute('disabled', parseInt(value) >= parseInt(this.input.max));
    }
  }

  updateQuantityRules({ data: { sectionId, productId, parsedHTML } }) {
    if (sectionId !== this.sectionId || productId !== this.productId) return;

    const selectors = ['.quantity__input', '.quantity__rules', '.quantity__label'];
    const quantityFormUpdated = parsedHTML.getElementById(`QuantityForm-${sectionId}-${this.productId}`);
    const quantityForm = this.closest(`#QuantityForm-${sectionId}-${this.productId}`);
    for (let selector of selectors) {
      const current = quantityForm.querySelector(selector);
      const updated = quantityFormUpdated.querySelector(selector);
      if (!current || !updated) continue;

      if (selector === '.quantity__input') {
        const attributes = ['data-cart-quantity', 'data-min', 'data-max', 'step'];
        for (let attribute of attributes) {
          const valueUpdated = updated.getAttribute(attribute);
          if (valueUpdated !== null) {
            current.setAttribute(attribute, valueUpdated);
          }
          else {
            current.removeAttribute(attribute);
          }
        }
      }
      else {
        current.innerHTML = updated.innerHTML;
      }
    }
  }

  setQuantityBoundries({ data: { sectionId, productId } }) {
    if (sectionId !== this.sectionId || productId !== this.productId) return;

    const data = {
      cartQuantity: this.input.hasAttribute('data-cart-quantity') ? parseInt(this.input.getAttribute('data-cart-quantity')) : 0,
      min: this.input.hasAttribute('data-min') ? parseInt(this.input.getAttribute('data-min')) : 1,
      max: this.input.hasAttribute('data-max') ? parseInt(this.input.getAttribute('data-max')) : null,
      step: this.input.hasAttribute('step') ? parseInt(this.input.getAttribute('step')) : 1
    };

    let min = data.min;
    const max = data.max === null ? data.max : data.max - data.cartQuantity;
    if (max !== null) min = Math.min(min, max);
    if (data.cartQuantity >= data.min) min = Math.min(min, data.step);

    this.input.min = min;

    if (max) {
      this.input.max = max;
    }
    else {
      this.input.removeAttribute('max');
    }
    this.input.value = min;

    theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.quantityUpdate, undefined);
  }

  reset() {
    this.input.value = this.input.defaultValue;
  }
}
customElements.define('one-time-quantity-input', OneTimeQuantityInput);

document.addEventListener('DOMContentLoaded',() => {
let is_ios = /(iPad|iPhone|iPod)/g.test(navigator.userAgent);
  if (is_ios) {
    document.body.classList.add('ios-device');
  }
});

class ToastNotification extends HTMLElement {
  constructor() {
    super();

    this.container = this.querySelector('.snotifyToast');
    this.closeBtn = this.querySelector('.snotifyToast__close');
    this.animateNotifcation = null;

    // Set ARIA role for accessibility
    this.container.setAttribute('role', 'alert');
  }

  connectedCallback() {
    if (this.closeBtn) {
      this.closeBtn.addEventListener('click', (e) => {
        e.preventDefault();
        this.hide();
      });
    }
  }

  disconnectedCallback() {
    if (this.closeBtn) {
      this.closeBtn.removeEventListener('click', this.hide.bind(this));
    }
  }

  /**
   * Notification Type
   * @param {string} title 
   * @param {string} body 
   * @param {object} options 
   */
  updateNotification(title, body, options){
    this.querySelector('.snotifyToast__title').textContent = title;
    this.querySelector('.snotifyToast__body').textContent = body;

    this.container.classList.add('open', `snotify-${options.type}`);
    this._manageToggle(options.timeout);
  }

  /**
   * Duration of notification to hide again
   * @param timeout 
   */
  _manageToggle(timeout) {
    this.progressBar = this.querySelector('[data-toast-pr]');
    const totalFrames = Math.ceil(timeout / 16.67) / 2;
    let frame =0; 
    let width = 0;

    this.animateNotifcation = () => {
      width = (frame / totalFrames) * 100;
      this.progressBar.setAttribute('style', `width: ${width}%`);
      if (frame < totalFrames) {
        frame++;
        requestAnimationFrame(this.animateNotifcation);
      } else {
        this.closeBtn.dispatchEvent(new Event('click'));
      }
    };
    requestAnimationFrame(this.animateNotifcation);
  }

  hide() {
    if (this.animateNotification) {
      cancelAnimationFrame(this.animateNotification);
    }
    this.container.className = 'snotifyToast';
    [this.querySelector('.snotifyToast__title'),this.querySelector('.snotifyToast__body')].forEach(ele => ele.textContent = '');
    this.querySelector("[data-toast-pr]").removeAttribute("style");
  }
}
customElements.define("toast-notification", ToastNotification);
window.notificationEle = document.querySelector('toast-notification');

document.addEventListener('DOMContentLoaded',() => {
  const triggerSubscriptionFlowButtons = document.querySelectorAll('[data-trigger-subscription-flow]');
  if(triggerSubscriptionFlowButtons){
    triggerSubscriptionFlowButtons.forEach(item => {
      item.addEventListener('click',(e) => {
        e.preventDefault();
        const targetSectionId = item.getAttribute('data-trigger-subscription-flow');
          if(targetSectionId){
            const targetSection = document.querySelector(`[data-scroll-section-id="${targetSectionId}"]`);
            if(targetSection){
              targetSection.scrollIntoView({ behavior: 'smooth' });
            }
          }
      });
    });
  }
});

function lockBodyScroll() {
  console.log("lockBodyScroll");
  if(document.body.classList.contains('ios-device')){
    const scrollY = window.scrollY;
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollY}px`;
    document.body.style.width = '100%';
    document.body.dataset.scrollY = scrollY; // store scroll position
  }
}

function unlockBodyScroll() {
  console.log("unlockBodyScroll"); 
  if(document.body.classList.contains('ios-device')){
    const scrollY = parseInt(document.body.dataset.scrollY || '0', 10);
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.width = '';
    window.scrollTo(0, scrollY);
  }
}

class SwapPlaylistSingle extends HTMLElement {
  constructor() {
    super();
  }
  connectedCallback() {
    this.swapButtons = this.querySelectorAll('[data-default-swap-playlist]');
    this.swapButtons.forEach(item => {
      item.addEventListener('click',async () => {
        if(item.classList.contains('subscribe-to-playlist')){
          const playlistHandle = item.getAttribute('data-playlist-handle');
          this.playlistHandle = playlistHandle;
          item.setAttribute('disabled', true);
          item.setAttribute('aria-disabled', true);
          item.setAttribute('aria-busy', true);
          await this.subscribeToPlaylist(playlistHandle);
          item.setAttribute('aria-busy', false);
          item.setAttribute('aria-disabled', false);
          item.removeAttribute('disabled');
          return;
        }
        else{
          const playlistHandle = item.getAttribute('data-playlist-handle');
           this.playlistHandle = playlistHandle;
          item.setAttribute('disabled', true);
          item.setAttribute('aria-disabled', true);
          item.setAttribute('aria-busy', true);
          await this.swapPlaylist();
          item.setAttribute('aria-busy', false);
          item.setAttribute('aria-disabled', false);
          item.removeAttribute('disabled');
        }
      });
    });
  }

  async subscribeToPlaylist(playlistHandle){
    const subscriptionModal = document.querySelector('subscription-modal');
    const subscriptionFlow = document.querySelector('subscription-flow');
    subscriptionFlow.setAttribute('data-playlist-handle',playlistHandle);
    subscriptionFlow.currentPlaylistHandle = playlistHandle;
    subscriptionFlow.verticalSlider.updateSlideMatrics();
    subscriptionFlow.prepareSummary();
    subscriptionModal.setAttribute('data-playlist-name',playlistHandle);
    subscriptionModal.show();
    return;
  }

  async swapPlaylist(){
    const subscriptionProperties = this.getNewProperties();
    try{
    
      const quantity = this.bundleSelections.reduce((acc,item) => acc + item.quantity, 0);
      if(quantity < parseInt(this.subscriptionData.include.bundle_selections.items_count)){
        window.notificationEle.updateNotification('Swap Playlist', 'No products found based on your selection', {
            type: 'info',
            timeout: 10000
        });         
        return;
      }
      let grindPreferenceArray = [];
      let products_sku = [];
      const grindEspressoValue = subscriptionProperties.find(property => property.name == 'grind_espresso')?.value;
      const grindFilterValue = subscriptionProperties.find(property => property.name == 'grind_filter')?.value;
      const noDecaf = subscriptionProperties.find(property => property.name == 'decaf')?.value || false;
      this.selectionProductListing.forEach(item => {
        if(item.tags.includes('espresso')){
          const variant = item.variants.find(variant => (variant.title == "300g" || variant.title == "300G" || variant.title == "300 G" || variant.title == "300 g"));
          grindPreferenceArray.push({variantId:variant.id,grind_Value:grindEspressoValue});
          if(variant.sku){
            products_sku.push({sku:variant.sku,grindType:grindEspressoValue});
          }
        }
        else{
          const variant = item.variants.find(variant => (variant.title == "300g" || variant.title == "300G" || variant.title == "300 G" || variant.title == "300 g"));
          grindPreferenceArray.push({variantId:variant.id,grind_Value:grindFilterValue});
          if(variant.sku){
            products_sku.push({sku:variant.sku,grindType:grindFilterValue});
          }
        }
      });

      const UpdateNewProperties =  window.subscriptionHelper.updateProperties(subscriptionProperties, {external_collection_id: String(this.externalCollectionId), external_variant_id: String(this.bundleObject.externalVariantId), playlist_handle: this.playlistHandle,grind_preference_array: JSON.stringify(grindPreferenceArray),products_sku: JSON.stringify(products_sku)});
      console.log(UpdateNewProperties,"UpdateNewProperties");
      const rechargeBundleObject = window.playlistUtilities.convertBundleObjectToRechargeBundleObject(this.bundleObject);
      console.log(rechargeBundleObject,"rechargeBundleObject");
      const isValid = await recharge.bundle.validateBundle(rechargeBundleObject);
      if(!isValid){
        window.notificationEle.updateNotification('Swap Playlist', 'Invalid Bundle', {
            type: 'info',
            timeout: 10000
        });         
        return;
      }
      const response = await recharge.bundle.updateBundle(window.rechargeSession,this.subscriptionData.id,rechargeBundleObject,{ order_interval_frequency: parseInt(this.subscriptionData.order_interval_frequency) },{ properties: UpdateNewProperties },{ commit: true });
      const response2 = await recharge.subscription.updateSubscription(window.rechargeSession, this.subscriptionData.id, { properties: UpdateNewProperties },{ commit: true });
      await recharge.address.updateAddress(window.rechargeSession, this.subscriptionData.address_id, {
        order_attributes:
        [{name:'_subscriptionData',value:JSON.stringify(UpdateNewProperties)}]
      }); 
      
      document.dispatchEvent(new CustomEvent('update:subscriptionDashboard'));
    }
    catch(error){
      console.error(error);
    }
  }

  getNewProperties(){
    const subscriptionData = this.subscriptionData;
    let subscriptionProperties = null;
    subscriptionData.properties.find((item)=>{
      if(item.name == '_subscriptionData'){
        subscriptionProperties = JSON.parse(item.value);
      }
    });
    if(subscriptionProperties == null){
      subscriptionProperties = subscriptionData.properties;
    }
    return subscriptionProperties;
  }

  renderSwapPlaylistdata(){
    let espressoCount = this.espressoCount;
    let filterCount = this.filterCount;
    let totalCount = this.totalCount;
    const noDecaf = this.subscriptionData.properties.find(property => property.name == 'decaf')?.value || false;
    if(filterCount == 0 && espressoCount == 0){
      const {filter,espresso} = window.subscriptionHelper.getFilterAndEspressoCountFromSubscription(this.subscriptionData);
      filterCount = filter;
      espressoCount = espresso;
      totalCount = filterCount + espressoCount;
    }
    const frequency = this.frequency;
    const playlistHandle = this.getAttribute('data-playlist-name');
    this.playlistItem = this;
    if(playlistHandle == 'mixtape'){
    const {bundleSelections,bundleObject,playlistData,totalProductPrice,selectionProductListing,discountedPrice,bundleProduct,sellingPlan,externalCollectionId} = window.playlistUtilities.getDynamicBundleSelection(playlistHandle,String(totalCount),{espresso:espressoCount,filter:filterCount},`${frequency} week`,noDecaf);
      this.bundleSelections = bundleSelections;
      this.bundleObject = bundleObject;
      this.playlistData = playlistData;
      this.totalProductPrice = totalProductPrice;
      this.selectionProductListing = selectionProductListing;
      this.discountedPrice = discountedPrice;
      this.bundleProduct = bundleProduct;
      this.sellingPlan = sellingPlan;
      this.externalCollectionId = externalCollectionId;
      this.updateDiscoverCoffeesHTML(this.playlistItem,bundleSelections,bundleObject,playlistData,totalProductPrice,selectionProductListing,discountedPrice,bundleProduct,sellingPlan,frequency,this.subscriptionData);
    }
    else{
      const {bundleSelections,bundleObject,playlistData,totalProductPrice,selectionProductListing,discountedPrice,bundleProduct,sellingPlan,externalCollectionId} = window.playlistUtilities.getStaticBundleSelection(playlistHandle,String(totalCount),{espresso:espressoCount,filter:filterCount},`${frequency} week`);
      this.bundleSelections = bundleSelections;
      this.bundleObject = bundleObject;
      this.playlistData = playlistData;
      this.totalProductPrice = totalProductPrice;
      this.selectionProductListing = selectionProductListing;
      this.discountedPrice = discountedPrice;
      this.bundleProduct = bundleProduct;
      this.sellingPlan = sellingPlan;
      this.externalCollectionId = externalCollectionId;
      this.updateDiscoverCoffeesHTML(this.playlistItem,bundleSelections,bundleObject,playlistData,totalProductPrice,selectionProductListing,discountedPrice,bundleProduct,sellingPlan,frequency,this.subscriptionData);
    }
    this.initOnetimeEvents();
  }
  initOnetimeEvents(){
    this.querySelectorAll('[data-add-onetime-product]').forEach(item => {
      item.addEventListener('click',() => {
        const productId = item.getAttribute('data-product-id');
        const productObject = window.playlistUtilities.getProductObjectById(productId);
        const oneTimeProductDrawer = document.querySelector('one-time-product-drawer');
        oneTimeProductDrawer.productObject = productObject;
        oneTimeProductDrawer.subscriptionData = this.subscriptionData;
        oneTimeProductDrawer.renderProductDetails();
        oneTimeProductDrawer.show();
      });
    });
  }

   updateDiscoverCoffeesHTML(playlistItem,bundleSelections,bundleObject,playlistData,totalProductPrice,selectionProductListing,discountedPrice,bundleProduct,sellingPlan,frequency,subscriptionData) {
    if(playlistItem){
      playlistItem.querySelectorAll('[data-discounted-price]').forEach(item => {
        item.innerHTML = theme.Currency.formatMoney(parseInt(discountedPrice), theme.settings.moneyFormat);
      });
      playlistItem.querySelectorAll('[data-playlist-frequency]').forEach(item => {
        item.textContent = subscriptionData.order_interval_frequency;
      });
      playlistItem.querySelector('[data-playlist-carousel]').innerHTML = '';
      selectionProductListing.forEach(item => {
        playlistItem.querySelector('[data-playlist-carousel]').innerHTML += this.createProductHTML(item);
      });
    }
  }
  createProductHTML(item) {
    return `
      <div class="account-dashboard-product-content cursor-pointer" data-add-onetime-product data-product-id="${item.id}">
          <img src="${item.featured_image}?width=250" alt="${item.title}" height="auto" width="auto" class="mt-0 mb-2 w-full">
          <div class="text-sm font-bold leading-tight tracking-none heading mb-1"> ${item.title} </div>
          <p class="text-xs mt-0 mb-0"> ${item.variants.find(variant => variant.title == "300g" || variant.title == "300G" || variant.title == "300 G" || variant.title == "300 g").title} </p>
      </div>
    `;
  }
}
customElements.define('swap-playlist-single', SwapPlaylistSingle);

document.addEventListener('DOMContentLoaded',() => {
  const tryASampleButtons = document.querySelectorAll('[data-profile-playlist-try-a-sample]');
  if(tryASampleButtons){
    tryASampleButtons.forEach(item => {
      item.addEventListener('click',() => {
        const sampleProductDrawer = document.querySelector('#Sample-Product-Common-Drawer-version-2');
        if(sampleProductDrawer){
          sampleProductDrawer.show();
        }
      });
    });
  }
});

class ScheduleNextOrderDrawer extends DrawerElement {
  constructor() {
    super();
    this.saveScheduleButton = this.querySelector('[schedule-next-order-button]');
    this.footer = this.querySelector('.drawer__footer');
  }
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener('DOMContentLoaded',() => {
      this.saveScheduleButton.addEventListener('click',async () => {
        await this.saveSchedule();
      });
    });
  }
  
  async saveSchedule(){
    this.saveScheduleButton.setAttribute('aria-busy', 'true');
    this.saveScheduleButton.classList.add('disabled');
    this.saveScheduleButton.setAttribute('disabled', 'true');

    try { 
      const response = await recharge.subscription.updateSubscriptionChargeDate(window.rechargeSession, this.subscriptionData.id, this.selectedDate,{commit: true});
      let oneTimeProducts = this.chargeData.line_items.filter(item => item.purchase_item_type == 'onetime').map(item => {
        return {
         purchase_item_id: parseInt(item.purchase_item_id),
         scheduled_at: this.selectedDate
        }
      });
      console.log(oneTimeProducts,"oneTimeProducts");
      console.log(this.selectedDate,"this.selectedDate");
      const response2 = await this.updateAllOneTimeProductsScheduledOrders(oneTimeProducts)
      console.log(response,"response");
      console.log(response2,"response2");
      this.saveScheduleButton.removeAttribute('aria-busy');
      this.saveScheduleButton.classList.remove('disabled');
      this.saveScheduleButton.removeAttribute('disabled');
      this.hide();
      document.dispatchEvent(new CustomEvent('update:subscriptionDashboard'));
    }
    catch(error){
      console.error(error);
      this.saveScheduleButton.removeAttribute('aria-busy');
      this.saveScheduleButton.classList.remove('disabled');
      this.saveScheduleButton.removeAttribute('disabled');
      window.notificationEle.updateNotification('Schedule Next Order', error.message, {
        type: 'error',
        timeout: 10000
      });
    }
    finally{
      this.saveScheduleButton.removeAttribute('aria-busy');
      this.saveScheduleButton.classList.remove('disabled');
      this.saveScheduleButton.removeAttribute('disabled');
    }

  }
  async updateAllOneTimeProductsScheduledOrders(items) {
    if (!Array.isArray(items) || items.length === 0) return;
    let promises = [];
    for(let item of items){
      const { purchase_item_id, scheduled_at } = item;
      promises.push(await recharge.onetime.updateOnetime(window.rechargeSession, purchase_item_id, {
        next_charge_scheduled_at: scheduled_at,
      },{commit: true}));
    }
  
    try {
      const results = await Promise.all(promises);
      console.log('✅ All updates completed successfully:', results);
      return results;
    } catch (error) {
      console.error('❌ One or more updates failed:', error);
      return null;
    }
  }
  renderData(){
    const disabledDate = this.chargeData.scheduled_at;
    const dateInput = this.querySelector('#schedule-datepicker');
    // Initialize datepicker
    this.datepicker = new Datepicker(dateInput, {
      minDate: new Date(new Date().setDate(new Date().getDate() + 1)),
      maxDate: new Date(new Date().setFullYear(new Date().getFullYear() + 1)),
      format: 'yyyy-mm-dd', // note: option name is 'format', not 'dateFormat'
      autohide: true,
      disabledDates: [new Date(disabledDate)],
    });

    // Listen for date selection event
    dateInput.addEventListener('changeDate', (event) => {
      const selectedDate = event.detail.date; // JS Date object
      this.saveScheduleButton.disabled = false;
      this.selectedDate = Utility.formatDate(selectedDate,"YYYY-MM-DD");
    });
  }
  beforeShow() {
    super.beforeShow();
    setTimeout(() => {
    }, 300);
  }
  afterHide() {
    super.afterHide();
    setTimeout(() => {
      this.datepicker.destroy();
    }, 300);
  }

  beforeHide() {
    super.beforeHide();

    setTimeout(() => {
    }, 300);
  }
}
customElements.define('schedule-next-order-drawer', ScheduleNextOrderDrawer);


class SwapeCuratedPlaylist extends HTMLElement {
  constructor() {
    super();
    this.playlistHandle = this.getAttribute('data-curated-playlist');
    this.querySelectorAll('[data-swape-curated-playlist]').forEach(item => {
      item.addEventListener('click',async () => {
        item.setAttribute('aria-busy', 'true');
        item.classList.add('disabled');
        item.setAttribute('disabled', 'true');
        await this.swapCuratedPlaylist();
        item.removeAttribute('aria-busy');
        item.classList.remove('disabled');
        item.removeAttribute('disabled');
      });
    });
  }
  connectedCallback() {
  
  }
  async swapCuratedPlaylist(){
    console.log("clicked")
    try{
      console.log(this.bundleSelections.length,"this.bundleSelections.length");
      console.log(this.subscriptionData.include.bundle_selections.items_count,"this.subscriptionData.include.bundle_selections.items_count");
      if(this.bundleSelections.length < this.subscriptionData.include.bundle_selections.items_count){
        window.notificationEle.updateNotification('Swap Playlist', 'No products found based on your selection', {
          type: 'info',
          timeout: 10000
        });
        return;
      }
      let properties = this.subscriptionData.properties;
      properties.find((item)=>{
        if(item.name == '_subscriptionData'){
          properties = JSON.parse(item.value);
        }
      });
      if(properties == null){
        properties = this.subscriptionData.properties;
      }
      const oldProperties = properties;
      const UpdateNewProperties = window.subscriptionHelper.updateOrAddProperties(oldProperties,{playlist_handle: this.playlistHandle,seasonal_order: "true"});
      console.log(oldProperties,"oldProperties"); 
      console.log(UpdateNewProperties,"UpdateNewProperties");
      const isValid = await recharge.bundle.validateBundle(this.rechargeBundleObject);
      if(!isValid){
        window.notificationEle.updateNotification('Swap Playlist', 'Invalid Bundle', {
            type: 'info',
            timeout: 10000
        });         
        return;
      }
      const rechargeObject = window.playlistUtilities.convertBundleObjectToRechargeBundleObject(this.bundleObject);
      const response = await recharge.bundle.updateBundle(window.rechargeSession,this.subscriptionData.id,rechargeObject,{ order_interval_frequency: parseInt(this.subscriptionData.order_interval_frequency) },{ properties: UpdateNewProperties },{ commit: true });
      const response2 = await recharge.subscription.updateSubscription(window.rechargeSession, this.subscriptionData.id, { properties: UpdateNewProperties },{ commit: true });
      await recharge.address.updateAddress(window.rechargeSession, this.subscriptionData.address_id, {
        order_attributes:
        [{name:'_subscriptionData',value:JSON.stringify(UpdateNewProperties)}]
      }); 
      document.dispatchEvent(new CustomEvent('update:subscriptionDashboard'));
    }
    catch(error){
      console.error(error);
    }
  }

  getCuratedPlaylistData(){
    playlist_handle
  }
}
customElements.define('swape-curated-single', SwapeCuratedPlaylist);

class SinglePrepaidSubscription extends HTMLElement {
  constructor() {
    super();
    this.subscriptionId = this.getAttribute('data-subscription-id');
  }
  connectedCallback() {
    this.prepaidChargeData();
  }
  async prepaidChargeData() {
    const rechargeResponse = await recharge.order.listOrders(window.rechargeSession,{status:'queued',sort_by:'scheduled_at-asc',purchase_item_id:this.subscriptionId})
    const upcomingOrders = rechargeResponse.orders;
    if(upcomingOrders.length > 0){
      this.upcomingOrders = upcomingOrders;
      this.renderData();
    }
  }
  renderData() {
   if(this.querySelector('[data-next-charge-data]')){
    this.querySelector('[data-next-charge-data]').innerHTML = `Next order ${Utility.formatDate(this.upcomingOrders[0].scheduled_at,"MMM DD, YYYY")}`;
   }
  }

}
customElements.define('single-prepaid-subscription', SinglePrepaidSubscription);